/* ── src/constants.js ── */
var CONFIG           = window.OA_CONFIG;
var LOGIN_URL        = CONFIG.BASE_URL + '/rpc/login_with_email';
var GOOGLE_LOGIN_URL = CONFIG.BASE_URL + '/rpc/login_with_google';
var APP_AUTH_KEY     = CONFIG.APP_AUTH_KEY;
var APP_ID           = CONFIG.APP_ID;
var GOOGLE_CLIENT_ID = CONFIG.GOOGLE_CLIENT_ID;

// ── Session ─────────────────────────────────────────────────────────────
// The JWT and its expiry (ms since epoch) are kept in localStorage, so a reload
// or a new tab goes straight into the app until the token expires.
var SESSION_EXPIRED_EVENT = 'oa:session-expired';

// Expiry from the token's own `exp` claim, for a session saved without jwt_expiry.
function jwtExpiryMs(token) {
  try {
    var payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch(e) { return 0; }
}

// data is a login_with_email / login_with_google response.
function saveSession(data, loginName) {
  var expiryMs = data.jwt_expiry ? Number(data.jwt_expiry) * 1000 : jwtExpiryMs(data.jwt_token);
  localStorage.setItem('last_login_name', loginName);
  localStorage.setItem('jwt_token', data.jwt_token);
  localStorage.setItem('jwt_expiry', String(expiryMs));
  localStorage.setItem('api_url', data.api_url);
}

function sessionExpiryMs() {
  var token = localStorage.getItem('jwt_token');
  if (!token) return 0;
  return Number(localStorage.getItem('jwt_expiry')) || jwtExpiryMs(token);
}

function hasValidSession() {
  return sessionExpiryMs() > Date.now() + 60 * 1000; // a minute's margin for clock drift
}

// POST to an authenticated RPC on the account's API server. Throws with the
// server's message (PostgREST puts RAISE EXCEPTION text in .message) on failure.
// A 401 means the token was rejected, so the app is told to go back to login.
async function callRpc(name, body) {
  var res = await fetch('https://' + localStorage.getItem('api_url') + '/rpc/' + name, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('jwt_token') },
    body: JSON.stringify(body || {}),
  });
  var data = await res.json().catch(function() { return null; });
  if (res.status === 401) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  if (!res.ok) throw new Error((data && data.message) || ('Request failed (' + res.status + ')'));
  return data;
}

// A stored file (task picture, signature, …) as a blob: URL, via files_get, which
// returns the bytes. Cached for the page's life; a failed fetch is not cached.
var fileUrlCache = {};
function fetchFileUrl(fileName) {
  if (!fileUrlCache[fileName]) {
    fileUrlCache[fileName] = fetch('https://' + localStorage.getItem('api_url') + '/rpc/files_get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': '*/*', 'Authorization': 'Bearer ' + localStorage.getItem('jwt_token') },
      body: JSON.stringify({ file_name: fileName }),
    })
      .then(function(r) { if (!r.ok) throw new Error('File not available (' + r.status + ')'); return r.blob(); })
      .then(function(b) { return URL.createObjectURL(b); })
      .catch(function(err) { delete fileUrlCache[fileName]; throw err; });
  }
  return fileUrlCache[fileName];
}

// ── Operator photos, kept in localStorage ──────────────────────────────
// operators_get returns operator_profile_pic_ts (when the photo last changed). A stored copy
// that is at least that new is shown straight away — no download and no initials-then-photo
// flicker; anything older (or missing) is fetched once with files_get and stored again.
// Entries: 'opic:<file name>' → {"ts": "<ISO>", "url": "data:image/…"}. Cleared on logout.
var OP_PHOTO_PREFIX = 'opic:';

function cachedOperatorPhoto(fileName, ts) {
  if (!fileName) return null;
  try {
    var e = JSON.parse(localStorage.getItem(OP_PHOTO_PREFIX + fileName) || 'null');
    if (!e || !e.url) return null;
    if (ts && new Date(ts).getTime() > new Date(e.ts).getTime()) return null;   // changed since stored
    return e.url;
  } catch (err) { return null; }
}

function storeOperatorPhoto(fileName, ts, dataUrl) {
  var entry = JSON.stringify({ ts: ts || new Date().toISOString(), url: dataUrl });
  try { localStorage.setItem(OP_PHOTO_PREFIX + fileName, entry); }
  catch (err) {
    // Storage full: drop the other stored photos and try once more; failing that, just don't keep it.
    try {
      Object.keys(localStorage).forEach(function(k) { if (k.indexOf(OP_PHOTO_PREFIX) === 0) localStorage.removeItem(k); });
      localStorage.setItem(OP_PHOTO_PREFIX + fileName, entry);
    } catch (err2) {}
  }
}

function blobToDataUrl(blob) {
  return new Promise(function(resolve, reject) {
    var r = new FileReader();
    r.onload = function() { resolve(r.result); };
    r.onerror = function() { reject(r.error); };
    r.readAsDataURL(blob);
  });
}

var opPhotoLoading = {};
function loadOperatorPhoto(fileName, ts) {
  var hit = cachedOperatorPhoto(fileName, ts);
  if (hit) return Promise.resolve(hit);
  var key = fileName + '|' + (ts || '');
  if (!opPhotoLoading[key]) {
    opPhotoLoading[key] = fetch('https://' + localStorage.getItem('api_url') + '/rpc/files_get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': '*/*', 'Authorization': 'Bearer ' + localStorage.getItem('jwt_token') },
      body: JSON.stringify({ file_name: fileName }),
    })
      .then(function(r) { if (!r.ok) throw new Error('File not available (' + r.status + ')'); return r.blob(); })
      .then(blobToDataUrl)
      .then(function(url) { storeOperatorPhoto(fileName, ts, url); return url; })
      .finally(function() { delete opPhotoLoading[key]; });
  }
  return opPhotoLoading[key];
}

// Store a file in the account's private storage, as the mobile app does
// (file_uploader.dart): the bytes as the body, the name in x-file-name.
// Names are <APPLICATION>_<index>_<uid>.<ext>, e.g. OPERATOR_PIC_0_<operator id>.jpg.
// Resolves to { file_name, file_id }. Re-uploading a name replaces the file.
function uploadFile(blob, fileName) {
  var safe = fileName.replace(/[^A-Za-z0-9._-]/g, '_').slice(-200);
  return fetch('https://' + localStorage.getItem('api_url') + '/rpc/files_upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream', 'x-file-name': safe, 'Authorization': 'Bearer ' + localStorage.getItem('jwt_token') },
    body: blob,
  })
    .then(function(r) { return r.json().catch(function() { return null; }).then(function(data) {
      if (!r.ok) throw new Error((data && data.message) || ('Upload failed (' + r.status + ')'));
      delete fileUrlCache[safe];
      return Array.isArray(data) ? data[0] : data;
    }); });
}

// An image file scaled down to fit maxSize×maxSize, as a JPEG blob — phone photos
// are several MB and an avatar never needs more than this.
function shrinkImage(file, maxSize) {
  return new Promise(function(resolve, reject) {
    var img = new Image();
    var url = URL.createObjectURL(file);
    img.onload = function() {
      var scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      var canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(function(b) { b ? resolve(b) : reject(new Error('Could not read the image')); }, 'image/jpeg', 0.88);
    };
    img.onerror = function() { URL.revokeObjectURL(url); reject(new Error('That file is not an image')); };
    img.src = url;
  });
}

// "just now", "5 min ago", "3 hours ago", "2 days ago", "4 months ago"
function timeAgo(ts) {
  if (!ts) return null;
  var s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  var plural = function(n, unit) { n = Math.floor(n); return n + ' ' + unit + (n === 1 ? '' : 's') + ' ago'; };
  if (s < 60)        return 'just now';
  if (s < 3600)      return Math.floor(s / 60) + ' min ago';
  if (s < 86400)     return plural(s / 3600, 'hour');
  if (s < 86400 * 30) return plural(s / 86400, 'day');
  if (s < 86400 * 365) return plural(s / (86400 * 30), 'month');
  return plural(s / (86400 * 365), 'year');
}

// ── Operator avatars (Operators page, Dashboard) ────────────────────────
var OP_AVATAR_COLORS = ['#7c3aed','#059669','#d97706','#2563eb','#db2777','#0891b2','#9333ea','#dc2626','#0d9488','#b45309'];

// operator_id is a uuid, so take a stable colour from its first 8 hex digits.
function operatorColor(id) {
  var n = parseInt(String(id || '0').replace(/-/g, '').slice(0, 8), 16) || 0;
  return OP_AVATAR_COLORS[n % OP_AVATAR_COLORS.length];
}

function operatorInitials(name) {
  return (name || '?').trim().split(/\s+/).map(function(w) { return w[0]; }).slice(0, 2).join('').toUpperCase();
}

// The first call of every session: straight after sign-in, and on opening the app with a
// saved token (App waits for it before showing a page). It records this browser and, for a
// new account with no operators, adds the demo data (ops.demo_seed). The profile it
// returns is kept in localStorage.
async function callLoginCompleted(jwtToken) {
  var off = -new Date().getTimezoneOffset();   // minutes east of UTC
  var tz = (off >= 0 ? '+' : '-') + String(Math.floor(Math.abs(off) / 60)).padStart(2, '0') + (Math.abs(off) % 60 ? ':' + String(Math.abs(off) % 60).padStart(2, '0') : '');
  var ua = navigator.userAgent || '';
  var browser = (/Edg\//.test(ua) && 'Edge') || (/Chrome\//.test(ua) && 'Chrome') || (/Firefox\//.test(ua) && 'Firefox') || (/Safari\//.test(ua) && 'Safari') || 'Browser';
  var res = await fetch(CONFIG.BASE_URL + '/rpc/login_completed', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + jwtToken },
    body: JSON.stringify({
      phone_make_model: ('Web ' + browser).slice(0, 60),
      app_version: (CONFIG.APP_VERSION + '+' + CONFIG.BUILD_NUMBER).slice(0, 30),
      os_version: String((navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || 'web').slice(0, 30),
      app_timezone: tz,
      app_language: window.OA_LANG || 'en',
      app_locale: String(navigator.language || 'en').slice(0, 20),
    }),
  });
  var data = await res.json().catch(function() { return null; });
  if (!res.ok || !data) throw new Error((data && data.message) || ('login_completed failed (' + res.status + ')'));
  var profile = Array.isArray(data) ? data[0] : data;
  if (!profile) return;
  Object.entries(profile).forEach(function([key, value]) {
    if (value !== null && value !== undefined) {
      localStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    }
  });
  if (profile.api_url) localStorage.setItem('api_url', profile.api_url);
}

// ── Account preferences (Configuration › Preferences) ───────────────────
// preferences_list rows, cached by name. Loaded at sign-in and rewritten by the
// Preferences page; PREFS_CHANGED_EVENT tells the menu, router and chat to re-read,
// so a switch takes effect at once, without a reload.
var PREFS_STORAGE_KEY   = 'oa_prefs';
var PREFS_CHANGED_EVENT = 'oa:prefs-changed';

function cachePrefs(list) {
  if (!Array.isArray(list)) return;
  var map = {};
  list.forEach(function(p) { map[p.preference_name] = p.preference_value; });
  localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(map));
  window.dispatchEvent(new Event(PREFS_CHANGED_EVENT));
}

function loadPrefs() {
  return callRpc('preferences_list', {}).then(cachePrefs).catch(function() {});
}

// A yes/no preference; the fallback until the account's values are known.
function prefBool(name, fallback) {
  var v;
  try { v = JSON.parse(localStorage.getItem(PREFS_STORAGE_KEY) || '{}')[name]; } catch(e) {}
  if (v === undefined || v === null || v === '') return fallback;
  return String(v).toLowerCase() === 'true';
}

// Re-render the caller whenever a preference changes.
function usePrefs() {
  var tick = React.useState(0)[1];
  React.useEffect(function() {
    var on = function() { tick(function(n) { return n + 1; }); };
    window.addEventListener(PREFS_CHANGED_EVENT, on);
    return function() { window.removeEventListener(PREFS_CHANGED_EVENT, on); };
  }, []);
}

// ── Refresh countdown ───────────────────────────────────────────────────
// The 2px bar at the top of the menu (Sidebar) drains over REFRESH_SECONDS; when it
// runs out it raises REFRESH_EVENT and the page on screen reloads its data in place
// (useRefresh), so nothing being typed or edited is lost.
var REFRESH_SECONDS = 30;
var REFRESH_EVENT   = 'oa:refresh';

function useRefresh(reload) {
  var ref = React.useRef(reload);
  ref.current = reload;
  React.useEffect(function() {
    var on = function() { if (ref.current) ref.current(); };
    window.addEventListener(REFRESH_EVENT, on);
    return function() { window.removeEventListener(REFRESH_EVENT, on); };
  }, []);
}

;
/* ── src/components/icons.jsx ── */
var EyeOff       = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
var Eye          = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
var MenuIcon     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
var SearchIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
var FilterIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></svg>;
var BellIcon     = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
var MsgIcon      = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
var CollapseIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>;
var ChevDown     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9"/></svg>;
var LocationIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
var ClockIcon    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>;
var GoogleIcon   = () => <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>;
var LogoutIcon   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
var EditIcon     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
var TrashIcon    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3,6 5,6 21,6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
var QrIcon       = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="5" y="5" width="3" height="3" fill="currentColor" stroke="none"/><rect x="16" y="5" width="3" height="3" fill="currentColor" stroke="none"/><rect x="16" y="16" width="3" height="3" fill="currentColor" stroke="none"/><rect x="5" y="16" width="3" height="3" fill="currentColor" stroke="none"/></svg>;
var ExportIcon   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
var PlusIcon        = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
var GridSmallIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="4" height="4"/><rect x="10" y="3" width="4" height="4"/><rect x="17" y="3" width="4" height="4"/><rect x="3" y="10" width="4" height="4"/><rect x="10" y="10" width="4" height="4"/><rect x="17" y="10" width="4" height="4"/><rect x="3" y="17" width="4" height="4"/><rect x="10" y="17" width="4" height="4"/><rect x="17" y="17" width="4" height="4"/></svg>;
var GridLargeIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="8"/><rect x="3" y="13" width="8" height="8"/><rect x="13" y="13" width="8" height="8"/></svg>;
var GalleryEmptyIcon = () => <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>;
var DeleteImgIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6"/><path d="M10,11v6"/><path d="M14,11v6"/><path d="M9,6V4a1,1,0,0,1,1-1h4a1,1,0,0,1,1,1V6"/></svg>;

;
/* ── src/components/BirdLogo.jsx ── */
// logo.png has wide margins (the bird fills about a third of it), which suits the
// login card. `mark` uses logo-mark.png, cropped to the bird, for small spaces;
// size is then its height.
var BirdLogo = function({ size = 48, mark = false }) {
  if (mark) {
    return <img src="src/logo-mark.png" alt="OpsAssist Logo" height={size} style={{ height: size + 'px', width: 'auto', display: 'block' }} />;
  }
  return <img src="src/logo.png" alt="OpsAssist Logo" width={size} height={size} style={{ objectFit: 'contain', display: 'block' }} />;
};

;
/* ── src/components/PasswordInput.jsx ── */
var PasswordInput = function({ placeholder, value, onChange, autoComplete }) {
  var { useState } = React;
  var [show, setShow] = useState(false);
  return (
    <div className="input-wrapper">
      <input type={show ? 'text' : 'password'} className="form-input" placeholder={placeholder} value={value} onChange={onChange} autoComplete={autoComplete} />
      <button type="button" className="input-eye-btn" onClick={() => setShow(v => !v)} tabIndex={-1}>
        {show ? <Eye /> : <EyeOff />}
      </button>
    </div>
  );
};

;
/* ── src/components/InlineCell.jsx ── */
// A table cell that turns into a text box when clicked. Enter or leaving the box
// saves, Escape cancels. onSave returns a promise; while it runs the box is locked,
// and if it fails the old value comes back (the page shows the error).
var InlineCell = function({ value, onSave, maxLength, required, className }) {
  var { useState } = React;
  var [editing, setEditing] = useState(false);
  var [draft, setDraft]     = useState('');
  var [saving, setSaving]   = useState(false);

  var start  = function() { setDraft(value || ''); setEditing(true); };
  var cancel = function() { setEditing(false); };
  var commit = function() {
    var next = draft.trim();
    if (next === (value || '') || (required && !next)) { cancel(); return; }
    setSaving(true);
    onSave(next)
      .then(function() { setSaving(false); setEditing(false); })
      .catch(function() { setSaving(false); setEditing(false); });
  };

  if (!editing) {
    return (
      <span className={'inline-cell' + (className ? ' ' + className : '')} title="Click to edit" tabIndex={0}
        onClick={start} onKeyDown={function(e) { if (e.key === 'Enter') start(); }}>
        {value || '—'}
      </span>
    );
  }
  return (
    <input className="inline-cell-input" value={draft} maxLength={maxLength} autoFocus disabled={saving}
      onChange={function(e) { setDraft(e.target.value); }}
      onBlur={commit}
      onKeyDown={function(e) {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }} />
  );
};

;
/* ── src/components/OperatorPhoto.jsx ── */
// An operator's round avatar for lists (Dashboard, Tasks): their photo when they have one
// (operators_get › operator_profile_pic_file_name / _ts), otherwise their initials on their
// colour. The photo comes from the localStorage copy when that is current — shown on the first
// paint, so nothing is downloaded and there is no flicker — and is only fetched when it changed
// (loadOperatorPhoto in constants.js). children (a status dot) are drawn on top. The Operators
// page has its own, clickable version (OperatorAvatar).
var OperatorPhoto = function({ fileName, ts, name, color, className, children }) {
  var { useState, useEffect } = React;
  var [url, setUrl] = useState(function() { return cachedOperatorPhoto(fileName, ts); });
  useEffect(function() {
    var hit = cachedOperatorPhoto(fileName, ts);
    setUrl(hit);
    if (!fileName || hit) return;
    var alive = true;
    loadOperatorPhoto(fileName, ts).then(function(u) { if (alive) setUrl(u); }).catch(function() {});
    return function() { alive = false; };
  }, [fileName, ts]);
  return (
    <span className={className + ' op-photo'} style={{ background: url ? '#fff' : color }}>
      {url ? <img src={url} alt={name || ''} /> : operatorInitials(name)}
      {children}
    </span>
  );
};

;
/* ── src/components/AddJobModal.jsx ── */
// Add Job: the three ways the mobile app creates a job (add_job_page.dart).
//   From a ready-made job  jobs_get_suggestions({suggestion}) -> pick one -> jobs_add(job_name, source_job_id = job_type_id)
//   Blank                  jobs_add(job_name, source_job_id = -1)
//   With AI                jobs_get_ai_builder({suggestion}) -> ai_task_id; poll
//                          jobs_get_ai_builder_progress({check_ai_queue_id}) every 5 s (up to 5 min)
//                          until ai_busy is false; show summary_html, then
//                          jobs_get_ai_build({build_ai_queue_id, job_name}) -> job_id
// onCreated(job_id) hands the new job back to the Jobs page.

var AI_POLL_MS = 5000;
var AI_POLL_MAX = 60;

// AI summaries and errors are HTML from the server: shown in a sandboxed frame, so
// nothing in them can run script or reach the page.
var HtmlPreview = function({ html }) {
  return <iframe className="aj-html" sandbox="" srcDoc={'<base target="_blank"><style>body{font:14px/1.5 Inter,system-ui,sans-serif;color:#111827;margin:12px}</style>' + (html || '')} title={t('addjob_preview')} />;
};

var AddJobModal = function({ onClose, onCreated }) {
  var { useState, useEffect, useRef } = React;
  var [mode, setMode]       = useState(null);     // null | 'template' | 'blank' | 'ai'
  var [name, setName]       = useState('');
  var [error, setError]     = useState('');
  var [busy, setBusy]       = useState(false);

  // From a ready-made job
  var [search, setSearch]       = useState('');
  var [templates, setTemplates] = useState(null);
  var [picked, setPicked]       = useState(null);

  // With AI
  var [prompt, setPrompt]   = useState('');
  var [ai, setAi]           = useState(null);    // { stage: 'working'|'review'|'failed', queueId, note, html, polls }
  var alive = useRef(true);
  useEffect(function() { return function() { alive.current = false; }; }, []);

  useEffect(function() {
    var onKey = function(e) { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [busy]);

  // Ready-made jobs, filtered by the search box (debounced).
  useEffect(function() {
    if (mode !== 'template') return;
    var t = setTimeout(function() {
      setTemplates(null);
      callRpc('jobs_get_suggestions', { suggestion: search.trim() || null })
        .then(function(d) { if (alive.current) setTemplates(Array.isArray(d) ? d : []); })
        .catch(function(err) { if (alive.current) { setTemplates([]); setError(err.message || window.t('addjob_list_error')); } });
    }, 300);
    return function() { clearTimeout(t); };
  }, [mode, search]);

  var choose = function(m) { setMode(m); setError(''); setName(''); setPicked(null); setAi(null); };
  var back = function() { if (!busy) choose(null); };

  var created = function(res) {
    var row = Array.isArray(res) ? res[0] : res;
    onCreated(row && row.job_id);
  };

  var createFromList = function(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError('');
    callRpc('jobs_add', { job_name: name.trim(), source_job_id: mode === 'template' && picked ? picked.job_type_id : -1 })
      .then(created)
      .catch(function(err) { setError(err.message || t('addjob_create_error')); setBusy(false); });
  };

  // ── AI ──
  var poll = function(queueId, n) {
    if (!alive.current) return;
    if (n >= AI_POLL_MAX) { setAi({ stage: 'failed', note: t('addjob_ai_too_long') }); return; }
    setTimeout(function() {
      if (!alive.current) return;
      callRpc('jobs_get_ai_builder_progress', { check_ai_queue_id: queueId })
        .then(function(res) {
          var p = Array.isArray(res) ? res[0] : res;
          if (!alive.current) return;
          if (p && p.ai_busy) { setAi(function(a) { return Object.assign({}, a, { polls: n + 1 }); }); poll(queueId, n + 1); return; }
          if (p && p.ai_success) {
            setAi({ stage: 'review', queueId: queueId, html: p.summary_html });
            setName(p.suggested_name || prompt.trim().slice(0, 100));
          } else {
            setAi({ stage: 'failed', html: p && p.error_html, note: t('addjob_ai_failed') });
          }
        })
        .catch(function(err) { if (alive.current) setAi({ stage: 'failed', note: err.message || t('addjob_ai_progress_error') }); });
    }, AI_POLL_MS);
  };

  var askAi = function(e) {
    e.preventDefault();
    if (!prompt.trim()) return;
    setBusy(true); setError('');
    callRpc('jobs_get_ai_builder', { suggestion: prompt.trim() })
      .then(function(res) {
        var r = Array.isArray(res) ? res[0] : res;
        if (!r || !r.ai_task_accepted) { setError((r && r.al_error) || t('addjob_ai_not_accepted')); return; }
        setAi({ stage: 'working', queueId: r.ai_task_id, note: r.ai_busy_description, polls: 0 });
        poll(String(r.ai_task_id), 0);
      })
      .catch(function(err) { setError(err.message || t('addjob_ai_unreachable')); })
      .finally(function() { setBusy(false); });
  };

  var buildAi = function(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError('');
    callRpc('jobs_get_ai_build', { build_ai_queue_id: String(ai.queueId), job_name: name.trim() })
      .then(created)
      .catch(function(err) { setError(err.message || t('addjob_create_error')); setBusy(false); });
  };

  var nameField = (
    <div className="form-group">
      <label className="form-label">{t('addjob_job_name')}</label>
      <input className="form-input" value={name} maxLength={100} autoFocus placeholder={t('addjob_job_name_placeholder')} onChange={function(e) { setName(e.target.value); }} />
    </div>
  );

  var titles = { template: t('addjob_template'), blank: t('addjob_blank'), ai: t('addjob_ai') };

  return (
    <div className="op-modal-backdrop" onClick={function() { if (!busy) onClose(); }}>
      <div className="op-modal aj-modal" onClick={function(e) { e.stopPropagation(); }}>
        <button type="button" className="qr-close" title={t('common_close')} aria-label={t('common_close')} onClick={function() { if (!busy) onClose(); }}>×</button>
        <div className="aj-head">
          {mode && <button type="button" className="aj-back" onClick={back} disabled={busy}>{t('common_back')}</button>}
          <h3 className="op-modal-title">{mode ? titles[mode] : t('addjob_title')}</h3>
        </div>

        {!mode && (
          <div className="aj-choices">
            <p className="aj-intro">{t('addjob_intro')}</p>
            <button type="button" className="aj-choice" onClick={function() { choose('template'); }}>
              <strong>{t('addjob_template')}</strong>
              <span>{t('addjob_template_desc')}</span>
            </button>
            <button type="button" className="aj-choice" onClick={function() { choose('blank'); }}>
              <strong>{t('addjob_blank')}</strong>
              <span>{t('addjob_blank_desc')}</span>
            </button>
            <button type="button" className="aj-choice aj-choice--ai" onClick={function() { choose('ai'); }}>
              <strong>{t('addjob_ai')}</strong>
              <span>{t('addjob_ai_desc')}</span>
            </button>
          </div>
        )}

        {mode === 'template' && !picked && (
          <div className="aj-body">
            <div className="lv-search aj-search">
              <span className="lv-search-icon"><SearchIcon /></span>
              <input className="lv-search-input" autoFocus placeholder={t('addjob_search_placeholder')} value={search} onChange={function(e) { setSearch(e.target.value); }} />
            </div>
            <div className="aj-list">
              {templates === null && <div className="jb-empty">{t('common_loading')}</div>}
              {templates && templates.length === 0 && <div className="jb-empty">{t('addjob_no_templates')}</div>}
              {templates && templates.map(function(t) {
                return (
                  <button type="button" key={t.job_type_id + ':' + t.job_name} className="aj-item" onClick={function() { setPicked(t); setName(t.job_name || ''); }}>
                    <strong>{t.job_name}</strong>
                    {t.job_description && <span>{t.job_description}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {((mode === 'template' && picked) || mode === 'blank') && (
          <form className="aj-body" onSubmit={createFromList}>
            {picked && (
              <div className="aj-picked">
                <span>{t('addjob_based_on') + ' '}<strong>{picked.job_name}</strong></span>
                <button type="button" className="link-btn link-btn--blue" onClick={function() { setPicked(null); }} disabled={busy}>{t('addjob_change')}</button>
              </div>
            )}
            {nameField}
            <div className="op-modal-actions">
              <button type="submit" className="btn-add" disabled={busy || !name.trim()}>{busy ? t('common_creating') : t('addjob_create_job')}</button>
            </div>
          </form>
        )}

        {mode === 'ai' && !ai && (
          <form className="aj-body" onSubmit={askAi}>
            <div className="form-group">
              <label className="form-label">{t('addjob_describe')}</label>
              <textarea className="form-input aj-prompt" value={prompt} maxLength={1000} autoFocus
                placeholder={t('addjob_describe_placeholder')}
                onChange={function(e) { setPrompt(e.target.value); }} />
            </div>
            <div className="op-modal-actions">
              <button type="submit" className="btn-add" disabled={busy || !prompt.trim()}>{busy ? t('addjob_sending') : t('addjob_ai')}</button>
            </div>
          </form>
        )}

        {mode === 'ai' && ai && ai.stage === 'working' && (
          <div className="aj-body aj-working">
            <div className="aj-spinner" />
            <p>{ai.note || t('addjob_ai_working')}</p>
            <p className="aj-muted">{t(ai.polls === 1 ? 'addjob_ai_wait_one' : 'addjob_ai_wait_many', { count: ai.polls || 0 })}</p>
          </div>
        )}

        {mode === 'ai' && ai && ai.stage === 'review' && (
          <form className="aj-body" onSubmit={buildAi}>
            <HtmlPreview html={ai.html} />
            {nameField}
            <div className="op-modal-actions">
              <button type="button" className="btn-filter" onClick={function() { setAi(null); }} disabled={busy}>{t('addjob_start_over')}</button>
              <button type="submit" className="btn-add" disabled={busy || !name.trim()}>{busy ? t('common_creating') : t('addjob_create_job')}</button>
            </div>
          </form>
        )}

        {mode === 'ai' && ai && ai.stage === 'failed' && (
          <div className="aj-body">
            {ai.html ? <HtmlPreview html={ai.html} /> : <div className="auth-error">{ai.note}</div>}
            <div className="op-modal-actions">
              <button type="button" className="btn-add" onClick={function() { setAi(null); }}>{t('common_try_again')}</button>
            </div>
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}
      </div>
    </div>
  );
};

;
/* ── src/components/OperatorFormsModal.jsx ── */
// Operators › Forms button: which forms an operator may fill in on the mobile app.
//   load  operator_forms_get(get_operator_id)  → { all_forms, forms: [{form_id, form_name, enabled, assigned}] }
//   save  operator_forms_set(set_operator_id, set_all_forms, set_form_ids)
// "All forms" gives every form the account has, including ones made later, so the
// individual ticks are then shown as included and cannot be changed.
var OperatorFormsModal = function({ operator, onClose, onSaved }) {
  var { useState, useEffect } = React;
  var [forms, setForms]     = useState(null);
  var [all, setAll]         = useState(false);
  var [picked, setPicked]   = useState({});      // form_id → true
  var [query, setQuery]     = useState('');
  var [saving, setSaving]   = useState(false);
  var [error, setError]     = useState('');

  useEffect(function() {
    callRpc('operator_forms_get', { get_operator_id: operator.operator_id })
      .then(function(r) {
        r = r || {};
        setAll(!!r.all_forms);
        setForms(r.forms || []);
        var p = {};
        (r.forms || []).forEach(function(f) { if (f.assigned) p[f.form_id] = true; });
        setPicked(p);
      })
      .catch(function(err) { setError(err.message || t('opforms_load_error')); setForms([]); });
  }, [operator.operator_id]);

  useEffect(function() {
    var esc = function(e) { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', esc);
    return function() { document.removeEventListener('keydown', esc); };
  }, [saving]);

  var toggle = function(id) { setPicked(function(p) { var n = Object.assign({}, p); if (n[id]) delete n[id]; else n[id] = true; return n; }); };
  var save = function() {
    setSaving(true); setError('');
    callRpc('operator_forms_set', { set_operator_id: operator.operator_id, set_all_forms: all, set_form_ids: Object.keys(picked) })
      .then(function() { onSaved && onSaved(); onClose(); })
      .catch(function(err) { setError(err.message || t('common_could_not_save')); setSaving(false); });
  };

  var q = query.trim().toLowerCase();
  var shown = (forms || []).filter(function(f) { return !q || String(f.form_name || '').toLowerCase().indexOf(q) !== -1; });
  var count = Object.keys(picked).length;

  return (
    <div className="op-modal-backdrop" onClick={function() { if (!saving) onClose(); }}>
      <div className="op-modal opf-modal" onClick={function(e) { e.stopPropagation(); }}>
        <button type="button" className="qr-close" title={t('common_close')} aria-label={t('common_close')} onClick={onClose} disabled={saving}>×</button>
        <div>
          <h3 className="op-modal-title">{t('opforms_title')}</h3>
          <p className="opf-sub">{t('opforms_subtitle', { name: operator.full_name || '' })}</p>
        </div>

        <label className={'opf-all' + (all ? ' opf-all--on' : '')}>
          <input type="checkbox" className="lv-cb" checked={all} onChange={function(e) { setAll(e.target.checked); }} disabled={forms === null} />
          <span>
            <strong>{t('opforms_all')}</strong>
            <small>{t('opforms_all_desc')}</small>
          </span>
        </label>

        {forms && forms.length > 6 && (
          <div className="lv-search opf-search">
            <span className="lv-search-icon"><SearchIcon /></span>
            <input className="lv-search-input" placeholder={t('opforms_search')} value={query} onChange={function(e) { setQuery(e.target.value); }} />
          </div>
        )}

        <div className={'opf-list' + (all ? ' opf-list--all' : '')}>
          {forms === null && <div className="jb-empty">{t('common_loading')}</div>}
          {forms && forms.length === 0 && <div className="jb-empty">{t('opforms_none')}</div>}
          {shown.map(function(f) {
            return (
              <label key={f.form_id} className="opf-item">
                <input type="checkbox" className="lv-cb" checked={all || !!picked[f.form_id]} disabled={all} onChange={function() { toggle(f.form_id); }} />
                <span className="opf-name">{f.form_name}</span>
                {!f.enabled && <span className="opf-off" title={t('opforms_switched_off_hint')}>{t('opforms_switched_off')}</span>}
              </label>
            );
          })}
        </div>

        {error && <div className="auth-error">{error}</div>}
        <div className="op-modal-actions opf-actions">
          <span className="opf-count">{all ? t('opforms_count_all') : t('opforms_count', { count: count })}</span>
          <button type="button" className="btn-filter" onClick={onClose} disabled={saving}>{t('common_cancel')}</button>
          <button type="button" className="btn-add" onClick={save} disabled={saving || forms === null}>{saving ? t('common_saving') : t('common_save')}</button>
        </div>
      </div>
    </div>
  );
};

;
/* ── src/components/Messages.jsx ── */
/* Messages — chat with the operators, from the top-right menu (app_api.chat_*).
   Same design as thermotrack.io's Messages panel and the OPS Assist app:
     chat_status    every 15 s (badge, operators ordered by their last message,
                    unread per operator, "Online" when the app was seen < 3 min ago)
     chat_messages  the open conversation, every 8 s; opening it marks the operator's
                    messages read (blue ticks on the phone)
     chat_send      text and/or one photo (shrunk to ≤ 1600 px JPEG, base64);
                    "Ask to acknowledge" makes it a red card the operator must confirm
     chat_file      a message's photo (raw bytes, fetched once per page load)            */

var CHAT_STATUS_MS = 15000;
var CHAT_CONVERSATION_MS = 8000;
var chatPhotoUrls = {};   /* chat_id → object URL */

function chatInitials(name) {
  var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function chatOnline(op) {
  return !!(op && op.last_seen_ts && Date.now() - new Date(op.last_seen_ts).getTime() < 3 * 60 * 1000);
}

function chatTime(ts) {
  return ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

function chatDay(ts) {
  var d = new Date(ts), now = new Date();
  var a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  var b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  var diff = Math.round((b - a) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/* the list's "when": time today, else the date */
function chatWhen(ts) {
  if (!ts) return '';
  var d = new Date(ts);
  return new Date().toDateString() === d.toDateString() ? chatTime(ts) : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/* any image the browser can draw → JPEG of at most 1600 px, as base64 (no data: prefix) */
function chatShrink(file) {
  return new Promise(function(resolve, reject) {
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function() {
      var scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      var c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.85).split(',')[1]);
    };
    img.onerror = function() { URL.revokeObjectURL(url); reject(new Error('That file is not a photo this browser can read.')); };
    img.src = url;
  });
}

var ChatTicks = function({ m }) {
  var read = !!m.read_ts, two = read || !!m.delivered_ts;
  var title = read ? 'Read' : two ? 'Delivered' : 'Sent';
  return (
    <span className={'chat-ticks' + (read ? ' chat-ticks--read' : '')} title={title}>
      <svg width="16" height="11" viewBox="0 0 16 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points={two ? '1,6 4,9 10,2' : '3,6 6,9 12,2'} />
        {two && <polyline points="6,9 7,9 14,2" />}
      </svg>
    </span>
  );
};

var ChatPhoto = function({ chatId }) {
  var { useState, useEffect } = React;
  var [url, setUrl]       = useState(chatPhotoUrls[chatId] || null);
  var [failed, setFailed] = useState(false);
  var [big, setBig]       = useState(false);

  useEffect(function() {
    if (url) return;
    var alive = true;
    fetch('https://' + localStorage.getItem('api_url') + '/rpc/chat_file', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': '*/*', 'Authorization': 'Bearer ' + localStorage.getItem('jwt_token') },
      body: JSON.stringify({ chat_id: chatId }),
    })
      .then(function(r) { if (!r.ok) throw new Error(); return r.blob(); })
      .then(function(b) { var u = URL.createObjectURL(b); chatPhotoUrls[chatId] = u; if (alive) setUrl(u); })
      .catch(function() { if (alive) setFailed(true); });
    return function() { alive = false; };
  }, [chatId]);

  return (
    <>
      <div className="chat-photo" onClick={function() { if (url) setBig(true); }}>
        {url ? <img src={url} alt="Photo" /> : <span>{failed ? 'Photo not available' : 'Loading photo…'}</span>}
      </div>
      {big && (
        <div className="chat-lightbox" onClick={function() { setBig(false); }}>
          <img src={url} alt="Photo" />
        </div>
      )}
    </>
  );
};

var ChatConversation = function({ op, onBack, onChanged }) {
  var { useState, useEffect, useRef } = React;
  var [messages, setMessages] = useState(null);
  var [error, setError]       = useState('');
  var [text, setText]         = useState('');
  var [askAck, setAskAck]     = useState(false);
  var [sending, setSending]   = useState(false);
  var listRef  = useRef(null);
  var fileRef  = useRef(null);
  var stickRef = useRef(true);   /* follow new messages while the end is in view */
  var countRef = useRef(0);

  var load = function() {
    return callRpc('chat_messages', { operator_id: op.operator_id, mark_read: true })
      .then(function(rows) {
        var list = Array.isArray(rows) ? rows : [];
        var hadUnread = list.some(function(m) { return !m.mine && !m.read_ts; });
        setMessages(list); setError('');
        if (hadUnread || op.unread) onChanged();
      })
      .catch(function(e) { setError(e.message); });
  };

  useEffect(function() {
    setMessages(null); stickRef.current = true; countRef.current = 0;
    load();
    var t = setInterval(load, CHAT_CONVERSATION_MS);
    return function() { clearInterval(t); };
  }, [op.operator_id]);

  useEffect(function() {
    var el = listRef.current;
    if (!el || !messages) return;
    if (messages.length !== countRef.current && stickRef.current) el.scrollTop = el.scrollHeight;
    countRef.current = messages.length;
  }, [messages]);

  var onScroll = function() {
    var el = listRef.current;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  var post = function(body) {
    setSending(true); setError('');
    return callRpc('chat_send', Object.assign({ operator_id: op.operator_id, ack_required: askAck }, body))
      .then(function(row) {
        stickRef.current = true;
        setMessages(function(list) { return (list || []).concat([row]); });
        setText(''); setAskAck(false);
        onChanged();
      })
      .catch(function(e) { setError(e.message); })
      .then(function() { setSending(false); });
  };

  var send = function() {
    if (!text.trim() || sending) return;
    post({ body: text.trim() });
  };

  var pickPhoto = function(e) {
    var f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    chatShrink(f)
      .then(function(b64) { return post({ body: text.trim() || null, file_base64: b64, file_name: 'photo.jpg' }); })
      .catch(function(err) { setError(err.message); });
  };

  var items = [];
  var lastDay = null;
  (messages || []).forEach(function(m) {
    var day = chatDay(m.create_ts);
    if (day !== lastDay) { lastDay = day; items.push(<div key={'d' + m.chat_id} className="chat-day"><span>{day}</span></div>); }
    var cls = 'chat-bubble' + (m.mine ? ' chat-bubble--mine' : '') + (m.ack_required ? ' chat-bubble--ack' : '');
    items.push(
      <div key={m.chat_id} className={'chat-row' + (m.mine ? ' chat-row--mine' : '')}>
        {!m.mine && <div className="chat-sender">{m.sender_name}</div>}
        <div className={cls}>
          {m.ack_required && <div className="chat-ack-title">&#9888; Acknowledgement requested</div>}
          {m.has_file && <ChatPhoto chatId={m.chat_id} />}
          {m.body && <div className="chat-text">{m.body}</div>}
          <div className="chat-meta">{chatTime(m.create_ts)}{m.mine && <ChatTicks m={m} />}</div>
          {m.ack_required && (
            <div className={'chat-ack-state' + (m.ack_ts ? ' chat-ack-state--done' : '')}>
              {m.ack_ts ? '✔ Acknowledged ' + chatTime(m.ack_ts) : 'Waiting for the operator to acknowledge'}
            </div>
          )}
        </div>
      </div>
    );
  });

  var name = op.full_name || op.nickname || 'Operator';
  return (
    <div className="chat-conv">
      <div className="chat-conv-head">
        <button className="chat-back" onClick={onBack} title="All conversations">&#8592;</button>
        <div className="chat-avatar">{chatInitials(name)}{chatOnline(op) && <i className="chat-online-dot" />}</div>
        <div className="chat-conv-title">
          <div className="chat-conv-name">{name}</div>
          <div className="chat-conv-sub">{chatOnline(op) ? 'Online' : op.last_seen_ts ? 'Last seen ' + chatWhen(op.last_seen_ts) + (new Date().toDateString() === new Date(op.last_seen_ts).toDateString() ? '' : ' ' + chatTime(op.last_seen_ts)) : 'Operator'}</div>
        </div>
      </div>

      <div className="chat-list" ref={listRef} onScroll={onScroll}>
        {messages === null && !error && <div className="chat-empty">Loading…</div>}
        {messages && messages.length === 0 && <div className="chat-empty">No messages yet. Write the first one below.</div>}
        {items}
      </div>

      {error && <div className="chat-error">{error}</div>}

      <div className="chat-compose">
        <label className={'chat-askack' + (askAck ? ' chat-askack--on' : '')}>
          <input type="checkbox" checked={askAck} onChange={function(e) { setAskAck(e.target.checked); }} disabled={sending} />
          Ask to acknowledge
        </label>
        <div className="chat-compose-row">
          <button className="chat-attach" title="Send a photo" disabled={sending} onClick={function() { fileRef.current.click(); }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickPhoto} />
          <textarea
            className="chat-input"
            rows="1"
            maxLength="4000"
            placeholder="Type a message"
            value={text}
            onChange={function(e) { setText(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <button className="chat-send" title="Send" disabled={sending || !text.trim()} onClick={send}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

/* The Messages button in the top bar: badge with the unread count, and the panel. */
var MessagesMenu = function() {
  var { useState, useEffect, useRef } = React;
  var [open, setOpen]       = useState(false);
  var [status, setStatus]   = useState(null);
  var [error, setError]     = useState('');
  var [openId, setOpenId]   = useState(null);
  var [search, setSearch]   = useState('');
  var boxRef = useRef(null);
  var panelRef = useRef(null);

  var refresh = function() {
    return callRpc('chat_status', {})
      .then(function(s) { setStatus(s); setError(''); })
      .catch(function(e) { setError(e.message); });
  };

  useEffect(function() {
    refresh();
    var t = setInterval(refresh, CHAT_STATUS_MS);
    window.addEventListener(PREFS_CHANGED_EVENT, refresh);   // Chat switched on/off on the Preferences page
    return function() { clearInterval(t); window.removeEventListener(PREFS_CHANGED_EVENT, refresh); };
  }, []);

  useEffect(function() {
    if (!open) return;
    refresh();
    // The panel is drawn in the page (a portal), docked full height on the right of the screen.
    var inside = function(el) { return (boxRef.current && boxRef.current.contains(el)) || (panelRef.current && panelRef.current.contains(el)); };
    var away = function(e) { if (!inside(e.target) && !e.target.closest('.chat-lightbox')) setOpen(false); };
    var esc = function(e) { if (e.key === 'Escape' && !document.querySelector('.chat-lightbox')) setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return function() { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  var unread = (status && status.unread) || 0;
  var operators = (status && status.operators) || [];
  var q = search.trim().toLowerCase();
  var shown = q ? operators.filter(function(o) {
    return [o.full_name, o.nickname, o.staff_id_number].some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; });
  }) : operators;
  var current = openId && operators.filter(function(o) { return o.operator_id === openId; })[0];

  // Configuration › Preferences › Chat: switched off, there is no Messages button
  // (chat_status then answers enabled = false, and chat_send refuses).
  usePrefs();
  if (!prefBool('Chat Enabled', true) || (status && status.enabled === false)) return null;

  return (
    <div className="msg-menu" ref={boxRef}>
      <button className={'header-icon-btn' + (open ? ' header-icon-btn--active' : '')} title="Messages" onClick={function() { setOpen(!open); }}>
        <MsgIcon />
        {unread > 0 && <span className="notification-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && ReactDOM.createPortal(
        <div className="msg-panel" ref={panelRef} role="dialog" aria-label="Messages">
          <button type="button" className="msg-close" title="Close" aria-label="Close" onClick={function() { setOpen(false); }}>×</button>
          {current ? (
            <ChatConversation op={current} onBack={function() { setOpenId(null); refresh(); }} onChanged={refresh} />
          ) : (
            <>
              <div className="msg-head">
                <div>
                  <div className="msg-title">Messages</div>
                  <div className="msg-sub">Communication with your operators</div>
                </div>
                {unread > 0 && <span className="msg-unread">{unread} unread</span>}
              </div>
              <div className="msg-search">
                <SearchIcon />
                <input placeholder="Search operators" value={search} onChange={function(e) { setSearch(e.target.value); }} />
              </div>
              <div className="msg-list">
                {!status && !error && <div className="chat-empty">Loading…</div>}
                {error && <div className="chat-error">{error}</div>}
                {status && shown.length === 0 && <div className="chat-empty">{q ? 'No operator matches.' : 'No operators yet.'}</div>}
                {shown.map(function(o) {
                  var name = o.full_name || o.nickname || 'Operator';
                  var preview = o.last_ts
                    ? (o.last_from_operator ? '' : 'You: ') + (o.last_body || (o.last_has_file ? '📷 Photo' : ''))
                    : 'No messages yet';
                  return (
                    <button key={o.operator_id} className={'msg-item' + (o.unread ? ' msg-item--unread' : '')} onClick={function() { setOpenId(o.operator_id); }}>
                      <div className="chat-avatar">{chatInitials(name)}{chatOnline(o) && <i className="chat-online-dot" />}</div>
                      <div className="msg-item-main">
                        <div className="msg-item-top">
                          <span className="msg-item-name">{name}</span>
                          <span className="msg-item-when">{chatWhen(o.last_ts)}</span>
                        </div>
                        <div className="msg-item-bottom">
                          <span className="msg-item-preview">{preview}</span>
                          {o.pending_ack > 0 && <span className="msg-pill msg-pill--ack" title="Waiting for acknowledgement">{o.pending_ack} to ack</span>}
                          {o.unread > 0 && <span className="msg-pill">{o.unread}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

;
/* ── src/components/Sidebar.jsx ── */
var NAV = [
  { section: 'OVERVIEW', items: [
    { id: 'dashboard', label: 'Dashboard', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
    { id: 'maps',      label: 'Maps and Views', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg> },
    { id: 'alerts',    label: 'Alerts',    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg> },
    { id: 'tasks',     label: 'Task History', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6h11"/><path d="M9 12h11"/><path d="M9 18h11"/><polyline points="3,6 4,7 6,5"/><polyline points="3,12 4,13 6,11"/><polyline points="3,18 4,19 6,17"/></svg> },
    { id: 'reports',   label: 'Reports',   children: ['exec_reports', 'data_export', 'form_entries'], icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
  ]},
  { section: 'OPERATIONS', items: [
    { id: 'jobs',      label: 'Jobs',      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg> },
    { id: 'operators', label: 'Operators', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  ]},
  { section: 'ADMINISTRATION', items: [
    { id: 'hr',        label: 'Human Resource', children: ['leaves', 'claims'], childPrefs: { leaves: 'Leave Enabled', claims: 'Claims Enabled' }, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 11l2 2 4-4"/></svg> },
    { id: 'customers', label: 'Customers', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
    { id: 'gallery',   label: 'Gallery',   icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg> },
  ]},
  { section: 'SETUP', items: [
    // pref: shown only while that account preference (Configuration › Preferences) is on
    { id: 'forms',    label: 'Forms',              pref: 'Forms Enabled', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
    { id: 'integrations', label: 'Integrations',  icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"/></svg> },
    { id: 'audits',   label: 'Audits',             icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
  ]},
  { section: 'CONFIGURATION', items: [
    { id: 'profile',  label: 'Profile',            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></svg> },
    { id: 'teams',    label: 'Teams',              icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { id: 'billing',  label: 'Billing',            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
    { id: 'preferences', label: 'Preferences',     icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg> },
  ]},
];

// The refresh countdown: drains over REFRESH_SECONDS, then asks the page on screen to
// reload its data (REFRESH_EVENT, see useRefresh) and starts again. A new page starts
// it afresh, since that page has just loaded.
var RefreshCountdown = function() {
  var { useState, useEffect } = React;
  var [round, setRound] = useState(0);
  useEffect(function() {
    var timer = setInterval(function() {
      window.dispatchEvent(new Event(REFRESH_EVENT));
      setRound(function(n) { return n + 1; });
    }, REFRESH_SECONDS * 1000);
    return function() { clearInterval(timer); };
  }, []);
  return (
    <div className="sidebar-countdown" title={'Refreshes every ' + REFRESH_SECONDS + ' seconds'}>
      <div key={round} className="sidebar-countdown-bar" style={{ animationDuration: REFRESH_SECONDS + 's' }} />
    </div>
  );
};

var Sidebar = function({ activePage, onNavSelect, isOpen, onLogout, isCollapsed, onToggleCollapse }) {
  usePrefs();
  var [openGroups, setOpenGroups] = React.useState({});   // parent menu items opened by hand
  var toggleGroup = function(id) { setOpenGroups(function(g) { return Object.assign({}, g, { [id]: !g[id] }); }); };
  return (
    <aside className={'sidebar' + (isOpen ? ' sidebar--open' : '') + (isCollapsed ? ' sidebar--collapsed' : '')}>
      <RefreshCountdown />
      <div className="sidebar-header">
        <div className="sidebar-brand">
          {!isCollapsed && <div className="sidebar-logo"><BirdLogo size={34} mark /></div>}
        </div>
        {/* Messages on the right of the header, beside the collapse button */}
        <div className="sidebar-actions">
          <MessagesMenu />
          <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title={isCollapsed ? 'Expand menu' : 'Collapse menu'}><CollapseIcon /></button>
        </div>
      </div>
      <nav className="sidebar-nav">
        {NAV.map(function({ section, items }, si) {
          return (
            <div key={section}>
              <div className="nav-section-label" style={si > 0 ? { marginTop: '16px' } : {}}>{t('nav_section_' + section.toLowerCase())}</div>
              {items.filter(function(item) { return !item.pref || prefBool(item.pref, true); }).map(function(item) {
                if (item.children) {
                  // children switched off in Preferences (childPrefs) are left out; none left, no parent
                  var kids = item.children.filter(function(id) { return !(item.childPrefs && item.childPrefs[id]) || prefBool(item.childPrefs[id], true); });
                  if (!kids.length) return null;
                  // A parent with sub-pages (Reports): opens and closes its list; collapsed, it goes to the first.
                  var inside = kids.indexOf(activePage) !== -1;
                  var expanded = inside || openGroups[item.id];
                  return (
                    <div key={item.id} className="nav-group">
                      <button className={'nav-item' + (inside ? ' nav-item--parent-active' : '')} aria-expanded={!!expanded}
                        onClick={function() { if (isCollapsed) onNavSelect(kids[0]); else toggleGroup(item.id); }}>
                        <span className="nav-item-icon">{item.icon}</span>
                        <span className="nav-item-label">{t('nav_' + item.id)}</span>
                        <span className={'nav-item-chevron' + (expanded ? ' nav-item-chevron--open' : '')}><ChevDown /></span>
                      </button>
                      {expanded && !isCollapsed && kids.map(function(id) {
                        return (
                          <button key={id} className={'nav-item nav-subitem' + (activePage === id ? ' nav-item--active' : '')} onClick={function() { onNavSelect(id); }}>
                            <span className="nav-item-label">{t('nav_' + id)}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                }
                return (
                  <button key={item.id} className={'nav-item' + (activePage === item.id ? ' nav-item--active' : '')} onClick={() => onNavSelect(item.id)}>
                    <span className="nav-item-icon">{item.icon}</span>
                    <span className="nav-item-label">{t('nav_' + item.id)}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        <button className="nav-item nav-item--logout" onClick={onLogout}>
          <span className="nav-item-icon"><LogoutIcon /></span>
          <span className="nav-item-label">{t('nav_logout')}</span>
        </button>
      </div>
    </aside>
  );
};

;
/* ── src/components/DemoBanner.jsx ── */
// A strip across the top of every page while the account has demo data (added at the first
// sign-in of an account with no operators — ops.demo_seed). Delete removes all of it
// (demo_data_delete) and reloads, so every screen shows the real data only.
// demo_status is asked once per page load and shared by every page.
var demoStatusPromise = null;
var DemoBanner = function() {
  var { useState, useEffect } = React;
  var [status, setStatus]   = useState(null);
  var [confirm, setConfirm] = useState(false);
  var [busy, setBusy]       = useState(false);
  var [error, setError]     = useState('');

  useEffect(function() {
    if (!demoStatusPromise) demoStatusPromise = callRpc('demo_status').catch(function() { demoStatusPromise = null; return null; });
    var alive = true;
    demoStatusPromise.then(function(s) { if (alive) setStatus(s); });
    return function() { alive = false; };
  }, []);

  if (!status || !status.has_demo) return null;

  var remove = function() {
    setBusy(true); setError('');
    callRpc('demo_data_delete')
      .then(function() { window.location.reload(); })
      .catch(function(err) { setError(err.message || t('demo_delete_error')); setBusy(false); });
  };

  return (
    <div className="demo-banner" role="status">
      <span className="demo-banner-tag">{t('demo_tag')}</span>
      <span className="demo-banner-text">
        {t('demo_text', { operators: status.operators, jobs: status.jobs, forms: status.forms })}
      </span>
      {error && <span className="demo-banner-err">{error}</span>}
      {!confirm
        ? <button type="button" className="demo-banner-btn" onClick={function() { setConfirm(true); }}>{t('demo_delete')}</button>
        : <span className="demo-banner-confirm">
            {t('demo_confirm')}
            <button type="button" className="demo-banner-btn demo-banner-btn--danger" onClick={remove} disabled={busy}>{busy ? t('common_deleting') : t('demo_delete_yes')}</button>
            <button type="button" className="demo-banner-link" onClick={function() { setConfirm(false); }} disabled={busy}>{t('common_cancel')}</button>
          </span>}
    </div>
  );
};

;
/* ── src/components/AppShell.jsx ── */
var AppShell = function({ activePage, onNavSelect, onLogout, children }) {
  var { useState } = React;
  var [sidebarOpen, setSidebarOpen]           = useState(false);
  var [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  var close = function() { setSidebarOpen(false); };
  var handleNav = function(id) { onNavSelect(id); close(); };

  var sidebarWidth = sidebarCollapsed ? 60 : 220;

  return (
    <div className="app-layout">
      <div className={'sidebar-overlay' + (sidebarOpen ? ' sidebar-overlay--visible' : '')} onClick={close} />
      <Sidebar
        activePage={activePage}
        onNavSelect={handleNav}
        isOpen={sidebarOpen}
        onLogout={onLogout}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={function() { setSidebarCollapsed(function(v) { return !v; }); }}
      />
      <div className="app-main" style={{ marginLeft: sidebarWidth + 'px' }}>
        {/* phones only: the menu is hidden off-screen until this opens it */}
        <button className="mobile-menu-btn" title="Menu" onClick={function() { setSidebarOpen(function(v) { return !v; }); }}><MenuIcon /></button>
        <main className="app-content"><DemoBanner />{children}</main>
      </div>
    </div>
  );
};

;
/* ── src/pages/Login.jsx ── */
var Login = function({ onLogin, onSignup }) {
  var { useState, useEffect, useCallback } = React;
  var [email, setEmail]       = useState(function() { return localStorage.getItem('last_login_name') || ''; });
  var [password, setPassword] = useState('');
  var [loading, setLoading]   = useState(false);
  var [gLoading, setGLoading] = useState(false);
  var [error, setError]       = useState('');

  var handleGoogleCredential = useCallback(async function(response) {
    setError('');
    setGLoading(true);
    var payload;
    try {
      var parts = response.credential.split('.');
      payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch(e) {
      setError(t('login_google_token_error'));
      setGLoading(false);
      return;
    }

    try {
      var res = await fetch(GOOGLE_LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login_name:   payload.email,
          display_name: payload.name,
          app_id:       APP_ID,
          token_id:     response.credential,
          app_auth_key: APP_AUTH_KEY,
        }),
      });
      var data = await res.json();
      if (!res.ok) {
        setError(t('login_google_server_error', { status: res.status, message: data && data.message ? data.message : '' }));
        return;
      }
      if (data.login_session === 'Login success!') {
        saveSession(data, payload.email);
        try { await callLoginCompleted(data.jwt_token); } catch(e) {}
        onLogin();
      } else {
        setError(data.message || data.login_session || t('login_google_failed'));
      }
    } catch(e) {
      setError(t('login_google_unreachable'));
    } finally {
      setGLoading(false);
    }
  }, [onLogin]);

  useEffect(function() {
    var init = function() {
      try {
        window.google.accounts.id.initialize({
          client_id:             GOOGLE_CLIENT_ID,
          callback:              handleGoogleCredential,
          auto_select:           false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt:  false,
          itp_support:           true,
        });
        var container = document.getElementById('google-hidden-btn');
        if (container) {
          window.google.accounts.id.renderButton(container, { type: 'standard', theme: 'outline', size: 'large' });
        }
      } catch(e) {}
    };
    if (window.google && window.google.accounts) {
      init();
    } else {
      var prev = window.onGoogleLibraryLoad;
      window.onGoogleLibraryLoad = function() { init(); if (prev) prev(); };
    }
  }, [handleGoogleCredential]);

  var handleGoogleClick = function() {
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      setError(t('login_google_not_ready'));
      return;
    }
    var hiddenBtn = document.querySelector('#google-hidden-btn [role="button"], #google-hidden-btn div[tabindex="0"]');
    if (hiddenBtn) {
      hiddenBtn.click();
    } else {
      window.google.accounts.id.prompt();
    }
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      var res = await fetch(LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_name: email, login_password: password, app_auth_key: APP_AUTH_KEY }),
      });
      var data = await res.json();
      if (data.login_session === 'Login success!') {
        saveSession(data, email);
        // First call after sign-in; a failure there must not stop the sign-in.
        try { await callLoginCompleted(data.jwt_token); } catch(e) {}
        onLogin();
      } else {
        setError(data.message || data.login_session || t('login_failed'));
      }
    } catch(e) {
      setError(t('common_network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-bg">
      <span className="auth-version">{t('common_version_build', { version: CONFIG.APP_VERSION, build: CONFIG.BUILD_NUMBER })}</span>
      <div className="auth-card">
        <div className="auth-logo"><BirdLogo size={162} /></div>
        <h1 className="auth-title">{t('login_title')}</h1>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('login_name_label')}</label>
            <input type="text" className="form-input" placeholder={t('login_name_placeholder')} value={email} onChange={function(e) { setEmail(e.target.value); }} autoComplete="email" required />
          </div>
          <div className="form-group">
            <label className="form-label">{t('common_password')}</label>
            <PasswordInput placeholder={t('login_password_placeholder')} value={password} onChange={function(e) { setPassword(e.target.value); }} autoComplete="current-password" />
          </div>
          {error && <div className="auth-error">{error}</div>}
          <div className="form-forgot">
            <button type="button" className="link-btn">{t('login_forgot_password')}</button>
          </div>
          <button type="submit" className="btn-primary" disabled={loading || gLoading}>
            {loading ? t('login_signing_in') : t('common_sign_in')}
          </button>
        </form>
        <div className="auth-divider"><span>{t('login_or_continue_with')}</span></div>
        <div id="google-hidden-btn" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px', overflow: 'hidden' }} />
        <div className="social-buttons">
          <button className="social-btn" title={t('login_google_title')} onClick={handleGoogleClick} disabled={gLoading}>
            {gLoading ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{'\u2026'}</span> : <GoogleIcon />}
          </button>
        </div>
        <p className="auth-footer">
          {t('login_no_account')}{' '}
          <button type="button" className="link-btn link-btn--blue" onClick={onSignup}>{t('login_sign_up')}</button>
        </p>
      </div>
    </div>
  );
};

;
/* ── src/pages/Signup.jsx ── */
// Create a manager account: email, industry and password.
//   industries   login_get_industries     (anonymous, before there is a session)
//   create       login_register_direct    (industry stored in ops.users_profile)
//   then signed straight in with login_with_email, the same as the Login page.
var Signup = function({ onSignin, onSignedUp }) {
  var { useState, useEffect } = React;
  var [email, setEmail]         = useState('');
  var [industry, setIndustry]   = useState('');
  var [password, setPassword]   = useState('');
  var [confirm, setConfirm]     = useState('');
  var [industries, setIndustries] = useState([]);
  var [loading, setLoading]     = useState(false);
  var [error, setError]         = useState('');

  useEffect(function() {
    fetch(CONFIG.BASE_URL + '/rpc/login_get_industries', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    })
      .then(function(res) { return res.ok ? res.json() : []; })
      .then(function(list) {
        setIndustries((Array.isArray(list) ? list : []).slice().sort(function(a, b) {
          return String(a.industry_type_description).localeCompare(String(b.industry_type_description));
        }));
      })
      .catch(function() { setError(t('signup_industries_error')); });
  }, []);

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    var login = email.trim().toLowerCase();
    if (!industry)                { setError(t('signup_industry_required')); return; }
    if (password.length < 8)      { setError(t('signup_password_too_short')); return; }
    if (password !== confirm)     { setError(t('signup_password_mismatch')); return; }

    setLoading(true);
    try {
      var res = await fetch(CONFIG.BASE_URL + '/rpc/login_register_direct', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login_name: login, login_password: password, display_name: login.split('@')[0],
          app_auth_key: APP_AUTH_KEY, industry_type_id: Number(industry),
        }),
      });
      var data = await res.json().catch(function() { return null; });
      if (!res.ok || !data || data.login_result !== 0) {
        setError((data && (data.login_session || data.message)) || t('signup_failed'));
        return;
      }

      // The account is ready (email is verified on creation), so sign in with it now.
      var lres  = await fetch(LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_name: login, login_password: password, app_auth_key: APP_AUTH_KEY }),
      });
      var ldata = await lres.json();
      if (ldata.login_session === 'Login success!') {
        saveSession(ldata, login);
        try { await callLoginCompleted(ldata.jwt_token); } catch(e2) {}
        onSignedUp();
      } else {
        setError(t('signup_created_sign_in'));
      }
    } catch(e) {
      setError(t('common_network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-bg">
      <div className="auth-card">
        <div className="auth-logo"><BirdLogo size={162} /></div>
        <h1 className="auth-title">{t('signup_title')}</h1>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('common_email')}</label>
            <input type="email" className="form-input" placeholder={t('signup_email_placeholder')} value={email} onChange={function(e) { setEmail(e.target.value); }} autoComplete="email" required />
          </div>
          <div className="form-group">
            <label className="form-label">{t('common_industry')}</label>
            <select className="form-input" value={industry} onChange={function(e) { setIndustry(e.target.value); }} required>
              <option value="">{industries.length ? t('signup_choose_industry') : t('common_loading')}</option>
              {industries.map(function(i) {
                return <option key={i.industry_type_id} value={String(i.industry_type_id)}>{i.industry_type_description}</option>;
              })}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('common_password')}</label>
            <PasswordInput placeholder={t('common_at_least_8')} value={password} onChange={function(e) { setPassword(e.target.value); }} autoComplete="new-password" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('signup_confirm_password')}</label>
            <PasswordInput placeholder={t('signup_retype_placeholder')} value={confirm} onChange={function(e) { setConfirm(e.target.value); }} autoComplete="new-password" />
          </div>
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" className="btn-primary" style={{ marginTop: '8px' }} disabled={loading}>
            {loading ? t('signup_creating_account') : t('signup_create_account')}
          </button>
        </form>
        <p className="auth-footer">
          {t('signup_have_account')}{' '}
          <button type="button" className="link-btn link-btn--blue" onClick={onSignin}>{t('common_sign_in')}</button>
        </p>
      </div>
    </div>
  );
};

;
/* ── src/pages/Home.jsx ── */
var Home = function({ activePage, onNavSelect, onLogout }) {
  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="app-empty-state">
        <p className="empty-hint">{t('home_hint')}</p>
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/Dashboard.jsx ── */
// Dashboard: one panel per enabled operator (app_api.dashboard_panels) — their day bar, current
// task, next tasks and exceptions — in a grid you can rearrange by dragging.
// ── Operator panels (right side) ─────────────────────────────────────────
// One fixed-size panel per operator from dashboard_panels: the current task (red "overdue"
// when it should have started but has not), the next tasks with their times, and the day's
// exceptions at the bottom. Drag a panel to reorder the grid; the order is kept in localStorage.
var DASH_ORDER_KEY = 'dash_panel_order';
var dashTime = function(ts) { return ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''; };
// Whole days from today (local): 0 today, 1 tomorrow, …
var dashDayOffset = function(ts) {
  var d = new Date(ts); d.setHours(0, 0, 0, 0);
  var today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};
// The day in front of a time when it is not today: "Tomorrow", or the date.
var dashDayLabel = function(ts) {
  var off = dashDayOffset(ts);
  if (off === 0) return '';
  if (off === 1) return t('dashboard_tomorrow');
  return new Date(ts).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
};
var DashPriority = function({ p }) {
  if (p !== 'H' && p !== 'M') return null;   // only Medium and High are shown
  return <span className={'dp-prio dp-prio--' + p}>{p === 'H' ? t('dashboard_priority_high') : t('dashboard_priority_medium')}</span>;
};

// The operator's day, 0–24 h local time: done tasks green, anything due that is not done red,
// tasks still to come grey, cancelled ones faint; a line marks now.
var DashDayBar = function({ day }) {
  var now = new Date();
  var start = new Date(now); start.setHours(0, 0, 0, 0);
  var dayMs = 24 * 3600 * 1000;
  var pct = function(d) { return Math.max(0, Math.min(100, (d - start) / dayMs * 100)); };
  var done = 0, late = 0;
  var segs = (day || []).map(function(x, i) {
    var a = new Date(x.start), b = x.end ? new Date(x.end) : new Date(a.getTime() + 15 * 60000);
    if (b <= start || a >= new Date(start.getTime() + dayMs)) return null;
    var cls = x.cancelled ? 'dp-seg--cancel' : x.done ? 'dp-seg--done' : a <= now ? 'dp-seg--late' : 'dp-seg--todo';
    if (x.done) done++; else if (!x.cancelled && a <= now) late++;
    return <span key={i} className={'dp-seg ' + cls} style={{ left: pct(a) + '%', width: Math.max(0.6, pct(b) - pct(a)) + '%' }}
      title={x.task_name + '  ' + dashTime(x.start) + (x.end ? '–' + dashTime(x.end) : '')} />;
  });
  return (
    <div className="dp-day" title={t('dashboard_day_summary', { done: done, late: late })}>
      <div className="dp-day-track">
        {segs}
        <span className="dp-day-now" style={{ left: pct(now) + '%' }} />
      </div>
      <div className="dp-day-hours"><span>0</span><span>6</span><span>12</span><span>18</span><span>24</span></div>
    </div>
  );
};

var OperatorPanel = function({ panel, drag, onAdd }) {
  // Nothing more today: say so, and show only the first 2 of the later tasks.
  var anyToday = panel.next.some(function(n) { return dashDayOffset(n.start) === 0; });
  var nextShown = anyToday ? panel.next : panel.next.slice(0, 2);
  // Overdue: due before now and neither done nor cancelled — shown as a pulsing red dot.
  var now = Date.now();
  // Each task's number in its day (1 = first, by time), as on Task History.
  var dayNo = {}, byDay = {};
  (panel.day || []).forEach(function(x) { var k = new Date(x.start).toDateString(); (byDay[k] = byDay[k] || []).push(x); });
  Object.keys(byDay).forEach(function(k) {
    byDay[k].sort(function(a, b) { return new Date(a.start) - new Date(b.start); }).forEach(function(x, i) { if (x.task_id) dayNo[x.task_id] = i + 1; });
  });
  var num = function(task) { return dayNo[task.task_id] ? <span className="dp-num" title={t('dashboard_task_no', { n: dayNo[task.task_id] })}>{dayNo[task.task_id]}</span> : null; };
  var overdue = (panel.day || []).filter(function(x) { return !x.done && !x.cancelled && new Date(x.start).getTime() <= now; }).length;
  var cur = panel.current;
  return (
    <article className={'dp-card' + (drag.dragging ? ' dp-card--dragging' : '') + (drag.over ? ' dp-card--over' : '')}
      draggable onDragStart={drag.onDragStart} onDragEnd={drag.onDragEnd} onDragOver={drag.onDragOver} onDrop={drag.onDrop}>
      <header className="dp-head" title={t('dashboard_drag_hint')}>
        <OperatorPhoto className="dp-avatar" fileName={panel.pic} name={panel.full_name} color={operatorColor(panel.operator_id)} />
        <div className="dp-who">
          <strong>{panel.full_name}</strong>
          <span>{panel.job_name || t('dashboard_no_job_assigned')}</span>
        </div>
        {overdue > 0 && <span className="dp-alert" title={t('dashboard_overdue_count', { count: overdue })} aria-label={t('dashboard_overdue_count', { count: overdue })} />}
        <span className="dp-grip" aria-hidden="true"><GripIcon /></span>
      </header>
      <DashDayBar day={panel.day} />

      <section className="dp-section">
        <div className="dp-label">{t('dashboard_current_task')}</div>
        {cur ? (
          <div className={'dp-task dp-task--current' + (cur.overdue ? ' dp-task--overdue' : '')}>
            <div className="dp-task-top">
              {num(cur)}
              <span className="dp-task-name" title={cur.task_name}>{cur.task_name}</span>
              <DashPriority p={cur.priority} />
            </div>
            <div className="dp-task-meta">
              {cur.overdue
                ? <span className="dp-late">{t('dashboard_overdue_since', { time: dashTime(cur.start) })}</span>
                : <span className="dp-running">{t('dashboard_in_progress_since', { time: dashTime(cur.started_at || cur.start) })}</span>}
            </div>
          </div>
        ) : <div className="dp-none">{t(anyToday ? 'dashboard_no_current' : 'dashboard_no_current_today')}</div>}
      </section>

      <section className="dp-section dp-section--next">
        <div className="dp-label">{t('dashboard_next_tasks')}</div>
        {panel.next.length === 0 && <div className="dp-none">{t('dashboard_no_next')}</div>}
        {nextShown.map(function(n) {
          return (
            <div key={n.task_id} className="dp-task dp-task--next">
              <div className="dp-next-top">
                <span className="dp-time">{dashDayLabel(n.start) && <span className="dp-day-label">{dashDayLabel(n.start)}</span>}{dashTime(n.start)}{n.end ? '–' + dashTime(n.end) : ''}</span>
                <DashPriority p={n.priority} />
              </div>
              <div className="dp-next-name">{num(n)}<span className="dp-task-name" title={n.task_name}>{n.task_name}</span></div>
            </div>
          );
        })}
      </section>

      <footer className="dp-foot">
        <div className={'dp-exceptions' + (panel.exceptions.length ? ' dp-exceptions--some' : '')}>
          <div className="dp-label">{t('dashboard_exceptions')}{panel.exceptions.length ? ' (' + panel.exceptions.length + ')' : ''}</div>
          {panel.exceptions.length === 0
            ? <div className="dp-none">{t('dashboard_no_exceptions')}</div>
            : panel.exceptions.slice(0, 2).map(function(x, i) {
                return <div key={i} className="dp-exc" title={x.comment || ''}><span className="dp-time">{dashTime(x.ts)}</span> {x.type || x.task_name}{x.comment ? ' — ' + x.comment : ''}</div>;
              })}
        </div>
        <button type="button" className="dp-add" title={t('dashboard_adhoc_hint')} aria-label={t('dashboard_adhoc_hint')} onClick={onAdd}>+</button>
      </footer>
    </article>
  );
};

// Dashboard card › Add: a one-off (ad-hoc) task for this operator, straight onto their live
// schedule through operator_adhoc_task_add (it is not part of any job).
var AdhocTaskModal = function({ panel, onClose, onAdded }) {
  var { useState, useEffect } = React;
  var pad = function(n) { return String(n).padStart(2, '0'); };
  var soon = new Date(Math.ceil(Date.now() / 300000) * 300000);   // the next 5 minutes
  var [name, setName]         = useState('');
  var [start, setStart]       = useState(pad(soon.getHours()) + ':' + pad(soon.getMinutes()));
  var [duration, setDuration] = useState('15');
  var [priority, setPriority] = useState('M');
  var [busy, setBusy]         = useState(false);
  var [error, setError]       = useState('');

  useEffect(function() {
    var esc = function(e) { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', esc);
    return function() { document.removeEventListener('keydown', esc); };
  }, [busy]);

  var submit = function(e) {
    e.preventDefault();
    if (!name.trim()) return;
    var at = new Date(); var hm = start.split(':');
    at.setHours(Number(hm[0]) || 0, Number(hm[1]) || 0, 0, 0);   // today, local time
    setBusy(true); setError('');
    callRpc('operator_adhoc_task_add', { add_operator_id: panel.operator_id, add_task_name: name, add_start: at.toISOString(),
                                          add_duration_minutes: Math.max(0, parseInt(duration, 10) || 0), add_priority: priority })
      .then(function() { onAdded(); onClose(); })
      .catch(function(err) { setError(err.message || t('common_could_not_save')); setBusy(false); });
  };

  return (
    <div className="op-modal-backdrop" onClick={function() { if (!busy) onClose(); }}>
      <form className="op-modal dp-adhoc" onClick={function(e) { e.stopPropagation(); }} onSubmit={submit}>
        <button type="button" className="qr-close" title={t('common_close')} aria-label={t('common_close')} onClick={onClose} disabled={busy}>×</button>
        <div>
          <h3 className="op-modal-title">{t('dashboard_adhoc_title')}</h3>
          <p className="opf-sub">{t('dashboard_adhoc_subtitle', { name: panel.full_name })}</p>
        </div>
        <div className="form-group">
          <label className="form-label">{t('jobs_task_name')}</label>
          <input className="form-input" value={name} maxLength={200} autoFocus placeholder={t('jobs_task_name_placeholder')} onChange={function(e) { setName(e.target.value); }} />
        </div>
        <div className="dp-adhoc-row">
          <label className="form-group"><span className="form-label">{t('jobs_start')}</span>
            <input type="time" className="form-input" value={start} onChange={function(e) { setStart(e.target.value); }} required /></label>
          <label className="form-group"><span className="form-label">{t('jobs_duration') + ' (' + t('jobs_unit_min') + ')'}</span>
            <input type="number" min="0" step="5" className="form-input" value={duration} onChange={function(e) { setDuration(e.target.value); }} /></label>
          <label className="form-group"><span className="form-label">{t('jobs_priority')}</span>
            <select className="form-input" value={priority} onChange={function(e) { setPriority(e.target.value); }}>
              <option value="H">{t('jobs_priority_high')}</option>
              <option value="M">{t('jobs_priority_medium')}</option>
              <option value="L">{t('jobs_priority_low')}</option>
            </select></label>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <div className="op-modal-actions">
          <button type="button" className="btn-filter" onClick={onClose} disabled={busy}>{t('common_cancel')}</button>
          <button type="submit" className="btn-add" disabled={busy || !name.trim()}>{busy ? t('jobs_adding') : t('dashboard_adhoc_create')}</button>
        </div>
      </form>
    </div>
  );
};

var Dashboard = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [panels, setPanels] = useState(null);
  var [error, setError]   = useState('');
  var [order, setOrder]   = useState(function() { try { return JSON.parse(localStorage.getItem(DASH_ORDER_KEY) || '[]'); } catch (e) { return []; } });
  var [dragId, setDragId] = useState(null);
  var [overId, setOverId] = useState(null);
  var [addFor, setAddFor] = useState(null);   // panel whose Add (ad-hoc task) window is open

  var load = function() {
    callRpc('dashboard_panels')
      .then(function(p) { setPanels(Array.isArray(p) ? p : []); setError(''); })
      .catch(function() { setError(t('dashboard_load_error')); setPanels(function(p) { return p || []; }); });
  };
  useEffect(load, []);
  useRefresh(load);   // the countdown at the top of the menu

  // Saved order first, then anyone new in name order.
  var pos = function(id) { var i = order.indexOf(id); return i === -1 ? 1e6 : i; };
  var sorted = (panels || []).slice().sort(function(a, b) { return pos(a.operator_id) - pos(b.operator_id); });

  var moveTo = function(fromId, toId) {
    if (!fromId || fromId === toId) return;
    var ids = sorted.map(function(p) { return p.operator_id; });
    ids.splice(ids.indexOf(fromId), 1);
    ids.splice(ids.indexOf(toId), 0, fromId);
    setOrder(ids);
    try { localStorage.setItem(DASH_ORDER_KEY, JSON.stringify(ids)); } catch (e) {}
  };
  var dragFor = function(p) {
    return {
      dragging: dragId === p.operator_id,
      over: overId === p.operator_id && dragId && dragId !== p.operator_id,
      onDragStart: function(e) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', p.operator_id); setDragId(p.operator_id); },
      onDragEnd: function() { setDragId(null); setOverId(null); },
      onDragOver: function(e) { if (!dragId) return; e.preventDefault(); setOverId(p.operator_id); },
      onDrop: function(e) { e.preventDefault(); moveTo(dragId, p.operator_id); setDragId(null); setOverId(null); },
    };
  };

  var runningCount = (panels || []).filter(function(p) { return p.current && !p.current.overdue; }).length;
  var overdueCount = (panels || []).filter(function(p) { return p.current && p.current.overdue; }).length;

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="dashboard-header">
        <div>
          <h2 className="dashboard-title">{t('dashboard_title')}</h2>
          <p className="dashboard-subtitle">
            {t('dashboard_subtitle', { active: (panels || []).length, tasks: runningCount })}
            {overdueCount > 0 && <span className="dp-subtitle-late">{' · ' + t('dashboard_overdue_count', { count: overdueCount })}</span>}
          </p>
        </div>
      </div>
      {error && <div className="auth-error" style={{ marginBottom: '12px' }}>{error}</div>}
      {panels === null && <div className="jb-placeholder">{t('common_loading')}</div>}
      {panels && panels.length === 0 && !error && <div className="jb-placeholder">{t('dashboard_no_operators')}</div>}
      {addFor && <AdhocTaskModal panel={addFor} onClose={function() { setAddFor(null); }} onAdded={load} />}
      <div className="dp-grid">
        {sorted.map(function(p) { return <OperatorPanel key={p.operator_id} panel={p} drag={dragFor(p)} onAdd={function() { setAddFor(p); }} />; })}
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/Maps.jsx ── */
// Maps and Views (under Dashboard): one tabbed panel.
//   Map               OpenStreetMap (Leaflet): operators at their last known position
//                     (operators_map) and the account's places (locations_get)
//   Indoor locations  places operators work inside — recognised by the phone from their
//                     Wi-Fi networks (locations_get › ssid_list)
// Leaflet is loaded from unpkg the first time the page opens.

var LEAFLET_VERSION = '1.9.4';
var leafletReady = null;
function ensureLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!leafletReady) {
    leafletReady = new Promise(function(resolve, reject) {
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@' + LEAFLET_VERSION + '/dist/leaflet.css';
      document.head.appendChild(css);
      var js = document.createElement('script');
      js.src = 'https://unpkg.com/leaflet@' + LEAFLET_VERSION + '/dist/leaflet.js';
      js.onload = function() { resolve(window.L); };
      js.onerror = function() { leafletReady = null; reject(new Error('Could not load the map library')); };
      document.head.appendChild(js);
    });
  }
  return leafletReady;
}

var escHtml = function(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
var ssids = function(loc) { return String(loc.ssid_list || '').split(/[,;\n]/).map(function(s) { return s.trim(); }).filter(Boolean); };

var MapsMap = function({ operators, locations }) {
  var { useEffect, useRef, useState } = React;
  var box = useRef(null);
  var map = useRef(null);
  var [error, setError] = useState('');

  useEffect(function() {
    var alive = true;
    ensureLeaflet().then(function(L) {
      if (!alive || !box.current) return;
      if (!map.current) {
        map.current = L.map(box.current, { zoomControl: true }).setView([1.3521, 103.8198], 11);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map.current);
      }
      var m = map.current;
      if (m._oaLayer) m.removeLayer(m._oaLayer);
      var layer = L.layerGroup().addTo(m);
      m._oaLayer = layer;
      var points = [];

      locations.forEach(function(loc) {
        if (loc.lat == null || loc.lon == null) return;
        var ll = [Number(loc.lat), Number(loc.lon)];
        points.push(ll);
        L.circleMarker(ll, { radius: 8, color: '#7c3aed', weight: 2, fillColor: '#ede9fe', fillOpacity: 0.9 })
          .bindPopup('<strong>' + escHtml(loc.location_name) + '</strong>' + (loc.location_address ? '<br>' + escHtml(loc.location_address) : '')
                     + (ssids(loc).length ? '<br><small>Wi-Fi: ' + escHtml(ssids(loc).join(', ')) + '</small>' : ''))
          .addTo(layer);
      });

      operators.forEach(function(op) {
        if (op.lat == null || op.lon == null) return;
        var ll = [Number(op.lat), Number(op.lon)];
        points.push(ll);
        var photo = cachedOperatorPhoto(op.operator_profile_pic_file_name, null);
        var name = String(op.operator_name || '').replace(/\(.*\)$/, '').trim();
        var inner = photo ? '<img src="' + photo + '" alt="">' : escHtml(operatorInitials(name));
        var icon = L.divIcon({
          className: 'mp-op-marker',
          html: '<span class="mp-op-pin" style="background:' + (photo ? '#fff' : operatorColor(op.operator_id)) + '">' + inner + '</span>',
          iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -16],
        });
        L.marker(ll, { icon: icon })
          .bindPopup('<strong>' + escHtml(op.operator_name) + '</strong>'
                     + (op.task_name ? '<br>' + escHtml(op.task_name) : '')
                     + (op.last_seen ? '<br><small>Last seen ' + escHtml(op.last_seen) + '</small>' : ''))
          .addTo(layer);
      });

      if (points.length === 1) m.setView(points[0], 15);
      else if (points.length > 1) m.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
      setTimeout(function() { m.invalidateSize(); }, 0);
    }).catch(function(err) { if (alive) setError(err.message); });
    return function() { alive = false; };
  }, [operators, locations]);

  useEffect(function() { return function() { if (map.current) { map.current.remove(); map.current = null; } }; }, []);

  return (
    <div className="mp-map-wrap">
      {error && <div className="auth-error" style={{ margin: '12px' }}>{error}</div>}
      <div ref={box} className="mp-map" />
      <div className="mp-legend">
        <span><span className="mp-legend-op" /> Operators ({operators.filter(function(o) { return o.lat != null; }).length})</span>
        <span><span className="mp-legend-loc" /> Places ({locations.filter(function(l) { return l.lat != null; }).length})</span>
      </div>
    </div>
  );
};

var MapsIndoor = function({ locations }) {
  var indoor = locations.filter(function(l) { return ssids(l).length; });
  if (!indoor.length) {
    return (
      <div className="mp-empty">
        <strong>No indoor locations yet</strong>
        <p>Indoor locations are the places your operators work inside — a warehouse, a hotel floor, an office. The phone recognises them from their Wi-Fi networks, where GPS is not precise enough.</p>
      </div>
    );
  }
  return (
    <div className="mp-indoor">
      {indoor.map(function(loc) {
        return (
          <article key={loc.location_id} className="mp-place">
            <h4>{loc.location_name}</h4>
            {loc.location_address && <p className="mp-place-addr">{loc.location_address}</p>}
            <div className="mp-wifi">
              {ssids(loc).map(function(s) { return <span key={s} className="mp-ssid">{s}</span>; })}
            </div>
          </article>
        );
      })}
    </div>
  );
};

var Maps = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [tab, setTab]             = useState(function() { return localStorage.getItem('maps_tab') || 'map'; });
  var [operators, setOperators] = useState([]);
  var [locations, setLocations] = useState([]);
  var [error, setError]         = useState('');
  var [loading, setLoading]     = useState(true);

  var load = function() {
    setError('');
    return Promise.all([
      callRpc('operators_map').catch(function(e) { setError(e.message || 'Could not load operator positions'); return []; }),
      callRpc('locations_get').catch(function() { return []; }),
    ]).then(function(res) {
      setOperators(Array.isArray(res[0]) ? res[0] : []);
      setLocations(Array.isArray(res[1]) ? res[1] : []);
    }).finally(function() { setLoading(false); });
  };
  useEffect(function() { load(); }, []);
  if (typeof useRefresh === 'function') useRefresh(load);

  var choose = function(k) { setTab(k); localStorage.setItem('maps_tab', k); };
  var indoorCount = locations.filter(function(l) { return ssids(l).length; }).length;

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Maps and Views</h2>
          <p>Where your operators are, and the places they work.</p>
        </div>
      </div>
      {error && <div className="auth-error" style={{ marginBottom: '12px' }}>{error}</div>}
      <div className="mp-panel">
        <div className="mp-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'map'} className={'mp-tab' + (tab === 'map' ? ' mp-tab--on' : '')} onClick={function() { choose('map'); }}>Map</button>
          <button role="tab" aria-selected={tab === 'indoor'} className={'mp-tab' + (tab === 'indoor' ? ' mp-tab--on' : '')} onClick={function() { choose('indoor'); }}>
            Indoor locations{indoorCount ? ' (' + indoorCount + ')' : ''}
          </button>
        </div>
        <div className="mp-body">
          {loading && <div className="jb-empty">{'Loading…'}</div>}
          {!loading && tab === 'map' && <MapsMap operators={operators} locations={locations} />}
          {!loading && tab === 'indoor' && <MapsIndoor locations={locations} />}
        </div>
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/ExecReports.jsx ── */
// Overview › Reports › Executive Reports — placeholder screen for the management summaries.
var ExecReports = function({ activePage, onNavSelect, onLogout }) {
  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>{t('nav_exec_reports')}</h2>
          <p>{t('exec_reports_subtitle')}</p>
        </div>
      </div>
      <div className="app-empty-state">
        <p className="empty-hint">{t('billing_coming_soon')}</p>
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/FormEntries.jsx ── */
// Form Entries — everything submitted (or just opened) through the account's forms.
//
// Ported from thermotrack.io's Form Entries screen (app.jsx FormEntriesPage), which talks to the
// same app_api.form_* functions. ThermoTrack's helpers are swapped for OpsAssist's:
//   API.call('/rpc/x', 'POST', body)   → callRpc('x', body)          (src/constants.js)
//   t(key), useLang()                  → FE_TEXT below (plain English, ready to translate)
//   page-header / tt-btn / tt-pill     → lv-header, btn-filter, btn-add, fe-pill (FormEntries.css)
//   ExportMenu + ensureExport()        → a small menu here; XLSX is already a global (index.html)
//
// What this database has: form_entries_list(from_ts, to_ts, filter_form_id, only_submitted,
// search_text), form_entry_raw(entry_id), form_get_list(), form_fields_list(get_form_id).
// ThermoTrack's form_entry_view (server-built HTML), form_entry_files / form_file_download
// (attachments) and form_entry_archive (archive / restore) do not exist here, so:
//   - View lays the answers out client-side from form_entry_raw + form_fields_list;
//   - attachments are not listed (answers that are image URLs / data URIs are shown inline);
//   - there is no Archive action or "Archived" filter.
//
// Everything sits inside one function so its helpers do not become globals; only FormEntries is.
var FormEntries = (function() {
  var { useState, useEffect, useRef } = React;

  /* ───── UI text, in one place so it can be translated later ───── */
  var FE_TEXT = {
    title: 'Form Entries',
    sub: 'Everything submitted through your forms.',
    all_forms: 'All forms',
    submitted_only: 'Submitted only',
    search: 'Search entries…',
    entries: 'entries',
    one_entry: 'entry',
    loading: 'Loading…',
    no_entries: 'No entries in this period.',
    col_form: 'Form',
    col_submission: 'Submission ID',
    col_reference: 'Reference',
    col_status: 'Status',
    col_opened: 'Opened',
    col_submitted: 'Submitted',
    col_answers: 'Answers',
    col_ip: 'Source IP',
    col_actions: 'Actions',
    status_submitted: 'Submitted',
    status_opened: 'Opened',
    columns: 'Columns',
    export: 'Export',
    export_xls: 'Excel (.xlsx)',
    export_csv: 'CSV',
    view: 'View',
    view_title: 'Form Entry',
    raw_data: 'Raw data',
    raw_title: 'Submitted data',
    no_raw: 'This entry has no saved data.',
    unparseable: 'The stored payload is not valid JSON — showing it exactly as saved.',
    raw_empty: 'Empty object — no fields in this raw record.',
    copy_json: 'Copy JSON',
    copy_id: 'Copy the submission ID',
    copied: 'Copied',
    close: 'Close',
    print: 'Print / PDF',
    other_answers: 'Other answers',
    no_answer: '—',
    yes: 'Yes',
    no: 'No',
    from: 'From',
    to: 'To',
    showing: 'Showing {n} entries',
    time_24h: 'Last 24 hours',
    time_7d: 'Last 7 days',
    time_30d: 'Last 30 days',
    time_90d: 'Last 90 days',
    time_month: 'This month',
    time_all: 'All time',
    time_custom: 'Custom range',
  };
  var T = function(k) { return FE_TEXT[k] || k; };

  /* Columns the cog can hide. Chosen columns are kept per browser. */
  var FE_COLS = [
    { col: 'form',       def: true,  label: 'col_form' },
    { col: 'submission', def: true,  label: 'col_submission' },
    { col: 'reference',  def: true,  label: 'col_reference' },
    { col: 'status',     def: true,  label: 'col_status' },
    { col: 'opened',     def: true,  label: 'col_opened' },
    { col: 'submitted',  def: true,  label: 'col_submitted' },
    { col: 'answers',    def: true,  label: 'col_answers' },
    { col: 'ip',         def: false, label: 'col_ip' },
  ];
  var FE_COLS_KEY = 'oa_form_entries_cols', FE_COLS_KNOWN_KEY = 'oa_form_entries_cols_known';

  var TIME_OPTS = ['24H', '7D', '30D', '90D', 'MONTH', 'ALL', 'CUSTOM'];
  var TIME_LABEL = { '24H': 'time_24h', '7D': 'time_7d', '30D': 'time_30d', '90D': 'time_90d', MONTH: 'time_month', ALL: 'time_all', CUSTOM: 'time_custom' };

  /* Field types that only show something on the form and never carry an answer. */
  var DISPLAY_ONLY = ['SEPARATOR', 'INFO', 'LABEL', 'HTML', 'QR', 'DYNAMIC_QR', 'BARCODE', 'URL', 'IMAGE'];

  /* [from, to] as ISO strings — the server takes both, so a preset ends at "now". */
  function timeWindow(range, tFrom, tTo) {
    var now = new Date(), start = new Date();
    switch (range) {
      case '24H':   start.setDate(now.getDate() - 1);  break;
      case '30D':   start.setDate(now.getDate() - 30); break;
      case '90D':   start.setDate(now.getDate() - 90); break;
      case 'MONTH': return [new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), now.toISOString()];
      case 'ALL':   return ['1970-01-01T00:00:00Z', now.toISOString()];
      case 'CUSTOM':
        return [tFrom ? new Date(tFrom).toISOString() : '1970-01-01T00:00:00Z',
                tTo   ? new Date(tTo).toISOString()   : now.toISOString()];
      default:      start.setDate(now.getDate() - 7);
    }
    return [start.toISOString(), now.toISOString()];
  }

  function fmtTs(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    } catch (e) { return iso; }
  }

  function tzShort() {
    try {
      var p = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
      var z = p.find(function(x) { return x.type === 'timeZoneName'; });
      return z ? z.value : '';
    } catch (e) { return ''; }
  }

  function exportFileName(ext) {
    var d = new Date(), pad = function(n) { return String(n).padStart(2, '0'); };
    return 'Form_Entries_' + d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + '_' + pad(d.getHours()) + pad(d.getMinutes()) + '.' + ext;
  }

  function parseJson(text) {
    if (!text) return null;
    try { var v = JSON.parse(text); return v && typeof v === 'object' && !Array.isArray(v) ? v : null; } catch (e) { return null; }
  }

  /* The answers in the order of the form. The form host saves each answer under the field's
     override name, else its title; older entries used field_<n>. Keys no field claims are
     listed after, so nothing in the payload is hidden. */
  function layoutAnswers(fields, data) {
    var used = {}, rows = [];
    (fields || []).forEach(function(f, i) {
      var keys = [f.field_override_name, f.field_title, f.field_caption, 'field_' + f.field_offset, 'field_' + (i + 1), f.form_field_id]
        .map(function(k) { return (k == null ? '' : String(k)); }).filter(Boolean);
      var key = keys.find(function(k) { return Object.prototype.hasOwnProperty.call(data, k) && !used[k]; });
      if (!key) {   /* keys can carry stray spaces (" Choice 2") */
        key = Object.keys(data).find(function(k) { return !used[k] && keys.some(function(c) { return c.trim() === k.trim(); }); });
      }
      if (key) used[key] = true;
      if (!key && DISPLAY_ONLY.indexOf(f.data_type_name) >= 0) return;
      rows.push({ id: f.form_field_id, label: (f.field_title || f.field_caption || f.field_override_name || key || '').trim(),
                  type: f.data_type_name, value: key ? data[key] : undefined });
    });
    var extra = Object.keys(data).filter(function(k) { return !used[k]; }).map(function(k) { return { id: 'x:' + k, label: k, value: data[k] }; });
    return { rows: rows, extra: extra };
  }

  var IMG_URL_RE = /^(https?:\/\/\S+\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?\S*)?|data:image\/[a-z+]+;base64,)/i;

  function answerText(v) {
    if (v === undefined || v === null || v === '') return T('no_answer');
    if (v === true) return T('yes');
    if (v === false) return T('no');
    if (Array.isArray(v)) return v.map(answerText).join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  function Answer({ value }) {
    if (typeof value === 'string' && IMG_URL_RE.test(value)) {
      return <a href={value} target="_blank" rel="noopener noreferrer"><img className="fe-answer-img" src={value} alt="" /></a>;
    }
    if (typeof value === 'string' && /^https?:\/\/\S+$/i.test(value)) {
      return <a className="fe-link" href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
    }
    var text = answerText(value);
    return <span className={text === T('no_answer') ? 'fe-muted' : ''}>{text}</span>;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  /* Print the laid-out entry from a plain window; the browser's print dialog also saves as PDF. */
  function printEntry(entry, layout) {
    var row = function(r) {
      var v = r.value;
      var cell = (typeof v === 'string' && IMG_URL_RE.test(v)) ? '<img src="' + esc(v) + '" style="max-width:320px;max-height:240px">' : esc(answerText(v));
      return '<tr><th>' + esc(r.label) + '</th><td>' + cell + '</td></tr>';
    };
    var html = '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(entry.form_name || 'Form') + '</title>' +
      '<style>body{font-family:Inter,Arial,sans-serif;color:#111827;margin:32px}h1{font-size:20px;margin:0 0 4px}' +
      '.m{color:#6b7280;font-size:12.5px;margin-bottom:18px}table{border-collapse:collapse;width:100%}' +
      'th,td{text-align:left;vertical-align:top;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:13px}' +
      'th{width:38%;color:#374151;font-weight:600}h2{font-size:14px;margin:22px 0 6px;color:#6b7280}</style></head><body>' +
      '<h1>' + esc(entry.form_name) + (entry.form_no ? ' · ' + esc(entry.form_no) : '') + '</h1>' +
      '<div class="m">' + (entry.reference_number != null ? esc(T('col_reference')) + ' ' + esc(entry.reference_number) + ' · ' : '') +
      esc(T('col_submitted')) + ': ' + esc(fmtTs(entry.submitted_ts)) + ' · ' + esc(entry.form_data_id) + '</div>' +
      '<table>' + layout.rows.map(row).join('') + '</table>' +
      (layout.extra.length ? '<h2>' + esc(T('other_answers')) + '</h2><table>' + layout.extra.map(row).join('') + '</table>' : '') +
      '</body></html>';
    var w = window.open('', '_blank');
    if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
    w.focus();
    setTimeout(function() { w.print(); }, 300);
  }

  function Modal({ title, sub, wide, onClose, children, actions }) {
    useEffect(function() {
      var h = function(e) { if (e.key === 'Escape') onClose(); };
      document.addEventListener('keydown', h);
      return function() { document.removeEventListener('keydown', h); };
    }, []);
    return (
      <div className="op-modal-backdrop" onMouseDown={function(e) { if (e.target === e.currentTarget) onClose(); }}>
        <div className={'op-modal fe-modal' + (wide ? ' fe-modal--wide' : '')} role="dialog" aria-modal="true" aria-label={title}>
          <div className="fe-modal-head">
            <div className="fe-modal-titles">
              <div className="op-modal-title">{title}</div>
              {sub}
            </div>
            <button type="button" className="fe-x" aria-label={T('close')} onClick={onClose}>&times;</button>
          </div>
          <div className="fe-modal-body">{children}</div>
          <div className="op-modal-actions">{actions}</div>
        </div>
      </div>
    );
  }

  var CopyGlyph = function() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>; };
  var TickGlyph = function() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>; };
  var CodeGlyph = function() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>; };
  var CogGlyph = function() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>; };

  function loadCols() {
    try {
      var s = JSON.parse(localStorage.getItem(FE_COLS_KEY) || 'null');
      if (Array.isArray(s)) {
        var known = null;
        try { known = JSON.parse(localStorage.getItem(FE_COLS_KNOWN_KEY) || 'null'); } catch (e) {}
        var out = new Set(s);
        /* a column added since the choice was saved starts visible */
        if (Array.isArray(known)) FE_COLS.forEach(function(c) { if (c.def && known.indexOf(c.col) < 0) out.add(c.col); });
        return out;
      }
    } catch (e) {}
    return new Set(FE_COLS.filter(function(c) { return c.def; }).map(function(c) { return c.col; }));
  }

  return function FormEntries({ activePage, onNavSelect, onLogout }) {
    var [entries, setEntries] = useState(null);       /* null while the first load runs */
    var [forms, setForms] = useState([]);
    var [loading, setLoading] = useState(true);
    var [error, setError] = useState('');
    var [timeRange, setTimeRange] = useState('7D');
    var [tFrom, setTFrom] = useState('');
    var [tTo, setTTo] = useState('');
    var [formId, setFormId] = useState('');
    var [onlySubmitted, setOnlySubmitted] = useState(true);
    var [search, setSearch] = useState('');
    var [visCols, setVisCols] = useState(loadCols);
    var [colOpen, setColOpen] = useState(false);
    var [expOpen, setExpOpen] = useState(false);
    var [copiedId, setCopiedId] = useState(null);
    var [raw, setRaw] = useState(null);     /* {entry, loading|row|error, copied} */
    var [view, setView] = useState(null);   /* {entry, loading|layout|error} */
    var colRef = useRef(null), expRef = useRef(null), loadSeq = useRef(0), fieldCache = useRef({});
    var tz = tzShort();

    useEffect(function() {
      try {
        localStorage.setItem(FE_COLS_KEY, JSON.stringify(Array.from(visCols)));
        localStorage.setItem(FE_COLS_KNOWN_KEY, JSON.stringify(FE_COLS.map(function(c) { return c.col; })));
      } catch (e) {}
    }, [visCols]);
    var showCol = function(c) { return visCols.has(c); };
    var toggleCol = function(c) { setVisCols(function(prev) { var n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; }); };

    useEffect(function() {
      var h = function(e) {
        if (colRef.current && !colRef.current.contains(e.target)) setColOpen(false);
        if (expRef.current && !expRef.current.contains(e.target)) setExpOpen(false);
      };
      document.addEventListener('mousedown', h);
      return function() { document.removeEventListener('mousedown', h); };
    }, []);

    useEffect(function() {
      callRpc('form_get_list', {})
        .then(function(d) { setForms(Array.isArray(d) ? d : []); })
        .catch(function() {});
    }, []);

    function load() {
      var w = timeWindow(timeRange, tFrom, tTo), seq = ++loadSeq.current;
      setLoading(true);
      callRpc('form_entries_list', {
        from_ts: w[0], to_ts: w[1],
        filter_form_id: formId || null,
        only_submitted: onlySubmitted,
        search_text: search.trim() || null,
      })
        .then(function(d) { if (seq === loadSeq.current) { setEntries(Array.isArray(d) ? d : []); setError(''); } })
        .catch(function(e) { if (seq === loadSeq.current) { setEntries([]); setError(e.message || String(e)); } })
        .finally(function() { if (seq === loadSeq.current) setLoading(false); });
    }

    useEffect(load, [timeRange, tFrom, tTo, formId, onlySubmitted]);
    /* the search box waits for a pause in typing */
    var firstSearch = useRef(true);
    useEffect(function() {
      if (firstSearch.current) { firstSearch.current = false; return; }
      var id = setTimeout(load, 400);
      return function() { clearTimeout(id); };
    }, [search]);

    function fetchRaw(entry) {
      return callRpc('form_entry_raw', { entry_id: entry.form_data_id }).then(function(d) { return Array.isArray(d) ? d[0] : d; });
    }
    function fetchFields(id) {
      if (!fieldCache.current[id]) {
        fieldCache.current[id] = callRpc('form_fields_list', { get_form_id: id })
          .then(function(d) { return Array.isArray(d) ? d : []; })
          .catch(function(e) { delete fieldCache.current[id]; throw e; });
      }
      return fieldCache.current[id];
    }

    function openRaw(entry) {
      setRaw({ entry: entry, loading: true });
      fetchRaw(entry)
        .then(function(row) { setRaw(row ? { entry: entry, row: row } : { entry: entry, error: T('no_raw') }); })
        .catch(function(e) { setRaw({ entry: entry, error: e.message || String(e) }); });
    }

    function openView(entry) {
      setView({ entry: entry, loading: true });
      Promise.all([fetchRaw(entry), fetchFields(entry.form_id)])
        .then(function(r) {
          var data = r[0] && parseJson(r[0].form_data_raw);
          if (!data) { setView({ entry: entry, error: T('no_raw') }); return; }
          setView({ entry: entry, layout: layoutAnswers(r[1], data) });
        })
        .catch(function(e) { setView({ entry: entry, error: e.message || String(e) }); });
    }

    function copyId(id) {
      if (navigator.clipboard) navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(function() { setCopiedId(function(c) { return c === id ? null : c; }); }, 1500);
    }

    /* Exports follow the visible columns, so what you see is what you get. */
    function exportRows() {
      return (entries || []).map(function(e) {
        var r = {};
        if (showCol('form'))       { r[T('col_form')] = e.form_name || ''; r['Form No.'] = e.form_no || ''; }
        if (showCol('submission')) r[T('col_submission')] = e.form_data_id || '';
        if (showCol('reference'))  r[T('col_reference')] = e.reference_number == null ? '' : e.reference_number;
        if (showCol('status'))     r[T('col_status')] = e.submitted ? T('status_submitted') : T('status_opened');
        if (showCol('opened'))     r[T('col_opened')] = fmtTs(e.opened_ts);
        if (showCol('submitted'))  r[T('col_submitted')] = e.submitted_ts ? fmtTs(e.submitted_ts) : '';
        if (showCol('answers'))    r[T('col_answers')] = Number(e.answer_count) || 0;
        if (showCol('ip'))         r[T('col_ip')] = e.source_ip || '';
        return r;
      });
    }
    function exportXls() {
      var rows = exportRows();
      if (!rows.length || typeof XLSX === 'undefined') return;
      var ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = Object.keys(rows[0]).map(function(k) { return { wch: Math.max(k.length + 2, 18) }; });
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Form Entries');
      XLSX.writeFile(wb, exportFileName('xlsx'));
    }
    function exportCsv() {
      var rows = exportRows();
      if (!rows.length) return;
      var headers = Object.keys(rows[0]);
      var cell = function(v) { var s = String(v == null ? '' : v); return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
      var csv = [headers.map(cell).join(',')].concat(rows.map(function(r) { return headers.map(function(h) { return cell(r[h]); }).join(','); })).join('\r\n');
      var a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
      a.download = exportFileName('csv');
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function() { URL.revokeObjectURL(a.href); }, 1000);
    }

    var cols = FE_COLS.filter(function(c) { return showCol(c.col); });
    var list = entries || [];
    var rawText = raw && raw.row ? (raw.row.form_data_pretty || raw.row.form_data_raw || '') : '';

    return (
      <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
        <div className="lv-header">
          <div className="lv-header-left">
            <h2>{T('title')}</h2>
            <p>{T('sub')}</p>
          </div>
          <div className="lv-header-right">
            <div className="fe-dd" ref={expRef}>
              <button type="button" className="btn-filter" disabled={!list.length} aria-haspopup="menu" aria-expanded={expOpen}
                onClick={function() { setExpOpen(function(o) { return !o; }); }}>
                <ExportIcon /> {T('export')} <ChevDown />
              </button>
              {expOpen && (
                <div className="fe-menu fe-menu--right" role="menu">
                  <button type="button" role="menuitem" className="fe-menu-item" onClick={function() { setExpOpen(false); exportXls(); }}>{T('export_xls')}</button>
                  <button type="button" role="menuitem" className="fe-menu-item" onClick={function() { setExpOpen(false); exportCsv(); }}>{T('export_csv')}</button>
                </div>
              )}
            </div>
            <div className="fe-dd" ref={colRef}>
              <button type="button" className={'btn-filter fe-icon-btn' + (colOpen ? ' fe-icon-btn--on' : '')} title={T('columns')} aria-label={T('columns')}
                aria-expanded={colOpen} onClick={function() { setColOpen(function(o) { return !o; }); }}>
                <CogGlyph />
              </button>
              {colOpen && (
                <div className="fe-menu fe-menu--right">
                  <div className="fe-menu-head">{T('columns')}</div>
                  {FE_COLS.map(function(c) {
                    return (
                      <label key={c.col} className="fe-menu-check">
                        <input type="checkbox" className="lv-cb" checked={visCols.has(c.col)} onChange={function() { toggleCol(c.col); }} />
                        {T(c.label)}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lv-toolbar fe-toolbar">
          <div className="fe-filters">
            <select className="fe-select" value={timeRange} onChange={function(e) { setTimeRange(e.target.value); }} aria-label="Time range">
              {TIME_OPTS.map(function(v) { return <option key={v} value={v}>{T(TIME_LABEL[v])}</option>; })}
            </select>
            {timeRange === 'CUSTOM' && (
              <>
                <label className="jb-field">{T('from')} <input type="datetime-local" className="jb-input" value={tFrom} onChange={function(e) { setTFrom(e.target.value); }} /></label>
                <label className="jb-field">{T('to')} <input type="datetime-local" className="jb-input" value={tTo} onChange={function(e) { setTTo(e.target.value); }} /></label>
              </>
            )}
            <select className="fe-select" value={formId} onChange={function(e) { setFormId(e.target.value); }} aria-label={T('col_form')}>
              <option value="">{T('all_forms')}</option>
              {forms.map(function(f) { return <option key={f.form_id} value={f.form_id}>{f.form_name}</option>; })}
            </select>
            <label className="lv-toggle">
              <input type="checkbox" className="lv-cb" checked={onlySubmitted} onChange={function(e) { setOnlySubmitted(e.target.checked); }} />
              {T('submitted_only')}
            </label>
            <div className="lv-search fe-search">
              <span className="lv-search-icon"><SearchIcon /></span>
              <input className="lv-search-input" placeholder={T('search')} aria-label={T('search')} value={search}
                onChange={function(e) { setSearch(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Escape') setSearch(''); }} />
              {search && <button className="lv-search-clear" title="Clear search" onClick={function() { setSearch(''); }}>&times;</button>}
            </div>
          </div>
          <span className="fe-count">{loading ? T('loading') : list.length + ' ' + (list.length === 1 ? T('one_entry') : T('entries'))}</span>
        </div>

        {error && <div className="auth-error" style={{ marginBottom: '12px' }}>{error}</div>}

        {entries === null && <div className="jb-placeholder">{T('loading')}</div>}
        {entries !== null && list.length === 0 && !error && <div className="jb-placeholder">{T('no_entries')}</div>}

        {list.length > 0 && (
          <div className="lv-table-wrap fe-table-wrap">
            <table className="lv-table fe-table">
              <thead>
                <tr>
                  {cols.map(function(c) {
                    return <th key={c.col}>{T(c.label)}{(c.col === 'opened' || c.col === 'submitted') && tz ? ' (' + tz + ')' : ''}</th>;
                  })}
                  <th className="fe-th-actions">{T('col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map(function(e) {
                  var copied = copiedId === e.form_data_id;
                  return (
                    <tr key={e.form_data_id}>
                      {showCol('form') && (
                        <td>
                          <div className="fe-form-name">{e.form_name}</div>
                          {e.form_no && <div className="fe-mono fe-muted">{e.form_no}</div>}
                          {e.form_description && <div className="fe-form-desc">{e.form_description}</div>}
                        </td>
                      )}
                      {showCol('submission') && (
                        <td className="fe-nowrap">
                          <span className="fe-id">
                            <span className="fe-mono fe-muted" title={e.form_data_id}>{String(e.form_data_id).split('-').slice(0, 4).join('-')}</span>
                            <button type="button" className={'fe-copy' + (copied ? ' fe-copy--done' : '')} onClick={function() { copyId(e.form_data_id); }}
                              aria-label={T('copy_id')} title={copied ? T('copied') : T('copy_id')}>
                              {copied ? <TickGlyph /> : <CopyGlyph />}
                            </button>
                          </span>
                        </td>
                      )}
                      {showCol('reference') && <td className="fe-mono">{e.reference_number == null ? '—' : e.reference_number}</td>}
                      {showCol('status') && (
                        <td><span className={'fe-pill ' + (e.submitted ? 'fe-pill--ok' : 'fe-pill--muted')}><span className="fe-pill-dot" />{e.submitted ? T('status_submitted') : T('status_opened')}</span></td>
                      )}
                      {showCol('opened') && <td className="fe-nowrap fe-muted">{fmtTs(e.opened_ts)}</td>}
                      {showCol('submitted') && <td className="fe-nowrap">{fmtTs(e.submitted_ts)}</td>}
                      {showCol('answers') && <td className={Number(e.answer_count) ? '' : 'fe-muted'}>{Number(e.answer_count) || 0}</td>}
                      {showCol('ip') && <td className="fe-mono fe-muted">{e.source_ip || '—'}</td>}
                      <td className="fe-nowrap fe-td-actions">
                        <span className="fe-actions">
                          <button type="button" className="btn-filter fe-btn-sm" onClick={function() { openView(e); }} disabled={!e.has_raw} title={e.has_raw ? '' : T('no_raw')}>
                            <Eye /> {T('view')}
                          </button>
                          <button type="button" className="lv-action-btn" onClick={function() { openRaw(e); }} disabled={!e.has_raw}
                            title={e.has_raw ? T('raw_data') : T('no_raw')} aria-label={T('raw_data') + ': ' + (e.form_name || '')}>
                            <CodeGlyph />
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="fe-footer">{T('showing').replace('{n}', list.length)}</div>
          </div>
        )}

        {/* View — the answers laid out in the order of the form */}
        {view && (
          <Modal wide title={T('view_title')} onClose={function() { setView(null); }}
            sub={
              <div className="fe-modal-sub">
                <strong>{view.entry.form_name}{view.entry.form_no ? ' · ' + view.entry.form_no : ''}{view.entry.reference_number != null ? ' · ' + T('col_reference') + ' ' + view.entry.reference_number : ''}</strong>
                <span>{T('col_submitted')}: {fmtTs(view.entry.submitted_ts)}{tz ? ' (' + tz + ')' : ''}</span>
                <span className="fe-mono">{view.entry.form_data_id}</span>
              </div>
            }
            actions={
              <>
                {view.layout && <button type="button" className="btn-filter" onClick={function() { printEntry(view.entry, view.layout); }}>{T('print')}</button>}
                {view.layout && <button type="button" className="btn-filter" onClick={function() { var en = view.entry; setView(null); openRaw(en); }}><CodeGlyph /> {T('raw_data')}</button>}
                <button type="button" className="btn-add" onClick={function() { setView(null); }}>{T('close')}</button>
              </>
            }>
            {view.loading && <div className="fe-loading">{T('loading')}</div>}
            {view.error && <div className="auth-error">{view.error}</div>}
            {view.layout && (
              <>
                <dl className="fe-answers">
                  {view.layout.rows.map(function(r) {
                    return (
                      <div key={r.id} className="fe-answer">
                        <dt>{r.label || '—'}</dt>
                        <dd><Answer value={r.value} /></dd>
                      </div>
                    );
                  })}
                </dl>
                {view.layout.extra.length > 0 && (
                  <>
                    <div className="fe-answers-head">{T('other_answers')}</div>
                    <dl className="fe-answers">
                      {view.layout.extra.map(function(r) {
                        return (
                          <div key={r.id} className="fe-answer">
                            <dt className="fe-mono">{r.label}</dt>
                            <dd><Answer value={r.value} /></dd>
                          </div>
                        );
                      })}
                    </dl>
                  </>
                )}
              </>
            )}
          </Modal>
        )}

        {/* Raw payload, exactly as stored */}
        {raw && (
          <Modal wide title={T('raw_title')} onClose={function() { setRaw(null); }}
            sub={raw.row && (
              <div className="fe-modal-sub">
                <strong>{raw.row.form_name}{raw.row.form_no ? ' · ' + raw.row.form_no : ''}</strong>
                <span>{T('col_opened')}: {fmtTs(raw.row.opened_ts)}{tz ? ' (' + tz + ')' : ''}</span>
                <span>{T('col_submitted')}: {fmtTs(raw.row.submitted_ts)}{tz ? ' (' + tz + ')' : ''}</span>
                {raw.row.source_ip && <span>{T('col_ip')}: {raw.row.source_ip}</span>}
              </div>
            )}
            actions={
              <>
                {raw.row && (
                  <button type="button" className="btn-filter" onClick={function() {
                    if (navigator.clipboard) navigator.clipboard.writeText(rawText);
                    setRaw(function(r) { return r && Object.assign({}, r, { copied: true }); });
                  }}><CopyGlyph /> {raw.copied ? T('copied') : T('copy_json')}</button>
                )}
                <button type="button" className="btn-add" onClick={function() { setRaw(null); }}>{T('close')}</button>
              </>
            }>
            {raw.loading && <div className="fe-loading">{T('loading')}</div>}
            {raw.error && <div className="auth-error">{raw.error}</div>}
            {raw.row && (
              <>
                {!raw.row.form_data_pretty && <div className="fe-warn">{T('unparseable')}</div>}
                <pre className="fe-raw">{rawText || '—'}</pre>
                {/^\s*\{\s*\}\s*$/.test(rawText) && <div className="fe-note">{T('raw_empty')}</div>}
              </>
            )}
          </Modal>
        )}
      </AppShell>
    );
  };
})();

;
/* ── src/pages/DataExport.jsx ── */
// Data Export: build a custom extract of the account's tasks — pick the fields, filter them, optionally
// group and summarise, preview, then download as CSV or Excel. Same design as thermotrack.io's Data
// Export: the page only sends field / operator / bucket / aggregate KEYS as JSON; the server keeps its
// own whitelist of them and writes all of the SQL itself.
//   rows   data_export(spec) — sql/2026-10-18_data_export.sql (request and response described there)
//
// Everything here lives inside one function scope; the only global it defines is DataExport.

var DataExport = (function() {
  var h = React.createElement;

  /* ── All the words on the page ── */
  var DX_TEXT = {
    title: 'Data Export',
    sub: 'Build your own extract of tasks, operators and jobs, then download it as CSV or Excel.',
    steps: ['Fields', 'Filters', 'Grouping', 'Preview & export'],
    stepFieldsHelp: 'Choose the columns of your export. Each row is one scheduled task.',
    searchFields: 'Search fields',
    selectAll: 'Select all',
    clear: 'Clear',
    clearAll: 'Clear all',
    selectedN: 'Selected ({n})',
    selectedHelp: 'These columns will be exported, in this order.',
    noneSelected: 'No fields selected yet.',
    noFieldsMatch: 'No fields match.',
    removeField: 'Remove {f}',
    noFields: 'Choose at least one field first.',
    dateRange: 'Date range',
    dateRangeHelp: 'Only tasks whose chosen date falls in this range (both days included).',
    dateField: 'Date',
    field: 'Field',
    operator: 'Condition',
    from: 'From',
    to: 'To',
    anyDate: 'Any date',
    presets: [['7', 'Last 7 days'], ['30', 'Last 30 days'], ['month', 'This month'], ['lastmonth', 'Last month']],
    filters: 'Filters',
    filtersHelp: 'Rows must match every filter.',
    noFilters: 'No filters: every task in the date range is exported.',
    addFilter: 'Add filter',
    removeFilter: 'Remove filter',
    selectValue: 'Pick values',
    grouping: 'Grouping',
    enableGrouping: 'Group and summarise rows',
    groupHint: 'One row per combination of the fields you group by, with the totals you pick.',
    rawHint: 'Off: one row per task, with the fields you chose.',
    groupBy: 'Group by',
    groupByNone: 'Choose fields in step 1 that can be grouped (names, states, dates…).',
    aggregates: 'Totals',
    countRows: 'Number of tasks',
    noAggFields: 'Choose number fields in step 1 (minutes, counts) to total them.',
    summary: 'Export summary',
    summaryFields: 'Fields',
    summaryFilters: 'Filters',
    summaryGrouping: 'Grouping',
    none: 'None',
    nFilters: '{n} filter(s)',
    grouped: 'Grouped',
    rawRows: 'One row per task',
    edit: 'Edit',
    preview: 'Preview',
    previewHint: 'drag a heading to move the column, click it to sort',
    totalRows: 'Rows',
    loading: 'Loading…',
    noRows: 'No rows match. Try a wider date range or fewer filters.',
    exportCsv: 'Download CSV',
    exportXlsx: 'Download Excel',
    exporting: 'Preparing download…',
    exportLimit: 'Exports are limited to 50,000 rows.',
    truncated: 'Only the first {n} of {t} rows were exported. Narrow the date range or add filters to get the rest.',
    rowsPerPage: 'Rows per page',
    pageOf: 'Page {p} of {n}',
    first: 'First page', prev: 'Previous page', next: 'Next page', last: 'Last page',
    back: 'Back',
    nextStep: 'Next',
    reset: 'Start again',
    yes: 'Yes',
    no: 'No',
    times: 'Dates and times are in {tz}.',
    fileName: 'Data Export',
    sheetName: 'Data',
    noXlsx: 'The Excel library has not loaded. Try CSV, or reload the page.',
    failed: 'Could not load the data.',
  };

  /* ── Filter operators per field type. value: none | one | two | list (the server checks them too) ── */
  var DX_TYPES = {
    number: [
      { op: 'eq', label: '=', value: 'one' }, { op: 'neq', label: '≠', value: 'one' },
      { op: 'gt', label: '>', value: 'one' }, { op: 'gte', label: '≥', value: 'one' },
      { op: 'lt', label: '<', value: 'one' }, { op: 'lte', label: '≤', value: 'one' },
      { op: 'between', label: 'Between', value: 'two' },
      { op: 'is_null', label: 'Is empty', value: 'none' }, { op: 'not_null', label: 'Has a value', value: 'none' },
    ],
    text: [
      { op: 'contains', label: 'Contains', value: 'one' }, { op: 'not_contains', label: 'Does not contain', value: 'one' },
      { op: 'eq', label: 'Equals', value: 'one' }, { op: 'starts_with', label: 'Starts with', value: 'one' },
      { op: 'ends_with', label: 'Ends with', value: 'one' },
      { op: 'is_null', label: 'Is empty', value: 'none' }, { op: 'not_null', label: 'Has a value', value: 'none' },
    ],
    timestamp: [
      { op: 'today', label: 'Today', value: 'none' }, { op: 'last_24h', label: 'Last 24 hours', value: 'none' },
      { op: 'last_7d', label: 'Last 7 days', value: 'none' }, { op: 'last_30d', label: 'Last 30 days', value: 'none' },
      { op: 'this_month', label: 'This month', value: 'none' }, { op: 'last_month', label: 'Last month', value: 'none' },
      { op: 'on_date', label: 'On', value: 'one' }, { op: 'before', label: 'Before', value: 'one' },
      { op: 'after', label: 'After', value: 'one' }, { op: 'between', label: 'Between', value: 'two' },
      { op: 'is_null', label: 'Is empty', value: 'none' }, { op: 'not_null', label: 'Has a value', value: 'none' },
    ],
    boolean: [{ op: 'is_true', label: 'Yes', value: 'none' }, { op: 'is_false', label: 'No', value: 'none' }],
    enum: [{ op: 'in', label: 'Is any of', value: 'list' }, { op: 'not_in', label: 'Is none of', value: 'list' }],
  };
  var DX_BUCKETS = [{ key: 'hour', label: 'Hour' }, { key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }];
  var DX_AGGS = [{ key: 'sum', label: 'Total' }, { key: 'avg', label: 'Average' }, { key: 'min', label: 'Min' }, { key: 'max', label: 'Max' }, { key: 'count', label: 'Count' }];

  /* ── The field catalogue. key must match the server's whitelist in app_api.data_export.
        type drives the filter operators; group: true = a group-by field, 'bucket' = a date grouped
        by hour / day / week / month; agg: true = can be totalled when grouping. ── */
  var DX_CATALOGUE = [
    { key: 'task', label: 'Task', fields: [
      { key: 'task_start',          label: 'Scheduled start',      type: 'timestamp', group: 'bucket' },
      { key: 'task_end',            label: 'Scheduled end',        type: 'timestamp' },
      { key: 'task_name',           label: 'Task name',            type: 'text', group: true },
      { key: 'task_description',    label: 'Task description',     type: 'text' },
      { key: 'task_state',          label: 'State',                type: 'enum', group: true, options: ['Not started', 'In progress', 'Paused', 'Restarted', 'Done', 'Cancelled'] },
      { key: 'card_type',           label: 'Card type',            type: 'enum', group: true, options: ['Task', 'Break', 'Clock in', 'Clock out'] },
      { key: 'priority',            label: 'Priority',             type: 'enum', group: true, options: ['High', 'Medium', 'Low'] },
      { key: 'location',            label: 'Location',             type: 'text', group: true },
      { key: 'cancellation_note',   label: 'Cancellation note',    type: 'text' },
    ]},
    { key: 'timing', label: 'Timing', fields: [
      { key: 'required_minutes',    label: 'Required minutes',     type: 'number', agg: true },
      { key: 'started_at',          label: 'Started',              type: 'timestamp', group: 'bucket' },
      { key: 'ended_at',            label: 'Ended',                type: 'timestamp' },
      { key: 'actual_minutes',      label: 'Actual minutes',       type: 'number', agg: true },
      { key: 'start_delay_minutes', label: 'Start delay (minutes)', type: 'number', agg: true },
      { key: 'done',                label: 'Done',                 type: 'boolean', group: true },
      { key: 'on_time',             label: 'Done on time',         type: 'boolean', group: true },
      { key: 'overdue',             label: 'Overdue',              type: 'boolean', group: true },
      { key: 'over_time',           label: 'Took longer than required', type: 'boolean', group: true },
    ]},
    { key: 'operator', label: 'Operator', fields: [
      { key: 'operator_name',       label: 'Operator name',        type: 'text', group: true },
      { key: 'nickname',            label: 'Nickname',             type: 'text', group: true },
      { key: 'staff_id',            label: 'Staff ID',             type: 'text', group: true },
      { key: 'operator_enabled',    label: 'Operator enabled',     type: 'boolean', group: true },
    ]},
    { key: 'job', label: 'Job', fields: [
      { key: 'job_name',            label: 'Job name',             type: 'text', group: true },
    ]},
    { key: 'exceptions', label: 'Exceptions', fields: [
      { key: 'exception_count',     label: 'Exception count',      type: 'number', agg: true },
      { key: 'has_exception',       label: 'Has exceptions',       type: 'boolean', group: true },
      { key: 'exception_types',     label: 'Exception types',      type: 'text', group: true },
      { key: 'exception_comments',  label: 'Exception comments',   type: 'text' },
    ]},
    { key: 'captures', label: 'Captures', fields: [
      { key: 'picture_count',       label: 'Picture count',        type: 'number', agg: true },
      { key: 'signature_count',     label: 'Signature count',      type: 'number', agg: true },
      { key: 'capture_skipped',     label: 'Capture not collected', type: 'boolean', group: true },
    ]},
  ];

  var DX_FIELDS = [];
  DX_CATALOGUE.forEach(function(c) { c.fields.forEach(function(f) { DX_FIELDS.push(Object.assign({ cat: c.key }, f)); }); });
  var dxField = function(k) { for (var i = 0; i < DX_FIELDS.length; i++) if (DX_FIELDS[i].key === k) return DX_FIELDS[i]; return null; };
  var dxLabel = function(k) { var f = dxField(k); return f ? f.label : k; };
  var dxOps = function(f) { return f ? DX_TYPES[f.type] : []; };
  var dxFmt = function(s, o) { return String(s).replace(/\{(\w+)\}/g, function(m, k) { return o[k] != null ? o[k] : m; }); };
  var dxBucketLabel = function(b) { var x = DX_BUCKETS.filter(function(y) { return y.key === b; })[0]; return x ? x.label : b; };
  var dxAggLabel = function(a) { var x = DX_AGGS.filter(function(y) { return y.key === a; })[0]; return x ? x.label : a; };
  var DX_DATE_FIELDS = DX_FIELDS.filter(function(f) { return f.type === 'timestamp'; });

  var dxPad = function(n) { return String(n).padStart(2, '0'); };
  var dxDay = function(d) { return d.getFullYear() + '-' + dxPad(d.getMonth() + 1) + '-' + dxPad(d.getDate()); };
  var dxPreset = function(p) {
    var now = new Date(), from = new Date(now), to = new Date(now);
    if (p === 'month') from = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (p === 'lastmonth') { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
    else from.setDate(now.getDate() - (Number(p) - 1));
    return { from: dxDay(from), to: dxDay(to) };
  };
  var DX_TZ = (function() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; } })();

  /* the header of an output column: the server sends stable keys, the page names them */
  var dxColLabel = function(c) {
    if (c.key === 'row_count') return DX_TEXT.countRows;
    if (c.bucket) return dxLabel(c.field) + ' (' + dxBucketLabel(c.bucket) + ')';
    if (c.fn) return dxLabel(c.field) + ' (' + dxAggLabel(c.fn) + ')';
    return dxLabel(c.field || c.key);
  };
  var dxCell = function(v) { return v === true ? DX_TEXT.yes : v === false ? DX_TEXT.no : v == null ? '' : v; };

  var DX_DEFAULT_FIELDS = ['task_start', 'operator_name', 'job_name', 'task_name', 'task_state', 'required_minutes', 'actual_minutes'];
  var dxDefaultFilters = function() { return [{ field: 'card_type', op: 'in', value: '', value2: '', values: ['Task'] }]; };

  /* ── small parts, defined once so they keep focus while typing ── */
  function DXChip(props) {
    return h('button', { type: 'button', className: 'dx-chip' + (props.on ? ' dx-chip--on' : ''), 'aria-pressed': !!props.on, onClick: props.onClick }, props.children);
  }
  function DXCard(props) {
    return (
      <section className={'dx-card' + (props.className ? ' ' + props.className : '')}>
        {props.title && <div className="dx-card-head"><h3>{props.title}</h3>{props.action}</div>}
        {props.children}
      </section>
    );
  }

  function DXFilterRow({ f, onChange, onRemove }) {
    var meta = dxField(f.field);
    var ops = dxOps(meta);
    var opMeta = ops.filter(function(o) { return o.op === f.op; })[0] || ops[0];
    var upd = function(patch) { onChange(Object.assign({}, f, patch)); };
    var inType = meta && meta.type === 'timestamp' ? 'date' : meta && meta.type === 'number' ? 'number' : 'text';
    var input = function(k) {
      return <input type={inType} className="jb-input dx-val" value={f[k] || ''} aria-label={dxLabel(f.field)} onChange={function(e) { var p = {}; p[k] = e.target.value; upd(p); }} />;
    };
    return (
      <div className="dx-filter">
        <select className="jb-input dx-sel" value={f.field} aria-label={DX_TEXT.field}
          onChange={function(e) { var m = dxField(e.target.value); upd({ field: e.target.value, op: dxOps(m)[0].op, value: '', value2: '', values: [] }); }}>
          {DX_CATALOGUE.map(function(c) {
            return <optgroup key={c.key} label={c.label}>{c.fields.map(function(x) { return <option key={x.key} value={x.key}>{x.label}</option>; })}</optgroup>;
          })}
        </select>
        <select className="jb-input dx-sel" aria-label={DX_TEXT.operator} value={opMeta ? opMeta.op : ''} onChange={function(e) { upd({ op: e.target.value, value: '', value2: '', values: [] }); }}>
          {ops.map(function(o) { return <option key={o.op} value={o.op}>{o.label}</option>; })}
        </select>
        {opMeta && opMeta.value === 'one' && input('value')}
        {opMeta && opMeta.value === 'two' && <>{input('value')}<span className="dx-arrow">→</span>{input('value2')}</>}
        {opMeta && opMeta.value === 'list' && (meta.options || []).map(function(opt) {
          var on = (f.values || []).indexOf(opt) !== -1;
          return <DXChip key={opt} on={on} onClick={function() {
            var v = (f.values || []).filter(function(x) { return x !== opt; });
            if (!on) v.push(opt);
            upd({ values: v });
          }}>{opt}</DXChip>;
        })}
        <button type="button" className="dx-x" aria-label={DX_TEXT.removeFilter} title={DX_TEXT.removeFilter} onClick={onRemove}>×</button>
      </div>
    );
  }

  /* a filter is sent only when it has what its operator needs */
  var dxFilterReady = function(f) {
    var m = dxField(f.field); if (!m) return false;
    var o = dxOps(m).filter(function(x) { return x.op === f.op; })[0]; if (!o) return false;
    if (o.value === 'one') return String(f.value || '').trim() !== '';
    if (o.value === 'two') return String(f.value || '').trim() !== '' && String(f.value2 || '').trim() !== '';
    if (o.value === 'list') return (f.values || []).length > 0;
    return true;
  };
  var dxFilterText = function(f) {
    var m = dxField(f.field), o = dxOps(m).filter(function(x) { return x.op === f.op; })[0];
    var v = o && o.value === 'list' ? (f.values || []).join(', ') : [f.value, f.value2].filter(Boolean).join(' → ');
    return { field: dxLabel(f.field), op: o ? o.label.toLowerCase() : f.op, value: v };
  };

  return function DataExport({ activePage, onNavSelect, onLogout }) {
    var { useState, useEffect, useRef } = React;
    var [step, setStep]         = useState(0);
    var [fields, setFields]     = useState(DX_DEFAULT_FIELDS);
    var [cat, setCat]           = useState(DX_CATALOGUE[0].key);
    var [q, setQ]               = useState('');
    var [dateField, setDateField] = useState('task_start');
    var [range, setRange]       = useState(function() { return dxPreset('7'); });
    var [filters, setFilters]   = useState(dxDefaultFilters);
    var [grpOn, setGrpOn]       = useState(false);
    var [grpKeys, setGrpKeys]   = useState([{ field: 'operator_name' }]);
    var [buckets, setBuckets]   = useState({});              // bucket per date field used as a group key
    var [aggs, setAggs]         = useState([{ field: '*', fn: 'count' }, { field: 'actual_minutes', fn: 'avg' }]);
    var [colOrder, setColOrder] = useState([]);
    var [sortBy, setSortBy]     = useState(null);            // { key, dir }
    var [dragCol, setDragCol]   = useState(null);
    var [page, setPage]         = useState(1);
    var [pageSize, setPageSize] = useState(25);
    var [preview, setPreview]   = useState(null);
    var [busy, setBusy]         = useState(false);
    var [exporting, setExporting] = useState(false);
    var [err, setErr]           = useState('');
    var [note, setNote]         = useState('');
    var reqNo = useRef(0);

    var has = function(k) { return fields.indexOf(k) !== -1; };
    var toggleField = function(k) { setFields(has(k) ? fields.filter(function(x) { return x !== k; }) : fields.concat([k])); };

    var groupable = DX_FIELDS.filter(function(f) { return f.group && has(f.key); });
    var aggable   = DX_FIELDS.filter(function(f) { return f.agg && has(f.key); });
    var keysUsed  = grpKeys.filter(function(k) { return has(k.field) && dxField(k.field) && dxField(k.field).group; });
    var aggsUsed  = aggs.filter(function(a) { return a.field === '*' || (has(a.field) && dxField(a.field) && dxField(a.field).agg); });

    var buildSpec = function() {
      var spec = {
        fields: fields,
        filters: filters.filter(dxFilterReady).map(function(f) {
          var o = { field: f.field, op: f.op };
          if (f.value) o.value = f.value;
          if (f.value2) o.value2 = f.value2;
          if (f.values && f.values.length) o.values = f.values;
          return o;
        }),
        tz: DX_TZ,
      };
      if (range.from || range.to) spec.date = { field: dateField, from: range.from || null, to: range.to || null };
      if (grpOn && keysUsed.length) spec.group = {
        keys: keysUsed.map(function(k) { return dxField(k.field).group === 'bucket' ? { field: k.field, bucket: buckets[k.field] || 'day' } : { field: k.field }; }),
        aggs: aggsUsed,
      };
      if (colOrder.length) spec.order = colOrder;
      if (sortBy) spec.sort = sortBy;
      return spec;
    };

    var runPreview = function(p, size) {
      if (!fields.length) { setErr(DX_TEXT.noFields); return; }
      var my = ++reqNo.current;
      setBusy(true); setErr('');
      callRpc('data_export', { spec: Object.assign(buildSpec(), { preview: true, page: p, page_size: size }) })
        .then(function(res) { if (my === reqNo.current) { setPreview(res); setBusy(false); } })
        .catch(function(e) { if (my === reqNo.current) { setErr(e.message || DX_TEXT.failed); setPreview(null); setBusy(false); } });
    };

    // the preview opens at page 1 whenever the summary step is reached or its order / sort changes
    useEffect(function() { if (step === 3) { setPage(1); runPreview(1, pageSize); } }, [step, colOrder, sortBy]);
    // a sort on a column that is no longer exported would be refused
    useEffect(function() {
      if (preview && sortBy && !(preview.columns || []).some(function(c) { return c.key === sortBy.key; })) setSortBy(null);
    }, [preview]);

    var goPage = function(p) { var last = (preview && preview.pages) || 1; var n = Math.min(Math.max(1, p), last); setPage(n); runPreview(n, pageSize); };
    var changePageSize = function(n) { setPageSize(n); setPage(1); runPreview(1, n); };
    var clickSort = function(k) {
      setSortBy(function(s) { return !s || s.key !== k ? { key: k, dir: 'asc' } : s.dir === 'asc' ? { key: k, dir: 'desc' } : null; });
    };
    var moveCol = function(from, to) {
      var keys = ((preview && preview.columns) || []).map(function(c) { return c.key; });
      var a = keys.indexOf(from), b = keys.indexOf(to);
      if (!from || from === to || a < 0 || b < 0) return;
      keys.splice(a, 1); keys.splice(b, 0, from);
      setColOrder(keys);
    };

    var download = function(fmt) {
      if (fmt === 'xlsx' && !window.XLSX) { setErr(DX_TEXT.noXlsx); return; }
      setExporting(true); setErr(''); setNote('');
      callRpc('data_export', { spec: Object.assign(buildSpec(), { preview: false, limit: 50000 }) })
        .then(function(res) {
          var cols = res.columns || [];
          var head = cols.map(dxColLabel);
          var rows = (res.rows || []).map(function(r) { return cols.map(function(c) { return dxCell(r[c.key]); }); });
          var name = DX_TEXT.fileName + ' ' + dxDay(new Date()) + '.' + fmt;
          if (fmt === 'csv') {
            var esc = function(v) { var s = v == null ? '' : String(v); return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
            var csv = [head].concat(rows).map(function(r) { return r.map(esc).join(','); }).join('\r\n');
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
            a.download = name; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(function() { URL.revokeObjectURL(a.href); }, 5000);
          } else {
            var ws = window.XLSX.utils.aoa_to_sheet([head].concat(rows));
            ws['!cols'] = head.map(function(t, i) {
              var w = String(t).length;
              rows.slice(0, 200).forEach(function(r) { w = Math.max(w, String(r[i] == null ? '' : r[i]).length); });
              return { wch: Math.min(60, w + 2) };
            });
            var wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, DX_TEXT.sheetName);
            window.XLSX.writeFile(wb, name);
          }
          if (res.truncated) setNote(dxFmt(DX_TEXT.truncated, { n: Number(res.limit).toLocaleString(), t: Number(res.total_rows).toLocaleString() }));
          setExporting(false);
        })
        .catch(function(e) { setErr(e.message || DX_TEXT.failed); setExporting(false); });
    };

    var resetAll = function() {
      setStep(0); setFields(DX_DEFAULT_FIELDS); setCat(DX_CATALOGUE[0].key); setQ('');
      setDateField('task_start'); setRange(dxPreset('7')); setFilters(dxDefaultFilters());
      setGrpOn(false); setGrpKeys([{ field: 'operator_name' }]); setBuckets({});
      setAggs([{ field: '*', fn: 'count' }, { field: 'actual_minutes', fn: 'avg' }]);
      setColOrder([]); setSortBy(null); setPage(1); setPreview(null); setErr(''); setNote('');
    };

    var next = function() { if (!fields.length) { setErr(DX_TEXT.noFields); return; } setErr(''); setStep(step + 1); };

    var summaryRows = [
      [DX_TEXT.summaryFields, fields.map(dxLabel).join(', ') || DX_TEXT.none, 0],
      [DX_TEXT.summaryFilters, (range.from || range.to ? dxLabel(dateField) + ': ' + (range.from || '…') + ' → ' + (range.to || '…') + '. ' : '') +
        (filters.filter(dxFilterReady).length ? dxFmt(DX_TEXT.nFilters, { n: filters.filter(dxFilterReady).length }) : ''), 1],
      [DX_TEXT.summaryGrouping, grpOn && keysUsed.length ? DX_TEXT.grouped + ': ' + keysUsed.map(function(k) { return dxLabel(k.field); }).join(', ') : DX_TEXT.rawRows, 2],
    ];
    var side = (
      <aside className="dx-card dx-side">
        <h3>{DX_TEXT.summary}</h3>
        {summaryRows.map(function(r) {
          return (
            <div key={r[0]} className="dx-side-row">
              <div className="dx-side-head">
                <span>{r[0]}</span>
                {r[2] !== step && <button type="button" className="link-btn link-btn--blue" onClick={function() { setStep(r[2]); }}>{DX_TEXT.edit}</button>}
              </div>
              <div className="dx-side-val">{r[1] || DX_TEXT.none}</div>
            </div>
          );
        })}
      </aside>
    );

    /* ── step 1: fields ── */
    var stepFields = function() {
      var c = DX_CATALOGUE.filter(function(x) { return x.key === cat; })[0] || DX_CATALOGUE[0];
      var ql = q.trim().toLowerCase();
      var list = ql ? DX_FIELDS.filter(function(f) { return f.label.toLowerCase().indexOf(ql) !== -1; }) : c.fields;
      return (
        <>
          <p className="dx-help">{DX_TEXT.stepFieldsHelp}</p>
          <div className="dx-fields">
            <nav className="dx-card dx-cats">
              {DX_CATALOGUE.map(function(x) {
                var n = x.fields.filter(function(f) { return has(f.key); }).length, on = !ql && x.key === c.key;
                return (
                  <button key={x.key} type="button" className={'dx-cat' + (on ? ' dx-cat--on' : '')} aria-pressed={on} onClick={function() { setCat(x.key); setQ(''); }}>
                    <span>{x.label}</span>
                    {n > 0 && <span className="dx-count">{n}</span>}
                  </button>
                );
              })}
            </nav>
            <div className="dx-card">
              <div className="dx-card-head">
                <h3>{ql ? DX_TEXT.searchFields : c.label}</h3>
                <span className="dx-links">
                  <button type="button" className="link-btn link-btn--blue" onClick={function() { var s = fields.slice(); list.forEach(function(f) { if (s.indexOf(f.key) === -1) s.push(f.key); }); setFields(s); }}>{DX_TEXT.selectAll}</button>
                  <button type="button" className="link-btn" onClick={function() { var ks = list.map(function(f) { return f.key; }); setFields(fields.filter(function(k) { return ks.indexOf(k) === -1; })); }}>{DX_TEXT.clear}</button>
                </span>
              </div>
              <div className="lv-search dx-search">
                <span className="lv-search-icon"><SearchIcon /></span>
                <input className="lv-search-input" type="search" placeholder={DX_TEXT.searchFields} aria-label={DX_TEXT.searchFields} value={q}
                  onChange={function(e) { setQ(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Escape') setQ(''); }} />
              </div>
              {list.map(function(f) {
                return (
                  <label key={f.key} className={'dx-field' + (has(f.key) ? ' dx-field--on' : '')}>
                    <input type="checkbox" className="lv-cb" checked={has(f.key)} onChange={function() { toggleField(f.key); }} />
                    <span className="dx-field-name">{f.label}</span>
                    {ql && <span className="lv-pill">{(DX_CATALOGUE.filter(function(x) { return x.key === f.cat; })[0] || {}).label}</span>}
                  </label>
                );
              })}
              {!list.length && <div className="jb-empty">{DX_TEXT.noFieldsMatch}</div>}
            </div>
            <div className="dx-card">
              <div className="dx-card-head">
                <h3>{dxFmt(DX_TEXT.selectedN, { n: fields.length })}</h3>
                {fields.length > 0 && <button type="button" className="link-btn link-btn--blue" onClick={function() { setFields([]); }}>{DX_TEXT.clearAll}</button>}
              </div>
              <p className="dx-muted">{DX_TEXT.selectedHelp}</p>
              {fields.map(function(k) {
                return (
                  <div key={k} className="dx-picked">
                    <span>{dxLabel(k)}</span>
                    <button type="button" className="dx-x" aria-label={dxFmt(DX_TEXT.removeField, { f: dxLabel(k) })} onClick={function() { toggleField(k); }}>×</button>
                  </div>
                );
              })}
              {!fields.length && <div className="dx-muted dx-pad">{DX_TEXT.noneSelected}</div>}
            </div>
          </div>
        </>
      );
    };

    /* ── step 2: date range + filters ── */
    var stepFilters = function() {
      return (
        <div className="dx-with-side">
          <div className="dx-stack">
            <DXCard title={DX_TEXT.dateRange}>
              <p className="dx-muted">{DX_TEXT.dateRangeHelp}</p>
              <div className="dx-range">
                <label className="jb-field">{DX_TEXT.dateField}
                  <select className="jb-input dx-sel" value={dateField} onChange={function(e) { setDateField(e.target.value); }}>
                    {DX_DATE_FIELDS.map(function(f) { return <option key={f.key} value={f.key}>{f.label}</option>; })}
                  </select>
                </label>
                <label className="jb-field">{DX_TEXT.from}
                  <input type="date" className="jb-input" value={range.from} max={range.to || undefined} onChange={function(e) { setRange(Object.assign({}, range, { from: e.target.value })); }} />
                </label>
                <label className="jb-field">{DX_TEXT.to}
                  <input type="date" className="jb-input" value={range.to} min={range.from || undefined} onChange={function(e) { setRange(Object.assign({}, range, { to: e.target.value })); }} />
                </label>
              </div>
              <div className="dx-chips">
                {DX_TEXT.presets.map(function(p) {
                  var r = dxPreset(p[0]);
                  return <DXChip key={p[0]} on={range.from === r.from && range.to === r.to} onClick={function() { setRange(r); }}>{p[1]}</DXChip>;
                })}
                <DXChip on={!range.from && !range.to} onClick={function() { setRange({ from: '', to: '' }); }}>{DX_TEXT.anyDate}</DXChip>
              </div>
            </DXCard>
            <DXCard title={DX_TEXT.filters}>
              <p className="dx-muted">{DX_TEXT.filtersHelp}</p>
              <div className="dx-stack dx-stack--tight">
                {!filters.length && <div className="dx-muted">{DX_TEXT.noFilters}</div>}
                {filters.map(function(f, i) {
                  return <DXFilterRow key={i} f={f}
                    onChange={function(nf) { var a = filters.slice(); a[i] = nf; setFilters(a); }}
                    onRemove={function() { setFilters(filters.filter(function(x, j) { return j !== i; })); }} />;
                })}
                <div>
                  <button type="button" className="btn-add" onClick={function() { setFilters(filters.concat([{ field: 'task_name', op: 'contains', value: '', value2: '', values: [] }])); }}>
                    <PlusIcon /> {DX_TEXT.addFilter}
                  </button>
                </div>
              </div>
            </DXCard>
          </div>
          {side}
        </div>
      );
    };

    /* ── step 3: grouping ── */
    var stepGrouping = function() {
      var keyOn = function(k) { return grpKeys.some(function(x) { return x.field === k; }); };
      var aggOn = function(k, fn) { return aggs.some(function(a) { return a.field === k && a.fn === fn; }); };
      var toggleAgg = function(k, fn) { setAggs(aggOn(k, fn) ? aggs.filter(function(a) { return !(a.field === k && a.fn === fn); }) : aggs.concat([{ field: k, fn: fn }])); };
      return (
        <div className="dx-with-side">
          <DXCard title={DX_TEXT.grouping}>
            <label className="op-modal-check dx-grp-on">
              <input type="checkbox" className="lv-cb" checked={grpOn} onChange={function(e) { setGrpOn(e.target.checked); }} />
              <strong>{DX_TEXT.enableGrouping}</strong>
            </label>
            <p className="dx-muted">{grpOn ? DX_TEXT.groupHint : DX_TEXT.rawHint}</p>
            <div className={'dx-grp' + (grpOn ? '' : ' dx-grp--off')} aria-disabled={!grpOn}>
              <div className="dx-label">{DX_TEXT.groupBy}</div>
              <div className="dx-chips">
                {groupable.map(function(f) {
                  return (
                    <span key={f.key} className="dx-chip-wrap">
                      <DXChip on={keyOn(f.key)} onClick={function() {
                        setGrpKeys(keyOn(f.key) ? grpKeys.filter(function(x) { return x.field !== f.key; }) : grpKeys.concat([{ field: f.key }]));
                      }}>{f.label}</DXChip>
                      {f.group === 'bucket' && keyOn(f.key) && (
                        <select className="jb-input dx-sel" value={buckets[f.key] || 'day'} aria-label={f.label}
                          onChange={function(e) { var b = Object.assign({}, buckets); b[f.key] = e.target.value; setBuckets(b); }}>
                          {DX_BUCKETS.map(function(b) { return <option key={b.key} value={b.key}>{b.label}</option>; })}
                        </select>
                      )}
                    </span>
                  );
                })}
                {!groupable.length && <span className="dx-muted">{DX_TEXT.groupByNone}</span>}
              </div>
              <div className="dx-label">{DX_TEXT.aggregates}</div>
              <div className="dx-chips"><DXChip on={aggOn('*', 'count')} onClick={function() { toggleAgg('*', 'count'); }}>{DX_TEXT.countRows}</DXChip></div>
              {aggable.map(function(f) {
                return (
                  <div key={f.key} className="dx-agg-row">
                    <span className="dx-agg-name">{f.label}</span>
                    <span className="dx-chips">
                      {DX_AGGS.map(function(a) { return <DXChip key={a.key} on={aggOn(f.key, a.key)} onClick={function() { toggleAgg(f.key, a.key); }}>{a.label}</DXChip>; })}
                    </span>
                  </div>
                );
              })}
              {!aggable.length && <div className="dx-muted">{DX_TEXT.noAggFields}</div>}
            </div>
          </DXCard>
          {side}
        </div>
      );
    };

    /* ── step 4: summary, preview, download ── */
    var stepPreview = function() {
      var cols = (preview && preview.columns) || [];
      var pages = (preview && preview.pages) || 1;
      var editBtn = function(s) { return <button type="button" className="btn-filter" onClick={function() { setStep(s); }}><EditIcon /> {DX_TEXT.edit}</button>; };
      var ready = filters.filter(dxFilterReady);
      return (
        <div className="dx-stack">
          <div className="dx-summary">
            <DXCard title={DX_TEXT.summaryFields} action={editBtn(0)}>
              <div className="dx-chips">{fields.map(function(k) { return <span key={k} className="lv-pill">{dxLabel(k)}</span>; })}</div>
            </DXCard>
            <DXCard title={DX_TEXT.summaryFilters} action={editBtn(1)}>
              {(range.from || range.to) && <div className="dx-line"><b>{dxLabel(dateField)}</b> {(range.from || '…') + ' → ' + (range.to || '…')}</div>}
              {ready.map(function(f, i) { var t = dxFilterText(f); return <div key={i} className="dx-line"><b>{t.field}</b> {t.op} {t.value}</div>; })}
              {!ready.length && !(range.from || range.to) && <span className="dx-muted">{DX_TEXT.none}</span>}
            </DXCard>
            <DXCard title={DX_TEXT.summaryGrouping} action={editBtn(2)}>
              {!(grpOn && keysUsed.length) ? <span className="dx-muted">{DX_TEXT.rawRows}</span> : (
                <>
                  <div className="dx-line"><b>{DX_TEXT.groupBy}:</b> {keysUsed.map(function(k) { var f = dxField(k.field); return f.label + (f.group === 'bucket' ? ' / ' + dxBucketLabel(buckets[k.field] || 'day') : ''); }).join(', ')}</div>
                  <div className="dx-line"><b>{DX_TEXT.aggregates}:</b> {aggsUsed.length ? aggsUsed.map(function(a) { return a.field === '*' ? DX_TEXT.countRows : dxLabel(a.field) + ' (' + dxAggLabel(a.fn) + ')'; }).join(', ') : DX_TEXT.countRows}</div>
                </>
              )}
            </DXCard>
          </div>

          <DXCard className="dx-preview-card" title={<>{DX_TEXT.preview} <span className="dx-muted dx-inline">— {DX_TEXT.previewHint}</span></>}
            action={preview && <span className="dx-total">{DX_TEXT.totalRows}: <b>{Number(preview.total_rows).toLocaleString()}</b></span>}>
            {busy && !preview && <div className="jb-empty">{DX_TEXT.loading}</div>}
            {preview && (
              <div className={'dx-table-wrap' + (busy ? ' dx-busy' : '')}>
                <table className="lv-table lv-table--compact dx-table">
                  <thead><tr>
                    {cols.map(function(c) {
                      var sorted = sortBy && sortBy.key === c.key;
                      return (
                        <th key={c.key} draggable title={DX_TEXT.previewHint}
                          className={(sorted ? 'dx-th--sorted' : '') + (dragCol === c.key ? ' dx-th--drag' : '')}
                          onDragStart={function(e) { setDragCol(c.key); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', c.key); } catch (x) {} }}
                          onDragOver={function(e) { if (dragCol) e.preventDefault(); }}
                          onDrop={function(e) { e.preventDefault(); moveCol(dragCol, c.key); setDragCol(null); }}
                          onDragEnd={function() { setDragCol(null); }}
                          onClick={function() { clickSort(c.key); }}
                          aria-sort={sorted ? (sortBy.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
                          <span className="dx-grip" aria-hidden="true">⠿</span>{dxColLabel(c)}
                          <span className="dx-sort">{sorted ? (sortBy.dir === 'desc' ? '▼' : '▲') : ''}</span>
                        </th>
                      );
                    })}
                  </tr></thead>
                  <tbody>
                    {(preview.rows || []).map(function(r, i) {
                      return <tr key={i}>{cols.map(function(c) {
                        return <td key={c.key} className={c.type === 'number' ? 'dx-num' : ''}>{dxCell(r[c.key])}</td>;
                      })}</tr>;
                    })}
                  </tbody>
                </table>
                {!(preview.rows || []).length && <div className="jb-empty">{DX_TEXT.noRows}</div>}
              </div>
            )}
            {preview && <p className="dx-muted dx-tz">{dxFmt(DX_TEXT.times, { tz: preview.tz || DX_TZ })}</p>}

            <div className="dx-actions">
              <button type="button" className="btn-export" disabled={exporting || busy} onClick={function() { download('csv'); }}><ExportIcon /> {DX_TEXT.exportCsv}</button>
              <button type="button" className="btn-export" disabled={exporting || busy} onClick={function() { download('xlsx'); }}><ExportIcon /> {DX_TEXT.exportXlsx}</button>
              <span className="dx-muted">{exporting ? DX_TEXT.exporting : DX_TEXT.exportLimit}</span>
              {preview && (
                <div className="dx-pager">
                  <span className="dx-muted">{DX_TEXT.rowsPerPage}</span>
                  <select className="jb-input dx-sel" value={pageSize} disabled={busy} onChange={function(e) { changePageSize(Number(e.target.value)); }}>
                    {[10, 25, 50, 100].map(function(n) { return <option key={n} value={n}>{n}</option>; })}
                  </select>
                  <button type="button" className="btn-filter dx-pg" title={DX_TEXT.first} aria-label={DX_TEXT.first} disabled={busy || page <= 1} onClick={function() { goPage(1); }}>«</button>
                  <button type="button" className="btn-filter dx-pg" title={DX_TEXT.prev} aria-label={DX_TEXT.prev} disabled={busy || page <= 1} onClick={function() { goPage(page - 1); }}>‹</button>
                  <span className="dx-page-of">{dxFmt(DX_TEXT.pageOf, { p: Number(preview.page || page).toLocaleString(), n: Number(pages).toLocaleString() })}</span>
                  <button type="button" className="btn-filter dx-pg" title={DX_TEXT.next} aria-label={DX_TEXT.next} disabled={busy || page >= pages} onClick={function() { goPage(page + 1); }}>›</button>
                  <button type="button" className="btn-filter dx-pg" title={DX_TEXT.last} aria-label={DX_TEXT.last} disabled={busy || page >= pages} onClick={function() { goPage(pages); }}>»</button>
                </div>
              )}
            </div>
            {note && <div className="dx-note">{note}</div>}
          </DXCard>
        </div>
      );
    };

    return (
      <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
        <div className="dx-page">
          <div className="lv-header">
            <div className="lv-header-left">
              <h2>{DX_TEXT.title}</h2>
              <p>{DX_TEXT.sub}</p>
            </div>
          </div>

          <ol className="dx-stepper">
            {DX_TEXT.steps.map(function(s, i) {
              return (
                <li key={s} className={'dx-step' + (i <= step ? ' dx-step--done' : '') + (i === step ? ' dx-step--on' : '')}>
                  <button type="button" onClick={function() { if (i > 0 && !fields.length) { setErr(DX_TEXT.noFields); return; } setErr(''); setStep(i); }} aria-current={i === step ? 'step' : undefined}>
                    <span className="dx-step-no">{i + 1}</span>
                    <span className="dx-step-name">{s}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          {err && <div className="auth-error dx-err">{err}</div>}

          {step === 0 && stepFields()}
          {step === 1 && stepFilters()}
          {step === 2 && stepGrouping()}
          {step === 3 && stepPreview()}

          <div className="dx-footer">
            <div className="dx-footer-left">
              {step > 0 && <button type="button" className="btn-filter" onClick={function() { setStep(step - 1); }}>← {DX_TEXT.back}</button>}
              <button type="button" className="btn-filter dx-reset" onClick={resetAll}>↺ {DX_TEXT.reset}</button>
            </div>
            {step < 3 && <button type="button" className="btn-add dx-next" onClick={next}>{DX_TEXT.nextStep}: {DX_TEXT.steps[step + 1]} →</button>}
          </div>
        </div>
      </AppShell>
    );
  };
})();

;
/* ── src/pages/Jobs.jsx ── */
// Jobs: the account's jobs on the left, the selected job's editor on the right.
// Follows the mobile app's job editor (mobile.opsassist.co, job_task_list.dart):
//   jobs        jobs_get, jobs_rename; new jobs through AddJobModal (ready-made, blank or AI)
//   tasks       job_task_list, jobs_tasks_add_typed (normal / clock in / clock out / break), job_task_delete, job_task_change_order_to (drag the grip),
//               job_task_update_basic (name, description, days, start time, duration — always all five),
//               jobs_tasks_info_priority (H / M / L)
// Every write reloads the task list, as the mobile app does.

var JOB_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
var JOB_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];   // jobs_day_<key> / jobs_day_short_<key>
var JOB_PRIORITIES = [{ key: 'H', labelKey: 'jobs_priority_high' }, { key: 'M', labelKey: 'jobs_priority_medium' }, { key: 'L', labelKey: 'jobs_priority_low' }];
var JOB_CARD_TYPES = { CI: 'jobs_clock_in', CO: 'jobs_clock_out', BK: 'jobs_break' };   // label keys
var JOB_SELECTED_KEY = 'jobs_selected';

// task_date is 7 positions, Sunday first; 'X' means off. Some stored masks use other
// letters, so anything that isn't 'X' counts as on, and saves write clean letters back.
var dayMaskToList = function(mask) {
  var m = String(mask || '').padEnd(7, 'X').slice(0, 7);
  return JOB_DAYS.map(function(_, i) { return m[i] !== 'X'; });
};
var dayListToMask = function(list) {
  return JOB_DAYS.map(function(d, i) { return list[i] ? d : 'X'; }).join('');
};

// "HH:MM" -> minutes after midnight (null when blank or malformed)
var timeToMins = function(t) {
  var m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? Math.min(1439, Number(m[1]) * 60 + Number(m[2])) : null;
};
var minsToTime = function(m) {
  m = ((m % 1440) + 1440) % 1440;
  return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
};
var fmtMins = function(m) {
  if (!m) return '0m';
  var h = Math.floor(m / 60), r = m % 60;
  return (h ? h + 'h' : '') + (h && r ? ' ' : '') + (r ? r + 'm' : '');
};
var taskMins = function(t) { return Math.max(0, parseInt(t.task_duration, 10) || 0); };

// Clock in has to stay the first task of the day and clock out the last.
// Returns why an order is not allowed, or null.
var clockOrderProblem = function(list) {
  var ci = list.findIndex(function(t) { return t.card_type === 'CI'; });
  var co = list.findIndex(function(t) { return t.card_type === 'CO'; });
  if (ci > 0) return t('jobs_clock_in_first');
  if (co >= 0 && co !== list.length - 1) return t('jobs_clock_out_last');
  return null;
};

// 0–24 h bar: every task drawn from its start time for its duration; breaks in their own
// colour, clock in / out as markers. A task running past midnight continues at 0.
// Drag a block (or marker) sideways to change the task's start time: it moves in
// 5-minute steps, shows the new time while moving, and is saved on release through
// onMove(task, "HH:MM"). A click without movement changes nothing.
var JobDayBar = function({ tasks, onMove }) {
  var { useState, useRef } = React;
  var [drag, setDrag] = useState(null);   // { id, start, mins } while dragging
  var track = useRef(null);
  var pct = function(m) { return (m / 1440 * 100) + '%'; };

  var beginDrag = function(t, start) {
    return function(e) {
      if (!onMove || e.button !== 0) return;
      e.preventDefault();
      var width = track.current.getBoundingClientRect().width;
      var x0 = e.clientX;
      var current = start;
      setDrag({ id: t.task_id, start: start, mins: start });
      var move = function(ev) {
        current = Math.round((start + (ev.clientX - x0) / width * 1440) / 5) * 5;
        current = Math.max(0, Math.min(1435, current));
        setDrag({ id: t.task_id, start: start, mins: current });
      };
      var up = function() {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        setDrag(null);
        if (current !== start) onMove(t, minsToTime(current));
      };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
    };
  };

  var marks = [];
  tasks.forEach(function(t) {
    var saved = timeToMins(t.task_start_time);
    if (saved == null) return;
    var dragging = drag && drag.id === t.task_id;
    var start = dragging ? drag.mins : saved;
    var dur = taskMins(t);
    var label = (t.task_name || window.t('jobs_task')) + '  ' + minsToTime(start) + (dur ? '–' + minsToTime(start + dur) : '') + (onMove ? '  ' + window.t('jobs_drag_to_move_hint') : '');
    var handlers = { onPointerDown: beginDrag(t, saved), title: dragging ? undefined : label };
    if (t.card_type === 'CI' || t.card_type === 'CO') {
      marks.push(<span key={t.task_id} className={'jb-bar-clock jb-bar-clock--' + t.card_type + (dragging ? ' jb-bar--dragging' : '')} style={{ left: pct(start) }} {...handlers} />);
    } else {
      var cls = 'jb-bar-seg' + (t.card_type === 'BK' ? ' jb-bar-seg--break' : '') + (dragging ? ' jb-bar--dragging' : '');
      var first = Math.min(dur, 1440 - start);
      marks.push(<span key={t.task_id} className={cls} style={{ left: pct(start), width: pct(first) }} {...handlers} />);
      if (dur > first) marks.push(<span key={t.task_id + '-wrap'} className={cls} style={{ left: 0, width: pct(Math.min(dur - first, 1440)) }} {...handlers} />);
    }
    if (dragging) marks.push(<span key="drag-time" className="jb-bar-time" style={{ left: pct(start) }}>{minsToTime(start)}</span>);
  });

  return (
    <div className="jb-bar">
      <div className={'jb-bar-track' + (onMove ? ' jb-bar-track--movable' : '')} ref={track}>
        {[6, 12, 18].map(function(h) { return <span key={h} className="jb-bar-grid" style={{ left: pct(h * 60) }} />; })}
        {marks}
      </div>
      <div className="jb-bar-hours">
        {[0, 6, 12, 18, 24].map(function(h) { return <span key={h} style={{ left: pct(h * 60) }}>{h}</span>; })}
      </div>
    </div>
  );
};

// Six-dot grip: the only part of a task row that starts a drag, so the text boxes
// inside the row stay selectable.
var GripIcon = function() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
      <circle cx="2.5" cy="3" r="1.5"/><circle cx="7.5" cy="3" r="1.5"/>
      <circle cx="2.5" cy="8" r="1.5"/><circle cx="7.5" cy="8" r="1.5"/>
      <circle cx="2.5" cy="13" r="1.5"/><circle cx="7.5" cy="13" r="1.5"/>
    </svg>
  );
};

var JobTaskRow = function({ task, index, onBasic, onPriority, onDelete, busy, drag }) {
  var { useState, useEffect } = React;
  var [start, setStart]       = useState(task.task_start_time || '');
  var [duration, setDuration] = useState(task.task_duration || '');
  useEffect(function() { setStart(task.task_start_time || ''); setDuration(task.task_duration || ''); }, [task.task_start_time, task.task_duration]);

  // The editor shows a failed save's error; here it only needs swallowing.
  var save = function(change) { onBasic(task, change).catch(function() {}); };

  var days = dayMaskToList(task.task_date);
  var toggleDay = function(i) {
    var next = days.slice(); next[i] = !next[i];
    save({ task_date: dayListToMask(next) });
  };
  var saveStart = function() { if (start && start !== task.task_start_time) save({ task_start_time: start }); };
  var saveDuration = function() {
    var mins = String(Math.max(0, parseInt(duration, 10) || 0));
    if (mins !== String(task.task_duration || '')) save({ task_duration: mins });
    else setDuration(task.task_duration || '');
  };
  var special = JOB_CARD_TYPES[task.card_type];

  return (
    <div className={'jb-task' + (busy ? ' jb-task--busy' : '') + (drag.dragging ? ' jb-task--dragging' : '')
                  + (drag.dropBefore ? ' jb-task--drop-before' : '') + (drag.dropAfter ? ' jb-task--drop-after' : '')}
      draggable={drag.armed} onDragStart={drag.onDragStart} onDragEnd={drag.onDragEnd}
      onDragOver={drag.onDragOver} onDrop={drag.onDrop}>
      <div className="jb-task-order">
        <span className="jb-task-num">{index + 1}</span>
        <span className="jb-grip" title={t('jobs_drag_to_move')} onMouseDown={drag.arm} onMouseUp={drag.disarm}><GripIcon /></span>
      </div>
      <div className="jb-task-main">
        <div className="jb-task-title">
          {special && <span className="jb-badge">{t(special)}</span>}
          <InlineCell value={task.task_name} required maxLength={200} className="inline-cell--name"
            onSave={function(v) { return onBasic(task, { task_name: v }); }} />
        </div>
        <div className="jb-task-desc">
          <InlineCell value={task.task_description} maxLength={1000}
            onSave={function(v) { return onBasic(task, { task_description: v }); }} />
        </div>
        <div className="jb-task-fields">
          <label className="jb-field">{t('jobs_start')}
            <input type="time" className="jb-input" value={start} onChange={function(e) { setStart(e.target.value); }} onBlur={saveStart} />
          </label>
          <label className="jb-field">{t('jobs_duration')}
            <input type="number" min="0" step="5" className="jb-input jb-input--num" value={duration}
              onChange={function(e) { setDuration(e.target.value); }} onBlur={saveDuration}
              onKeyDown={function(e) { if (e.key === 'Enter') e.target.blur(); }} />
            <span className="jb-unit">{t('jobs_unit_min')}</span>
          </label>
          <div className="jb-days" role="group" aria-label={t('jobs_days')}>
            {JOB_DAYS.map(function(d, i) {
              return <button key={i} type="button" title={t('jobs_day_' + JOB_DAY_KEYS[i])} className={'jb-day' + (days[i] ? ' jb-day--on' : '')} onClick={function() { toggleDay(i); }}>{t('jobs_day_short_' + JOB_DAY_KEYS[i])}</button>;
            })}
          </div>
          <label className="jb-field">{t('jobs_priority')}
            <select className="jb-input" value={task.task_priority || 'L'} onChange={function(e) { onPriority(task, e.target.value); }}>
              {JOB_PRIORITIES.map(function(p) { return <option key={p.key} value={p.key}>{t(p.labelKey)}</option>; })}
            </select>
          </label>
        </div>
      </div>
      <button className="lv-action-btn delete" title={t('jobs_delete_task')} onClick={function() { onDelete(task); }}><TrashIcon /></button>
    </div>
  );
};

var JobEditor = function({ job, onJobsChanged }) {
  var { useState, useEffect } = React;
  var [tasks, setTasks]     = useState(null);
  var [error, setError]     = useState('');
  var [busyId, setBusyId]   = useState(null);
  var [newTask, setNewTask] = useState('');
  var [addMode, setAddMode] = useState(null);   // the Add task panel: null | 'menu' | 'name'
  var [addError, setAddError] = useState('');
  var [armedId, setArmedId] = useState(null);   // grip pressed on this task
  var [dragId, setDragId]   = useState(null);   // task being dragged
  var [dropAt, setDropAt]   = useState(null);   // insert position, 0..tasks.length

  var loadTasks = function() {
    return callRpc('job_task_list', { list_job_id: job.job_id })
      .then(function(data) { setTasks((Array.isArray(data) ? data : []).slice().sort(function(a, b) { return (a.task_order || 0) - (b.task_order || 0); })); })
      .catch(function(err) { setError(t('jobs_load_tasks_error', { message: err.message || '' })); setTasks([]); });
  };
  useEffect(function() { setTasks(null); setError(''); loadTasks(); }, [job.job_id]);

  // Run one write for a task, then reload the list; errors show above the tasks.
  var run = function(taskId, promise) {
    setError(''); setBusyId(taskId);
    return promise
      .then(function() { return loadTasks(); })
      .then(function() { onJobsChanged(); })
      .catch(function(err) { setError(err.message || t('common_could_not_save')); loadTasks(); throw err; })
      .finally(function() { setBusyId(null); });
  };

  var saveBasic = function(task, change) {
    var t = Object.assign({}, task, change);
    return run(task.task_id, callRpc('job_task_update_basic', {
      edit_job_id:         job.job_id,
      edit_task_id:        task.task_id,
      set_task_name:       t.task_name || '',
      set_task_description: t.task_description || '',
      set_task_date:       t.task_date || 'XXXXXXX',
      set_task_start_time: t.task_start_time || '00:00',
      set_task_duration:   String(t.task_duration || '0'),
    }));
  };
  var savePriority = function(task, p) {
    run(task.task_id, callRpc('jobs_tasks_info_priority', { task_id: task.task_id, job_id: job.job_id, new_priority: p })).catch(function() {});
  };
  var deleteTask = function(task) {
    if (!window.confirm(t('jobs_delete_task_confirm', { name: task.task_name || '' }))) return;
    run(task.task_id, callRpc('job_task_delete', { edit_job_id: job.job_id, delete_task_id: task.task_id })).catch(function() {});
  };
  // Add task: pick the type first; a normal task then asks for its name. The server
  // (jobs_tasks_add_typed) places clock in first, clock out last and the rest just
  // before clock out, and refuses duplicate names — checked here first as well.
  var normName = function(n) { return String(n || '').trim().replace(/\s+/g, ' ').toLowerCase(); };
  var sameName = function(a, b) { return normName(a) === normName(b); };
  var closeAdd = function() { setAddMode(null); setNewTask(''); setAddError(''); };
  var addTyped = function(type, name) {
    if (type === 'TK' && (tasks || []).some(function(t) { return sameName(t.task_name, name); })) {
      setAddError(t('jobs_task_exists', { name: name.trim() }));
      return;
    }
    setAddError('');
    run('new', callRpc('jobs_tasks_add_typed', { add_job_id: job.job_id, add_card_type: type, add_task_name: type === 'TK' ? name : null }))
      .then(closeAdd)
      .catch(function(err) { setError(''); setAddError(err.message || t('jobs_add_task_error')); });
  };
  var hasType = function(type) { return (tasks || []).some(function(t) { return t.card_type === type; }); };

  // ── Drag to reorder ──
  var endDrag = function() { setArmedId(null); setDragId(null); setDropAt(null); };
  var dropTask = function() {
    var from = tasks.findIndex(function(t) { return t.task_id === dragId; });
    var to = dropAt;
    endDrag();
    if (from < 0 || to == null || to === from || to === from + 1) return;
    var pos = to > from ? to - 1 : to;              // its index once moved
    var next = tasks.slice();
    var moved = next.splice(from, 1)[0];
    next.splice(pos, 0, moved);
    var problem = clockOrderProblem(next);
    if (problem) { setError(problem); return; }
    setTasks(next);                                  // show it moved straight away
    run(moved.task_id, callRpc('job_task_change_order_to', { edit_job_id: job.job_id, edit_task_id: moved.task_id, new_order_no: pos + 1 })).catch(function() {});
  };
  var dragProps = function(t, i) {
    return {
      armed: armedId === t.task_id,
      dragging: dragId === t.task_id,
      dropBefore: dragId && dropAt === i,
      dropAfter: dragId && i === tasks.length - 1 && dropAt === tasks.length,
      arm: function() { setArmedId(t.task_id); },
      disarm: function() { if (!dragId) setArmedId(null); },
      onDragStart: function(e) {
        if (armedId !== t.task_id) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', t.task_id);
        setDragId(t.task_id); setError('');
      },
      onDragEnd: endDrag,
      onDragOver: function(e) {
        if (!dragId) return;
        e.preventDefault();
        var r = e.currentTarget.getBoundingClientRect();
        setDropAt(e.clientY < r.top + r.height / 2 ? i : i + 1);
      },
      onDrop: function(e) { e.preventDefault(); dropTask(); },
    };
  };

  var renameJob = function(name) {
    setError('');
    return callRpc('jobs_rename', { edit_job_id: job.job_id, new_job_name: name })
      .then(function() { onJobsChanged(); })
      .catch(function(err) { setError(err.message || t('jobs_rename_error')); throw err; });
  };

  var list = tasks || [];
  var work = list.filter(function(t) { return t.card_type !== 'CI' && t.card_type !== 'CO' && t.card_type !== 'BK'; });
  var breaks = list.filter(function(t) { return t.card_type === 'BK'; });
  var sum = function(l) { return l.reduce(function(s, t) { return s + taskMins(t); }, 0); };
  var orderProblem = tasks && clockOrderProblem(tasks);

  return (
    <div className="jb-editor">
      <div className="jb-editor-head">
        <div className="jb-editor-title">
          <InlineCell value={job.job_name} required maxLength={100} className="jb-job-name" onSave={renameJob} />
          {orderProblem && <span className="jb-warn">{orderProblem + ' ' + t('jobs_drag_into_place')}</span>}
        </div>
        <dl className="jb-stats">
          <div><dt>{t('jobs_stat_tasks')}</dt><dd>{tasks ? work.length : '—'}</dd></div>
          <div><dt>{t('jobs_stat_task_duration')}</dt><dd>{tasks ? fmtMins(sum(work)) : '—'}</dd></div>
          <div><dt>{t('jobs_stat_break_duration')}</dt><dd>{tasks ? fmtMins(sum(breaks)) : '—'}</dd></div>
        </dl>
        {tasks && <JobDayBar tasks={tasks} onMove={function(t, time) { saveBasic(t, { task_start_time: time }).catch(function() {}); }} />}
      </div>

      {error && <div className="auth-error" style={{ marginBottom: '12px' }}>{error}</div>}
      {tasks === null && <div className="jb-empty">{t('jobs_loading_tasks')}</div>}
      {tasks && tasks.length === 0 && <div className="jb-empty">{t('jobs_no_tasks')}</div>}
      {tasks && tasks.map(function(t, i) {
        return <JobTaskRow key={t.task_id} task={t} index={i} busy={busyId === t.task_id}
          onBasic={saveBasic} onPriority={savePriority} onDelete={deleteTask} drag={dragProps(t, i)} />;
      })}

      <div className="jb-add-task">
        <button type="button" className="btn-add" onClick={function() { setAddMode('menu'); setAddError(''); }}>{t('jobs_add_task')}</button>
      </div>

      {addMode && (
        <div className="op-modal-backdrop" onClick={function() { if (busyId !== 'new') closeAdd(); }}>
          <div className="op-modal aj-modal" onClick={function(e) { e.stopPropagation(); }}
            onKeyDown={function(e) { if (e.key === 'Escape' && busyId !== 'new') closeAdd(); }}>
            <button type="button" className="qr-close" title={t('common_close')} aria-label={t('common_close')} onClick={closeAdd} disabled={busyId === 'new'}>×</button>
            <div className="aj-head">
              {addMode === 'name' && <button type="button" className="aj-back" onClick={function() { setAddMode('menu'); setAddError(''); }} disabled={busyId === 'new'}>{t('common_back')}</button>}
              <h3 className="op-modal-title">{addMode === 'name' ? t('jobs_normal_task') : t('jobs_add_task_title')}</h3>
            </div>

            {addMode === 'menu' && (
              <div className="aj-choices">
                <p className="aj-intro">{t('jobs_add_intro', { job: job.job_name })}</p>
                <button type="button" className="aj-choice" onClick={function() { setAddMode('name'); setAddError(''); }}>
                  <strong>{t('jobs_normal_task')}</strong>
                  <span>{t('jobs_normal_task_desc')}</span>
                </button>
                <button type="button" className="aj-choice" disabled={hasType('CI') || busyId === 'new'} onClick={function() { addTyped('CI'); }}>
                  <strong>{t('jobs_clock_in')}</strong>
                  <span>{hasType('CI') ? t('jobs_clock_in_exists') : t('jobs_clock_in_desc')}</span>
                </button>
                <button type="button" className="aj-choice" disabled={hasType('CO') || busyId === 'new'} onClick={function() { addTyped('CO'); }}>
                  <strong>{t('jobs_clock_out')}</strong>
                  <span>{hasType('CO') ? t('jobs_clock_out_exists') : t('jobs_clock_out_desc')}</span>
                </button>
                <button type="button" className="aj-choice" disabled={busyId === 'new'} onClick={function() { addTyped('BK'); }}>
                  <strong>{t('jobs_break')}</strong>
                  <span>{t('jobs_break_desc')}</span>
                </button>
              </div>
            )}

            {addMode === 'name' && (
              <form className="aj-body" onSubmit={function(e) { e.preventDefault(); if (newTask.trim()) addTyped('TK', newTask); }}>
                <div className="form-group">
                  <label className="form-label">{t('jobs_task_name')}</label>
                  <input className="form-input" placeholder={t('jobs_task_name_placeholder')} value={newTask} maxLength={200} autoFocus
                    onChange={function(e) { setNewTask(e.target.value); setAddError(''); }} />
                </div>
                <div className="op-modal-actions">
                  <button type="submit" className="btn-add" disabled={!newTask.trim() || busyId === 'new'}>{busyId === 'new' ? t('jobs_adding') : t('jobs_create_task')}</button>
                </div>
              </form>
            )}

            {addError && <div className="auth-error">{addError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

var Jobs = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [jobs, setJobs]           = useState(null);
  var [error, setError]         = useState('');
  var [selectedId, setSelectedId] = useState(function() { return localStorage.getItem(JOB_SELECTED_KEY); });
  var [query, setQuery]         = useState('');
  var [adding, setAdding]       = useState(false);   // the Add Job dialog is open

  var loadJobs = function() {
    return callRpc('jobs_get')
      .then(function(data) {
        var list = (Array.isArray(data) ? data : []).slice().sort(function(a, b) { return String(a.job_name).localeCompare(String(b.job_name)); });
        setJobs(list);
        return list;
      })
      .catch(function(err) { setError(t('jobs_load_error', { message: err.message || '' })); setJobs([]); return []; });
  };

  useEffect(function() {
    loadJobs();
  }, []);
  useRefresh(loadJobs);   // the countdown at the top of the menu

  var select = function(id) {
    setSelectedId(id);
    if (id) localStorage.setItem(JOB_SELECTED_KEY, id); else localStorage.removeItem(JOB_SELECTED_KEY);
  };

  // A job was created in the Add Job dialog: show it.
  var jobCreated = function(id) {
    setAdding(false);
    loadJobs().then(function() { if (id) select(id); });
  };

  var visible = (jobs || []).filter(function(j) {
    var q = query.trim().toLowerCase();
    return !q || [j.job_name, j.operator_name].some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; });
  });
  var selected = (jobs || []).find(function(j) { return j.job_id === selectedId; }) || null;

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>{t('jobs_title')}</h2>
          <p>{t('jobs_subtitle')}</p>
        </div>
      </div>
      {adding && <AddJobModal onClose={function() { setAdding(false); }} onCreated={jobCreated} />}

      <div className="lv-toolbar">
        <button className="btn-add" onClick={function() { setAdding(true); }}>{t('jobs_add_job')}</button>
        <div className="lv-search">
          <span className="lv-search-icon"><SearchIcon /></span>
          <input className="lv-search-input" placeholder={t('jobs_search_placeholder')} value={query}
            onChange={function(e) { setQuery(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Escape') setQuery(''); }} />
          {query && <button className="lv-search-clear" title={t('common_clear_search')} onClick={function() { setQuery(''); }}>{'×'}</button>}
        </div>
      </div>
      {error && <div className="auth-error" style={{ marginBottom: '16px' }}>{error}</div>}

      <div className="jb-layout">
        <aside className="jb-list">
          {jobs === null && <div className="jb-empty">{t('jobs_loading')}</div>}
          {jobs && visible.length === 0 && <div className="jb-empty">{jobs.length ? t('jobs_no_match') : t('jobs_none')}</div>}
          {visible.map(function(j) {
            return (
              <button key={j.job_id} className={'jb-item' + (j.job_id === selectedId ? ' jb-item--active' : '')} onClick={function() { select(j.job_id); }}>
                <span className="jb-item-name">{j.job_name}</span>
                <span className="jb-item-meta">
                  {t('jobs_item_tasks', { count: j.task_count || 0 })}{j.task_duration_hours ? ' · ' + Number(j.task_duration_hours).toFixed(1) + ' h' : ''}
                </span>
                <span className={'jb-item-op' + (j.operator_id ? '' : ' jb-item-op--none')}>{j.operator_id ? (j.operator_name || t('jobs_assigned')) : t('jobs_not_assigned')}</span>
              </button>
            );
          })}
        </aside>

        <section className="jb-main">
          {selected
            ? <JobEditor key={selected.job_id} job={selected} onJobsChanged={loadJobs} />
            : <div className="jb-placeholder">{jobs && jobs.length ? t('jobs_select_hint') : ''}</div>}
        </section>
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/Tasks.jsx ── */
// Tasks: operators on the left; on the right the selected operator's tasks for a date
// range, one panel each — what the task is, when it was due and how long it should
// take, when it actually started and ended, and the pictures and signatures captured.
//   operators   operators_get
//   tasks       tasks_manager_list(selected_operator_id, from_day, to_day)
//               tasks_manager_list_all(from_day, to_day) — "All Tasks", every enabled operator
//   files       files_get(file_name) -> bytes (fetchFileUrl in constants.js)

var TASK_ALL = '__all__';   // the "All Tasks" entry above the operators
var TASK_STATES = {
  NEW:       { label: 'Not started', bg: '#f3f4f6', text: '#4b5563' },
  START:     { label: 'In progress', bg: '#dbeafe', text: '#1d4ed8' },
  PAUSED:    { label: 'Paused',      bg: '#fef9c3', text: '#854d0e' },
  RESTART:   { label: 'Restarted',   bg: '#dbeafe', text: '#1d4ed8' },
  STOP:      { label: 'Done',        bg: '#dcfce7', text: '#166534' },
  COMPLETED: { label: 'Done',        bg: '#dcfce7', text: '#166534' },
  CANCEL:    { label: 'Cancelled',   bg: '#fee2e2', text: '#991b1b' },
};
var TASK_PRIORITY = { H: 'High', M: 'Medium', L: 'Low' };
var TASK_CARD_TYPES = { BK: 'Break', CI: 'Clock in', CO: 'Clock out' };

var isoDay = function(d) {
  var pad = function(n) { return String(n).padStart(2, '0'); };
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
};
var fmtTime = function(ts) { return ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null; };
var fmtDay  = function(ts) { return new Date(ts).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' }); };
var fmtSeconds = function(s) {
  if (s == null) return null;
  var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h ? h + 'h ' : '') + (h || m ? m + 'm ' : '') + sec + 's';
};

// One captured picture or signature: fetched on demand, click to see it full size.
var TaskFile = function({ file, label, onOpen, signature }) {
  var { useState, useEffect } = React;
  var [url, setUrl]       = useState(null);
  var [failed, setFailed] = useState(false);

  useEffect(function() {
    if (file.cannot_collect || !file.file_name) return;
    var alive = true;
    fetchFileUrl(file.file_name)
      .then(function(u) { if (alive) setUrl(u); })
      .catch(function() { if (alive) setFailed(true); });
    return function() { alive = false; };
  }, [file.file_name]);

  if (file.cannot_collect) return <div className="tk-file tk-file--note" title={label}>Could not collect</div>;
  return (
    <button type="button" className={'tk-file' + (signature ? ' tk-file--sig' : '')} title={label} onClick={function() { if (url) onOpen(url, label); }} disabled={!url}>
      {url ? <img src={url} alt={label} /> : <span>{failed ? 'Not available' : 'Loading…'}</span>}
    </button>
  );
};

// The task's notes (info_item_text). Usually a whole HTML page from the job editor, with its
// own <style>: shown on request in a sandboxed frame (no script, nothing reaches this page).
// Notes without markup are shown as text.
var TaskInfo = function({ html }) {
  var { useState } = React;
  var [open, setOpen] = useState(false);
  if (!/<[a-z!][\s\S]*>/i.test(html)) return <div className="tk-info">{html}</div>;
  return (
    <div className="tk-info-block">
      <button type="button" className="link-btn link-btn--blue" onClick={function() { setOpen(!open); }}>{open ? 'Hide task details' : 'Task details'}</button>
      {open && <iframe className="tk-info-frame" sandbox="" srcDoc={'<base target="_blank">' + html} title="Task details" />}
    </div>
  );
};

var TaskPanel = function({ task, dayNo, onOpen, showOperator }) {
  var state = TASK_STATES[task.task_state] || { label: task.task_state || 'Unknown', bg: '#f3f4f6', text: '#4b5563' };
  var required = task.required_minutes != null ? task.required_minutes * 60 : null;
  var over = required != null && task.actual_seconds != null && task.actual_seconds > required;
  var special = TASK_CARD_TYPES[task.card_type];

  return (
    <article className="tk-panel">
      <header className="tk-panel-head">
        <div className="tk-panel-lead">
          {dayNo != null && <span className="jb-task-num tk-day-no" title={'Task ' + dayNo + ' of the day'}>{dayNo}</span>}
          <div className="tk-panel-title">
            <h4>{task.task_name || 'Untitled task'}</h4>
            {(showOperator || task.job_name) && <span className="tk-job">{[showOperator ? task.operator_name : null, task.job_name].filter(Boolean).join(' · ')}</span>}
          </div>
        </div>
        <div className="tk-badges">
          {special && <span className="jb-badge">{special}</span>}
          <span className="tk-badge" style={{ background: state.bg, color: state.text }}>{state.label}</span>
          {task.card_type === 'TK' && <span className={'tk-prio tk-prio--' + task.task_priority}>{TASK_PRIORITY[task.task_priority] || task.task_priority}</span>}
        </div>
      </header>

      {task.task_description && <p className="tk-desc">{task.task_description}</p>}
      {task.info_item_text && task.info_item_text.trim() && <TaskInfo html={task.info_item_text} />}
      {(task.info_item_location_link || task.info_item_location_text) && (
        <div className="tk-line"><LocationIcon />
          {task.info_item_location_link
            ? <a href={/^https?:\/\//.test(task.info_item_location_link) ? task.info_item_location_link : 'https://' + task.info_item_location_link} target="_blank" rel="noopener noreferrer">{task.info_item_location_text || 'Location'}</a>
            : <span>{task.info_item_location_text}</span>}
        </div>
      )}

      <dl className="tk-times">
        <div><dt>Due</dt><dd>{fmtTime(task.task_start_timestamp) || '—'}</dd></div>
        <div><dt>Time required</dt><dd>{task.required_minutes != null ? task.required_minutes + ' min' : '—'}</dd></div>
        <div><dt>Started</dt><dd>{fmtTime(task.started_at) || '—'}</dd></div>
        <div><dt>Ended</dt><dd>{fmtTime(task.ended_at) || '—'}</dd></div>
        <div><dt>Time taken</dt><dd className={over ? 'tk-over' : ''}>{fmtSeconds(task.actual_seconds) || '—'}</dd></div>
      </dl>

      {task.cancellation_note && <div className="tk-cancel">{'Cancelled: ' + task.cancellation_note}</div>}

      {task.pictures.length > 0 && (
        <div className="tk-files-block">
          <div className="tk-files-label">{'Pictures (' + task.pictures.length + ')'}</div>
          <div className="tk-files">
            {task.pictures.map(function(f, i) { return <TaskFile key={f.file_name || i} file={f} label={'Picture ' + (i + 1) + ' — ' + task.task_name} onOpen={onOpen} />; })}
          </div>
        </div>
      )}
      {task.signatures.length > 0 && (
        <div className="tk-files-block">
          <div className="tk-files-label">{task.signatures.length > 1 ? 'Signatures (' + task.signatures.length + ')' : 'Signature'}</div>
          <div className="tk-files">
            {task.signatures.map(function(f, i) { return <TaskFile key={f.file_name || i} file={f} label={'Signature — ' + task.task_name} onOpen={onOpen} signature />; })}
          </div>
        </div>
      )}
    </article>
  );
};

var Tasks = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [operators, setOperators] = useState(null);
  var [selectedId, setSelectedId] = useState(TASK_ALL);   // opens on All Tasks
  var [query, setQuery]   = useState('');
  var [range, setRange]   = useState(function() { var to = new Date(), from = new Date(); from.setDate(to.getDate() - 6); return { from: isoDay(from), to: isoDay(to) }; });
  var [tasks, setTasks]   = useState(null);
  var [error, setError]   = useState('');
  var [hideClock, setHideClock] = useState(true);
  var [viewer, setViewer] = useState(null);

  useEffect(function() {
    callRpc('operators_get')
      .then(function(data) {
        // Disabled operators are left out of the list.
        var list = (Array.isArray(data) ? data : []).filter(function(o) { return o.enabled; }).sort(function(a, b) { return String(a.full_name).localeCompare(String(b.full_name)); });
        setOperators(list);
      })
      .catch(function(err) { setError('Could not load operators. ' + (err.message || '')); setOperators([]); });
  }, []);

  useEffect(function() {
    if (!selectedId) return;
    var alive = true;
    setTasks(null); setError('');
    var req = selectedId === TASK_ALL
      ? callRpc('tasks_manager_list_all', { from_day: range.from, to_day: range.to })
      : callRpc('tasks_manager_list', { selected_operator_id: selectedId, from_day: range.from, to_day: range.to });
    req
      .then(function(data) { if (alive) setTasks(Array.isArray(data) ? data : []); })
      .catch(function(err) { if (alive) { setError(err.message || 'Could not load tasks'); setTasks([]); } });
    return function() { alive = false; };
  }, [selectedId, range.from, range.to]);

  var select = function(id) { setSelectedId(id); };
  var isAll = selectedId === TASK_ALL;

  var q = query.trim().toLowerCase();
  var visibleOps = (operators || []).filter(function(o) {
    return !q || [o.full_name, o.nickname, o.job_name].some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; });
  });
  var shown = (tasks || []).filter(function(t) { return !hideClock || t.card_type === 'TK'; });

  // Group by day, newest day first (the API orders by start time, latest first); within a day
  // the cards run in time order, so they read 1, 2, 3 with their task number for the day.
  var groups = [];
  shown.forEach(function(t) {
    var day = t.task_start_timestamp ? isoDay(new Date(t.task_start_timestamp)) : 'none';
    var g = groups[groups.length - 1];
    if (!g || g.day !== day) groups.push(g = { day: day, ts: t.task_start_timestamp, tasks: [] });
    g.tasks.push(t);
  });
  var apiPos = function(t) { return tasks.indexOf(t); };   // tasks due at the same time keep the API's order, as the numbers do
  var tsOf   = function(t) { return t.task_start_timestamp ? new Date(t.task_start_timestamp).getTime() : 0; };
  groups.forEach(function(g) { g.tasks.sort(function(a, b) { return tsOf(a) - tsOf(b) || apiPos(a) - apiPos(b); }); });
  // Each day's tasks numbered in time order, 1 = the first of the day (the API lists the latest
  // first; tasks due at the same time keep its order). Clock-in and break cards are not
  // numbered, so the numbers stay the same with them shown or hidden.
  var dayNo = {}, byDay = {};
  (tasks || []).forEach(function(t, i) {
    if (t.card_type !== 'TK') return;
    var day = (t.task_start_timestamp ? isoDay(new Date(t.task_start_timestamp)) : 'none') + '|' + (t.operator_id || '');   // per operator per day
    (byDay[day] = byDay[day] || []).push({ t: t, i: i, ts: t.task_start_timestamp ? new Date(t.task_start_timestamp).getTime() : 0 });
  });
  Object.keys(byDay).forEach(function(day) {
    byDay[day].sort(function(a, b) { return a.ts - b.ts || a.i - b.i; })
      .forEach(function(x, n) { dayNo[x.t.task_id] = n + 1; });
  });
  var done = shown.filter(function(t) { return t.task_state === 'STOP' || t.task_state === 'COMPLETED'; }).length;
  var selectedOp = (operators || []).find(function(o) { return o.operator_id === selectedId; });

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      {viewer && (
        <div className="tk-viewer" onClick={function() { setViewer(null); }}>
          <img src={viewer.url} alt={viewer.label} />
          <div className="tk-viewer-label">{viewer.label}</div>
        </div>
      )}
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Task History</h2>
          <p>Each operator's tasks with their timings and what was captured.</p>
        </div>
      </div>

      <div className="jb-layout">
        <aside className="jb-list">
          <div className="lv-search jb-search">
            <span className="lv-search-icon"><SearchIcon /></span>
            <input className="lv-search-input" placeholder="Search operators" value={query}
              onChange={function(e) { setQuery(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Escape') setQuery(''); }} />
          </div>
          <button className={'jb-item tk-op tk-all' + (isAll ? ' jb-item--active' : '')} onClick={function() { select(TASK_ALL); }}>
            <span className="tk-op-avatar tk-all-icon" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6h11"/><path d="M9 12h11"/><path d="M9 18h11"/><polyline points="3,6 4,7 6,5"/><polyline points="3,12 4,13 6,11"/><polyline points="3,18 4,19 6,17"/></svg>
            </span>
            <span className="tk-op-text">
              <span className="jb-item-name">All Tasks</span>
              <span className="jb-item-op jb-item-op--none">Every operator</span>
            </span>
          </button>
          {operators === null && <div className="jb-empty">{'Loading operators…'}</div>}
          {operators && visibleOps.length === 0 && <div className="jb-empty">{operators.length ? 'No operators match.' : 'No operators yet.'}</div>}
          {visibleOps.map(function(o) {
            return (
              <button key={o.operator_id} className={'jb-item tk-op' + (o.operator_id === selectedId ? ' jb-item--active' : '')} onClick={function() { select(o.operator_id); }}>
                <OperatorPhoto className="tk-op-avatar" fileName={o.operator_profile_pic_file_name} ts={o.operator_profile_pic_ts} name={o.full_name}
                  color={o.enabled ? operatorColor(o.operator_id) : '#9ca3af'} />
                <span className="tk-op-text">
                  <span className="jb-item-name">{o.full_name}</span>
                  <span className={'jb-item-op' + (o.job_name ? '' : ' jb-item-op--none')}>{o.job_name || 'No job assigned'}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <section className="jb-main">
          <div className="tk-toolbar">
            <div className="tk-toolbar-title">
              <strong>{isAll ? 'All Tasks' : (selectedOp ? selectedOp.full_name : 'Select an operator')}</strong>
              {tasks && <span>{shown.length + ' tasks · ' + done + ' done'}</span>}
            </div>
            <div className="tk-toolbar-controls">
              <label className="jb-field">From <input type="date" className="jb-input" value={range.from} max={range.to} onChange={function(e) { if (e.target.value) setRange(function(r) { return Object.assign({}, r, { from: e.target.value }); }); }} /></label>
              <label className="jb-field">To <input type="date" className="jb-input" value={range.to} min={range.from} max={isoDay(new Date())} onChange={function(e) { if (e.target.value) setRange(function(r) { return Object.assign({}, r, { to: e.target.value }); }); }} /></label>
              <label className="op-modal-check"><input type="checkbox" className="lv-cb" checked={hideClock} onChange={function(e) { setHideClock(e.target.checked); }} /> Hide clock-in / breaks</label>
            </div>
          </div>

          {error && <div className="auth-error" style={{ marginBottom: '12px' }}>{error}</div>}
          {selectedId && tasks === null && <div className="jb-placeholder">{'Loading tasks…'}</div>}
          {tasks && shown.length === 0 && !error && <div className="jb-placeholder">No tasks in this date range.</div>}
          {groups.map(function(g) {
            return (
              <div key={g.day} className="tk-day">
                <h3 className="tk-day-title">{g.ts ? fmtDay(g.ts) : 'No date'}</h3>
                <div className="tk-grid">
                  {g.tasks.map(function(t) { return <TaskPanel key={t.task_id} task={t} dayNo={dayNo[t.task_id]} showOperator={isAll} onOpen={function(url, label) { setViewer({ url: url, label: label }); }} />; })}
                </div>
              </div>
            );
          })}
        </section>
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/Operators.jsx ── */
// The operator's login QR code. Download saves it as a PNG; Share uses the device's
// share sheet where the browser can share files, otherwise copies the image so it can
// be pasted into a chat. The code logs the operator in, so share it only with them.
var QrModal = function({ name, qrCode, onClose }) {
  var { useState, useEffect } = React;
  var [status, setStatus] = useState(null);
  var fileName = 'QR - ' + (name || 'operator').replace(/[^A-Za-z0-9 ._-]/g, '').trim() + '.png';

  useEffect(function() {
    var onKey = function(e) { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, []);

  var pngBlob = function() {
    var bin = atob(qrCode), bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: 'image/png' });
  };

  var download = function() {
    var url = URL.createObjectURL(pngBlob());
    var a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
    setStatus({ ok: true, text: t('operators_qr_downloaded') });
  };

  // Clipboard copy: the fallback when there is no share sheet, or it refuses.
  var copy = function() {
    if (!(navigator.clipboard && window.ClipboardItem)) {
      setStatus({ ok: false, text: t('operators_qr_no_share') });
      return;
    }
    navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob() })])
      .then(function() { setStatus({ ok: true, text: t('operators_qr_copied') }); })
      .catch(function() { setStatus({ ok: false, text: t('operators_qr_no_share') }); });
  };

  var share = function() {
    var file = new File([pngBlob()], fileName, { type: 'image/png' });
    if (!(navigator.canShare && navigator.canShare({ files: [file] }))) { copy(); return; }
    navigator.share({ files: [file], title: t('operators_qr_label', { name: name || t('operators_qr_operator') }), text: t('operators_qr_share_text', { name: name || t('operators_qr_the_operator') }) })
      .then(function() { setStatus({ ok: true, text: t('operators_qr_shared') }); })
      .catch(function(err) { if (!err || err.name !== 'AbortError') copy(); });  // cancelled by the user: say nothing
  };

  return (
    <div className="qr-backdrop" onClick={onClose}>
      <div className="qr-modal" role="dialog" aria-label={t('operators_qr_label', { name: name || t('operators_qr_operator') })} onClick={function(e) { e.stopPropagation(); }}>
        <button type="button" className="qr-close" title={t('common_close')} aria-label={t('common_close')} onClick={onClose}>{'\u00d7'}</button>
        <h3 className="qr-title">{name}</h3>
        <p className="qr-sub">{t('operators_qr_subtitle')}</p>
        {qrCode
          ? <img className="qr-img" src={'data:image/png;base64,' + qrCode} alt={t('operators_qr_alt')} />
          : <p className="qr-none">{t('operators_qr_none')}</p>}
        {qrCode && (
          <div className="qr-actions">
            <button type="button" className="btn-filter" onClick={download}><ExportIcon /> {t('operators_qr_download')}</button>
            <button type="button" className="btn-add" onClick={share}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/></svg>
              {t('operators_qr_share')}
            </button>
          </div>
        )}
        {status && <p className={status.ok ? 'qr-status pf-ok' : 'qr-status pf-err'}>{status.text}</p>}
      </div>
    </div>
  );
};

// Add (operator == null) or edit one operator. Saves through operators_add /
// operators_update, then hands control back so the list can be reloaded.
var OperatorModal = function({ operator, onClose, onSaved }) {
  var { useState } = React;
  var isEdit = !!operator;
  var [form, setForm] = useState({
    full_name:       isEdit ? (operator.full_name || '')       : '',
    nickname:        isEdit ? (operator.nickname || '')        : '',
    staff_id_number: isEdit ? (operator.staff_id_number || '') : '',
    enabled:         isEdit ? !!operator.enabled               : true,
    live_capture:    isEdit ? !!operator.live_capture          : false,
  });
  var [saving, setSaving] = useState(false);
  var [error, setError]   = useState('');

  var set = function(key) {
    return function(e) {
      var value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm(function(f) { return Object.assign({}, f, { [key]: value }); });
    };
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!form.full_name.trim()) { setError(t('operators_name_required')); return; }
    setError('');
    setSaving(true);
    var body = {
      full_name:       form.full_name.trim(),
      nickname:        form.nickname.trim(),
      staff_id_number: form.staff_id_number.trim(),
      enabled:         form.enabled,
      live_capture:    form.live_capture,
    };
    try {
      if (isEdit) {
        await callRpc('operators_update', Object.assign({ operator_id_to_update: operator.operator_id }, body));
      } else {
        await callRpc('operators_add', body);
      }
      onSaved();
    } catch(err) {
      setError(err.message || t('operators_modal_save_error'));
      setSaving(false);
    }
  };

  return (
    <div className="op-modal-backdrop" onClick={saving ? null : onClose}>
      <form className="op-modal" onClick={function(e) { e.stopPropagation(); }} onSubmit={handleSubmit}>
        <h3 className="op-modal-title">{isEdit ? t('operators_edit_title') : t('operators_add_title')}</h3>
        <div className="form-group">
          <label className="form-label">{t('common_full_name')}</label>
          <input className="form-input" value={form.full_name} onChange={set('full_name')} maxLength={100} autoFocus required />
        </div>
        <div className="op-modal-row">
          <div className="form-group">
            <label className="form-label">{t('common_nickname')}</label>
            <input className="form-input" value={form.nickname} onChange={set('nickname')} maxLength={50} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('common_staff_id')}</label>
            <input className="form-input" value={form.staff_id_number} onChange={set('staff_id_number')} maxLength={50} />
          </div>
        </div>
        <label className="op-modal-check"><input type="checkbox" className="lv-cb" checked={form.enabled} onChange={set('enabled')} /> {t('common_enabled')}</label>
        <label className="op-modal-check"><input type="checkbox" className="lv-cb" checked={form.live_capture} onChange={set('live_capture')} /> {t('operators_live_capture')}</label>
        {error && <div className="auth-error">{error}</div>}
        <div className="op-modal-actions">
          <button type="button" className="btn-filter" onClick={onClose} disabled={saving}>{t('common_cancel')}</button>
          <button type="submit" className="btn-add" disabled={saving}>{saving ? t('common_saving') : (isEdit ? t('operators_save_changes') : t('operators_add_button'))}</button>
        </div>
      </form>
    </div>
  );
};

// "Are you sure?" before something is deleted (an operator, a customer, …). onConfirm may
// return a promise: the popup stays open while it runs and shows its error if it fails.
var ConfirmDeleteModal = function({ name, what, onConfirm, onClose }) {
  var { useState } = React;
  var [busy, setBusy]   = useState(false);
  var [error, setError] = useState('');
  what = what || t('operators_operator');
  var confirm = function() {
    setBusy(true); setError('');
    Promise.resolve(onConfirm()).catch(function(err) { setError(err.message || t('common_confirm_delete_error', { what: what })); setBusy(false); });
  };
  return (
    <div className="op-modal-backdrop" onClick={busy ? null : onClose}>
      <div className="op-modal" role="alertdialog" aria-label={t('common_confirm_delete_label', { what: what })} onClick={function(e) { e.stopPropagation(); }}>
        <h3 className="op-modal-title">{t('common_confirm_delete_title', { what: what })}</h3>
        <p className="op-modal-text">{(function(p) { return [p[0], <strong key="name">{name || t('common_confirm_delete_this', { what: what })}</strong>, p.slice(1).join('{name}')]; })(t('common_confirm_delete_text').split('{name}'))}</p>
        {error && <div className="auth-error">{error}</div>}
        <div className="op-modal-actions">
          <button type="button" className="btn-filter" onClick={onClose} disabled={busy} autoFocus>{t('common_cancel')}</button>
          <button type="button" className="btn-danger" onClick={confirm} disabled={busy}>{busy ? t('common_deleting') : t('common_delete')}</button>
        </div>
      </div>
    </div>
  );
};

// The operator's photo (or initials); click to choose a new one. Stored like the
// mobile app's operator form: OPERATOR_PIC_0_<operator id>.jpg via files_upload,
// then recorded on the operator with operators_update.
var OperatorAvatar = function({ op, onPicSaved, onError }) {
  var { useState, useEffect, useRef } = React;
  var picName = op.operator_profile_pic_file_name;
  var picTs = op.operator_profile_pic_ts;
  // The localStorage copy when current (first paint, no download); fetched only when changed.
  var [url, setUrl]       = useState(function() { return cachedOperatorPhoto(picName, picTs); });
  var [busy, setBusy]     = useState(false);
  var input = useRef(null);

  useEffect(function() {
    var hit = cachedOperatorPhoto(picName, picTs);
    setUrl(hit);
    if (!picName || hit) return;
    var alive = true;
    loadOperatorPhoto(picName, picTs).then(function(u) { if (alive) setUrl(u); }).catch(function() {});
    return function() { alive = false; };
  }, [picName, picTs]);

  var choose = function(e) {
    var file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); onError('');
    var shrunk;
    shrinkImage(file, 512)
      .then(function(blob) { shrunk = blob; return uploadFile(blob, 'OPERATOR_PIC_0_' + op.operator_id + '.jpg'); })
      .then(function(saved) {
        return callRpc('operators_update', {
          operator_id_to_update: op.operator_id,
          full_name:       op.full_name,
          nickname:        op.nickname || '',
          staff_id_number: op.staff_id_number || '',
          enabled:         !!op.enabled,
          live_capture:    !!op.live_capture,
          operator_profile_pic_file_name: saved.file_name,
          operator_profile_pic_file_id:   saved.file_id,
        })
          // Keep the new photo locally straight away, stamped now, so no one has to download it.
          .then(function() { return blobToDataUrl(shrunk); })
          .then(function(dataUrl) {
            var ts = new Date().toISOString();
            storeOperatorPhoto(saved.file_name, ts, dataUrl);
            onPicSaved(saved.file_name, ts);
          });
      })
      .catch(function(err) { onError(t('operators_photo_error', { name: op.full_name || t('operators_operator'), message: err.message || t('operators_unknown_error') })); })
      .finally(function() { setBusy(false); });
  };

  return (
    <button type="button" className={'op-avatar' + (busy ? ' op-avatar--busy' : '')} title={t('operators_set_photo')}
      style={{ background: url ? '#fff' : operatorColor(op.operator_id) }} onClick={function() { if (!busy) input.current.click(); }}>
      {url ? <img src={url} alt={op.full_name} /> : operatorInitials(op.full_name)}
      <span className="op-avatar-cam" aria-hidden="true">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
      </span>
      <input ref={input} type="file" accept="image/*" style={{ display: 'none' }} onChange={choose} />
    </button>
  );
};

// How long ago the operator was last active — the later of their last login and their
// phone last checking in — as one line ("6 months ago"). The dot shows how recent: green
// within 30 minutes, amber within a day, grey otherwise. Hover for both exact times.
var LastSeen = function({ login, seen }) {
  if (!login && !seen) return <span className="ls-never">{t('operators_never')}</span>;
  var latest = [login, seen].filter(Boolean).sort(function(a, b) { return new Date(b) - new Date(a); })[0];
  var ago = Date.now() - new Date(latest).getTime();
  var dot = ago < 30 * 60 * 1000 ? '#22c55e' : ago < 24 * 3600 * 1000 ? '#f59e0b' : '#d1d5db';
  var title = (login ? t('operators_last_login', { time: new Date(login).toLocaleString() }) : t('operators_no_login'))
            + (seen ? '\n' + t('operators_last_seen', { time: new Date(seen).toLocaleString() }) : '');
  return (
    <span className="ls" title={title}>
      <span className="ls-dot" style={{ background: dot }} />
      <span>{timeAgo(latest)}</span>
    </span>
  );
};

// Search box text against the columns shown in the list.
var operatorMatches = function(op, query) {
  if (!query) return true;
  var q = query.toLowerCase();
  return [op.full_name, op.nickname, op.staff_id_number, op.job_name].some(function(v) {
    return v && String(v).toLowerCase().indexOf(q) !== -1;
  });
};

var Operators = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [rows, setRows]       = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError]     = useState('');
  var [qrModal, setQrModal] = useState(null);
  var [formsFor, setFormsFor] = useState(null);   // operator whose forms are being allocated
  var [editModal, setEditModal] = useState(null); // null = closed, { operator: null } = add, { operator: op } = edit
  var [confirmDelete, setConfirmDelete] = useState(null); // the operator waiting on "Are you sure?"
  var [sortCol, setSortCol] = useState(null);
  var [sortDir, setSortDir] = useState('asc');
  var [query, setQuery]     = useState('');
  var [saveError, setSaveError] = useState('');
  // Disabled operators are hidden unless "Display Disabled" is ticked; the choice is remembered.
  var [showDisabled, setShowDisabled] = useState(function() { return localStorage.getItem('operators_show_disabled') === '1'; });
  var toggleDisabled = function(e) {
    setShowDisabled(e.target.checked);
    localStorage.setItem('operators_show_disabled', e.target.checked ? '1' : '0');
  };

  var handleSort = function(col) {
    if (sortCol === col) {
      setSortDir(function(d) { return d === 'asc' ? 'desc' : 'asc'; });
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  var getSortedRows = function() {
    if (!sortCol) return rows;
    return rows.slice().sort(function(a, b) {
      var av = a[sortCol] != null ? String(a[sortCol]).toLowerCase() : '';
      var bv = b[sortCol] != null ? String(b[sortCol]).toLowerCase() : '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  var visibleRows = getSortedRows().filter(function(op) { return (showDisabled || op.enabled) && operatorMatches(op, query.trim()); });
  var disabledCount = rows.filter(function(op) { return !op.enabled; }).length;

  var SortIcon = function({ col }) {
    if (sortCol !== col) return <span style={{ marginLeft:'4px', opacity:'0.3', fontSize:'10px' }}>&#8597;</span>;
    return <span style={{ marginLeft:'4px', fontSize:'10px', color:'#2563eb' }}>{sortDir === 'asc' ? '\u25b2' : '\u25bc'}</span>;
  };

  var loadOperators = function() {
    setError('');
    // Last login / last seen come from a separate call; if it fails the list still shows.
    return Promise.all([callRpc('operators_get'), callRpc('operators_last_seen').catch(function() { return []; })])
      .then(function(res) {
        var seen = {};
        (Array.isArray(res[1]) ? res[1] : []).forEach(function(s) { seen[s.operator_id] = s; });
        setRows((Array.isArray(res[0]) ? res[0] : []).map(function(op) {
          var s = seen[op.operator_id] || {};
          return Object.assign({}, op, { last_login: s.last_login || null, last_seen: s.last_seen || null });
        }));
        setLoading(false);
      })
      .catch(function() { setError(t('operators_load_error')); setLoading(false); });
  };

  useEffect(function() { loadOperators(); }, []);
  useRefresh(loadOperators);   // the countdown at the top of the menu

  var handleSaved = function() {
    setEditModal(null);
    loadOperators();
  };

  // Inline edit of one field: operators_update needs every field, so the rest come from the row.
  var saveField = function(op, key, value) {
    setSaveError('');
    var next = Object.assign({}, op, { [key]: value });
    return callRpc('operators_update', {
      operator_id_to_update: op.operator_id,
      full_name:       next.full_name,
      nickname:        next.nickname || '',
      staff_id_number: next.staff_id_number || '',
      enabled:         !!next.enabled,
      live_capture:    !!next.live_capture,
    }).then(function() {
      setRows(function(r) { return r.map(function(x) { return x.operator_id === op.operator_id ? Object.assign({}, x, { [key]: value }) : x; }); });
    }).catch(function(err) {
      setSaveError(t('operators_save_error', { name: op.full_name || t('operators_operator'), message: err.message || t('operators_unknown_error') }));
      throw err;
    });
  };

  var deleteRow = function(id) { setRows(function(r) { return r.filter(function(x) { return x.operator_id !== id; }); }); };

  var handleExport = function() {
    var now = new Date();
    var pad = function(n) { return String(n).padStart(2, '0'); };
    var exportedAt = now.getFullYear() + '-' + pad(now.getDate()) + '-' + pad(now.getMonth()+1) + ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes());
    var fileName = t('operators_export_file', { date: exportedAt }) + '.xlsx';
    var sheetData = [
      [t('operators_col_full_name'), t('operators_col_nickname'), t('operators_col_current_job'), t('operators_col_staff_id'), t('operators_col_enabled'), t('operators_export_exported_at')],
    ].concat(rows.map(function(op) {
      return [op.full_name || '', op.nickname || '', op.job_name || '', op.staff_id_number || '', op.enabled ? t('common_yes') : t('common_no'), exportedAt];
    }));
    var ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws['!cols'] = [{ wch:28 }, { wch:20 }, { wch:28 }, { wch:16 }, { wch:10 }, { wch:18 }];
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('operators_title'));
    XLSX.writeFile(wb, fileName);
  };

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      {qrModal && <QrModal name={qrModal.name} qrCode={qrModal.qrCode} onClose={function() { setQrModal(null); }} />}
      {formsFor && <OperatorFormsModal operator={formsFor} onClose={function() { setFormsFor(null); }} />}
      {confirmDelete && <ConfirmDeleteModal name={confirmDelete.full_name}
        onConfirm={function() { deleteRow(confirmDelete.operator_id); setConfirmDelete(null); }}
        onClose={function() { setConfirmDelete(null); }} />}
      {editModal && <OperatorModal operator={editModal.operator} onClose={function() { setEditModal(null); }} onSaved={handleSaved} />}

      <div className="lv-header">
        <div className="lv-header-left">
          <h2>{t('operators_title')}</h2>
          <p>{t('operators_subtitle')}</p>
        </div>
        <div className="lv-header-right">
          <button className="btn-export" onClick={handleExport} disabled={rows.length === 0}><ExportIcon /> {t('common_export')}</button>
        </div>
      </div>

      <div className="lv-toolbar">
        <button className="btn-add" onClick={function() { setEditModal({ operator: null }); }}>{t('operators_add')}</button>
        <div className="lv-toolbar-right">
          <label className="lv-toggle" title={t(disabledCount === 1 ? 'operators_disabled_count_one' : 'operators_disabled_count_many', { count: disabledCount })}>
            <input type="checkbox" className="lv-cb" checked={showDisabled} onChange={toggleDisabled} />
            {t('operators_display_disabled')}{disabledCount ? ' (' + disabledCount + ')' : ''}
          </label>
          <div className="lv-search">
            <span className="lv-search-icon"><SearchIcon /></span>
            <input className="lv-search-input" placeholder={t('operators_search_placeholder')} value={query}
              onChange={function(e) { setQuery(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Escape') setQuery(''); }} />
            {query && <button className="lv-search-clear" title={t('common_clear_search')} onClick={function() { setQuery(''); }}>{'\u00d7'}</button>}
          </div>
        </div>
      </div>

      {loading && <div style={{ padding:'32px', textAlign:'center', color:'#6b7280' }}>{t('operators_loading')}</div>}
      {error   && <div className="auth-error" style={{ marginBottom:'16px' }}>{error}</div>}
      {saveError && <div className="auth-error" style={{ marginBottom:'16px' }}>{saveError}</div>}

      {!loading && !error && (
        <div className="lv-table-wrap">
          <table className="lv-table lv-table--compact">
            <thead>
              <tr>
                <th style={{ width:'48px' }}></th>
                <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={function() { handleSort('full_name'); }}>{t('operators_col_full_name')}<SortIcon col="full_name" /></th>
                <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={function() { handleSort('nickname'); }}>{t('operators_col_nickname')}<SortIcon col="nickname" /></th>
                <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={function() { handleSort('job_name'); }}>{t('operators_col_current_job')}<SortIcon col="job_name" /></th>
                <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={function() { handleSort('staff_id_number'); }}>{t('operators_col_staff_id')}<SortIcon col="staff_id_number" /></th>
                <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={function() { handleSort('last_login'); }}>{t('operators_col_last_login')}<SortIcon col="last_login" /></th>
                <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={function() { handleSort('enabled'); }}>{t('operators_col_enabled')}<SortIcon col="enabled" /></th>
                <th>{t('common_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr><td colSpan="8" style={{ textAlign:'center', color:'#9ca3af', padding:'32px' }}>
                  {rows.length === 0 ? t('operators_none')
                    : query.trim() ? t('operators_no_match', { query: query })
                    : t('operators_none_enabled', { count: disabledCount })}
                </td></tr>
              )}
              {visibleRows.map(function(op) {
                var hasJob = op.job_id != null;
                return (
                  <tr key={op.operator_id}>
                    <td style={{ width:'40px', paddingRight:'0' }}>
                      <OperatorAvatar op={op} onError={setSaveError} onPicSaved={function(name, ts) {
                        // the new timestamp makes the avatar pick up the stored new photo
                        setRows(function(r) { return r.map(function(x) { return x.operator_id === op.operator_id ? Object.assign({}, x, { operator_profile_pic_file_name: name, operator_profile_pic_ts: ts }) : x; }); });
                      }} />
                    </td>
                    <td>
                      <InlineCell value={op.full_name} required maxLength={100} className="inline-cell--name"
                        onSave={function(v) { return saveField(op, 'full_name', v); }} />
                    </td>
                    <td className="lv-id">
                      <InlineCell value={op.nickname} maxLength={50} onSave={function(v) { return saveField(op, 'nickname', v); }} />
                    </td>
                    <td>
                      {op.job_name ? <span className="op-job">{op.job_name}</span> : <span className="ls-never">{t('operators_no_job')}</span>}
                    </td>
                    <td className="lv-date">
                      <InlineCell value={op.staff_id_number} maxLength={50} onSave={function(v) { return saveField(op, 'staff_id_number', v); }} />
                    </td>
                    <td><LastSeen login={op.last_login} seen={op.last_seen} /></td>
                    <td>
                      <span style={{ display:'inline-block', fontSize:'11px', fontWeight:'600', padding:'3px 8px', borderRadius:'4px', background: op.enabled ? '#dcfce7' : '#f3f4f6', color: op.enabled ? '#166534' : '#6b7280' }}>
                        {op.enabled ? t('common_yes') : t('common_no')}
                      </span>
                    </td>
                    <td>
                      <div className="lv-actions">
                        <button className="lv-action-btn" title={t('common_edit')} onClick={function() { setEditModal({ operator: op }); }}><EditIcon /></button>
                        <button className="lv-action-btn" title={t('operators_show_qr')} onClick={function() { setQrModal({ name: op.full_name, qrCode: op.login_qr_code }); }}><QrIcon /></button>
                        <button className="lv-action-btn" title={t('opforms_button')} onClick={function() { setFormsFor(op); }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        </button>
                        <button
                          className="lv-action-btn delete"
                          title={hasJob ? t('operators_cannot_delete') : t('common_delete')}
                          onClick={function() { if (!hasJob) setConfirmDelete(op); }}
                          style={{ opacity: hasJob ? 0.35 : 1, cursor: hasJob ? 'not-allowed' : 'pointer' }}
                        ><TrashIcon /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
};

;
/* ── src/pages/Leave.jsx ── */
// Administration › Leave — every leave applied for on the account, in the standard list view.
//   leave_list(from_date, to_date)   the leave overlapping the dates (blank = open-ended)
//   leave_status_list                Applied / Approved / Rejected, for the filter
//   operator_leave_types_get         Medical, Annual, … for the filter
// The dates are filtered by the server; status, type, operator and the search box filter the
// loaded list as you type. The countdown at the top of the menu reloads it (useRefresh).
var LEAVE_STATUS_CLASS = { 0: 'lv-pill--amber', 1: 'lv-pill--green', 2: 'lv-pill--red' };

function leaveDate(d) {
  if (!d) return '—';
  var p = String(d).split('-');
  var mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(p[1]) - 1];
  return Number(p[2]) + ' ' + mon + ' ' + p[0];
}

var LeavePage = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [rows, setRows]         = useState([]);
  var [types, setTypes]       = useState([]);
  var [statuses, setStatuses] = useState([]);
  var [loading, setLoading]   = useState(true);
  var [error, setError]       = useState('');
  var [f, setF] = useState({ status: '', type: '', operator: '', from: '', to: '', q: '' });
  var [sort, setSort] = useState({ col: 'leave_start', dir: 'desc' });

  var load = function() {
    return callRpc('leave_list', { from_date: f.from || null, to_date: f.to || null })
      .then(function(data) { setRows(Array.isArray(data) ? data : []); setError(''); })
      .catch(function(e) { setError(e.message || 'Could not load the leave.'); })
      .finally(function() { setLoading(false); });
  };
  useEffect(function() { load(); }, [f.from, f.to]);
  useRefresh(load);   // the countdown at the top of the menu
  useEffect(function() {
    callRpc('operator_leave_types_get').then(function(d) { setTypes(Array.isArray(d) ? d : []); }).catch(function() {});
    callRpc('leave_status_list').then(function(d) { setStatuses(Array.isArray(d) ? d : []); }).catch(function() {});
  }, []);

  var set = function(key) { return function(e) { var v = e.target.value; setF(function(x) { return Object.assign({}, x, { [key]: v }); }); }; };
  var filtered = f.status !== '' || f.type !== '' || f.operator !== '' || f.from || f.to || f.q.trim();
  var clear = function() { setF({ status: '', type: '', operator: '', from: '', to: '', q: '' }); };

  // the operators who have leave in the loaded dates, for the Operator filter
  var operators = [];
  rows.forEach(function(r) { if (!operators.some(function(o) { return o.id === r.operator_id; })) operators.push({ id: r.operator_id, name: r.operator_name }); });
  operators.sort(function(a, b) { return String(a.name).localeCompare(String(b.name)); });

  var q = f.q.trim().toLowerCase();
  var shown = rows.filter(function(r) {
    if (f.status !== '' && String(r.leave_status_id) !== f.status) return false;
    if (f.type !== '' && String(r.leave_type_id) !== f.type) return false;
    if (f.operator !== '' && r.operator_id !== f.operator) return false;
    if (q && ![r.operator_name, r.staff_id_number, r.leave_type, r.leave_status, r.leave_comment]
               .some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; })) return false;
    return true;
  }).sort(function(a, b) {
    var x = a[sort.col], y = b[sort.col];
    var c = (x == null) - (y == null) || (typeof x === 'number' ? x - y : String(x || '').localeCompare(String(y || '')));
    return sort.dir === 'asc' ? c : -c;
  });
  var days = shown.reduce(function(n, r) { return n + (r.leave_days || 0); }, 0);

  var th = function(col, label) {
    var on = sort.col === col;
    return (
      <th style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}
          onClick={function() { setSort({ col: col, dir: on && sort.dir === 'asc' ? 'desc' : 'asc' }); }}>
        {label}
        <span style={{ marginLeft:'4px', fontSize:'10px', color: on ? '#2563eb' : undefined, opacity: on ? 1 : 0.3 }}>
          {on ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </th>
    );
  };

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Leave</h2>
          <p>Every leave your operators have applied for.</p>
        </div>
      </div>

      <div className="lv-filters">
        <label className="lv-filter">Status
          <select className="jb-input" value={f.status} onChange={set('status')}>
            <option value="">All</option>
            {statuses.map(function(s) { return <option key={s.leave_status_id} value={String(s.leave_status_id)}>{s.leave_status}</option>; })}
          </select>
        </label>
        <label className="lv-filter">Type
          <select className="jb-input" value={f.type} onChange={set('type')}>
            <option value="">All</option>
            {types.map(function(t) { return <option key={t.leave_type_id} value={String(t.leave_type_id)}>{t.leave_description}</option>; })}
          </select>
        </label>
        <label className="lv-filter">Operator
          <select className="jb-input" value={f.operator} onChange={set('operator')}>
            <option value="">All</option>
            {operators.map(function(o) { return <option key={o.id} value={o.id}>{o.name}</option>; })}
          </select>
        </label>
        <label className="lv-filter">From
          <input type="date" className="jb-input" value={f.from} max={f.to || undefined} onChange={set('from')} />
        </label>
        <label className="lv-filter">To
          <input type="date" className="jb-input" value={f.to} min={f.from || undefined} onChange={set('to')} />
        </label>
        <div className="lv-search lv-filter-search">
          <span className="lv-search-icon"><SearchIcon /></span>
          <input className="lv-search-input" placeholder="Search operator, staff ID or comment" value={f.q} onChange={set('q')}
                 onKeyDown={function(e) { if (e.key === 'Escape') setF(function(x) { return Object.assign({}, x, { q: '' }); }); }} />
          {f.q && <button className="lv-search-clear" title="Clear search" onClick={function() { setF(function(x) { return Object.assign({}, x, { q: '' }); }); }}>{'×'}</button>}
        </div>
        {filtered && <button type="button" className="btn-filter" onClick={clear}>Clear filters</button>}
      </div>

      {error && <div className="auth-error" style={{ marginBottom:'16px' }}>{error}</div>}
      {loading && <div style={{ padding:'32px', textAlign:'center', color:'#6b7280' }}>Loading leave{'…'}</div>}

      {!loading && !error && (
        <>
          <div className="lv-count">
            {shown.length} leave application{shown.length === 1 ? '' : 's'} {'·'} {days} day{days === 1 ? '' : 's'}
            {filtered && rows.length !== shown.length ? ' (of ' + rows.length + ')' : ''}
          </div>
          <div className="lv-table-wrap">
            <table className="lv-table lv-table--compact">
              <thead>
                <tr>
                  {th('operator_name', 'Operator')}
                  {th('leave_type', 'Type')}
                  {th('leave_start', 'From')}
                  {th('leave_end', 'To')}
                  {th('leave_days', 'Days')}
                  {th('leave_status_id', 'Status')}
                  <th>Comment</th>
                  {th('applied_ts', 'Applied')}
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan="8" style={{ textAlign:'center', color:'#9ca3af', padding:'32px' }}>
                    {rows.length === 0 && !filtered ? 'No leave has been applied for yet.' : 'No leave matches these filters.'}
                  </td></tr>
                )}
                {shown.map(function(r) {
                  return (
                    <tr key={r.leave_id}>
                      <td>
                        <div style={{ fontWeight:600, color:'#111827' }}>{r.operator_name}</div>
                        {r.staff_id_number && <div className="lv-id" style={{ fontSize:'12px' }}>{r.staff_id_number}</div>}
                      </td>
                      <td>{r.leave_type || '—'}</td>
                      <td className="lv-date" style={{ whiteSpace:'nowrap' }}>{leaveDate(r.leave_start)}</td>
                      <td className="lv-date" style={{ whiteSpace:'nowrap' }}>{leaveDate(r.leave_end)}</td>
                      <td>{r.leave_days == null ? '—' : r.leave_days}</td>
                      <td><span className={'lv-pill ' + (LEAVE_STATUS_CLASS[r.leave_status_id] || '')}>{r.leave_status || '—'}</span></td>
                      <td className="lv-id" style={{ maxWidth:'280px' }}>{r.leave_comment || ''}</td>
                      <td className="lv-id" style={{ whiteSpace:'nowrap' }}>{r.applied_ts ? new Date(r.applied_ts).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' }) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppShell>
  );
};

;
/* ── src/pages/Claims.jsx ── */
// Administration › Claims — every expense claim on the account, in the standard list view, and
// adding one for an operator.
//   claims_list(from_date, to_date)   the claims dated between the dates (blank = open-ended)
//   claim_status_list                 Requested / Approved / Rejected, for the filter
//   operator_claim_types_get          Fuel, Parking, …, for the filter and the Add form
//   claims_add(claim_operator_id, claim_date, claim_type_id, claim_value, claim_note)
//   operators_get                     the operators to choose from, loaded when Add opens
// The dates are filtered by the server; status, type, operator and the search box filter the
// loaded list as you type. The countdown at the top of the menu reloads it (useRefresh).
var CLAIM_STATUS_CLASS = { 0: 'lv-pill--amber', 1: 'lv-pill--green', 2: 'lv-pill--red' };

function claimAmount(v) {
  return v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function todayIso() {
  var d = new Date(), pad = function(n) { return String(n).padStart(2, '0'); };
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

var ClaimModal = function({ types, onClose, onSaved }) {
  var { useState, useEffect } = React;
  var [operators, setOperators] = useState(null);   // null = loading
  var [form, setForm] = useState({ operator: '', date: todayIso(), type: types.length ? String(types[0].claim_type_id) : '', value: '', note: '' });
  var [saving, setSaving] = useState(false);
  var [error, setError]   = useState('');
  var set = function(key) { return function(e) { var v = e.target.value; setForm(function(f) { return Object.assign({}, f, { [key]: v }); }); }; };

  useEffect(function() {
    callRpc('operators_get')
      .then(function(d) {
        var list = (Array.isArray(d) ? d : []).slice().sort(function(a, b) { return String(a.full_name || '').localeCompare(String(b.full_name || '')); });
        setOperators(list);
      })
      .catch(function(e) { setOperators([]); setError(e.message || 'Could not load the operators.'); });
  }, []);
  useEffect(function() {   // the types can arrive after the form opens
    if (!form.type && types.length) setForm(function(f) { return Object.assign({}, f, { type: String(types[0].claim_type_id) }); });
  }, [types]);

  var handleSubmit = async function(e) {
    e.preventDefault();
    var value = Number(form.value);
    if (!form.operator) { setError('Choose the operator.'); return; }
    if (!(value > 0))   { setError('Enter an amount more than 0.'); return; }
    setSaving(true); setError('');
    try {
      await callRpc('claims_add', {
        claim_operator_id: form.operator,
        claim_date:        form.date,
        claim_type_id:     Number(form.type),
        claim_value:       value,
        claim_note:        form.note,
      });
      onSaved();
    } catch(err) {
      setError(err.message || 'Could not add the claim.');
      setSaving(false);
    }
  };

  return (
    <div className="op-modal-backdrop" onClick={saving ? null : onClose}>
      <form className="op-modal" onClick={function(e) { e.stopPropagation(); }} onSubmit={handleSubmit}>
        <h3 className="op-modal-title">Add Claim</h3>
        <div className="form-group">
          <label className="form-label">Operator</label>
          <select className="form-input" value={form.operator} onChange={set('operator')} required autoFocus disabled={!operators}>
            <option value="">{operators ? 'Choose an operator' : 'Loading operators…'}</option>
            {(operators || []).map(function(o) {
              return <option key={o.operator_id} value={o.operator_id}>{o.full_name}{o.staff_id_number ? ' (' + o.staff_id_number + ')' : ''}</option>;
            })}
          </select>
        </div>
        <div className="op-modal-row">
          <div className="form-group">
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={form.date} max={todayIso()} onChange={set('date')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="form-input" value={form.type} onChange={set('type')} required>
              {types.map(function(t) { return <option key={t.claim_type_id} value={String(t.claim_type_id)}>{t.claim_description}</option>; })}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Amount</label>
          <input className="form-input" type="number" inputMode="decimal" min="0.01" step="0.01" placeholder="0.00" value={form.value} onChange={set('value')} required />
        </div>
        <div className="form-group">
          <label className="form-label">Note</label>
          <textarea className="form-input" rows="3" maxLength={500} value={form.note} onChange={set('note')} placeholder="What the claim is for" style={{ resize:'vertical', fontFamily:'inherit' }} />
        </div>
        {error && <div className="auth-error">{error}</div>}
        <div className="op-modal-actions">
          <button type="button" className="btn-filter" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn-add" disabled={saving || !operators}>{saving ? 'Saving…' : 'Add claim'}</button>
        </div>
      </form>
    </div>
  );
};

var ClaimsPage = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [rows, setRows]         = useState([]);
  var [types, setTypes]       = useState([]);
  var [statuses, setStatuses] = useState([]);
  var [loading, setLoading]   = useState(true);
  var [error, setError]       = useState('');
  var [adding, setAdding]     = useState(false);
  var [f, setF] = useState({ status: '', type: '', operator: '', from: '', to: '', q: '' });
  var [sort, setSort] = useState({ col: 'claim_date', dir: 'desc' });

  var load = function() {
    return callRpc('claims_list', { from_date: f.from || null, to_date: f.to || null })
      .then(function(data) { setRows(Array.isArray(data) ? data : []); setError(''); })
      .catch(function(e) { setError(e.message || 'Could not load the claims.'); })
      .finally(function() { setLoading(false); });
  };
  useEffect(function() { load(); }, [f.from, f.to]);
  useRefresh(load);   // the countdown at the top of the menu
  useEffect(function() {
    callRpc('operator_claim_types_get').then(function(d) { setTypes(Array.isArray(d) ? d : []); }).catch(function() {});
    callRpc('claim_status_list').then(function(d) { setStatuses(Array.isArray(d) ? d : []); }).catch(function() {});
  }, []);

  var set = function(key) { return function(e) { var v = e.target.value; setF(function(x) { return Object.assign({}, x, { [key]: v }); }); }; };
  var filtered = f.status !== '' || f.type !== '' || f.operator !== '' || f.from || f.to || f.q.trim();
  var clear = function() { setF({ status: '', type: '', operator: '', from: '', to: '', q: '' }); };

  // the operators who have claims in the loaded dates, for the Operator filter
  var operators = [];
  rows.forEach(function(r) { if (!operators.some(function(o) { return o.id === r.operator_id; })) operators.push({ id: r.operator_id, name: r.operator_name }); });
  operators.sort(function(a, b) { return String(a.name).localeCompare(String(b.name)); });

  var q = f.q.trim().toLowerCase();
  var shown = rows.filter(function(r) {
    if (f.status !== '' && String(r.claim_status_id) !== f.status) return false;
    if (f.type !== '' && String(r.claim_type_id) !== f.type) return false;
    if (f.operator !== '' && r.operator_id !== f.operator) return false;
    if (q && ![r.operator_name, r.staff_id_number, r.claim_type, r.claim_status, r.claim_note, claimAmount(r.claim_value)]
               .some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; })) return false;
    return true;
  }).sort(function(a, b) {
    var x = a[sort.col], y = b[sort.col];
    if (sort.col === 'claim_value') { x = x == null ? null : Number(x); y = y == null ? null : Number(y); }
    var c = (x == null) - (y == null) || (typeof x === 'number' ? x - y : String(x || '').localeCompare(String(y || '')));
    return sort.dir === 'asc' ? c : -c;
  });
  var total = shown.reduce(function(n, r) { return n + (Number(r.claim_value) || 0); }, 0);

  var th = function(col, label, style) {
    var on = sort.col === col;
    return (
      <th style={Object.assign({ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }, style)}
          onClick={function() { setSort({ col: col, dir: on && sort.dir === 'asc' ? 'desc' : 'asc' }); }}>
        {label}
        <span style={{ marginLeft:'4px', fontSize:'10px', color: on ? '#2563eb' : undefined, opacity: on ? 1 : 0.3 }}>
          {on ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </th>
    );
  };

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Claims</h2>
          <p>Every expense claim your operators have made.</p>
        </div>
      </div>

      <div className="lv-toolbar">
        <button className="btn-add" onClick={function() { setAdding(true); }}>+ Add Claim</button>
      </div>

      <div className="lv-filters">
        <label className="lv-filter">Status
          <select className="jb-input" value={f.status} onChange={set('status')}>
            <option value="">All</option>
            {statuses.map(function(s) { return <option key={s.claim_status_id} value={String(s.claim_status_id)}>{s.claim_status}</option>; })}
          </select>
        </label>
        <label className="lv-filter">Type
          <select className="jb-input" value={f.type} onChange={set('type')}>
            <option value="">All</option>
            {types.map(function(t) { return <option key={t.claim_type_id} value={String(t.claim_type_id)}>{t.claim_description}</option>; })}
          </select>
        </label>
        <label className="lv-filter">Operator
          <select className="jb-input" value={f.operator} onChange={set('operator')}>
            <option value="">All</option>
            {operators.map(function(o) { return <option key={o.id} value={o.id}>{o.name}</option>; })}
          </select>
        </label>
        <label className="lv-filter">From
          <input type="date" className="jb-input" value={f.from} max={f.to || undefined} onChange={set('from')} />
        </label>
        <label className="lv-filter">To
          <input type="date" className="jb-input" value={f.to} min={f.from || undefined} onChange={set('to')} />
        </label>
        <div className="lv-search lv-filter-search">
          <span className="lv-search-icon"><SearchIcon /></span>
          <input className="lv-search-input" placeholder="Search operator, staff ID, note or amount" value={f.q} onChange={set('q')}
                 onKeyDown={function(e) { if (e.key === 'Escape') setF(function(x) { return Object.assign({}, x, { q: '' }); }); }} />
          {f.q && <button className="lv-search-clear" title="Clear search" onClick={function() { setF(function(x) { return Object.assign({}, x, { q: '' }); }); }}>{'×'}</button>}
        </div>
        {filtered && <button type="button" className="btn-filter" onClick={clear}>Clear filters</button>}
      </div>

      {error && <div className="auth-error" style={{ marginBottom:'16px' }}>{error}</div>}
      {loading && <div style={{ padding:'32px', textAlign:'center', color:'#6b7280' }}>Loading claims{'…'}</div>}

      {!loading && !error && (
        <>
          <div className="lv-count">
            {shown.length} claim{shown.length === 1 ? '' : 's'} {'·'} {claimAmount(total)} total
            {filtered && rows.length !== shown.length ? ' (of ' + rows.length + ')' : ''}
          </div>
          <div className="lv-table-wrap">
            <table className="lv-table lv-table--compact">
              <thead>
                <tr>
                  {th('operator_name', 'Operator')}
                  {th('claim_date', 'Date')}
                  {th('claim_type', 'Type')}
                  {th('claim_value', 'Amount', { textAlign:'right' })}
                  {th('claim_status_id', 'Status')}
                  <th>Note</th>
                  {th('created_ts', 'Submitted')}
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan="7" style={{ textAlign:'center', color:'#9ca3af', padding:'32px' }}>
                    {rows.length === 0 && !filtered ? 'No claims yet.' : 'No claims match these filters.'}
                  </td></tr>
                )}
                {shown.map(function(r) {
                  return (
                    <tr key={r.claim_id}>
                      <td>
                        <div style={{ fontWeight:600, color:'#111827' }}>{r.operator_name}</div>
                        {r.staff_id_number && <div className="lv-id" style={{ fontSize:'12px' }}>{r.staff_id_number}</div>}
                      </td>
                      <td className="lv-date" style={{ whiteSpace:'nowrap' }}>{leaveDate(r.claim_date)}</td>
                      <td>{r.claim_type || '—'}</td>
                      <td style={{ textAlign:'right', fontWeight:600, color:'#111827', fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{claimAmount(r.claim_value)}</td>
                      <td><span className={'lv-pill ' + (CLAIM_STATUS_CLASS[r.claim_status_id] || '')}>{r.claim_status || '—'}</span></td>
                      <td className="lv-id" style={{ maxWidth:'280px' }}>{r.claim_note || ''}</td>
                      <td className="lv-id" style={{ whiteSpace:'nowrap' }}>{r.created_ts ? new Date(r.created_ts).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' }) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {adding && <ClaimModal types={types} onClose={function() { setAdding(false); }} onSaved={function() { setAdding(false); load(); }} />}
    </AppShell>
  );
};

;
/* ── src/pages/Customers.jsx ── */
// Administration › Customers — the account's customers (ops.customers), in the standard list view.
//   customers_list                     every customer on the account, by name
//   customers_add(customer_name, …)    a new customer; the name is required and unique on the account
//   customers_delete(customer_id_to_delete)
// Delete asks first (ConfirmDeleteModal, Operators.jsx). The search box filters the loaded list.
var CUSTOMER_FIELDS = [
  { key: 'customer_name', label: 'Customer name', max: 150, required: true },
  { key: 'first_name',    label: 'First name',    max: 100 },
  { key: 'last_name',     label: 'Last name',     max: 100 },
  { key: 'email',         label: 'Email',         max: 200, type: 'email' },
  { key: 'phone_number',  label: 'Phone',         max: 50, type: 'tel' },
  { key: 'mobile_number', label: 'Mobile',        max: 50, type: 'tel' },
  { key: 'tax_number',    label: 'Tax number',    max: 50 },
];

var CustomerModal = function({ onClose, onSaved }) {
  var { useState } = React;
  var [form, setForm]     = useState({ customer_name: '', first_name: '', last_name: '', email: '', phone_number: '', mobile_number: '', tax_number: '' });
  var [saving, setSaving] = useState(false);
  var [error, setError]   = useState('');
  var set = function(key) { return function(e) { var v = e.target.value; setForm(function(f) { return Object.assign({}, f, { [key]: v }); }); }; };

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!form.customer_name.trim()) { setError('Customer name is required.'); return; }
    setSaving(true); setError('');
    try {
      await callRpc('customers_add', form);
      onSaved();
    } catch(err) {
      setError(err.message || 'Could not add the customer.');
      setSaving(false);
    }
  };

  var input = function(fd, autoFocus) {
    return (
      <div className="form-group" key={fd.key}>
        <label className="form-label">{fd.label}</label>
        <input className="form-input" type={fd.type || 'text'} value={form[fd.key]} onChange={set(fd.key)} maxLength={fd.max} required={fd.required} autoFocus={autoFocus} />
      </div>
    );
  };
  var byKey = function(k) { return CUSTOMER_FIELDS.filter(function(fd) { return fd.key === k; })[0]; };

  return (
    <div className="op-modal-backdrop" onClick={saving ? null : onClose}>
      <form className="op-modal" onClick={function(e) { e.stopPropagation(); }} onSubmit={handleSubmit}>
        <h3 className="op-modal-title">Add Customer</h3>
        {input(byKey('customer_name'), true)}
        <div className="op-modal-row">{input(byKey('first_name'))}{input(byKey('last_name'))}</div>
        {input(byKey('email'))}
        <div className="op-modal-row">{input(byKey('phone_number'))}{input(byKey('mobile_number'))}</div>
        {input(byKey('tax_number'))}
        {error && <div className="auth-error">{error}</div>}
        <div className="op-modal-actions">
          <button type="button" className="btn-filter" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn-add" disabled={saving}>{saving ? 'Saving…' : 'Add customer'}</button>
        </div>
      </form>
    </div>
  );
};

var Customers = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [rows, setRows]       = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError]     = useState('');
  var [query, setQuery]     = useState('');
  var [adding, setAdding]   = useState(false);
  var [confirmDelete, setConfirmDelete] = useState(null); // the customer waiting on "Are you sure?"

  var load = function() {
    return callRpc('customers_list')
      .then(function(data) { setRows(Array.isArray(data) ? data : []); setError(''); })
      .catch(function(e) { setError(e.message || 'Could not load the customers.'); })
      .finally(function() { setLoading(false); });
  };
  useEffect(function() { load(); }, []);
  useRefresh(load);   // the countdown at the top of the menu

  var deleteCustomer = function(c) {
    return callRpc('customers_delete', { customer_id_to_delete: c.customer_id }).then(function() {
      setRows(function(r) { return r.filter(function(x) { return x.customer_id !== c.customer_id; }); });
      setConfirmDelete(null);
    });
  };

  var contact = function(c) { return [c.first_name, c.last_name].filter(Boolean).join(' '); };
  var q = query.trim().toLowerCase();
  var shown = rows.filter(function(c) {
    return !q || [c.customer_name, contact(c), c.email, c.phone_number, c.mobile_number, c.tax_number]
      .some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; });
  });

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Customers</h2>
          <p>The customers your jobs and forms are done for.</p>
        </div>
      </div>

      <div className="lv-toolbar">
        <button className="btn-add" onClick={function() { setAdding(true); }}>+ Add Customer</button>
        <div className="lv-toolbar-right">
          <div className="lv-search">
            <span className="lv-search-icon"><SearchIcon /></span>
            <input className="lv-search-input" placeholder="Search name, contact, email or phone" value={query}
              onChange={function(e) { setQuery(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Escape') setQuery(''); }} />
            {query && <button className="lv-search-clear" title="Clear search" onClick={function() { setQuery(''); }}>{'×'}</button>}
          </div>
        </div>
      </div>

      {error && <div className="auth-error" style={{ marginBottom:'16px' }}>{error}</div>}
      {loading && <div style={{ padding:'32px', textAlign:'center', color:'#6b7280' }}>Loading customers{'…'}</div>}

      {!loading && !error && (
        <>
          <div className="lv-count">
            {shown.length} customer{shown.length === 1 ? '' : 's'}
            {q && rows.length !== shown.length ? ' (of ' + rows.length + ')' : ''}
          </div>
          <div className="lv-table-wrap">
            <table className="lv-table lv-table--compact">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Mobile</th>
                  <th>Tax number</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan="8" style={{ textAlign:'center', color:'#9ca3af', padding:'32px' }}>
                    {rows.length === 0 ? 'No customers yet. Add one to get started.' : 'No customers match your search.'}
                  </td></tr>
                )}
                {shown.map(function(c) {
                  return (
                    <tr key={c.customer_id}>
                      <td style={{ fontWeight:600, color:'#111827' }}>{c.customer_name || '—'}</td>
                      <td>{contact(c) || '—'}</td>
                      <td className="lv-id">{c.email ? <a href={'mailto:' + c.email} className="link-btn--blue">{c.email}</a> : '—'}</td>
                      <td className="lv-id" style={{ whiteSpace:'nowrap' }}>{c.phone_number || '—'}</td>
                      <td className="lv-id" style={{ whiteSpace:'nowrap' }}>{c.mobile_number || '—'}</td>
                      <td className="lv-id">{c.tax_number || '—'}</td>
                      <td className="lv-id" style={{ whiteSpace:'nowrap' }}>{c.create_ts ? new Date(c.create_ts).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' }) : '—'}</td>
                      <td>
                        <div className="lv-actions">
                          <button className="lv-action-btn delete" title="Delete" onClick={function() { setConfirmDelete(c); }}><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {adding && <CustomerModal onClose={function() { setAdding(false); }} onSaved={function() { setAdding(false); load(); }} />}
      {confirmDelete && <ConfirmDeleteModal what="customer" name={confirmDelete.customer_name}
        onConfirm={function() { return deleteCustomer(confirmDelete); }}
        onClose={function() { setConfirmDelete(null); }} />}
    </AppShell>
  );
};

;
/* ── src/pages/Gallery.jsx ── */
// Administration › Gallery — the account's shared image gallery, the same one the mobile app shows:
// the manager and every operator see, label, download and delete the same images.
//   gallery_get                                  the account's images, newest first
//   files_upload, then gallery_add(file, label)  an upload, named GALLERY_PIC_0_<uuid>.<ext> so it never
//                                                replaces another image; the label starts as the file name
//   gallery_update(id, label)                    relabel (click the label)
//   gallery_delete(id)                           after "Are you sure?" (ConfirmDeleteModal, Operators.jsx)
// The images come from files_get (fetchFileUrl), cached for the page's life.
var GALLERY_MAX_BYTES = 8 * 1024 * 1024;   // bigger photos are scaled down before upload (the server takes 20 MB)

var DownloadIcon = function() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
};

function galleryExt(name) { var m = /\.([A-Za-z0-9]{1,5})$/.exec(name || ''); return m ? m[1].toLowerCase() : 'jpg'; }
function galleryUid() {
  return (window.crypto && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, '')
    : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

// Saves an image under its label, e.g. "Front gate.jpg".
function downloadGalleryImage(item) {
  return fetchFileUrl(item.file_name).then(function(url) {
    var a = document.createElement('a');
    a.href = url;
    a.download = (item.file_description || 'image').replace(/[\\/:*?"<>|]+/g, ' ').trim() + '.' + galleryExt(item.file_name);
    document.body.appendChild(a); a.click(); a.remove();
  });
}

var GalleryImage = function({ fileName, alt, className }) {
  var { useState, useEffect } = React;
  var [url, setUrl]       = useState(null);
  var [failed, setFailed] = useState(false);
  useEffect(function() {
    var live = true;
    setUrl(null); setFailed(false);
    if (!fileName) { setFailed(true); return; }
    fetchFileUrl(fileName).then(function(u) { if (live) setUrl(u); }).catch(function() { if (live) setFailed(true); });
    return function() { live = false; };
  }, [fileName]);
  if (failed) return <div className="gallery-item-missing" title="Image not available"><GalleryEmptyIcon /></div>;
  if (!url)   return <div className="gallery-item-loading" />;
  return <img src={url} alt={alt} className={className} />;
};

// Full-size view, with the label, who added it, and Download.
var GalleryPreview = function({ item, onClose, onError }) {
  var { useEffect } = React;
  useEffect(function() {
    var onKey = function(e) { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, []);
  return (
    <div className="gallery-preview" onClick={onClose}>
      <div className="gallery-preview-body" onClick={function(e) { e.stopPropagation(); }}>
        <GalleryImage fileName={item.file_name} alt={item.file_description} className="gallery-preview-img" />
        <div className="gallery-preview-bar">
          <div style={{ minWidth: 0 }}>
            <div className="gallery-preview-label">{item.file_description || 'Untitled'}</div>
            <div className="gallery-item-meta">{galleryMeta(item)}</div>
          </div>
          <button className="btn-filter" onClick={function() { downloadGalleryImage(item).catch(function(e) { onError(e.message); }); }}><DownloadIcon /> Download</button>
          <button className="btn-filter" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

function galleryMeta(item) {
  var when = item.create_ts ? new Date(item.create_ts).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' }) : '';
  return [item.added_by, when].filter(Boolean).join(' · ');
}

var Gallery = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect, useRef } = React;
  var [images, setImages]     = useState([]);
  var [loading, setLoading]   = useState(true);
  var [error, setError]       = useState('');
  var [uploading, setUploading] = useState(0);   // files still uploading
  var [viewSize, setViewSize] = useState('large');
  var [query, setQuery]       = useState('');
  var [preview, setPreview]   = useState(null);
  var [confirmDelete, setConfirmDelete] = useState(null);
  var fileInputRef            = useRef(null);

  var load = function() {
    return callRpc('gallery_get')
      .then(function(data) { setImages(Array.isArray(data) ? data : []); })
      .catch(function(e) { setError(e.message || 'Could not load the gallery.'); })
      .finally(function() { setLoading(false); });
  };
  useEffect(function() { load(); }, []);
  useRefresh(load);   // the countdown at the top of the menu — picks up images added on the phones

  var uploadOne = function(file) {
    var label = file.name.replace(/\.[^.]+$/, '') || 'Image';
    var ready = file.size > GALLERY_MAX_BYTES ? shrinkImage(file, 2560) : Promise.resolve(file);
    return ready
      .then(function(blob) {
        var ext = blob === file ? galleryExt(file.name) : 'jpg';
        return uploadFile(blob, 'GALLERY_PIC_0_' + galleryUid() + '.' + ext);
      })
      .then(function(up) { return callRpc('gallery_add', { in_file_id: up.file_id, in_file_description: label }); })
      .then(function(rows) { if (Array.isArray(rows) && rows[0]) setImages(function(prev) { return [rows[0]].concat(prev); }); })
      .catch(function(e) { setError('Could not add ' + file.name + ': ' + (e.message || 'upload failed')); })
      .finally(function() { setUploading(function(n) { return n - 1; }); });
  };

  var handleFileChange = function(e) {
    var files = Array.from(e.target.files).filter(function(f) { return /^image\//.test(f.type); });
    e.target.value = '';
    if (!files.length) return;
    setError('');
    setUploading(function(n) { return n + files.length; });
    files.reduce(function(p, f) { return p.then(function() { return uploadOne(f); }); }, Promise.resolve());
  };

  var relabel = function(item, label) {
    setError('');
    return callRpc('gallery_update', { in_update_gallery_id: item.gallery_id, in_update_file_description: label })
      .then(function(rows) { if (Array.isArray(rows)) setImages(rows); })
      .catch(function(e) { setError('Could not rename the image: ' + (e.message || 'unknown error')); throw e; });
  };

  var remove = function(item) {
    return callRpc('gallery_delete', { in_delete_gallery_id: item.gallery_id }).then(function(rows) {
      setImages(Array.isArray(rows) ? rows : function(prev) { return prev.filter(function(i) { return i.gallery_id !== item.gallery_id; }); });
      setConfirmDelete(null);
    });
  };

  var download = function(item) { downloadGalleryImage(item).catch(function(e) { setError('Could not download the image: ' + (e.message || 'unknown error')); }); };

  var q = query.trim().toLowerCase();
  var shown = images.filter(function(i) {
    return !q || [i.file_description, i.added_by].some(function(v) { return v && String(v).toLowerCase().indexOf(q) !== -1; });
  });

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="gallery-header">
        <div>
          <div className="gallery-title">Gallery</div>
          <div className="gallery-subtitle">
            {images.length} image{images.length !== 1 ? 's' : ''} · shared with all your operators in the mobile app
            {uploading > 0 ? ' · uploading ' + uploading + '…' : ''}
          </div>
        </div>
        <div className="gallery-actions">
          <div className="lv-search">
            <span className="lv-search-icon"><SearchIcon /></span>
            <input className="lv-search-input" placeholder="Search labels" value={query}
              onChange={function(e) { setQuery(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Escape') setQuery(''); }} />
            {query && <button className="lv-search-clear" title="Clear search" onClick={function() { setQuery(''); }}>{'×'}</button>}
          </div>
          <div className="view-toggle">
            <button className={'view-toggle-btn' + (viewSize === 'small' ? ' view-toggle-btn--active' : '')} onClick={function() { setViewSize('small'); }} title="Small icons">
              <GridSmallIcon /><span>Small</span>
            </button>
            <button className={'view-toggle-btn' + (viewSize === 'large' ? ' view-toggle-btn--active' : '')} onClick={function() { setViewSize('large'); }} title="Large icons">
              <GridLargeIcon /><span>Large</span>
            </button>
          </div>
          <button className="gallery-add-btn" onClick={function() { if (fileInputRef.current) fileInputRef.current.click(); }}>
            <PlusIcon /> Add Image
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={handleFileChange} />
        </div>
      </div>

      {error && <div className="auth-error" style={{ marginBottom:'16px' }}>{error}</div>}
      {loading && <div style={{ padding:'32px', textAlign:'center', color:'#6b7280' }}>Loading gallery{'…'}</div>}

      {!loading && images.length === 0 && (
        <div className="gallery-empty">
          <div className="gallery-empty-icon"><GalleryEmptyIcon /></div>
          <p className="gallery-empty-text">No images yet</p>
          <p className="gallery-empty-hint">Click "Add Image" to upload one. Your operators will see it in the app.</p>
        </div>
      )}
      {!loading && images.length > 0 && shown.length === 0 && (
        <div style={{ padding:'32px', textAlign:'center', color:'#9ca3af' }}>No images match your search.</div>
      )}

      {shown.length > 0 && (
        <div className={'gallery-grid gallery-grid--' + viewSize}>
          {shown.map(function(img) {
            return (
              <div key={img.gallery_id} className="gallery-item">
                <div className="gallery-item-img-wrap" onClick={function() { setPreview(img); }} title="View">
                  <GalleryImage fileName={img.file_name} alt={img.file_description} className="gallery-item-img" />
                  <div className="gallery-item-tools" onClick={function(e) { e.stopPropagation(); }}>
                    <button className="gallery-item-tool" onClick={function() { download(img); }} title="Download" disabled={!img.file_name}><DownloadIcon /></button>
                    <button className="gallery-item-tool gallery-item-tool--delete" onClick={function() { setConfirmDelete(img); }} title="Delete image"><DeleteImgIcon /></button>
                  </div>
                </div>
                <div className="gallery-item-name">
                  <InlineCell value={img.file_description} maxLength={100} required onSave={function(v) { return relabel(img, v); }} />
                </div>
                {viewSize === 'large' && <div className="gallery-item-meta">{galleryMeta(img)}</div>}
              </div>
            );
          })}
        </div>
      )}

      {preview && <GalleryPreview item={preview} onClose={function() { setPreview(null); }} onError={function(m) { setError('Could not download the image: ' + m); }} />}
      {confirmDelete && <ConfirmDeleteModal what="image" name={confirmDelete.file_description}
        onConfirm={function() { return remove(confirmDelete); }}
        onClose={function() { setConfirmDelete(null); }} />}
    </AppShell>
  );
};

;
/* ── src/pages/Profile.jsx ── */
// Configuration > Profile: the account's company details, language / timezone and password.
//   company, industry, country  account_company_get / account_company_set
//   language, timezone          account_profile_get / account_profile_set
//   password                    login_change_password
// Each card saves on its own, so a mistake in one doesn't hold up the others.

// Country codes come from the API; the browser supplies the names.
var countryName = (function() {
  var names = null;
  try { names = new Intl.DisplayNames(['en'], { type: 'region' }); } catch(e) {}
  return function(code) {
    try { return (names && names.of(String(code).toUpperCase())) || String(code).toUpperCase(); }
    catch(e) { return String(code).toUpperCase(); }
  };
})();

var ProfileCard = function({ title, subtitle, children }) {
  return (
    <section className="pf-card">
      <h3 className="pf-card-title">{title}</h3>
      {subtitle && <p className="pf-card-sub">{subtitle}</p>}
      <div className="pf-card-body">{children}</div>
    </section>
  );
};

// Save button plus the result of the last save under it.
var SaveRow = function({ saving, status, label }) {
  return (
    <div className="pf-save-row">
      {status && <span className={status.ok ? 'pf-ok' : 'pf-err'}>{status.text}</span>}
      <button type="submit" className="btn-add" disabled={saving}>{saving ? t('common_saving') : (label || t('common_save'))}</button>
    </div>
  );
};

var CompanyCard = function({ company, industries, countries, onSaved }) {
  var { useState } = React;
  var [form, setForm] = useState({
    company_name:     company.company_name || '',
    industry_type_id: company.industry_type_id == null ? '' : String(company.industry_type_id),
    country_code:     company.country_code || '',
  });
  var [saving, setSaving] = useState(false);
  var [status, setStatus] = useState(null);
  var set = function(key) { return function(e) { var v = e.target.value; setForm(function(f) { return Object.assign({}, f, { [key]: v }); }); setStatus(null); }; };

  var submit = function(e) {
    e.preventDefault();
    setSaving(true);
    callRpc('account_company_set', {
      set_company_name:     form.company_name,
      set_industry_type_id: form.industry_type_id === '' ? null : Number(form.industry_type_id),
      set_country_code:     form.country_code || null,
    }).then(function(res) {
      setStatus({ ok: true, text: t('common_saved') });
      onSaved(Array.isArray(res) ? res[0] : res);
    }).catch(function(err) {
      setStatus({ ok: false, text: err.message || t('common_could_not_save') });
    }).finally(function() { setSaving(false); });
  };

  return (
    <ProfileCard title={t('profile_company')} subtitle={company.email ? t('profile_signed_in_as', { email: company.email }) : null}>
      <form onSubmit={submit} className="pf-form">
        <div className="form-group">
          <label className="form-label">{t('profile_company_name')}</label>
          <input className="form-input" value={form.company_name} onChange={set('company_name')} maxLength={100} placeholder={t('profile_company_placeholder')} />
        </div>
        <div className="pf-row">
          <div className="form-group">
            <label className="form-label">{t('common_industry')}</label>
            <select className="form-input" value={form.industry_type_id} onChange={set('industry_type_id')}>
              <option value="">{t('common_not_set')}</option>
              {industries.map(function(i) { return <option key={i.industry_type_id} value={String(i.industry_type_id)}>{i.industry_type_description}</option>; })}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('profile_country')}</label>
            <select className="form-input" value={form.country_code} onChange={set('country_code')}>
              <option value="">{t('common_not_set')}</option>
              {countries.map(function(c) { return <option key={c.country_code} value={c.country_code}>{c.name + ' (+' + c.dial_prefix + ')'}</option>; })}
            </select>
          </div>
        </div>
        <SaveRow saving={saving} status={status} />
      </form>
    </ProfileCard>
  );
};

var PreferencesCard = function({ profile, languages, timezones, onSaved }) {
  var { useState } = React;
  var [form, setForm] = useState({ language: profile.overide_language || '', timezone: profile.overide_timezone || '' });
  var [saving, setSaving] = useState(false);
  var [status, setStatus] = useState(null);
  var set = function(key) { return function(e) { var v = e.target.value; setForm(function(f) { return Object.assign({}, f, { [key]: v }); }); setStatus(null); }; };

  // An older value such as "+08" isn't a zone name; keep it selectable so saving doesn't drop it.
  var zoneOptions = timezones.slice();
  if (form.timezone && !timezones.some(function(z) { return z.name === form.timezone; })) {
    zoneOptions.unshift({ name: form.timezone, label: form.timezone });
  }

  // account_profile_set replaces every account setting, so the rest go back unchanged.
  var submit = function(e) {
    e.preventDefault();
    setSaving(true);
    callRpc('account_profile_set', {
      set_timestamp_on_signatures:            profile.timestamp_on_signatures,
      set_timestamp_on_pictures:              profile.timestamp_on_pictures,
      set_geocoding_on_signatures:            profile.geocoding_on_signatures,
      set_geocoding_on_pictures:              profile.geocoding_on_pictures,
      set_timestamp_format:                   profile.timestamp_format,
      set_allow_actions_after_task_completed: profile.allow_actions_after_task_completed,
      set_logo_pic_file_name:                 profile.logo_pic_file_name,
      set_logo_pic_file_id:                   profile.logo_pic_file_id,
      set_motivation_messages:                profile.motivation_messages,
      set_overide_language:                   form.language || null,
      set_overide_timezone:                   form.timezone || null,
    }).then(function() {
      setStatus({ ok: true, text: t('common_saved') });
      onSaved({ overide_language: form.language || null, overide_timezone: form.timezone || null });
    }).catch(function(err) {
      setStatus({ ok: false, text: err.message || t('common_could_not_save') });
    }).finally(function() { setSaving(false); });
  };

  return (
    <ProfileCard title={t('profile_language_timezone')}>
      <form onSubmit={submit} className="pf-form">
        <div className="pf-row">
          <div className="form-group">
            <label className="form-label">{t('profile_language')}</label>
            <select className="form-input" value={form.language} onChange={set('language')}>
              <option value="">{t('common_default')}</option>
              {languages.map(function(l) { return <option key={l.lang} value={l.lang}>{l.lang_description}</option>; })}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('profile_timezone')}</label>
            <select className="form-input" value={form.timezone} onChange={set('timezone')}>
              <option value="">{t('common_default')}</option>
              {zoneOptions.map(function(z) { return <option key={z.name} value={z.name}>{z.label}</option>; })}
            </select>
          </div>
        </div>
        <SaveRow saving={saving} status={status} />
      </form>
    </ProfileCard>
  );
};

var PasswordCard = function({ googleAccount }) {
  var { useState } = React;
  var [form, setForm] = useState({ current: '', next: '', confirm: '' });
  var [saving, setSaving] = useState(false);
  var [status, setStatus] = useState(null);
  var set = function(key) { return function(e) { var v = e.target.value; setForm(function(f) { return Object.assign({}, f, { [key]: v }); }); setStatus(null); }; };

  if (googleAccount) {
    return (
      <ProfileCard title={t('common_password')}>
        <p className="pf-note">{t('profile_google_no_password')}</p>
      </ProfileCard>
    );
  }

  var submit = function(e) {
    e.preventDefault();
    if (form.next.length < 8)        { setStatus({ ok: false, text: t('profile_new_password_short') }); return; }
    if (form.next !== form.confirm)  { setStatus({ ok: false, text: t('profile_new_password_mismatch') }); return; }
    setSaving(true);
    callRpc('login_change_password', { current_password: form.current, new_password: form.next })
      .then(function(res) {
        var r = Array.isArray(res) ? res[0] : res;
        if (r && r.result === 0) {
          setStatus({ ok: true, text: t('profile_password_changed') });
          setForm({ current: '', next: '', confirm: '' });
        } else {
          setStatus({ ok: false, text: (r && r.message) || t('profile_password_error') });
        }
      })
      .catch(function(err) { setStatus({ ok: false, text: err.message || t('profile_password_error') }); })
      .finally(function() { setSaving(false); });
  };

  return (
    <ProfileCard title={t('common_password')}>
      <form onSubmit={submit} className="pf-form">
        <div className="form-group">
          <label className="form-label">{t('profile_current_password')}</label>
          <PasswordInput value={form.current} onChange={set('current')} autoComplete="current-password" placeholder={t('profile_current_password')} />
        </div>
        <div className="pf-row">
          <div className="form-group">
            <label className="form-label">{t('profile_new_password')}</label>
            <PasswordInput value={form.next} onChange={set('next')} autoComplete="new-password" placeholder={t('common_at_least_8')} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('profile_confirm_new_password')}</label>
            <PasswordInput value={form.confirm} onChange={set('confirm')} autoComplete="new-password" placeholder={t('profile_type_again')} />
          </div>
        </div>
        <SaveRow saving={saving} status={status} label={t('profile_change_password')} />
      </form>
    </ProfileCard>
  );
};

var Profile = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect } = React;
  var [data, setData]   = useState(null);
  var [error, setError] = useState('');

  useEffect(function() {
    var first = function(r) { return Array.isArray(r) ? r[0] || {} : r || {}; };
    Promise.all([
      callRpc('account_company_get'),
      callRpc('account_profile_get'),
      callRpc('industry_list'),
      callRpc('country_list'),
      callRpc('language_list'),
      callRpc('timezones'),
    ]).then(function(res) {
      var countries = (res[3] || []).map(function(c) { return Object.assign({ name: countryName(c.country_code) }, c); })
        .sort(function(a, b) { return a.name.localeCompare(b.name); });
      var timezones = (res[5] || [])
        .filter(function(z) { return z.timezone_name && z.timezone_name.indexOf('posix/') !== 0 && z.timezone_name.indexOf('/') !== -1; })
        .map(function(z) { return { name: z.timezone_name, label: z.timezone_name.replace(/_/g, ' ') + (z.timezone_abbr ? ' (' + z.timezone_abbr + ')' : '') }; })
        .sort(function(a, b) { return a.name.localeCompare(b.name); });
      setData({
        company: first(res[0]), profile: first(res[1]),
        industries: res[2] || [], countries: countries, languages: res[4] || [], timezones: timezones,
      });
    }).catch(function(err) { setError(t('profile_load_error', { message: err.message || '' })); });
  }, []);

  var merge = function(key) {
    return function(values) { setData(function(d) { return Object.assign({}, d, { [key]: Object.assign({}, d[key], values) }); }); };
  };

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>{t('profile_title')}</h2>
          <p>{t('profile_subtitle')}</p>
        </div>
      </div>
      {error && <div className="auth-error">{error}</div>}
      {!data && !error && <div style={{ padding:'32px', textAlign:'center', color:'#6b7280' }}>{t('profile_loading')}</div>}
      {data && (
        <div className="pf-grid">
          <CompanyCard company={data.company} industries={data.industries} countries={data.countries} onSaved={merge('company')} />
          <PreferencesCard profile={data.profile} languages={data.languages} timezones={data.timezones} onSaved={merge('profile')} />
          <PasswordCard googleAccount={!!data.company.google_account} />
        </div>
      )}
      <div className="pf-version">{t('profile_version', { version: CONFIG.APP_VERSION, build: CONFIG.BUILD_NUMBER })}</div>
    </AppShell>
  );
};

;
/* ── src/pages/Integrations.jsx ── */
/* Integrations — AI access (MCP) and API keys, laid out like thermotrack.io's Integrations page.
   Layout only for now: nothing here calls the API; the settings and keys are sample data held in state. */
var MCP_URL = 'https://mcp.opsassist.co';

var IG_SAMPLE_KEYS = [
  { id: 1, name: 'Production Server', key: 'oa_live_8f2c1d9e4b7a3f6c', scopes: ['read:jobs', 'read:operators'], created: '12 Sep 2026 09:14', expires: '12 Sep 2027 09:14', lastUsed: '2 hours ago', status: 'active' },
  { id: 2, name: 'Payroll Export',    key: 'oa_live_1a9b7c3e5d2f8e41', scopes: ['read:tasks'],                    created: '03 Jun 2026 16:40', expires: '03 Sep 2026 16:40', lastUsed: null,          status: 'expired' },
];
var IG_SCOPES = [
  { scope: 'read:jobs',       label: 'Read jobs' },
  { scope: 'write:jobs',      label: 'Write jobs' },
  { scope: 'read:tasks',      label: 'Read tasks' },
  { scope: 'write:tasks',     label: 'Write tasks' },
  { scope: 'read:operators',  label: 'Read operators' },
  { scope: 'write:operators', label: 'Write operators' },
];

var IgKeyIcon = function(props) {
  return <svg width={props.size || 17} height={props.size || 17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={props.style}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>;
};

var McpPanel = function() {
  var { useState } = React;
  var [on, setOn]             = useState(false);
  var [copied, setCopied]     = useState(false);
  var [toolText, setToolText] = useState('');
  var [revoked, setRevoked]   = useState(null);
  var [form, setForm]         = useState({ allow_raw: false, max_rows_per_call: 2000, max_raw_days: 31, max_rows_per_day: 100000, allowed_tools: [] });
  var rowsToday = 0;
  var usedPct = form.max_rows_per_day > 0 ? Math.min(100, Math.round(rowsToday / form.max_rows_per_day * 100)) : 0;

  var copyUrl = function() {
    try { navigator.clipboard.writeText(MCP_URL); } catch (e) {}
    setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
  };
  var addTool = function() {
    var names = toolText.split(/[\s,]+/).map(function(s) { return s.trim(); }).filter(Boolean);
    if (!names.length) return;
    setForm(function(f) { return Object.assign({}, f, { allowed_tools: Array.from(new Set(f.allowed_tools.concat(names))) }); });
    setToolText('');
  };
  var removeTool = function(tool) {
    setForm(function(f) { return Object.assign({}, f, { allowed_tools: f.allowed_tools.filter(function(x) { return x !== tool; }) }); });
  };
  var setField = function(k, v) { setForm(function(f) { var n = Object.assign({}, f); n[k] = v; return n; }); };

  return (
    <div className="ig-card">
      <div className="ig-card-head">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.8L18.7 9.7 13.9 11.6 12 16.4 10.1 11.6 5.3 9.7 10.1 7.8z"/><path d="M18.5 15l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></svg>
        <span className="ig-card-title">AI access (MCP)</span>
        <span className={'ig-state' + (on ? ' ig-state--on' : '')}>{on ? 'On' : 'Off'}</span>
        <label className="ig-switch">
          <span>Enable AI access</span>
          <input type="checkbox" checked={on} onChange={function(e) { setOn(e.target.checked); }} />
        </label>
      </div>
      <div className="ig-card-sub">Let an AI assistant read this account through mcp.opsassist.co, within the limits you set here.</div>

      <div style={{ marginBottom: 16 }}>
        <div className="ig-label">Connect URL</div>
        <div className="ig-url">
          <span className="ig-url-text">{MCP_URL}</span>
          <button className={'ig-icon-btn' + (copied ? ' ig-icon-btn--ok' : '')} onClick={copyUrl} title="Copy">
            {copied
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
          </button>
        </div>
        <div className="ig-hint">Add this as a custom connector in Claude, ChatGPT, Cursor or any MCP client. Signing in uses your OPS Assist account.</div>
      </div>

      <div className="ig-section">
        <div className="ig-label" style={{ marginBottom: 10 }}>Limits</div>
        <div className="ig-limits">
          <label className="ig-check">
            <input type="checkbox" checked={form.allow_raw} onChange={function(e) { setField('allow_raw', e.target.checked); }} />
            Allow raw records
          </label>
          {[['max_rows_per_call', 'Rows per call'], ['max_raw_days', 'Raw days per call'], ['max_rows_per_day', 'Rows per day']].map(function(pair) {
            return (
              <div key={pair[0]}>
                <div className="ig-mini-label">{pair[1]}</div>
                <input type="number" min={0} className="ig-num" value={form[pair[0]]} onChange={function(e) { setField(pair[0], e.target.value); }} />
              </div>
            );
          })}
          <div>
            <div className="ig-mini-label">Used today</div>
            <div className="ig-used">
              {rowsToday.toLocaleString()} / {Number(form.max_rows_per_day || 0).toLocaleString()}
              <span> · {usedPct}%</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="ig-mini-label" style={{ marginBottom: 6 }}>Tools allowed</div>
          <div className="ig-tools">
            {form.allowed_tools.length === 0 && <span className="ig-hint" style={{ marginTop: 0 }}>Every tool is allowed</span>}
            {form.allowed_tools.map(function(tool) {
              return (
                <span key={tool} className="ig-chip">
                  {tool}
                  <button onClick={function() { removeTool(tool); }}>×</button>
                </span>
              );
            })}
            <input className="ig-num ig-tool-input" value={toolText} placeholder="tool name"
              onChange={function(e) { setToolText(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); addTool(); } }} />
            <button className="ig-btn" onClick={addTool}>Add</button>
          </div>
          <div className="ig-hint">Leave empty to allow every tool. Add a name to limit AI clients to those tools only.</div>
        </div>

        <div className="ig-actions">
          <button className="btn-add">Save</button>
          <button className="ig-btn-danger" onClick={function() { setRevoked(0); }}>Sign out all AI clients</button>
          {revoked !== null && <span className="ig-hint" style={{ marginTop: 0 }}>{revoked} AI sessions ended.</span>}
        </div>
      </div>
    </div>
  );
};

var ApiKeysPanel = function() {
  var { useState } = React;
  var [keys, setKeys]           = useState(IG_SAMPLE_KEYS);
  var [showModal, setShowModal] = useState(false);
  var [newName, setNewName]     = useState('');
  var [newScopes, setNewScopes] = useState([]);
  var [modalError, setModalError] = useState('');

  var maskKey = function(k) { return k ? '••••••••••••' + k.slice(-4) : '—'; };
  var toggleScope = function(s) { setNewScopes(function(prev) { return prev.indexOf(s) !== -1 ? prev.filter(function(x) { return x !== s; }) : prev.concat([s]); }); };
  var openModal = function() { setNewName(''); setNewScopes([]); setModalError(''); setShowModal(true); };
  var createKey = function() {
    var desc = newName.trim();
    if (!desc || !newScopes.length) return;
    if (keys.some(function(k) { return k.name.toLowerCase() === desc.toLowerCase(); })) { setModalError('A key named "' + desc + '" already exists.'); return; }
    setKeys(function(prev) { return prev.concat([{ id: Date.now(), name: desc, key: 'oa_live_' + Math.random().toString(16).slice(2, 18), scopes: newScopes, created: 'Just now', expires: null, lastUsed: null, status: 'active' }]); });
    setShowModal(false);
  };
  var setStatus = function(id, status) { setKeys(function(prev) { return prev.map(function(k) { return k.id === id ? Object.assign({}, k, { status: status }) : k; }); }); };
  var deleteKey = function(id) { setKeys(function(prev) { return prev.filter(function(k) { return k.id !== id; }); }); };
  var canCreate = newName.trim() && newScopes.length;

  return (
    <div className="ig-card">
      <div className="ig-card-head">
        <IgKeyIcon style={{ color: '#2563eb', flexShrink: 0 }} />
        <span className="ig-card-title">API keys</span>
        <button className="btn-add" style={{ marginLeft: 'auto' }} onClick={openModal}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Create API Key
        </button>
      </div>
      <div className="ig-card-sub">API keys grant programmatic access to your account over the REST API.</div>

      <div className="ig-keys">
        {keys.map(function(k) {
          return (
            <div key={k.id} className="ig-key">
              <div className="ig-key-top">
                <div className="ig-key-name">
                  <IgKeyIcon size={18} style={{ color: '#2563eb', flexShrink: 0 }} />
                  <span className="ig-key-title">{k.name}</span>
                  <span className={'ig-pill ig-pill--' + k.status}><span className="ig-pill-dot" />{k.status.charAt(0).toUpperCase() + k.status.slice(1)}</span>
                  <span className="ig-masked" title="Only the last four characters are shown. The full key was shown once, when it was created.">{maskKey(k.key)}</span>
                </div>
                <div className="ig-key-btns">
                  <button className="ig-btn">API Logs</button>
                  <button className="ig-btn">Scopes</button>
                  {k.status === 'active' && <button className="ig-btn" onClick={function() { setStatus(k.id, 'revoked'); }}>Revoke</button>}
                  <button className="lv-action-btn delete" title="Delete" onClick={function() { deleteKey(k.id); }}><TrashIcon /></button>
                </div>
              </div>
              <div className="ig-key-meta">
                <span>Created {k.created}</span>
                {k.expires && <span className={k.status === 'expired' ? 'ig-expired' : ''}>{k.status === 'expired' ? 'Expired' : 'Expires'} {k.expires}</span>}
                {k.lastUsed && <span>Last used {k.lastUsed}</span>}
              </div>
              <div className="ig-key-scopes">
                {k.scopes.map(function(sc) { return <span key={sc} className="ig-scope-tag">{sc}</span>; })}
              </div>
            </div>
          );
        })}
        {keys.length === 0 && (
          <div className="ig-empty">
            <IgKeyIcon size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p>No API keys yet. Create one to get started.</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="ig-modal-backdrop" onClick={function() { setShowModal(false); }}>
          <div className="ig-modal" onClick={function(e) { e.stopPropagation(); }}>
            <h3>Create API Key</h3>
            <p className="ig-modal-sub">Give your key a name and choose the permissions it needs.</p>

            <label className="ig-form-lbl">Key Name</label>
            <input className="ig-form-inp" placeholder="e.g. Production Server" value={newName} onChange={function(e) { setNewName(e.target.value); setModalError(''); }} />

            <label className="ig-form-lbl" style={{ marginTop: 16 }}>Scopes</label>
            <div className="ig-scope-list">
              {IG_SCOPES.map(function(s) {
                var checked = newScopes.indexOf(s.scope) !== -1;
                return (
                  <label key={s.scope} className={'ig-scope' + (checked ? ' ig-scope--on' : '')}>
                    <input type="checkbox" checked={checked} onChange={function() { toggleScope(s.scope); }} />
                    <span><span className="ig-scope-label">{s.label}</span><span className="ig-scope-code">{s.scope}</span></span>
                  </label>
                );
              })}
            </div>

            <div className="ig-note">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              The key is shown once after it is created. Choose only the permissions it needs.
            </div>
            {modalError && <div className="ig-error">{modalError}</div>}

            <div className="ig-modal-actions">
              <button className="ig-btn" onClick={function() { setShowModal(false); }}>Cancel</button>
              <button className="btn-add" disabled={!canCreate} style={{ opacity: canCreate ? 1 : .4 }} onClick={createKey}>Create Key</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

var Integrations = function({ activePage, onNavSelect, onLogout }) {
  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Integrations</h2>
          <p>Manage API keys or AI MCP access for programmatic access to OPS Assist.</p>
        </div>
      </div>

      <div className="ig-info">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span>API keys grant programmatic access to your OPS Assist account. Keep them secret — treat them like passwords. Keys are only shown in full once at creation.</span>
      </div>

      <McpPanel />
      <ApiKeysPanel />
    </AppShell>
  );
};

;
/* ── src/pages/FormsStrings.js ── */
// English text for the Forms editor (src/pages/Forms.jsx), taken from thermotrack.io's
// lang/i18n_en.js so the screen reads the same. Forms.jsx looks keys up with t(key), which
// falls back to the key itself — a field type without a label here shows its database name.
var FORMS_STRINGS = {
  auto_at_fields_note: "Send a test row checks the table first and adds any that are missing (the token needs schema.bases:write).",
  auto_at_fields_title: "Your Airtable must have these fields:",
  auto_at_howto_title: "Connect Airtable in four steps",
  auto_at_step1: "In Airtable, open the base and table the answers should go to; the Base ID (starts with app) is in the page address.",
  auto_at_step2: "Create a personal access token at airtable.com/create/tokens with the scopes data.records:write and schema.bases:write, limited to that base.",
  auto_at_step3: "Paste the Base ID, the table name exactly as shown in Airtable, and the token below; save and switch it on.",
  auto_at_step4: "Press Send a test row: the fields listed below are created in the table if missing, and a row marked TEST arrives.",
  auto_deliveries: "Deliveries",
  auto_f_base_id: "Base ID",
  auto_f_client_id: "OAuth client ID",
  auto_f_client_secret: "OAuth client secret",
  auto_f_refresh: "Refresh token",
  auto_f_rest_url: "Endpoint URL",
  auto_f_sheet_id: "Spreadsheet ID",
  auto_f_sheet_name: "Tab name",
  auto_f_slack_url: "Slack webhook URL",
  auto_f_table_name: "Table name",
  auto_f_token: "Personal access token",
  auto_f_webhook: "Zapier webhook URL",
  auto_gs_howto_title: "These credentials are yours, from your own Google account",
  auto_gs_step1: "In the Google Cloud console, create a project and enable the Google Sheets API.",
  auto_gs_step2: "Create an OAuth client (Desktop app is simplest via APIs & Services and click on Credentials) — that gives you the client ID and secret.",
  auto_gs_step3: "On the OAuth consent screen, select Data access then add the scope .../auth/spreadsheets, add yourself as a user, and publish it — while it stays in Testing, Google expires the refresh token after 7 days.",
  auto_gs_step4: "Authorise once with access_type=offline (the OAuth Playground does this in a browser with your own credentials) and copy the refresh token.",
  auto_gs_step4b: "The spreadsheet ID is the long code in the sheet’s web address, between /spreadsheets/d/ and /edit — open the sheet in Google Sheets and copy it from the address bar.",
  auto_gs_step5: "Paste all three below, add the spreadsheet ID and tab, save, then send a test row.",
  auto_h_base_id: "Starts with app — from the base URL or the Airtable API page.",
  auto_h_refresh: "From your own Google project — see the steps above.",
  auto_h_rest_url: "The https:// address that receives each submission as JSON.",
  auto_h_sheet_id: "The long id in the sheet URL, between /d/ and /edit.",
  auto_h_sheet_name: "The tab a row is appended to. Sheet1 if left empty.",
  auto_h_slack_url: "Starts https://hooks.slack.com/services/ — hidden once saved.",
  auto_h_table_name: "The table answers are added to, exactly as it is named in Airtable.",
  auto_h_token: "Needs data.records:write on this base.",
  auto_h_webhook: "Starts https://hooks.zapier.com/hooks/catch/ — hidden once saved.",
  auto_last_sent: "Last sent",
  auto_queue_note: "Each submission is queued and pushed within a minute, so a slow destination never delays the form.",
  auto_rest_bearer: "Send a bearer token",
  auto_rest_extra: "Additional JSON",
  auto_rest_extra_hint: "Fixed values added to the top level of every request; a key here replaces the standard one of the same name.",
  auto_rest_howto_title: "Send each submission to your own API",
  auto_rest_json_bad: "Additional JSON must be a JSON object, for example {\"site\": \"North\"}.",
  auto_rest_method: "Method",
  auto_rest_sample: "What is sent",
  auto_rest_step1: "Enter the https:// address of your endpoint and the method it expects.",
  auto_rest_step2: "If it needs a bearer token, switch the token on and paste it — it is hidden once saved.",
  auto_rest_step3: "Add any fixed values your system needs under Additional JSON; they are added to every request.",
  auto_rest_step4: "Save, switch it on and press Send a test row: a request with \"reference\": \"TEST\" arrives.",
  auto_rest_token: "Bearer token",
  auto_rest_token_hint: "Sent as Authorization: Bearer <token>. Hidden once saved.",
  auto_sl_fmt_fields: "Send as Fields",
  auto_sl_fmt_json: "Send as JSON",
  auto_sl_format: "Data fields",
  auto_sl_howto_title: "Connect Slack in four steps",
  auto_sl_img_link: "Send images as links",
  auto_sl_img_note: "Pictures are posted in the message; tick this to list them as links instead.",
  auto_sl_step1: "In Slack, open api.slack.com/apps, create an app (from scratch) in your workspace.",
  auto_sl_step2: "Turn on Incoming Webhooks and add a webhook to the channel the submissions should post in.",
  auto_sl_step3: "Copy the webhook URL (starts https://hooks.slack.com/services/) and paste it here; save and switch it on.",
  auto_sl_step4: "Press Send a test row: a message marked TEST appears in the channel; from then on every submission is posted there.",
  auto_soon: "Not available yet. When it arrives, each submission will be queued and delivered here the same way Google Sheets and Airtable are, with the same delivery log.",
  auto_soon_badge: "Soon",
  auto_stat_failed: "Given up",
  auto_stat_queued: "Queued",
  auto_stat_sent: "Sent",
  auto_state_failed: "Given up",
  auto_state_queued: "Queued",
  auto_state_sent: "Sent",
  auto_tab_airtable: "Airtable",
  auto_tab_email: "Email",
  auto_tab_rest: "REST API",
  auto_tab_sheets: "Google Sheets",
  auto_tab_slack: "Slack",
  auto_tab_zapier: "Zapier",
  auto_test: "Send a test row",
  auto_testing: "Sending…",
  auto_zap_howto_title: "Connect a Zap in four steps",
  auto_zap_step1: "In Zapier, create a Zap with the trigger Webhooks by Zapier, event Catch Hook.",
  auto_zap_step2: "Copy the webhook URL Zapier shows, paste it below, save and switch it on.",
  auto_zap_step3: "Press Send a test row so Zapier sees a sample: form, reference, time submitted and the answers.",
  auto_zap_step4: "Add your actions in Zapier (email, CRM, spreadsheet, chat) — every submission then runs the Zap.",
  btn_cancel: "Cancel",
  btn_delete: "Delete",
  btn_deleting: "Deleting…",
  btn_save: "Save",
  btn_saving: "Saving…",
  fe_copied: "Copied",
  forms_add_field: "Add Field",
  forms_add_search: "Search field types…",
  forms_ai_build: "Build with AI",
  forms_ai_building: "Building",
  forms_ai_creating: "Creating the form…",
  forms_ai_describe: "Describe the form you need",
  forms_ai_ex1: "A delivery check sheet with the driver name, temperature, a photo of the load and a signature",
  forms_ai_ex2: "A temperature log with time and location",
  forms_ai_ex3: "A vehicle inspection with a checklist and photos",
  forms_ai_examples: "Example prompts",
  forms_ai_failed: "The AI request failed.",
  forms_ai_generate: "Generate draft",
  forms_ai_help: "Describe the form: what to collect, which fields and any checks. The AI builds a draft for you to review.",
  forms_ai_name_help: "Optional. Leave it empty to use a name based on your description.",
  forms_ai_name_ph: "Leave empty to use the description",
  forms_ai_placeholder: "A delivery check sheet with the driver name, temperature, photo of the load and a signature.",
  forms_ai_rejected: "That description could not be used.",
  forms_ai_timeout: "The AI is taking too long. Try again in a moment.",
  forms_ai_waiting: "The AI is working on it…",
  forms_audio_mobile_only: "Mobile app only — not shown on the web form",
  forms_auto_email_sub: "Send an email each time this form is submitted, laid out like the form with the answers filled in.",
  forms_auto_email_title: "Email Automation",
  forms_auto_err_email: "Not a valid email address: {v}",
  forms_auto_err_max: "At most 10 recipients.",
  forms_auto_err_none: "Add at least one recipient before turning the email on.",
  forms_auto_files_note: "Photos, files and signatures are not included in the email.",
  forms_auto_from: "From name",
  forms_auto_hidden: "This field will be invisible on the form and only save this data to the server",
  forms_auto_message: "Message",
  forms_auto_message_ph: "Optional note shown above the answers.",
  forms_auto_mode: "Email content",
  forms_auto_mode_DATA: "Email Message Data Only",
  forms_auto_mode_FILES: "Email Message data with files attached",
  forms_auto_mode_FULL: "Email full content",
  forms_auto_mode_PDF: "Email attach form submission as PDF",
  forms_auto_mode_hint_DATA: "The answers only — sent the moment the form is saved.",
  forms_auto_mode_hint_FILES: "The answers, with the photos, signatures and files attached (up to 15 MB). Sent about a minute after the form is saved.",
  forms_auto_mode_hint_FULL: "The answers with every photo and file shown in the email. Files are copied to a content server under long random links that anyone holding the link can open, and are removed after 30 days. Sent about a minute after the form is saved.",
  forms_auto_mode_hint_PDF: "The answers, plus the whole submission as a PDF attachment, pictures included. Sent about a minute after the form is saved.",
  forms_auto_never: "Not sent yet",
  forms_auto_off: "Off",
  forms_auto_on: "On",
  forms_auto_prev_latest: "Latest Entry",
  forms_auto_prev_no_entry: "No submissions yet, so the sample is shown.",
  forms_auto_prev_sample: "Sample",
  forms_auto_prev_subject: "Subject",
  forms_auto_preview: "Email Preview",
  forms_auto_saved: "Email automation saved.",
  forms_auto_sent_stats: "Sent for {n} submissions, last on {ts}",
  forms_auto_subject: "Subject",
  forms_auto_subject_hint: "You can use {form_name}, {form_no} and {date} in the subject.",
  forms_auto_test: "Send Test Email",
  forms_auto_test_hint: "Save first. A test goes to the \"Send to\" addresses, using the latest submission.",
  forms_auto_test_sent: "Test email queued for {to}.",
  forms_auto_to: "Send to",
  forms_auto_to_field: "Also send to the address entered in",
  forms_auto_to_field_empty: "Add an Email field to the form to also send a copy to the person who fills it in.",
  forms_auto_to_field_none: "No one else",
  forms_auto_to_hint: "Separate addresses with commas. Up to 10.",
  forms_auto_to_ph: "name@company.com, another@company.com",
  forms_cat_include_first: "Include this form in the catalogue first.",
  forms_col_short: "Col ",
  forms_copy: "Copy Form",
  forms_copy_hint: "Fields, settings and automations are copied. The copy starts as a draft.",
  forms_copy_link: "Copy Link",
  forms_copy_name: "Name for the copy",
  forms_create: "Create",
  forms_create_draft: "Create draft",
  forms_delete_confirm: "Delete this form?",
  forms_delete_field: "Delete Field",
  forms_delete_warn: "The form and all of its fields are removed permanently.",
  forms_draft: "DRAFT",
  forms_draft_note: "It is created as a draft. Review the fields, then publish it when ready.",
  forms_drag_hint: "Drag a field to change its order.",
  forms_dur_d: "days",
  forms_dur_h: "hours",
  forms_dur_m: "min",
  forms_dur_s: "sec",
  forms_empty_ai: "Describe what you need and get a draft to review.",
  forms_empty_new: "Start from a blank form and add fields yourself.",
  forms_empty_sub: "Pick a form on the left, or start a new one.",
  forms_empty_title: "Select a form to edit",
  forms_empty_tpl: "Copy a ready-made form such as a cold-store inspection.",
  forms_f_advanced: "Advanced properties",
  forms_f_advanced_hint: "Leave a box empty to use the default shown inside it.",
  forms_f_caption: "Caption",
  forms_f_colour: "Colour",
  forms_f_column: "Column",
  forms_f_items: "Items",
  forms_f_items_hint: "One entry per line. Each entry becomes its own input on the form.",
  forms_f_items_ph: "One per line",
  forms_f_options: "Options",
  forms_f_override: "Automation name",
  forms_f_override_hint: "When set, the answer is sent to every automation (Sheets, Airtable, Zapier) under this name instead of the title.",
  forms_f_required: "Required",
  forms_f_title: "Title",
  forms_f_type: "Type",
  forms_field_untitled: "Untitled field",
  forms_fields_count: "fields",
  forms_from_template: "From Template",
  forms_grp_basic: "Basic fields",
  forms_grp_choice: "Choices and ratings",
  forms_grp_layout: "Text and layout",
  forms_grp_media: "Photos, files and signatures",
  forms_grp_other: "Other",
  forms_grp_sensor: "Scanning and capture",
  forms_grp_time: "Date and time",
  forms_has_config: "Advanced properties are set on this field.",
  forms_layout_desktop: "Desktop",
  forms_layout_mobile: "Mobile",
  forms_layout_tablet: "Tablet",
  forms_link_copied: "Link copied to the clipboard.",
  forms_live: "LIVE",
  forms_loading: "Loading…",
  forms_more: "More",
  forms_name_exists: "A form with that name already exists.",
  forms_new: "New Form",
  forms_new_help: "Give the form a name; you add its fields next.",
  forms_no_fields: "This form has no fields yet.",
  forms_no_match: "No forms match that search.",
  forms_no_templates: "No templates available",
  forms_none: "No forms yet.",
  forms_o_both: "Both",
  forms_o_humidity: "Humidity",
  forms_o_temperature: "Temperature",
  forms_open: "Open",
  forms_p_accepted_formats: "Accepted formats",
  forms_p_allow_multiple: "Allow multiple",
  forms_p_auto_browser: "Browser / device",
  forms_p_auto_gps: "GPS location",
  forms_p_auto_ip: "IP address",
  forms_p_auto_ip_location: "Region (from IP)",
  forms_p_auto_locate: "Find my location first",
  forms_p_auto_submitted: "Date & time submitted",
  forms_p_auto_user: "User",
  forms_p_background_color: "Canvas background",
  forms_p_canvas_height: "Canvas height",
  forms_p_canvas_width: "Canvas width",
  forms_p_capture_mode: "Capture mode",
  forms_p_currency_code: "Currency code",
  forms_p_currency_symbol: "Currency symbol",
  forms_p_date_format: "Date format",
  forms_p_decimal_places: "Decimal places",
  forms_p_default: "Default",
  forms_p_default_now: "Default to now",
  forms_p_default_today: "Default to today",
  forms_p_diagram: "Diagram",
  forms_p_dial_prefix: "Dial prefix",
  forms_p_drift_pct: "% drift allowed",
  forms_p_duration_format: "Format",
  forms_p_emoji_scale: "Number of faces",
  forms_p_last_value: "Last value",
  forms_p_location_geotag: "Location geotag",
  forms_p_map_layer: "Map style",
  forms_p_max_file_size: "Max file size",
  forms_p_max_length: "Max length",
  forms_p_max_seconds: "Maximum length (seconds)",
  forms_p_max_value: "Maximum",
  forms_p_meter_digits: "Number of digits",
  forms_p_meter_photo: "Photo option",
  forms_p_min_value: "Minimum",
  forms_p_nfc_show_number: "Show the tag number once scanned",
  forms_p_no: "No",
  forms_p_pen_color: "Pen colour",
  forms_p_pen_size: "Pen size",
  forms_p_placeholder: "Placeholder",
  forms_p_reset: "Reset",
  forms_p_sensor_hours: "Graph length (hours)",
  forms_p_sensor_measure: "Reading stored",
  forms_p_sensor_series: "Graph shows",
  forms_p_step: "Step",
  forms_p_time_format: "Time format",
  forms_p_timestamp_geotag: "Timestamp geotag",
  forms_p_unit: "Unit",
  forms_p_use_24_hour: "24 hour clock",
  forms_p_yes: "Yes",
  forms_pick_one: "Select a form on the left, or create a new one.",
  forms_preview: "Preview",
  forms_preview_add_file: "Click to add a file",
  forms_preview_add_image: "Click to add an image",
  forms_preview_add_photo_annotated: "Take a photo and draw on it",
  forms_preview_audio: "Record a voice note",
  forms_preview_audio_max: "Up to {s} s",
  forms_preview_auto: "Recorded automatically when you save",
  forms_preview_duration_saved: "Saved as total minutes",
  forms_preview_empty: "Add a field to see it here.",
  forms_preview_hotspot: "Tap the picture to mark each spot",
  forms_preview_hotspot_none: "No issues found",
  forms_preview_map: "Tap the map to set the point",
  forms_preview_meter_drift: "Asks to confirm a reading more than {pct}% from {last}",
  forms_preview_meter_photo: "Photograph the meter",
  forms_preview_nfc: "Tap to scan an NFC tag",
  forms_preview_nfc_app: "Scanned in the mobile app",
  forms_preview_sensor: "Choose a sensor",
  forms_preview_sensor_hours: "last {h} hours",
  forms_preview_sensor_when: "stored when the form is submitted",
  forms_preview_stamp_place: "GPS stamped",
  forms_preview_stamp_time: "time stamped",
  forms_preview_up_to: "Up to",
  forms_public: "Public",
  forms_publish: "Publish",
  forms_publish_first: "Publish the form first",
  forms_s_background: "Header colour",
  forms_s_body: "Body colour",
  forms_s_catalogue: "Include in Catalogue",
  forms_s_catalogue_hint: "Anyone with the catalogue link can open any published form ticked here.",
  forms_s_catalogue_link: "Your Catalogue",
  forms_s_description: "Description",
  forms_s_description_ph: "What this form is for — shown under the name on the form itself.",
  forms_s_layout: "Layout",
  forms_s_link: "Public link",
  forms_s_logo: "Logo file name",
  forms_s_logo_hint: "PNG, JPEG, WebP or SVG, up to 2 MB. Shown in the header of the form and its emails.",
  forms_s_logo_none: "No logo of its own — the OPS Assist logo is shown.",
  forms_s_logo_panel: "Form logo",
  forms_s_logo_remove: "Remove",
  forms_s_logo_select: "Select logo",
  forms_s_logo_toobig: "That file is {size} — the limit is 2 MB. Shrink it (around 400 px wide is plenty) and try again.",
  forms_s_name: "Form name",
  forms_s_name_hint: "The name must be at least 4 characters.",
  forms_s_number: "Form number",
  forms_s_public: "Anyone with the link can fill this in",
  forms_s_share_cat: "Catalogue sharing",
  forms_s_share_public: "Public share",
  forms_s_share_title: "URL Sharing",
  forms_search: "Search forms…",
  forms_settings_saved: "Settings saved.",
  forms_share: "Share",
  forms_sub: "Build the forms your team and customers fill in.",
  forms_tab_audit: "Audit",
  forms_tab_automation: "Automation",
  forms_tab_fields: "Fields",
  forms_tab_settings: "Settings",
  forms_template: "Template",
  forms_title: "Forms",
  forms_tpl_help: "Pick a template; a new draft form is made from it for you to change.",
  forms_type_ADDRESS: "Address",
  forms_type_AUDIO_NOTE: "Audio Note",
  forms_type_AUTO_DATA: "Auto Data",
  forms_type_BARCODE: "Barcode",
  forms_type_CHECKBOX: "Checkbox",
  forms_type_CURRENCY: "Currency",
  forms_type_DATE: "Date",
  forms_type_DRAW: "Drawing",
  forms_type_DURATION: "Duration",
  forms_type_DYNAMIC_QR: "Dynamic QR Code",
  forms_type_EMAIL: "Email Address",
  forms_type_EMOJI: "Emoji Rating",
  forms_type_FILE: "File Attachment",
  forms_type_GPS_POINT: "GPS Point / Map Picker",
  forms_type_HOTSPOT: "Body / Vehicle Diagram",
  forms_type_HTML: "HTML Block",
  forms_type_IMAGE: "Photo",
  forms_type_INFO: "Information Block",
  forms_type_LABEL: "Label",
  forms_type_LIST: "Selection List",
  forms_type_MEMO: "Multi-line Text",
  forms_type_METER_READING: "Meter Reading",
  forms_type_NFC_SCAN: "NFC Scan",
  forms_type_NUMBER: "Number",
  forms_type_PHONE: "Phone Number",
  forms_type_PHOTO_ANNOTATED: "Photo with Annotation",
  forms_type_QR: "QR Code",
  forms_type_SCORE: "Score (1–5)",
  forms_type_SENSOR_GRAPH: "Sensor Graph",
  forms_type_SENSOR_VALUE: "Sensor Value",
  forms_type_SIGN: "Signature",
  forms_type_SLIDER: "Slider",
  forms_type_TEXT: "Text",
  forms_type_TIME: "Time",
  forms_type_TIMESTAMP: "Date and Time",
  forms_type_URL: "Web Link",
  forms_type_YESNO: "Yes / No / N/A",
  forms_unpublish: "Unpublish",
  forms_version: "Version",
  forms_yesno_na: "N/A",
  forms_yesno_no: "No",
  forms_yesno_yes: "Yes",
  lbl_active: "Active",
  lbl_inactive: "Inactive",
  lbl_more_actions: "More actions",
  lbl_no_data: "No data",
  loading: "Loading…",
};

;
/* ── src/pages/Forms.jsx ── */
// Forms — build and edit the forms operators and customers fill in.
//
// Ported from thermotrack.io's Forms screen (app.jsx CfgFormsPage and the components above it),
// which talks to the same app_api.form_* functions this database has. The code below the shims
// is that screen with ThermoTrack's helpers swapped for OpsAssist's:
//   API.call('/rpc/x', 'POST', body)   → callRpc('x', body)          (src/constants.js)
//   window.TT_CONFIG.FORMS             → window.OA_CONFIG.FORMS      (config.js: field property catalogue)
//   t(key), useLang()                  → FORMS_STRINGS               (src/pages/FormsStrings.js, English)
//   SearchBox, RowMenu                 → the small versions just below
//   ThermoTrack's CSS variables        → set on .fm-page in src/index.css (light theme)
//
// Everything sits inside one function so its many helpers do not become globals; only Forms is.
var Forms = (function() {
  var { useState, useEffect, useRef } = React;

  /* ───── shims for the ThermoTrack helpers the screen was written against ───── */
  var _currentLang = 'en';
  var useLang = function() {};
  var t = function(key) { return FORMS_STRINGS[key] || key; };

  // The list's filter box, in the toolbar style the other OpsAssist lists use.
  function SearchBox({ value, onChange, placeholder, width, className }) {
    return (
      <div className={'lv-search ' + (className || '')} style={{ width: width || 300 }}>
        <span className="lv-search-icon"><SearchIcon /></span>
        <input className="lv-search-input" placeholder={placeholder} value={value} onChange={function(e) { onChange(e.target.value); }} />
        {value && <button className="lv-search-clear" title="Clear search" onClick={function() { onChange(''); }}>&times;</button>}
      </div>
    );
  }

  // ⋯ button with a small menu of actions; a danger item gets a separator above it.
  function RowMenu({ items, label, className }) {
    const [pos, setPos] = useState(null);
    const btn = useRef(null), menu = useRef(null);
    const list = (items || []).filter(Boolean);
    useEffect(() => {
      if (!pos) return;
      const away = e => { if (!menu.current?.contains(e.target) && !btn.current?.contains(e.target)) setPos(null); };
      const esc  = e => { if (e.key === 'Escape') setPos(null); };
      const gone = () => setPos(null);
      document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
      window.addEventListener('scroll', gone, true); window.addEventListener('resize', gone);
      return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc);
                     window.removeEventListener('scroll', gone, true); window.removeEventListener('resize', gone); };
    }, [pos]);
    const open = () => {
      const r = btn.current.getBoundingClientRect(), h = list.length * 42 + 20;
      setPos({ right: Math.max(8, window.innerWidth - r.right), ...(r.bottom + h > window.innerHeight - 8 ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }) });
    };
    const firstDanger = list.findIndex(i => i.danger);
    return (
      <>
        <button ref={btn} type="button" className={'fm-btn ' + (className || '')} aria-haspopup="menu" aria-expanded={!!pos}
          aria-label={label || t('lbl_more_actions')} title={label || t('lbl_more_actions')}
          onClick={e => { e.stopPropagation(); pos ? setPos(null) : open(); }} style={{width:36, padding:0, justifyContent:'center'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
        </button>
        {pos && ReactDOM.createPortal(
          <div ref={menu} role="menu" className="fm-page" onClick={e => e.stopPropagation()}
            style={{position:'fixed', ...pos, zIndex:5000, minWidth:190, background:'#fff', border:'1px solid #e5e7eb', borderRadius:10,
                    boxShadow:'0 12px 32px rgba(17,24,39,.16)', padding:6}}>
            {list.map((it, i) => (
              <React.Fragment key={it.key}>
                {i === firstDanger && i > 0 && <div role="separator" style={{height:1, background:'#e5e7eb', margin:'6px 4px'}} />}
                <button type="button" role="menuitem" disabled={it.disabled}
                  onClick={() => { setPos(null); it.onClick(); }}
                  style={{display:'flex', alignItems:'center', gap:10, width:'100%', padding:'8px 10px', border:'none', borderRadius:7, background:'transparent',
                          color: it.danger ? '#dc2626' : '#111827', font:'inherit', fontSize:13.5, cursor: it.disabled ? 'not-allowed' : 'pointer', textAlign:'left', opacity: it.disabled ? .5 : 1}}
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {it.icon && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flex:'none'}}>{it.icon}</svg>}
                  {it.label}
                </button>
              </React.Fragment>
            ))}
          </div>, document.body)}
      </>
    );
  }

  /* ───── the Forms screen, as thermotrack.io has it ───── */
  const FORMS_CFG = () => window.OA_CONFIG?.FORMS || {};

  /* This database has no form_logo_set / form_logo_clear yet (thermotrack.io's need content-storage
     helpers OpsAssist lacks), so Settings leaves the logo panel out. A logo already on a form still shows. */
  const LOGO_UPLOAD = false;

  /* The server default for one property of one field type. Shown as the
     input's placeholder so an empty box reads as "use the default".   */
  const formPropDefault = (type, key) => {
    const perType = FORMS_CFG().TYPE_DEFAULTS?.[type] || {};
    if (key in perType) return perType[key];
    return FORMS_CFG().PROPS?.[key]?.def;
  };
  /* An unlisted type offers no advanced properties, so a type added to the
     database before the catalog knows about it simply hides the section. */
  const formPropsFor = (type) => FORMS_CFG().TYPE_PROPS?.[type] || [];

  /* Field types are identifiers on the server (TEXT, DRAW, DYNAMIC_QR …) and
     form_field_types() only carries an English description. Show the
     translated label, falling back to that description — and then to the
     raw code — so a type added to the database before its translation
     still reads sensibly. */
  const formTypeLabel = (name, types) => {
    const key   = `forms_type_${name}`;
    const label = t(key);
    if (label !== key) return label;
    return types?.find(x => x.data_type_name === name)?.data_type_description || name;
  };
  const formPropValue = (cfg, type, key) => {
    const v = cfg?.[key];
    return (v === undefined || v === null) ? formPropDefault(type, key) : v;
  };
  const isOptionType = (type) => (FORMS_CFG().OPTION_TYPES || []).includes(type);
  /* Single-control types: the Items box is hidden and list_data tracks the
     title, so form_build still expands the field into exactly one entry. */
  const hidesItems   = (type) => (FORMS_CFG().HIDE_ITEMS_TYPES || []).includes(type);

  /* list_data is one comma separated string on the server; the editor and
     the preview both work with it as a list of items.                  */
  const listItems      = (s) => (s || '').split(',').map(v => v.trim()).filter(Boolean);
  const listDataToText = (s) => listItems(s).join('\n');
  const textToListData = (s) => (s || '').split('\n').map(v => v.trim()).filter(Boolean).join(',');

  /* Drop empty boxes (= use the default) and coerce to the JSON types the
     database whitelist expects. */
  const buildFieldConfig = (type, draft) => {
    const out = {};
    formPropsFor(type).forEach(key => {
      const def = FORMS_CFG().PROPS?.[key];
      if (!def) return;
      const raw = draft[key];
      if (raw === undefined || raw === null || raw === '') return;
      if (def.type === 'number') { const n = Number(raw); if (!isNaN(n)) out[key] = n; }
      else if (def.type === 'bool') out[key] = !!raw;
      else if (def.type === 'list') {
        const arr = String(raw).split(',').map(v => v.trim()).filter(Boolean);
        if (arr.length) out[key] = arr;
      }
      else out[key] = String(raw);
    });
    return out;
  };

  /* ───── Live preview ─────
     Mirrors what app_api.form_build hands the public form: one control per
     list_data item, except for the option types which become a single
     control carrying every option.                                      */
  /* Barcode preview — CODE128 drawn from list_data, same as the public form */
  function FormPreviewBarcode({ value }) {
    const ref = useRef(null);
    const [bad, setBad] = useState(false);
    useEffect(() => {
      if (!ref.current || !value) return;
      try {
        window.JsBarcode(ref.current, String(value), {
          format:'CODE128', displayValue:true, fontSize:12, height:50, width:1.5, margin:4, background:'transparent',
        });
        setBad(false);
      } catch (_) { setBad(true); }
    }, [value]);
    if (!value) return <div style={{fontSize:11, color:'#94a3b8', textAlign:'center'}}>▮▯▮▯▮</div>;
    if (bad) return <div style={{fontSize:12, fontFamily:'monospace', textAlign:'center', color:'#0f172a'}}>{String(value)}</div>;
    return <div style={{display:'flex', justifyContent:'center', overflowX:'auto'}}><svg ref={ref} style={{maxWidth:'100%'}} /></div>;
  }

  function FormPreviewField({ field }) {
    const type   = field.data_type_name;
    const cfg    = field.field_config || {};
    const items  = listItems(field.list_data);
    const boxSt  = { width:'100%', padding:'7px 9px', borderRadius:6, border:'1px solid #cbd5e1',
                     background:'#fff', color:'#0f172a', fontSize:12, boxSizing:'border-box' };
    const ph     = (item) => formPropValue(cfg, type, 'placeholder') || item;

    const control = (item, i) => {
      switch (type) {
        case 'MEMO':
          return <textarea key={i} rows={3} disabled placeholder={ph(item)} style={boxSt} />;
        case 'NUMBER':
          return <input key={i} disabled type="number" placeholder={ph(item)} style={boxSt} />;
        case 'SCORE': {
          const lo = Number(formPropValue(cfg, type, 'min_value')) || 1;
          const hi = Number(formPropValue(cfg, type, 'max_value')) || 5;
          const stars = []; for (let n = lo; n <= hi; n++) stars.push(n);
          return (
            <div key={i} style={{display:'flex', gap:2, padding:'2px 0'}}>
              {stars.map(n => <span key={n} style={{fontSize:22, lineHeight:1, color:'#cbd5e1'}}>★</span>)}
            </div>
          );
        }
        case 'CURRENCY':
          return (
            <div key={i} style={{display:'flex', alignItems:'center', gap:6}}>
              <span style={{fontSize:12, color:'#475569'}}>{formPropValue(cfg, type, 'currency_symbol')}</span>
              <input disabled type="number" placeholder={ph(item)} style={boxSt} />
            </div>
          );
        case 'PHONE':
          return (
            <div key={i} style={{display:'flex', alignItems:'center', gap:6}}>
              <span style={{fontSize:12, color:'#475569'}}>{formPropValue(cfg, type, 'dial_prefix')}</span>
              <input disabled placeholder={ph(item)} style={boxSt} />
            </div>
          );
        case 'DATE': case 'TIME': case 'TIMESTAMP': {
          /* Public form shows these in the configured format, not the
             browser locale — mirror that here.                         */
          const p2  = n => String(n).padStart(2, '0');
          const now = new Date();
          const df  = formPropValue(cfg, type, 'date_format') || 'YYYY-MM-DD';
          const tf  = formPropValue(cfg, type, 'time_format') || 'HH:MM';
          const u24 = formPropValue(cfg, type, 'use_24_hour') !== false;
          const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const dateStr = df.replace('YYYY', now.getFullYear()).replace('MMM', MON[now.getMonth()])
                            .replace('MM', p2(now.getMonth() + 1)).replace('DD', p2(now.getDate()));
          let h = now.getHours(); const ap = h >= 12 ? ' PM' : ' AM'; if (!u24) h = h % 12 || 12;
          const timeStr = p2(h) + ':' + p2(now.getMinutes()) + (tf === 'HH:MM:SS' ? ':' + p2(now.getSeconds()) : '') + (u24 ? '' : ap);
          const pattern = (type === 'DATE' ? df : type === 'TIME' ? tf + (u24 ? '' : ' AM') : df + ' ' + tf + (u24 ? '' : ' AM'));
          const showDate = type !== 'TIME' && formPropValue(cfg, type, 'default_to_today') !== false;
          const showTime = type !== 'DATE' && formPropValue(cfg, type, 'default_to_now')   !== false;
          const val = type === 'DATE' ? (showDate ? dateStr : '')
                    : type === 'TIME' ? (showTime ? timeStr : '')
                    : (showDate && showTime) ? dateStr + ' ' + timeStr : '';
          return (
            <div key={i}>
              <div style={{...boxSt, display:'flex', alignItems:'center', justifyContent:'space-between', gap:6}}>
                <span style={{color: val ? '#0f172a' : '#94a3b8', fontVariantNumeric:'tabular-nums'}}>{val || pattern}</span>
                <span style={{color:'#94a3b8'}}>{type === 'TIME' ? '🕒' : '📅'}</span>
              </div>
            </div>
          );
        }
        case 'CHECKBOX':
          return (
            <label key={i} style={{display:'flex', alignItems:'center', gap:7, fontSize:12, color:'#0f172a'}}>
              <input type="checkbox" disabled /> {item}
            </label>
          );
        case 'URL': {
          /* Anything that already looks absolute is linked as-is; a bare
             host gets https:// so the preview link actually resolves. */
          const raw  = (item || '').trim();
          const href = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : (raw ? 'https://' + raw : '');
          return (
            <div key={i} style={{...boxSt, border:'none', background:'transparent', padding:'2px 0',
                                 display:'flex', alignItems:'center', gap:7}}>
              <span style={{color:'#94a3b8', flexShrink:0}}>🔗</span>
              {href
                ? <a href={href} target="_blank" rel="noreferrer noopener"
                     style={{color:'#1d4ed8', textDecoration:'underline', overflow:'hidden',
                             textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{raw}</a>
                : <span style={{color:'#94a3b8'}}>{ph(item)}</span>}
            </div>
          );
        }
        case 'YESNO':
          return (
            <div key={i} style={{display:'flex', gap:6}}>
              {[['yes', t('forms_yesno_yes'), '#16a34a'],
                ['no',  t('forms_yesno_no'),  '#dc2626'],
                ['na',  t('forms_yesno_na'),  '#64748b']].map(([k, label, colour]) => (
                <span key={k} style={{
                  flex:1, textAlign:'center', padding:'6px 4px', borderRadius:6, fontSize:12,
                  fontWeight:600, border:`1px solid ${colour}55`, background:`${colour}11`, color:colour,
                }}>{label}</span>
              ))}
            </div>
          );
        case 'IMAGE': case 'FILE':
          return (
            <div key={i} style={{...boxSt, borderStyle:'dashed', textAlign:'center', color:'#94a3b8', padding:'14px 9px'}}>
              {type === 'IMAGE' ? '📷 ' + t('forms_preview_add_image') : '📎 ' + t('forms_preview_add_file')}
              <div style={{fontSize:10, marginTop:3}}>
                {listItems(String(formPropValue(cfg, type, 'accepted_formats') || '')).join(', ')}
                {' · '}{formPropValue(cfg, type, 'max_file_size')}
              </div>
            </div>
          );
        case 'PHOTO_ANNOTATED': {
          /* The filler photographs, draws on the picture in the pen colour
             below, and can add more — so the preview shows the pen and the
             stamp the picture will carry, not just a drop box. */
          const stamps = [
            formPropValue(cfg, type, 'timestamp_geotag') !== false ? t('forms_preview_stamp_time') : '',
            formPropValue(cfg, type, 'location_geotag')  !== false ? t('forms_preview_stamp_place') : '',
          ].filter(Boolean);
          return (
            <div key={i} style={{...boxSt, borderStyle:'dashed', textAlign:'center', color:'#94a3b8', padding:'14px 9px'}}>
              <span style={{color: formPropValue(cfg, type, 'pen_color')}}>✏️</span>
              {' 📷 ' + t('forms_preview_add_photo_annotated')}
              <div style={{fontSize:10, marginTop:3}}>
                {t('forms_preview_up_to') + ' ' + (FORMS_CFG().PHOTO_ANNOTATED_MAX || 6)}
                {' · '}{formPropValue(cfg, type, 'max_file_size')}
                {stamps.length ? ' · ' + stamps.join(' · ') : ''}
              </div>
            </div>
          );
        }
        case 'METER_READING': {
          /* One dark box per digit, like the wheels of the meter itself; the
             camera button sits above when the field offers it, and the drift
             rule is spelled out under the digits when one is set. */
          const n     = Math.min(Math.max(Number(formPropValue(cfg, type, 'meter_digits')) || 6, 1), 12);
          const drift = Number(formPropValue(cfg, type, 'drift_pct')) || 0;
          const last  = formPropValue(cfg, type, 'last_value');
          return (
            <div key={i}>
              {formPropValue(cfg, type, 'meter_photo') !== false && (
                <div style={{...boxSt, borderStyle:'dashed', textAlign:'center', color:'#94a3b8', padding:'7px 9px', marginBottom:6}}>
                  📷 {t('forms_preview_meter_photo')}
                </div>
              )}
              <div style={{display:'flex', gap:4, justifyContent:'center', flexWrap:'wrap'}}>
                {Array.from({ length: n }).map((_, d) => (
                  <div key={d} style={{width:24, height:32, borderRadius:5, background:'#0f172a', color:'#f8fafc',
                                       display:'flex', alignItems:'center', justifyContent:'center',
                                       fontFamily:'monospace', fontSize:16, fontWeight:700}}>0</div>
                ))}
              </div>
              {drift > 0 && last != null && last !== '' && Number(last) !== 0 && (
                <div style={{fontSize:10, color:'#94a3b8', textAlign:'center', marginTop:4}}>
                  {t('forms_preview_meter_drift').replace('{pct}', drift).replace('{last}', last)}
                </div>
              )}
            </div>
          );
        }
        case 'SLIDER': {
          /* The range the filler will slide across, ends labelled with the unit */
          const num  = (k, d) => { const v = Number(formPropValue(cfg, type, k)); return Number.isFinite(v) ? v : d; };
          const mn   = num('min_value', 0);
          const st   = Math.max(num('step', 1), 1);
          const mx   = Math.max(num('max_value', 100), mn + st);
          const unit = String(formPropValue(cfg, type, 'unit') || '').trim();
          const lbl  = v => unit ? `${v} ${unit}` : String(v);
          return (
            <div key={i}>
              <input type="range" disabled min={mn} max={mx} step={st} defaultValue={mn + Math.round((mx - mn) / st / 2) * st}
                     style={{width:'100%', accentColor:'#1d4ed8'}} />
              <div style={{display:'flex', justifyContent:'space-between', fontSize:10, color:'#94a3b8'}}>
                <span>{lbl(mn)}</span><span>{lbl(mx)}</span>
              </div>
            </div>
          );
        }
        case 'GPS_POINT': {
          const sat = formPropValue(cfg, type, 'map_layer') === 'satellite';
          return (
            <div key={i}>
              <div style={{height:110, borderRadius:6, position:'relative', overflow:'hidden', border:'1px solid #cbd5e1',
                           background: sat ? 'linear-gradient(135deg,#365314,#3f6212 40%,#57534e 70%,#1e3a8a)'
                                           : 'repeating-linear-gradient(0deg,#f1f5f9 0 22px,#e2e8f0 22px 23px),repeating-linear-gradient(90deg,#f1f5f9 0 22px,#e2e8f0 22px 23px)'}}>
                <span style={{position:'absolute', left:'50%', top:'50%', transform:'translate(-50%,-100%)', fontSize:26}}>📍</span>
                <span style={{position:'absolute', right:6, top:6, background:'#fff', borderRadius:4, padding:'1px 6px', fontSize:10, color:'#475569'}}>
                  {sat ? '🛰️' : '🗺️'} ± </span>
              </div>
              <div style={{fontSize:10, color:'#94a3b8', textAlign:'center', marginTop:4}}>{t('forms_preview_map')} · lat, lon</div>
            </div>
          );
        }
        /* a sensor picker with its latest reading, or with a graph of the last hours */
        case 'SENSOR_VALUE': {
          const hum = formPropValue(cfg, type, 'sensor_measure') === 'humidity';
          return (
            <div key={i} className="pv-sensor">
              <select disabled style={boxSt}><option>📡 {t('forms_preview_sensor')}</option></select>
              <div style={{fontSize:20, fontWeight:700, color:'#0f172a', marginTop:6}}>{hum ? '52.4 %' : '4.2 °'}</div>
              <div style={{fontSize:10, color:'#94a3b8'}}>{hum ? t('forms_o_humidity') : t('forms_o_temperature')} · {t('forms_preview_sensor_when')}</div>
            </div>
          );
        }
        case 'SENSOR_GRAPH': {
          const ser = formPropValue(cfg, type, 'sensor_series');
          const hrs = formPropValue(cfg, type, 'sensor_hours') || 24;
          return (
            <div key={i} className="pv-sensor-graph">
              <select disabled style={boxSt}><option>📡 {t('forms_preview_sensor')}</option></select>
              <svg viewBox="0 0 200 60" style={{width:'100%', height:60, marginTop:6, background:'#f8fafc', borderRadius:6}}>
                {ser !== 'humidity' && <polyline fill="none" stroke="#ef4444" strokeWidth="2" points="0,40 25,35 50,38 75,22 100,26 125,18 150,24 175,20 200,28" />}
                {ser !== 'temperature' && <polyline fill="none" stroke="#3b82f6" strokeWidth="2" points="0,20 25,24 50,30 75,32 100,40 125,44 150,38 175,42 200,36" />}
              </svg>
              <div style={{fontSize:10, color:'#94a3b8', textAlign:'center'}}>{t('forms_preview_sensor_hours').replace('{h}', hrs)}</div>
            </div>
          );
        }
        /* scanned in the mobile app only; the public form says so */
        case 'NFC_SCAN': {
          const show = formPropValue(cfg, type, 'nfc_show_number');
          return (
            <div key={i} style={{...boxSt, background:'#f8fafc', textAlign:'center', borderStyle:'dashed', padding:'10px 8px'}}>
              <div style={{fontSize:22}}>📶</div>
              <div style={{fontSize:12, fontWeight:600, color:'#334155'}}>{t('forms_preview_nfc')}</div>
              {(show === true || show === 'true') && <div style={{fontFamily:'monospace', fontSize:11, color:'#0f172a', marginTop:3}}>04:A2:3B:1C:5D:80</div>}
              <div style={{fontSize:10, color:'#94a3b8', marginTop:3}}>{t('forms_preview_nfc_app')}</div>
            </div>
          );
        }
        case 'EMOJI': {
          const faces = formPropValue(cfg, type, 'emoji_scale') === '3' ? ['😟','😐','😄'] : ['😡','😟','😐','🙂','😄'];
          return (
            <div key={i} style={{display:'flex', justifyContent:'center', gap:10, fontSize:26}}>
              {faces.map(f => <span key={f} style={{opacity:0.75}}>{f}</span>)}
            </div>
          );
        }
        case 'AUTO_DATA': {
          /* what the filler is told will be recorded, in the order it is listed */
          const on = ['auto_ip','auto_ip_location','auto_user','auto_submitted','auto_browser','auto_gps']
            .filter(k => formPropValue(cfg, type, k) === true || formPropValue(cfg, type, k) === 'true');
          return (
            <div key={i} style={{...boxSt, background:'#f8fafc', color:'#475569', fontSize:11, padding:'8px 10px', borderStyle:'dashed'}}>
              {/* the filler never sees this field — only the designer does */}
              <div style={{fontStyle:'italic', color:'#64748b', marginBottom:5}}>👁‍🗨 {t('forms_auto_hidden')}</div>
              <div style={{fontWeight:700, marginBottom:3}}>🛈 {t('forms_preview_auto')}</div>
              {on.length ? on.map(k => <div key={k}>• {t('forms_p_' + k)}</div>) : <div>—</div>}
            </div>
          );
        }
        case 'HOTSPOT': {
          /* the chosen picture, with a sample marker in the pen colour */
          const d = ['person','car','truck','trailer','tractor','excavator','bulldozer','forklift','car_flat','car_34','underside','bus_seats','cab_dash','wheelchair_lift'].includes(formPropValue(cfg, type, 'diagram')) ? formPropValue(cfg, type, 'diagram') : 'person';
          return (
            <div key={i}>
              <div style={{position:'relative', border:'1px solid #e2e8f0', borderRadius:6, overflow:'hidden'}}>
                <img src={`src/diagrams/${d}.svg`} alt={d} style={{display:'block', width:'100%'}} />
                <span style={{position:'absolute', left:'38%', top:'34%', width:18, height:18, marginLeft:-9, marginTop:-9,
                              borderRadius:'50%', background: formPropValue(cfg, type, 'pen_color'), color:'#fff',
                              fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center',
                              border:'2px solid #fff'}}>1</span>
              </div>
              <div style={{fontSize:10, color:'#94a3b8', textAlign:'center', marginTop:4}}>{t('forms_preview_hotspot')}</div>
              {/* shown while nothing is marked; ticking it answers the field */}
              <label style={{display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#475569', marginTop:6}}>
                <input type="checkbox" disabled style={{margin:0}} /> {t('forms_preview_hotspot_none')}
              </label>
            </div>
          );
        }
        case 'DURATION': {
          /* one box per part of the chosen format, labelled underneath */
          const parts = ({ 'h:mm:ss':['h','m','s'], 'mm:ss':['m','s'], 'd h:mm':['d','h','m'] })[formPropValue(cfg, type, 'duration_format')] || ['h','m'];
          return (
            <div key={i}>
              <div style={{display:'flex', gap:8, justifyContent:'center'}}>
                {parts.map(p => (
                  <div key={p} style={{textAlign:'center'}}>
                    <div style={{...boxSt, width:52, textAlign:'center', color:'#94a3b8'}}>{p === 'd' || p === 'h' ? '0' : '00'}</div>
                    <div style={{fontSize:10, color:'#94a3b8', marginTop:2}}>{t('forms_dur_' + p)}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:10, color:'#94a3b8', textAlign:'center', marginTop:4}}>{t('forms_preview_duration_saved')}</div>
            </div>
          );
        }
        case 'AUDIO_NOTE':
          return (
            <div key={i} style={{...boxSt, borderStyle:'dashed', textAlign:'center', color:'#94a3b8', padding:'12px 9px'}}>
              🎙️ {t('forms_preview_audio')}
              <div style={{fontSize:10, marginTop:3}}>
                {t('forms_preview_audio_max').replace('{s}', formPropValue(cfg, type, 'max_seconds'))} · M4A
              </div>
              {/* the public form leaves it out; only the mobile app records */}
              <div style={{fontSize:10.5, marginTop:5, fontStyle:'italic', color:'#64748b'}}>📱 {t('forms_audio_mobile_only')}</div>
            </div>
          );
        case 'DRAW': case 'SIGN':
          return (
            <div key={i} style={{
              width:'100%', maxWidth: formPropValue(cfg, type, 'canvas_width'),
              height: Math.min(formPropValue(cfg, type, 'canvas_height'), 140),
              border:'1px dashed #cbd5e1', borderRadius:6,
              background: formPropValue(cfg, type, 'background_color'),
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'#94a3b8', fontSize:11,
            }}>{type === 'SIGN' ? '✒️' : '✏️'}</div>
          );
        case 'BARCODE':
          return <FormPreviewBarcode key={i} value={field.list_data || item} />;
        case 'QR': case 'DYNAMIC_QR': {
          const text = field.list_data || item;
          let src = '';
          if (text && window.qrcode) {
            try { const q = window.qrcode(0, 'M'); q.addData(String(text)); q.make(); src = q.createDataURL(4, 6); }
            catch (_) { src = ''; }
          }
          return src
            ? <div key={i} style={{display:'flex', justifyContent:'center', padding:'4px 0'}}>
                <img src={src} alt="QR" style={{width:140, height:140, imageRendering:'pixelated'}} />
              </div>
            : <div key={i} style={{...boxSt, textAlign:'center', color:'#94a3b8', padding:'14px 9px'}}>▣ {text}</div>;
        }
        case 'HTML':
          return <div key={i} style={{fontSize:12, color:'#0f172a'}}
                      dangerouslySetInnerHTML={{ __html: String(field.list_data || '').replace(/\\"/g, '"') }} />;
        case 'INFO':
          return <div key={i} style={{fontSize:12, color:'#475569', lineHeight:1.5}}>{item}</div>;
        default:
          return <input key={i} disabled placeholder={ph(item)} style={boxSt} />;
      }
    };

    if (type === 'SEPARATOR') {
      const cap = (field.field_title || '').trim();
      return (
        <div style={{display:'flex', alignItems:'center', gap:10, margin:'18px 0'}}>
          <div style={{flex:1, height:1, background:'#cbd5e1'}} />
          {cap && <span style={{fontSize:11, fontWeight:600, color:'#64748b', whiteSpace:'nowrap'}}>{cap}</span>}
          {cap && <div style={{flex:1, height:1, background:'#cbd5e1'}} />}
        </div>
      );
    }

    return (
      <div style={{marginBottom:12, background: field.field_colour || '#FFFFFF', borderRadius:8, padding:'10px 12px'}}>
        {field.field_title && (
          <div style={{fontSize:12, fontWeight:600, color:'#0f172a', marginBottom:3}}>
            {field.field_title}
            {field.required && <span style={{color:'#dc2626'}}> *</span>}
          </div>
        )}
        {field.field_caption && !['DATE','TIME','TIMESTAMP'].includes(type) && (
          <div style={{fontSize:11, color:'#64748b', marginBottom:5, lineHeight:1.4}}>{field.field_caption}</div>
        )}
        <div style={{display:'flex', flexDirection:'column', gap:6}}>
          {type === 'HTML'
            ? control(field.list_data, 0)
            : isOptionType(type)
              ? <select disabled style={boxSt}>{items.map((o,i) => <option key={i}>{o}</option>)}</select>
              : (type === 'DRAW' || type === 'SIGN')
                ? control('', 0)                       /* one canvas, items ignored */
                : items.length ? items.map(control) : control('', 0)}
        </div>
      </div>
    );
  }

  function FormPreview({ form, fields }) {
    useLang();
    const layouts = FORMS_CFG().LAYOUTS || [];
    const width   = (layouts.find(l => l.key === (form.layout || 'M')) || layouts[0] || {}).width || 390;
    const cols    = Math.max(1, ...fields.map(f => f.field_column || 1));
    const byCol   = Array.from({length: cols}, (_, i) => fields.filter(f => (f.field_column || 1) === i + 1));

    return (
      <div style={{display:'flex', justifyContent:'center', padding:'16px 0'}}>
        <div style={{
          width:'100%', maxWidth:width, background: form.form_body_colour || '#FFFFFF', borderRadius:12,
          border:'1px solid var(--border)', overflow:'hidden', boxShadow:'0 6px 20px rgba(0,0,0,0.18)',
        }}>
          <div style={{background: form.form_background_colour || '#000000', padding:'16px 18px', color:'#fff',
                       display:'flex', gap:12, alignItems:'center'}}>
            {/* the form's own logo, as the public form and the app show it */}
            {/^https:\/\//.test(form.logo_pic_file_name || '') && (
              <img className="pv-logo" src={form.logo_pic_file_name} alt=""
                   style={{width:44, height:44, objectFit:'contain', borderRadius:8, flex:'none'}} />
            )}
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:15, fontWeight:700}}>{form.form_name}</div>
              {form.form_description && (
                <div style={{fontSize:11.5, opacity:0.9, marginTop:4, lineHeight:1.45, whiteSpace:'pre-wrap'}}>
                  {form.form_description}
                </div>
              )}
              {form.form_no && <div style={{fontSize:11, opacity:0.8, marginTop:2}}>{form.form_no}</div>}
            </div>
          </div>
          <div style={{padding:'16px 18px', display:'grid', gap:14,
                       gridTemplateColumns:`repeat(${cols}, minmax(0, 1fr))`}}>
            {byCol.map((colFields, i) => (
              <div key={i}>
                {colFields.map(f => <FormPreviewField key={f.form_field_id} field={f} />)}
              </div>
            ))}
          </div>
          {!fields.length && (
            <div style={{padding:'30px 18px', textAlign:'center', color:'#94a3b8', fontSize:12}}>
              {t('forms_preview_empty')}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ───── One field: collapsed row, or the expanded property editor ───── */
  function FormFieldRow({ field, types, open, onOpen, onSave, onDelete, onDragStart, onDragOver, onDrop, dragging }) {
    useLang();
    const [draft,   setDraft]   = useState(null);
    const [adv,     setAdv]     = useState(false);
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState('');

    /* Rebuild the draft whenever this row is opened, or the row underneath
       it changes after a save. */
    useEffect(() => {
      if (!open) { setDraft(null); setError(''); return; }
      const cfg = field.field_config || {};
      const d = {
        data_type_name: field.data_type_name,
        field_title:    field.field_title   || '',
        field_caption:  field.field_caption || '',
        required:       !!field.required,
        field_colour:   field.field_colour  || '#FFFFFF',
        field_column:   field.field_column  || 1,
        items:          listDataToText(field.list_data),
        field_override_name: field.field_override_name || '',
      };
      Object.keys(FORMS_CFG().PROPS || {}).forEach(k => {
        const v = cfg[k];
        d[k] = Array.isArray(v) ? v.join(', ') : (v === undefined || v === null ? '' : v);
      });
      setDraft(d);
    }, [open, field.version_no, field.form_field_id]);

    const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

    const save = async () => {
      setSaving(true); setError('');
      try {
        await onSave(field, draft);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setSaving(false);
      }
    };

    const typeDesc = types.find(x => x.data_type_name === field.data_type_name)?.data_type_description || '';
    const typeName = formTypeLabel(field.data_type_name, types);

    return (
      <div
        draggable={!open}
        onDragStart={e => onDragStart(e, field)}
        onDragOver={onDragOver}
        onDrop={e => onDrop(e, field)}
        style={{
          border:'1px solid var(--border)', borderRadius:10, marginBottom:8,
          background:'var(--card-bg)', opacity: dragging ? 0.4 : 1,
        }}>

        {/* Collapsed row */}
        <div onClick={() => onOpen(open ? null : field.form_field_id)}
             style={{display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer'}}>
          <span title={t('forms_drag_hint')} style={{color:'var(--text-muted)', cursor:'grab', fontSize:14, lineHeight:1}}>⠿</span>
          <span style={{
            fontSize:10, fontWeight:700, letterSpacing:0.3, padding:'2px 7px', borderRadius:20,
            background:'var(--tabs-bg)', color:'var(--text-muted)', flexShrink:0, whiteSpace:'nowrap',
          }} title={`${field.data_type_name}${typeDesc ? ' — ' + typeDesc : ''}`}>{typeName}</span>
          <span style={{fontSize:13, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
            {field.field_title || t('forms_field_untitled')}
            {field.required && <span style={{color:'var(--red)'}}> *</span>}
          </span>
          {Object.keys(field.field_config || {}).length > 0 && (
            <span title={t('forms_has_config')} style={{fontSize:11, color:'var(--accent)'}}>⚙</span>
          )}
          <span style={{fontSize:11, color:'var(--text-muted)'}}>{t('forms_col_short')}{field.field_column || 1}</span>
          <span style={{width:12, height:12, borderRadius:3, border:'1px solid var(--border)',
                        background: field.field_colour || '#FFFFFF', flexShrink:0}} />
          <button onClick={e => { e.stopPropagation(); onDelete(field); }}
                  title={t('forms_delete_field')}
                  style={{background:'none', border:'none', padding:'2px 4px', cursor:'pointer',
                          color:'var(--text-muted)', display:'flex', alignItems:'center', flexShrink:0}}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </button>
          <span style={{color:'var(--text-muted)', fontSize:11}}>{open ? '▲' : '▼'}</span>
        </div>

        {/* Expanded editor */}
        {open && draft && (
          <div style={{borderTop:'1px solid var(--border)', padding:'14px 14px 16px'}}>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:12}}>
              <label style={{fontSize:12}}>
                <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_f_title')}</div>
                <input value={draft.field_title} onChange={e => set('field_title', e.target.value)}
                       style={{width:'100%', padding:'7px 9px', borderRadius:7, border:'1px solid var(--input-border)',
                               background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box'}} />
              </label>

              <label style={{fontSize:12}}>
                <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_f_type')}</div>
                <select value={draft.data_type_name} onChange={e => set('data_type_name', e.target.value)}
                        style={{width:'100%', padding:'7px 9px', borderRadius:7, border:'1px solid var(--input-border)',
                                background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box'}}>
                  {types.map(ty => (
                    <option key={ty.data_type_name} value={ty.data_type_name}>
                      {formTypeLabel(ty.data_type_name, types)}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{fontSize:12}}>
                <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_f_column')}</div>
                <select value={draft.field_column} onChange={e => set('field_column', Number(e.target.value))}
                        style={{width:'100%', padding:'7px 9px', borderRadius:7, border:'1px solid var(--input-border)',
                                background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box'}}>
                  <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                </select>
              </label>

              <label style={{fontSize:12}}>
                <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_f_colour')}</div>
                <input type="color" value={draft.field_colour} onChange={e => set('field_colour', e.target.value)}
                       style={{width:'100%', height:33, padding:2, borderRadius:7,
                               border:'1px solid var(--input-border)', background:'var(--input-bg)', boxSizing:'border-box'}} />
              </label>
            </div>

            {/* Date-like fields show their format inside the box, so no caption */}
            {!['DATE','TIME','TIMESTAMP','SEPARATOR'].includes(draft.data_type_name) && (
            <label style={{fontSize:12, display:'block', marginTop:12}}>
              <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_f_caption')}</div>
              <input value={draft.field_caption} onChange={e => set('field_caption', e.target.value)}
                     style={{width:'100%', padding:'7px 9px', borderRadius:7, border:'1px solid var(--input-border)',
                             background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box'}} />
            </label>
            )}

            {/* Single-control fields — items would only multiply the control */}
            {!hidesItems(draft.data_type_name) && (
            <label style={{fontSize:12, display:'block', marginTop:12}}>
              <div style={{color:'var(--text-muted)', marginBottom:4}}>
                {isOptionType(draft.data_type_name) ? t('forms_f_options') : t('forms_f_items')}
              </div>
              <textarea rows={3} value={draft.items} onChange={e => set('items', e.target.value)}
                        placeholder={t('forms_f_items_ph')}
                        style={{width:'100%', padding:'7px 9px', borderRadius:7, border:'1px solid var(--input-border)',
                                background:'var(--input-bg)', color:'var(--text)', fontSize:13,
                                fontFamily:'inherit', resize:'vertical', boxSizing:'border-box'}} />
              <div style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>{t('forms_f_items_hint')}</div>
            </label>
            )}

            {draft.data_type_name !== 'SEPARATOR' && (
            <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, marginTop:12, cursor:'pointer'}}>
              <input type="checkbox" checked={draft.required} onChange={e => set('required', e.target.checked)} />
              {t('forms_f_required')}
            </label>
            )}

            {/* Advanced — the automation name (every type) and the forms.sub_field properties */}
            {draft.data_type_name !== 'SEPARATOR' && (
            <div style={{marginTop:14, borderTop:'1px solid var(--border)', paddingTop:12}}>
              <button onClick={() => setAdv(a => !a)}
                      style={{background:'none', border:'none', color:'var(--accent)', fontSize:12,
                              fontWeight:600, cursor:'pointer', padding:0}}>
                {adv ? '▾' : '▸'} {t('forms_f_advanced')}
              </button>
              {adv && (
                <>
                  <div style={{fontSize:11, color:'var(--text-muted)', margin:'8px 0 10px', lineHeight:1.5}}>
                    {t('forms_f_advanced_hint')}
                  </div>
                  <label style={{fontSize:12, display:'block', marginBottom:12}}>
                    <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_f_override')}</div>
                    <input className="f-override" value={draft.field_override_name} maxLength={120}
                           onChange={e => set('field_override_name', e.target.value)}
                           style={{width:'100%', padding:'7px 9px', borderRadius:7, border:'1px solid var(--input-border)',
                                   background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box', fontFamily:'monospace'}} />
                    <div style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>{t('forms_f_override_hint')}</div>
                  </label>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:12}}>
                    {formPropsFor(draft.data_type_name).map(key => {
                      const def = FORMS_CFG().PROPS?.[key];
                      if (!def) return null;
                      const fallback = formPropDefault(draft.data_type_name, key);
                      const inputSt = { width:'100%', padding:'7px 9px', borderRadius:7,
                                        border:'1px solid var(--input-border)', background:'var(--input-bg)',
                                        color:'var(--text)', fontSize:13, boxSizing:'border-box' };
                      return (
                        <label key={key} style={{fontSize:12}}>
                          <div style={{color:'var(--text-muted)', marginBottom:4}}>{t(def.labelKey)}</div>
                          {def.type === 'bool' ? (
                            <select value={draft[key] === '' ? '' : String(draft[key])}
                                    onChange={e => set(key, e.target.value === '' ? '' : e.target.value === 'true')}
                                    style={inputSt}>
                              <option value="">{t('forms_p_default')} ({String(fallback)})</option>
                              <option value="true">{t('forms_p_yes')}</option>
                              <option value="false">{t('forms_p_no')}</option>
                            </select>
                          ) : def.type === 'select' ? (
                            <select value={draft[key]} onChange={e => set(key, e.target.value)} style={inputSt}>
                              <option value="">{t('forms_p_default')} ({fallback})</option>
                              {def.options.map(o => <option key={o} value={o}>{(def.optionKeys && def.optionKeys[o] && t(def.optionKeys[o])) || (def.optionLabels && def.optionLabels[o]) || o}</option>)}
                            </select>
                          ) : def.type === 'colour' ? (
                            <div style={{display:'flex', gap:6}}>
                              <input type="color" value={draft[key] || fallback}
                                     onChange={e => set(key, e.target.value)}
                                     style={{...inputSt, width:52, padding:2, height:33}} />
                              <button onClick={() => set(key, '')}
                                      style={{fontSize:11, padding:'0 10px', borderRadius:7,
                                              border:'1px solid var(--border)', background:'var(--input-bg)',
                                              color:'var(--text-muted)', cursor:'pointer'}}>
                                {t('forms_p_reset')}
                              </button>
                            </div>
                          ) : (
                            <input type={def.type === 'number' ? 'number' : 'text'}
                                   value={draft[key]} onChange={e => set(key, e.target.value)}
                                   placeholder={fallback === undefined ? '' : String(fallback)}
                                   style={inputSt} />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            )}

            {error && (
              <div style={{fontSize:12, color:'var(--red)', background:'rgba(239,68,68,0.08)',
                           border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, padding:'8px 12px', marginTop:12}}>
                {error}
              </div>
            )}

            <div style={{display:'flex', gap:10, marginTop:16}}>
              <button onClick={save} disabled={saving}
                      style={{padding:'8px 18px', borderRadius:8, border:'none', background:'#1d4ed8',
                              color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.5:1}}>
                {saving ? t('btn_saving') : t('btn_save')}
              </button>
              <button onClick={() => onOpen(null)} disabled={saving}
                      style={{padding:'8px 18px', borderRadius:8, border:'1px solid var(--border)',
                              background:'var(--input-bg)', color:'var(--text)', fontSize:13,
                              fontWeight:600, cursor:'pointer'}}>
                {t('btn_cancel')}
              </button>
              <button onClick={() => onDelete(field)} disabled={saving}
                      style={{padding:'8px 18px', borderRadius:8, border:'1px solid rgba(239,68,68,0.4)',
                              background:'rgba(239,68,68,0.08)', color:'var(--red)', fontSize:13,
                              fontWeight:600, cursor:'pointer', marginLeft:'auto'}}>
                {t('forms_delete_field')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ───── Automation: one tab per integration ─────
     The tabs come from config.js (Automation_integrations), so which ones exist is a
     configuration question, not a code change. Email is the original automation and runs
     inline when a form is submitted; Google Sheets and Airtable are queued at submit time
     (forms.integration_queue) and pushed a minute later by a cron, so a slow or broken
     destination never holds up the person submitting the form. The ones that are enabled
     but not ready say so rather than pretending to be configurable.                    */
  const AUTO_TABS = () => (window.OA_CONFIG?.Automation_integrations || [
    { key:'EMAIL', labelKey:'auto_tab_email', enabled:true, ready:true },
  ]).filter(x => x.enabled);

  /* English fallback for a string that has not been translated yet */
  const aT = (key, fb) => { const v = t(key); return v && v !== key ? v : fb; };

  const AUTO_ICONS = {
    mail:    <><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22 6 12 13 2 6"/></>,
    sheet:   <><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="9" x2="9" y2="21"/></>,
    table:   <><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="3" x2="12" y2="21"/></>,
    chat:    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>,
    bolt:    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>,
    api:     <><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>,
  };

  /* what each integration needs, and how to explain it */
  const AUTO_FIELDS = {
    GOOGLE_SHEETS: [
      { k:'spreadsheet_id', labelKey:'auto_f_sheet_id',     fb:'Spreadsheet ID',  hintKey:'auto_h_sheet_id',     hint:'The long id in the sheet URL, between /d/ and /edit.' },
      { k:'sheet_name',     labelKey:'auto_f_sheet_name',   fb:'Tab name',        hintKey:'auto_h_sheet_name',   hint:'The tab a row is appended to. Sheet1 if left empty.' },
      { k:'client_id',      labelKey:'auto_f_client_id',    fb:'OAuth client ID' },
      { k:'client_secret',  labelKey:'auto_f_client_secret',fb:'OAuth client secret', secret:true },
      { k:'refresh_token',  labelKey:'auto_f_refresh',      fb:'Refresh token',   secret:true, hintKey:'auto_h_refresh', hint:'From your own Google project — see the steps above.' },
    ],
    AIRTABLE: [
      { k:'base_id',    labelKey:'auto_f_base_id',    fb:'Base ID',    hintKey:'auto_h_base_id',    hint:'Starts with app — from the base URL or the Airtable API page.' },
      { k:'table_name', labelKey:'auto_f_table_name', fb:'Table name', hintKey:'auto_h_table_name', hint:'The table answers are added to, exactly as it is named in Airtable.' },
      { k:'token',      labelKey:'auto_f_token',      fb:'Personal access token', secret:true, hintKey:'auto_h_token', hint:'Needs data.records:write on this base.' },
    ],
    /* the Catch Hook URL is a credential: masked when read back, like a token */
    ZAPIER: [
      { k:'webhook_url', labelKey:'auto_f_webhook', fb:'Zapier webhook URL', secret:true, hintKey:'auto_h_webhook', hint:'Starts https://hooks.zapier.com/hooks/catch/ — hidden once saved.' },
    ],
    SLACK: [
      { k:'webhook_url', labelKey:'auto_f_slack_url', fb:'Slack webhook URL', secret:true, hintKey:'auto_h_slack_url', hint:'Starts https://hooks.slack.com/services/ — hidden once saved.' },
    ],
    /* method, bearer token and additional JSON have their own controls below */
    REST_API: [
      { k:'url', labelKey:'auto_f_rest_url', fb:'Endpoint URL', hintKey:'auto_h_rest_url', hint:'The https:// address that receives each submission as JSON.' },
    ],
  };
  const AUTO_NAMES = { GOOGLE_SHEETS:'Google Sheets', AIRTABLE:'Airtable', ZAPIER:'Zapier', SLACK:'Slack', REST_API:'REST API' };

  /* REST_API's additional JSON: the editor's text, or the stored object; null when it is not a JSON object */
  const restExtraText = v => typeof v === 'string' ? v : (v && typeof v === 'object' ? JSON.stringify(v, null, 2) : '');
  const restExtraObj  = v => {
    const s = restExtraText(v).trim();
    if (!s) return {};
    try { const o = JSON.parse(s); return o && typeof o === 'object' && !Array.isArray(o) ? o : null; } catch { return null; }
  };

  /* field types whose answer reaches Airtable as a column (display-only and file types do not) */
  const AIRTABLE_SKIP = ['SEPARATOR', 'INFO', 'LABEL', 'HTML', 'QR', 'DYNAMIC_QR', 'BARCODE', 'URL', 'IMAGE', 'FILE', 'DRAW', 'SIGN', 'PHOTO_ANNOTATED', 'AUDIO_NOTE'];
  function AutomationIntegrationPanel({ form, type, icon, fields }) {
    useLang();
    const [row,   setRow]   = useState(null);
    const [draft, setDraft] = useState({});
    const [busy,  setBusy]  = useState(false);
    const [testing, setTesting] = useState(false);   /* the test button is on its way to the destination */
    const [msg,   setMsg]   = useState(null);      /* { ok, text } */
    const [log,   setLog]   = useState([]);

    const fieldsFor = AUTO_FIELDS[type] || [];

    const load = () => {
      callRpc('form_integration_get', { get_form_id: form.form_id })
        .then(list => {
          /* nothing saved yet still opens an empty, switched-off panel */
          const r = (Array.isArray(list) ? list : []).find(x => x.integration === type)
                 || { integration: type, enabled: false, config: {}, implemented: true, queued: 0, sent: 0, failed: 0 };
          setRow(r);
          setDraft({ ...(r?.config || {}) });
        })
        .catch(e => setMsg({ ok:false, text:e.message }));
      callRpc('form_integration_log', { log_form_id: form.form_id, log_limit: 8 })
        .then(l => setLog((Array.isArray(l) ? l : []).filter(x => x.integration === type)))
        .catch(() => {});
    };
    useEffect(() => { setRow(null); setDraft({}); setMsg(null); setLog([]); load(); }, [form.form_id, type]);

    const restExtra = type === 'REST_API' ? restExtraObj(draft.additional_json) : {};
    const save = async (enabled) => {
      if (restExtra === null) { setMsg({ ok:false, text: aT('auto_rest_json_bad', 'Additional JSON must be a JSON object, for example {"site": "North"}.') }); return; }
      setBusy(true); setMsg(null);
      try {
        const res = await callRpc('form_integration_set', {
          set_form_id: form.form_id, set_type: type,
          set_enabled: enabled === undefined ? !!row?.enabled : enabled,
          set_config: type === 'REST_API' ? { ...draft, additional_json: restExtra } : draft,
        });
        const r = Array.isArray(res) ? res[0] : res;
        /* a save that worked just greys the button again; only a refusal is shown */
        setMsg(r?.ok ? null : { ok: false, text: r?.message || '' });
        load();
      } catch (e) { setMsg({ ok:false, text:e.message }); }
      finally { setBusy(false); }
    };

    const test = async () => {
      setBusy(true); setTesting(true); setMsg(null);
      try {
        const res = await callRpc('form_integration_test', { test_form_id: form.form_id, test_type: type });
        const r = Array.isArray(res) ? res[0] : res;
        setMsg({ ok: !!r?.ok, text: r?.message || '' });
        load();
      } catch (e) { setMsg({ ok:false, text:e.message }); }
      finally { setBusy(false); setTesting(false); }
    };
    /* Save is grey until something was edited, and grey again once it is saved */
    const dirty = !!row && JSON.stringify(draft) !== JSON.stringify(row.config || {});
    const atFields = (fields || []).filter(f => AIRTABLE_SKIP.indexOf(f.data_type_name) < 0)
      .map(f => ((f.field_override_name || '').trim() || (f.field_title || '').trim())).filter(Boolean);

    const lblSt  = { display:'block', fontSize:11, fontWeight:600, color:'var(--text-muted)', marginBottom:4, letterSpacing:'.02em' };
    const inpSt  = { width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid var(--input-border,var(--border))',
                     background:'var(--input-bg)', color:'var(--text)', fontSize:13 };
    const hintSt = { fontSize:11, color:'var(--text-muted)', marginTop:4, lineHeight:1.4 };
    const btnSt  = { padding:'8px 16px', borderRadius:8, border:'1px solid var(--border)',
                     background:'var(--input-bg)', color:'var(--text)', fontSize:12.5, fontWeight:600, cursor:'pointer' };
    const fmtTs = iso => { try { return new Date(iso).toLocaleString(_currentLang || 'en',
      { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', hour12:false }); } catch { return iso || ''; } };
    const STATE = { 0: aT('auto_state_queued','Queued'), 1: aT('auto_state_sent','Sent'), 2: aT('auto_state_failed','Given up') };

    /* a failed load says why, rather than "Loading…" for ever */
    if (!row) return <div style={{padding:20, color: msg && !msg.ok ? 'var(--red)' : 'var(--text-muted)', fontSize:13}}>
      {msg && !msg.ok ? msg.text : (t('loading') || 'Loading…')}</div>;

    return (
      <div style={{display:'flex', gap:16, padding:16, alignItems:'flex-start', flexWrap:'wrap'}}>
        <div style={{flex:'1 1 420px', minWidth:320}}>
          <div style={{border:'1px solid var(--border)', borderRadius:10, overflow:'hidden'}}>
            <div style={{display:'flex', alignItems:'center', gap:12, padding:'14px 16px',
                         background:'var(--tabs-bg)', borderBottom:'1px solid var(--border)'}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>{AUTO_ICONS[icon] || AUTO_ICONS.table}</svg>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontWeight:700, fontSize:14}}>
                  {AUTO_NAMES[type] || type}
                </div>
                <div style={{fontSize:11.5, color:'var(--text-muted)'}}>
                  {aT('auto_queue_note', 'Each submission is queued and pushed within a minute, so a slow destination never delays the form.')}
                </div>
              </div>
              <label style={{display:'inline-flex', alignItems:'center', gap:8, fontSize:12.5, cursor:'pointer'}}>
                <input type="checkbox" checked={!!row.enabled} disabled={busy}
                       onChange={e => save(e.target.checked)} />
                {row.enabled ? (t('lbl_active') || 'Active') : (t('lbl_inactive') || 'Off')}
              </label>
            </div>

            <div style={{padding:16, display:'grid', gap:14}}>
              {type === 'GOOGLE_SHEETS' && (
                <div style={{padding:'12px 14px', borderRadius:9, border:'1px solid var(--border)',
                             background:'var(--input-bg)', fontSize:12, lineHeight:1.65, color:'var(--text-muted)'}}>
                  <div style={{fontWeight:700, color:'var(--text)', marginBottom:4}}>
                    {aT('auto_gs_howto_title', 'These credentials are yours, from your own Google account')}
                  </div>
                  <ol style={{margin:'0 0 0 16px', padding:0}}>
                    <li>{aT('auto_gs_step1', 'In the Google Cloud console, create a project and enable the Google Sheets API.')}</li>
                    <li>{aT('auto_gs_step2', 'Create an OAuth client (Desktop app is simplest via APIs & Services and click on Credentials) — that gives you the client ID and secret.')}</li>
                    <li>{aT('auto_gs_step3', 'On the OAuth consent screen, select Data access then add the scope .../auth/spreadsheets, add yourself as a user, and publish it — while it stays in Testing, Google expires the refresh token after 7 days.')}</li>
                    <li>{aT('auto_gs_step4', 'Authorise once with access_type=offline (the OAuth Playground does this in a browser with your own credentials) and copy the refresh token.')}</li>
                    <li>{aT('auto_gs_step4b', 'The spreadsheet ID is the long code in the sheet’s web address, between /spreadsheets/d/ and /edit — open the sheet in Google Sheets and copy it from the address bar.')}</li>
                    <li>{aT('auto_gs_step5', 'Paste all three below, add the spreadsheet ID and tab, save, then send a test row.')}</li>
                  </ol>
                </div>
              )}
              {type === 'AIRTABLE' && (
                <div style={{padding:'12px 14px', borderRadius:9, border:'1px solid var(--border)',
                             background:'var(--input-bg)', fontSize:12, lineHeight:1.65, color:'var(--text-muted)'}}>
                  <div style={{fontWeight:700, color:'var(--text)', marginBottom:4}}>
                    {aT('auto_at_howto_title', 'Connect Airtable in four steps')}
                  </div>
                  <ol style={{margin:'0 0 0 16px', padding:0}}>
                    <li>{aT('auto_at_step1', 'In Airtable, open the base and table the answers should go to; the Base ID (starts with app) is in the page address.')}</li>
                    <li>{aT('auto_at_step2', 'Create a personal access token at airtable.com/create/tokens with the scopes data.records:write and schema.bases:write, limited to that base.')}</li>
                    <li>{aT('auto_at_step3', 'Paste the Base ID, the table name exactly as shown in Airtable, and the token below; save and switch it on.')}</li>
                    <li>{aT('auto_at_step4', 'Press Send a test row: the fields listed below are created in the table if missing, and a row marked TEST arrives.')}</li>
                  </ol>
                </div>
              )}
              {type === 'SLACK' && (
                <div style={{padding:'12px 14px', borderRadius:9, border:'1px solid var(--border)',
                             background:'var(--input-bg)', fontSize:12, lineHeight:1.65, color:'var(--text-muted)'}}>
                  <div style={{fontWeight:700, color:'var(--text)', marginBottom:4}}>
                    {aT('auto_sl_howto_title', 'Connect Slack in four steps')}
                  </div>
                  <ol style={{margin:'0 0 0 16px', padding:0}}>
                    <li>{aT('auto_sl_step1', 'In Slack, open api.slack.com/apps, create an app (from scratch) in your workspace.')}</li>
                    <li>{aT('auto_sl_step2', 'Turn on Incoming Webhooks and add a webhook to the channel the submissions should post in.')}</li>
                    <li>{aT('auto_sl_step3', 'Copy the webhook URL (starts https://hooks.slack.com/services/) and paste it here; save and switch it on.')}</li>
                    <li>{aT('auto_sl_step4', 'Press Send a test row: a message marked TEST appears in the channel; from then on every submission is posted there.')}</li>
                  </ol>
                </div>
              )}
              {type === 'ZAPIER' && (
                <div style={{padding:'12px 14px', borderRadius:9, border:'1px solid var(--border)',
                             background:'var(--input-bg)', fontSize:12, lineHeight:1.65, color:'var(--text-muted)'}}>
                  <div style={{fontWeight:700, color:'var(--text)', marginBottom:4}}>
                    {aT('auto_zap_howto_title', 'Connect a Zap in four steps')}
                  </div>
                  <ol style={{margin:'0 0 0 16px', padding:0}}>
                    <li>{aT('auto_zap_step1', 'In Zapier, create a Zap with the trigger Webhooks by Zapier, event Catch Hook.')}</li>
                    <li>{aT('auto_zap_step2', 'Copy the webhook URL Zapier shows, paste it below, save and switch it on.')}</li>
                    <li>{aT('auto_zap_step3', 'Press Send a test row so Zapier sees a sample: form, reference, time submitted and the answers.')}</li>
                    <li>{aT('auto_zap_step4', 'Add your actions in Zapier (email, CRM, spreadsheet, chat) — every submission then runs the Zap.')}</li>
                  </ol>
                </div>
              )}
              {type === 'REST_API' && (
                <div style={{padding:'12px 14px', borderRadius:9, border:'1px solid var(--border)',
                             background:'var(--input-bg)', fontSize:12, lineHeight:1.65, color:'var(--text-muted)'}}>
                  <div style={{fontWeight:700, color:'var(--text)', marginBottom:4}}>
                    {aT('auto_rest_howto_title', 'Send each submission to your own API')}
                  </div>
                  <ol style={{margin:'0 0 0 16px', padding:0}}>
                    <li>{aT('auto_rest_step1', 'Enter the https:// address of your endpoint and the method it expects.')}</li>
                    <li>{aT('auto_rest_step2', 'If it needs a bearer token, switch the token on and paste it — it is hidden once saved.')}</li>
                    <li>{aT('auto_rest_step3', 'Add any fixed values your system needs under Additional JSON; they are added to every request.')}</li>
                    <li>{aT('auto_rest_step4', 'Save, switch it on and press Send a test row: a request with "reference": "TEST" arrives.')}</li>
                  </ol>
                </div>
              )}
              {fieldsFor.map(f => (
                <div key={f.k}>
                  <label style={lblSt}>{aT(f.labelKey, f.fb)}</label>
                  <input style={inpSt} type="text" autoComplete="off" spellCheck={false}
                         value={draft[f.k] ?? ''} disabled={busy}
                         onChange={e => setDraft(d => ({ ...d, [f.k]: e.target.value }))} />
                  {f.hint && <div style={hintSt}>{aT(f.hintKey, f.hint)}</div>}
                </div>
              ))}

              {type === 'SLACK' && (
                <div className="auto-sl-opts" style={{display:'grid', gap:10}}>
                  <div>
                    <label style={lblSt}>{aT('auto_sl_format', 'Data fields')}</label>
                    <select style={inpSt} value={draft.data_format === 'json' ? 'json' : 'fields'} disabled={busy}
                            onChange={e => setDraft(d => ({ ...d, data_format: e.target.value }))}>
                      <option value="fields">{aT('auto_sl_fmt_fields', 'Send as Fields')}</option>
                      <option value="json">{aT('auto_sl_fmt_json', 'Send as JSON')}</option>
                    </select>
                  </div>
                  {/* thermotrack.io's "Send images as links" is left out: OPS Assist does not record a
                      submission's uploaded files, so Slack messages carry the answers only */}
                </div>
              )}
              {type === 'REST_API' && (() => {
                const bearerOn = draft.use_bearer === true || draft.use_bearer === 'true';
                /* what one request carries, with this form's own field names */
                const sample = {
                  form_name: form.form_name || '', form_no: form.form_no || '', reference: '1042',
                  submitted: '2026-01-31T09:15:00Z', entry_id: '…',
                  answers: Object.fromEntries(atFields.slice(0, 6).map(n => [n, '…'])),
                  source: 'opsassist.co', ...(restExtra || {}),
                };
                return (
                  <div className="auto-rest-opts" style={{display:'grid', gap:14}}>
                    <div>
                      <label style={lblSt}>{aT('auto_rest_method', 'Method')}</label>
                      <select className="auto-rest-method" style={{...inpSt, width:'auto', minWidth:140}} disabled={busy}
                              value={String(draft.method || '').toUpperCase() === 'PUT' ? 'PUT' : 'POST'}
                              onChange={e => setDraft(d => ({ ...d, method: e.target.value }))}>
                        {['POST','PUT'].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{display:'inline-flex', alignItems:'center', gap:8, fontSize:12.5, cursor:'pointer'}}>
                        <input type="checkbox" className="auto-rest-bearer" checked={bearerOn} disabled={busy}
                               onChange={e => setDraft(d => ({ ...d, use_bearer: e.target.checked }))} />
                        {aT('auto_rest_bearer', 'Send a bearer token')}
                      </label>
                      {bearerOn && (
                        <div style={{marginTop:8}}>
                          <label style={lblSt}>{aT('auto_rest_token', 'Bearer token')}</label>
                          <input className="auto-rest-token" style={inpSt} type="text" autoComplete="off" spellCheck={false}
                                 value={draft.bearer_token ?? ''} disabled={busy}
                                 onChange={e => setDraft(d => ({ ...d, bearer_token: e.target.value }))} />
                          <div style={hintSt}>{aT('auto_rest_token_hint', 'Sent as Authorization: Bearer <token>. Hidden once saved.')}</div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={lblSt}>{aT('auto_rest_extra', 'Additional JSON')}</label>
                      <textarea className="auto-rest-extra" rows={5} spellCheck={false} disabled={busy}
                                placeholder={'{\n  "site": "North",\n  "system": "erp"\n}'}
                                value={restExtraText(draft.additional_json)}
                                onChange={e => setDraft(d => ({ ...d, additional_json: e.target.value }))}
                                style={{...inpSt, fontFamily:'monospace', fontSize:12, resize:'vertical',
                                        borderColor: restExtra === null ? '#dc2626' : undefined}} />
                      <div style={{...hintSt, color: restExtra === null ? '#dc2626' : hintSt.color}}>
                        {restExtra === null
                          ? aT('auto_rest_json_bad', 'Additional JSON must be a JSON object, for example {"site": "North"}.')
                          : aT('auto_rest_extra_hint', 'Fixed values added to the top level of every request; a key here replaces the standard one of the same name.')}
                      </div>
                    </div>
                    <div>
                      <label style={lblSt}>{aT('auto_rest_sample', 'What is sent')}</label>
                      <pre className="auto-rest-sample" style={{margin:0, padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)',
                                   background:'var(--card-bg)', fontSize:11.5, lineHeight:1.5, maxHeight:220, overflow:'auto', whiteSpace:'pre-wrap'}}>
                        {(String(draft.method || 'POST').toUpperCase()) + ' ' + (draft.url || 'https://…') + '\n'
                          + 'Content-Type: application/json' + (bearerOn ? '\nAuthorization: Bearer ••••' : '') + '\n\n'
                          + JSON.stringify(sample, null, 2)}
                      </pre>
                    </div>
                  </div>
                );
              })()}
              {type === 'AIRTABLE' && (
                <div className="auto-at-fields" style={{padding:'10px 14px', borderRadius:9, border:'1px solid var(--border)', background:'var(--input-bg)', fontSize:12}}>
                  <div style={{fontWeight:700, color:'var(--text)', marginBottom:6}}>{t('auto_at_fields_title')}</div>
                  <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
                    {atFields.map(n => (
                      <span key={n} style={{padding:'2px 8px', borderRadius:999, border:'1px solid var(--border)', background:'var(--card-bg)', fontFamily:'monospace', fontSize:11.5}}>{n}</span>
                    ))}
                    {!atFields.length && <span style={{color:'var(--text-muted)'}}>—</span>}
                  </div>
                  <div style={hintSt}>{t('auto_at_fields_note')}</div>
                </div>
              )}
              <style>{'@keyframes fm-spin { to { transform: rotate(360deg) } }'}</style>
              <div style={{display:'flex', gap:10, alignItems:'center', flexWrap:'wrap'}}>
                <button className="auto-save" style={{...btnSt, background:'#1d4ed8', color:'#fff', border:'none',
                                opacity: dirty && !busy ? 1 : 0.45, cursor: dirty && !busy ? 'pointer' : 'not-allowed'}}
                        onClick={() => save()} disabled={busy || !dirty}>{t('btn_save') || 'Save'}</button>
                <button className="auto-test" style={{...btnSt, display:'inline-flex', alignItems:'center', gap:8,
                                opacity: row.enabled && !busy ? 1 : 0.45, cursor: testing ? 'wait' : row.enabled && !busy ? 'pointer' : 'not-allowed',
                                background: testing ? 'var(--border)' : btnSt.background}}
                        onClick={test} disabled={busy || !row.enabled}>
                  {testing && <span aria-hidden="true" style={{width:13, height:13, borderRadius:'50%', border:'2px solid var(--text-muted)',
                                                                 borderTopColor:'transparent', animation:'fm-spin 0.8s linear infinite'}} />}
                  {testing ? t('auto_testing') : aT('auto_test', 'Send a test row')}
                </button>
                {msg && (
                  <span style={{fontSize:12.5, color: msg.ok ? '#16a34a' : '#dc2626'}}>{msg.text}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* what has actually been delivered */}
        <div style={{flex:'1 1 320px', minWidth:280}}>
          <div style={{border:'1px solid var(--border)', borderRadius:10, overflow:'hidden'}}>
            <div style={{padding:'12px 16px', background:'var(--tabs-bg)', borderBottom:'1px solid var(--border)',
                         fontWeight:700, fontSize:13}}>{aT('auto_deliveries', 'Deliveries')}</div>
            <div style={{padding:'12px 16px', fontSize:12.5, color:'var(--text-muted)', display:'grid', gap:6}}>
              <div>{aT('auto_stat_queued','Queued')}: <b style={{color:'var(--text)'}}>{row.queued ?? 0}</b> · {aT('auto_stat_sent','Sent')}: <b style={{color:'var(--text)'}}>{row.sent ?? 0}</b> · {aT('auto_stat_failed','Given up')}: <b style={{color:'var(--text)'}}>{row.failed ?? 0}</b></div>
              {row.last_sent && <div>{aT('auto_last_sent','Last sent')}: {fmtTs(row.last_sent)}</div>}
              {row.last_error && <div style={{color:'#dc2626', wordBreak:'break-word'}}>{row.last_error}</div>}
            </div>
            {log.length > 0 && (
              <div style={{borderTop:'1px solid var(--border)'}}>
                {log.map((l, i) => (
                  <div key={i} style={{display:'flex', justifyContent:'space-between', gap:10, padding:'8px 16px',
                                        borderTop: i ? '1px solid var(--border)' : 'none', fontSize:12}}>
                    <span style={{color:'var(--text-muted)'}}>{fmtTs(l.create_ts)}{l.reference ? ' · ' + l.reference : ''}</span>
                    <span style={{color: l.state === 1 ? '#16a34a' : l.state === 2 ? '#dc2626' : 'var(--text-muted)'}}>
                      {STATE[l.state]}{l.attempts > 1 ? ' (' + l.attempts + ')' : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function AutomationSoonPanel({ name, type }) {
    useLang();
    return (
      <div style={{padding:24}}>
        {type === 'SLACK' && (
          <div style={{padding:'12px 14px', borderRadius:9, border:'1px solid var(--border)', maxWidth:560, marginBottom:14,
                       background:'var(--input-bg)', fontSize:12, lineHeight:1.65, color:'var(--text-muted)'}}>
            <div style={{fontWeight:700, color:'var(--text)', marginBottom:4}}>
              {aT('auto_sl_howto_title', 'Connect Slack in four steps')}
            </div>
            <ol style={{margin:'0 0 0 16px', padding:0}}>
              <li>{aT('auto_sl_step1', 'In Slack, open api.slack.com/apps, create an app (from scratch) in your workspace.')}</li>
              <li>{aT('auto_sl_step2', 'Turn on Incoming Webhooks and add a webhook to the channel the submissions should post in.')}</li>
              <li>{aT('auto_sl_step3', 'Copy the webhook URL (starts https://hooks.slack.com/services/) and paste it here; save and switch it on.')}</li>
              <li>{aT('auto_sl_step4', 'Press Send a test row: a message marked TEST appears in the channel; from then on every submission is posted there.')}</li>
            </ol>
          </div>
        )}
        <div style={{border:'1px dashed var(--border)', borderRadius:10, padding:'22px 24px', maxWidth:560}}>
          <div style={{fontWeight:700, fontSize:14, marginBottom:6}}>{name}</div>
          <div style={{fontSize:13, color:'var(--text-muted)', lineHeight:1.6}}>
            {aT('auto_soon', 'Not available yet. When it arrives, each submission will be queued and delivered here the same way Google Sheets and Airtable are, with the same delivery log.')}
          </div>
        </div>
      </div>
    );
  }

  function FormAutomationTab({ form, fields }) {
    useLang();
    const tabs = AUTO_TABS();
    const [tab, setTab] = useState(() => {
      try { const saved = localStorage.getItem('oa_forms_auto_tab'); if (saved && tabs.some(x => x.key === saved)) return saved; } catch {}
      return tabs[0]?.key || 'EMAIL';
    });
    const pick = (k) => { setTab(k); try { localStorage.setItem('oa_forms_auto_tab', k); } catch {} };
    const current = tabs.find(x => x.key === tab) || tabs[0];
    const LABELS = { EMAIL:'Email', GOOGLE_SHEETS:'Google Sheets', AIRTABLE:'Airtable', SLACK:'Slack', ZAPIER:'Zapier', REST_API:'REST API' };

    return (
      <div>
        <div style={{display:'flex', gap:2, padding:'10px 16px 0', borderBottom:'1px solid var(--border)', flexWrap:'wrap'}}>
          {tabs.map(x => {
            const on = current?.key === x.key;
            return (
              <button key={x.key} onClick={() => pick(x.key)}
                style={{display:'inline-flex', alignItems:'center', gap:7, padding:'8px 14px',
                  border:'none', borderBottom: on ? '2px solid var(--accent)' : '2px solid transparent',
                  background:'transparent', cursor:'pointer', fontSize:13,
                  fontWeight: on ? 700 : 500, color: on ? 'var(--text)' : 'var(--text-muted)'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">{AUTO_ICONS[x.icon] || AUTO_ICONS.table}</svg>
                {aT(x.labelKey, LABELS[x.key] || x.key)}
                {!x.ready && (
                  <span style={{fontSize:9.5, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
                                padding:'2px 6px', borderRadius:999, background:'var(--input-bg)',
                                border:'1px solid var(--border)', color:'var(--text-muted)'}}>
                    {aT('auto_soon_badge', 'Soon')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {!current ? null
          : !current.ready ? <AutomationSoonPanel name={aT(current.labelKey, LABELS[current.key] || current.key)} type={current.key} />
          : current.key === 'EMAIL' ? <AutomationEmailPanel form={form} fields={fields} />
          : <AutomationIntegrationPanel form={form} type={current.key} icon={current.icon} fields={fields} />}
      </div>
    );
  }

  /* ───── Automation tab ─────
     Optional actions run each time the form is submitted
     (forms.forms_automation, run by app_api.form_post). Email is the first
     panel. Its preview is the HTML the server itself builds and sends, so
     what you see is exactly what arrives.                               */
  const AUTO_EMAIL_RE = /^[a-z0-9._%+'-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  const autoEmailList = (s) => (s || '').split(/[,;\s]+/).map(v => v.trim()).filter(Boolean);

  function AutomationEmailPanel({ form, fields }) {
    useLang();
    const [draft,   setDraft]   = useState(null);
    const [saved,   setSaved]   = useState(null);
    const [busy,    setBusy]    = useState(false);
    const [msg,     setMsg]     = useState(null);     /* { ok, text } */
    const [latest,  setLatest]  = useState(false);
    const [preview, setPreview] = useState(null);     /* { subject, html, usedEntry } | { error } */

    const fromRow = (r) => ({
      enabled:           !!r?.enabled,
      email_to:          r?.email_to || '',
      email_to_field_id: r?.email_to_field_id || '',
      email_subject:     r?.email_subject || '',
      email_from_name:   r?.email_from_name || '',
      email_message:     r?.email_message || '',
      email_mode:        r?.email_mode || 'DATA',
    });
    const [stats, setStats] = useState({ last: null, count: 0 });

    const load = () => callRpc('form_automation_get', { get_form_id: form.form_id })
      .then(d => {
        const r = Array.isArray(d) ? d[0] : d;
        const v = fromRow(r);
        setDraft(v); setSaved(v);
        setStats({ last: r?.last_sent_ts || null, count: Number(r?.sent_count || 0) });
      });

    useEffect(() => {
      setDraft(null); setSaved(null); setMsg(null); setPreview(null);
      load().catch(e => setMsg({ ok: false, text: e.message || String(e) }));
    }, [form.form_id]);

    /* The preview follows the unsaved subject and message after a pause in
       typing, and rebuilds when the form's fields or settings change. */
    useEffect(() => {
      if (!draft) return;
      const id = setTimeout(() => {
        callRpc('form_automation_email_preview', {
          preview_form_id: form.form_id,
          preview_subject: draft.email_subject || null,
          preview_message: draft.email_message || null,
          preview_latest:  latest,
        })
          .then(d => {
            const r = Array.isArray(d) ? d[0] : d;
            setPreview({ subject: r?.email_subject || '', html: r?.email_html || '', usedEntry: !!r?.used_entry });
          })
          .catch(e => setPreview({ error: e.message || String(e) }));
      }, 500);
      return () => clearTimeout(id);
    }, [form.form_id, form.form_version, fields, draft?.email_subject, draft?.email_message, latest]);

    if (!draft) {
      return (
        <div style={{padding:'30px 16px', textAlign:'center', color: msg ? 'var(--red)' : 'var(--text-muted)', fontSize:13}}>
          {msg ? msg.text : t('forms_loading')}
        </div>
      );
    }

    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
    const set = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setMsg(null); };

    /* Same rules as app_api.form_automation_email_set, in the user's language */
    const validate = () => {
      const list = autoEmailList(draft.email_to);
      const bad  = list.find(v => !AUTO_EMAIL_RE.test(v));
      if (bad) return t('forms_auto_err_email').replace('{v}', bad);
      if (new Set(list.map(v => v.toLowerCase())).size > 10) return t('forms_auto_err_max');
      if (draft.enabled && !list.length && !draft.email_to_field_id) return t('forms_auto_err_none');
      return '';
    };

    const save = async () => {
      const err = validate();
      if (err) { setMsg({ ok: false, text: err }); return; }
      setBusy(true); setMsg(null);
      try {
        await callRpc('form_automation_email_set', {
          set_form_id:           form.form_id,
          set_enabled:           draft.enabled,
          set_email_to:          draft.email_to.trim() || null,
          set_email_to_field_id: null,                      /* the field was removed from the panel */
          set_email_subject:     draft.email_subject.trim() || null,
          set_email_from_name:   draft.email_from_name.trim() || null,
          set_email_message:     draft.email_message.trim() || null,
          set_email_mode:        draft.email_mode || 'DATA',
        });
        await load();                                        /* saved: the button greys again, nothing to say */
      } catch (e) {
        setMsg({ ok: false, text: e.message || String(e) });
      } finally { setBusy(false); }
    };

    const sendTest = async () => {
      setBusy(true); setMsg(null);
      try {
        const d = await callRpc('form_automation_email_test', { test_form_id: form.form_id });
        const r = Array.isArray(d) ? d[0] : d;
        setMsg({ ok: true, text: t('forms_auto_test_sent').replace('{to}', r?.sent_to || '') });
      } catch (e) {
        setMsg({ ok: false, text: e.message || String(e) });
      } finally { setBusy(false); }
    };

    const canTest = !dirty && autoEmailList(saved.email_to).length > 0;
    const inputSt = { width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid var(--input-border)',
                      background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box' };
    const labelSt = { fontSize:12, display:'block', marginBottom:14 };
    const capSt   = { color:'var(--text-muted)', marginBottom:4 };
    const hintSt  = { fontSize:11, color:'var(--text-muted)', marginTop:4, lineHeight:1.4 };
    const btnSt   = { padding:'8px 16px', borderRadius:8, border:'1px solid var(--border)',
                      background:'var(--input-bg)', color:'var(--text)', fontSize:12.5, fontWeight:600 };
    const fmtTs = iso => {
      try {
        return new Date(iso).toLocaleString(_currentLang || 'en', {
          day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:false });
      } catch { return iso; }
    };

    return (
      <div style={{display:'flex', gap:16, padding:16, alignItems:'flex-start', flexWrap:'wrap'}}>
        {/* ── Panels ── */}
        <div style={{flex:'1 1 380px', minWidth:300}}>
          <div style={{border:'1px solid var(--border)', borderRadius:10, overflow:'hidden'}}>
            <div style={{display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px',
                         background:'var(--tabs-bg)', borderBottom:'1px solid var(--border)'}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, marginTop:1}}>
                <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22 6 12 13 2 6"/>
              </svg>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:14, fontWeight:700}}>{t('forms_auto_email_title')}</div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:3, lineHeight:1.45}}>{t('forms_auto_email_sub')}</div>
              </div>
              <label style={{display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:12.5,
                             fontWeight:700, whiteSpace:'nowrap',
                             color: draft.enabled ? 'var(--green)' : 'var(--text-muted)'}}>
                <input type="checkbox" checked={draft.enabled} onChange={e => set('enabled', e.target.checked)}
                       style={{width:16, height:16, accentColor:'#16a34a', cursor:'pointer'}} />
                {draft.enabled ? t('forms_auto_on') : t('forms_auto_off')}
              </label>
            </div>

            <div style={{padding:16, opacity: draft.enabled ? 1 : 0.75}}>
              <label style={labelSt}>
                <div style={capSt}>{t('forms_auto_to')}</div>
                <textarea rows={2} value={draft.email_to} placeholder={t('forms_auto_to_ph')}
                          onChange={e => set('email_to', e.target.value)}
                          style={{...inputSt, fontFamily:'inherit', resize:'vertical'}} />
                <div style={hintSt}>{t('forms_auto_to_hint')}</div>
              </label>

              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'0 14px'}}>
                <label style={labelSt}>
                  <div style={capSt}>{t('forms_auto_from')}</div>
                  <input value={draft.email_from_name} placeholder="OPS Assist Forms" maxLength={80}
                         onChange={e => set('email_from_name', e.target.value)} style={inputSt} />
                </label>
                <label style={labelSt}>
                  <div style={capSt}>{t('forms_auto_subject')}</div>
                  <input value={draft.email_subject} placeholder="New form submission: {form_name}" maxLength={200}
                         onChange={e => set('email_subject', e.target.value)} style={inputSt} />
                </label>
              </div>
              <div style={{...hintSt, marginTop:-10, marginBottom:14}}>{t('forms_auto_subject_hint')}</div>

              <label style={labelSt}>
                <div style={capSt}>{t('forms_auto_message')}</div>
                <textarea rows={3} value={draft.email_message} placeholder={t('forms_auto_message_ph')} maxLength={2000}
                          onChange={e => set('email_message', e.target.value)}
                          style={{...inputSt, fontFamily:'inherit', resize:'vertical'}} />
              </label>

              {/* what the email carries: the data, files attached, a PDF, or everything linked */}
              <label style={labelSt}>
                <div style={capSt}>{t('forms_auto_mode')}</div>
                <select value={draft.email_mode} onChange={e => set('email_mode', e.target.value)}
                        style={{...inputSt, cursor:'pointer'}}>
                  {/* thermotrack.io also offers FILES and FULL; they need the uploaded files on record,
                      which OPS Assist does not keep (the database accepts DATA and PDF) */}
                  {['DATA', 'PDF'].map(m => (
                    <option key={m} value={m}>{t('forms_auto_mode_' + m)}</option>
                  ))}
                </select>
                <div style={hintSt}>{t('forms_auto_mode_hint_' + draft.email_mode)}</div>
              </label>

              <div style={{fontSize:11.5, color:'var(--text-muted)', marginBottom:12}}>
                {stats.count
                  ? t('forms_auto_sent_stats').replace('{n}', stats.count).replace('{ts}', fmtTs(stats.last))
                  : t('forms_auto_never')}
              </div>

              {msg && (
                <div style={{fontSize:12.5, marginBottom:12, color: msg.ok ? 'var(--green)' : 'var(--red)'}}>{msg.text}</div>
              )}

              <div style={{display:'flex', gap:8, flexWrap:'wrap', alignItems:'center'}}>
                <button className="btn-primary auto-save" style={{width:'auto', padding:'9px 20px', opacity: dirty && !busy ? 1 : 0.45,
                                                                    cursor: dirty && !busy ? 'pointer' : 'not-allowed'}}
                        onClick={save} disabled={busy || !dirty}>
                  {busy ? t('btn_saving') : t('btn_save')}
                </button>
                <button style={{...btnSt, cursor: canTest && !busy ? 'pointer' : 'default', opacity: canTest ? 1 : 0.5}}
                        onClick={sendTest} disabled={busy || !canTest} title={canTest ? '' : t('forms_auto_test_hint')}>
                  {t('forms_auto_test')}
                </button>
              </div>
              {!canTest && <div style={hintSt}>{t('forms_auto_test_hint')}</div>}
            </div>
          </div>
        </div>

        {/* ── Email preview ── */}
        <div style={{flex:'1 1 340px', minWidth:300}}>
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap'}}>
            <div style={{fontSize:12, fontWeight:600, color:'var(--text-muted)', flex:1}}>{t('forms_auto_preview')}</div>
            <div style={{display:'flex', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden'}}>
              {[[false, t('forms_auto_prev_sample')], [true, t('forms_auto_prev_latest')]].map(([v, label]) => (
                <button key={String(v)} onClick={() => setLatest(v)}
                        style={{padding:'5px 12px', border:'none', fontSize:11.5, fontWeight:600, cursor:'pointer',
                                background: latest === v ? 'var(--accent)' : 'var(--input-bg)',
                                color: latest === v ? '#fff' : 'var(--text-muted)'}}>{label}</button>
              ))}
            </div>
          </div>
          {preview?.error && <div style={{fontSize:12.5, color:'var(--red)'}}>{preview.error}</div>}
          {preview && !preview.error && (
            <>
              <div style={{fontSize:12, marginBottom:8, color:'var(--text)'}}>
                <span style={{color:'var(--text-muted)'}}>{t('forms_auto_prev_subject')}:</span> {preview.subject}
              </div>
              {latest && !preview.usedEntry && (
                <div style={{fontSize:11.5, color:'var(--text-muted)', marginBottom:8}}>{t('forms_auto_prev_no_entry')}</div>
              )}
              {/* sandboxed: no scripts or navigation from owner-authored HTML blocks */}
              <iframe title={t('forms_auto_preview')} sandbox="" srcDoc={preview.html}
                      style={{width:'100%', height:640, border:'1px solid var(--border)', borderRadius:10, background:'#f1f5f9'}} />
            </>
          )}
          {!preview && (
            <div style={{padding:'30px 0', textAlign:'center', color:'var(--text-muted)', fontSize:12}}>{t('forms_loading')}</div>
          )}
        </div>
      </div>
    );
  }

  /* ───── Audit tab ─────
     Who changed this form, and when: forms.form_audit, written by triggers on the
     form, its fields and its automation settings. Submissions and automation runs
     are not edits and never appear here. English only, by design.               */
  const AUDIT_COLOURS = {
    'Form created':'#2563eb', 'Published':'#16a34a', 'Unpublished':'#d97706', 'Form deleted':'#dc2626',
    'Field added':'#0891b2', 'Field removed':'#dc2626', 'Automation removed':'#dc2626',
  };
  function FormAuditTab({ form, fields }) {
    const [rows, setRows] = useState(null);
    const [err,  setErr]  = useState('');
    const [tick, setTick] = useState(0);

    /* reloads whenever the form or its fields change, so a save shows up at once */
    useEffect(() => {
      let live = true;
      callRpc('form_audit', { audit_form_id: form.form_id })
        .then(d => { if (live) { setRows(Array.isArray(d) ? d : []); setErr(''); } })
        .catch(e => { if (live) setErr(e.message || String(e)); });
      return () => { live = false; };
    }, [form.form_id, form.form_version, form.enabled, form.form_name, fields, tick]);

    const fmt = iso => {
      try {
        return new Date(iso).toLocaleString(_currentLang || 'en', { day:'2-digit', month:'short', year:'numeric',
          hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false });
      } catch { return iso; }
    };
    const th = { textAlign:'left', padding:'8px 10px', fontSize:11, fontWeight:700, color:'var(--text-muted)',
                 borderBottom:'1px solid var(--border)', whiteSpace:'nowrap', textTransform:'uppercase', letterSpacing:'.04em' };
    const td = { padding:'8px 10px', fontSize:12.5, borderBottom:'1px solid var(--border)', verticalAlign:'top' };

    return (
      <div style={{padding:16}}>
        <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10}}>
          <div style={{flex:1, fontSize:12, color:'var(--text-muted)'}}>
            Every change made to this form, newest first. Submissions and automation runs are not listed.
          </div>
          <button onClick={() => setTick(x => x + 1)}
                  style={{padding:'6px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--input-bg)',
                          color:'var(--text)', fontSize:12, fontWeight:600, cursor:'pointer'}}>Refresh</button>
        </div>
        {err && <div style={{fontSize:13, color:'var(--red)'}}>{err}</div>}
        {!rows && !err && <div style={{padding:'26px 0', textAlign:'center', color:'var(--text-muted)', fontSize:13}}>Loading…</div>}
        {rows && !rows.length && <div style={{padding:'26px 0', textAlign:'center', color:'var(--text-muted)', fontSize:13}}>No changes recorded yet.</div>}
        {rows && rows.length > 0 && (
          <div style={{border:'1px solid var(--border)', borderRadius:10, overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={th}>Date / time</th><th style={th}>Change</th><th style={th}>Details</th>
                <th style={th}>By</th><th style={th}>Ver.</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{...td, whiteSpace:'nowrap', color:'var(--text-muted)', fontVariantNumeric:'tabular-nums'}}>{fmt(r.event_ts)}</td>
                    <td style={{...td, whiteSpace:'nowrap', fontWeight:700, color: AUDIT_COLOURS[r.action] || 'var(--text)'}}>{r.action}</td>
                    <td style={{...td, wordBreak:'break-word', minWidth:220}}>{r.detail || ''}</td>
                    <td style={{...td, whiteSpace:'nowrap', color:'var(--text-muted)'}}>{r.user_email || 'system'}</td>
                    <td style={{...td, color:'var(--text-muted)'}}>{r.form_version ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  /* "Building" with dots that keep running while the AI works */
  function RunningDots() {
    const [n, setN] = useState(1);
    useEffect(() => { const id = setInterval(() => setN(x => x % 6 + 1), 350); return () => clearInterval(id); }, []);
    return <span style={{display:'inline-block', minWidth:'3.4em', textAlign:'left'}}>{'.'.repeat(n)}</span>;
  }

  function CfgFormsPage() {
    useLang();

    const [forms,        setForms]        = useState([]);
    const [formsLoading, setFormsLoading] = useState(true);
    const [loadError,    setLoadError]    = useState('');
    const [filter,       setFilter]       = useState('');
    const [selectedId,   setSelectedId]   = useState(null);

    const [fields,       setFields]       = useState([]);
    const [fieldsLoading, setFieldsLoading] = useState(false);
    const [openFieldId,  setOpenFieldId]  = useState(null);
    const [types,        setTypes]        = useState([]);
    const [tab,          setTab]          = useState('fields');

    const [addOpen,      setAddOpen]      = useState(false);
    const [modal,        setModal]        = useState(null);   /* new | template | ai | delete */
    const [newName,      setNewName]      = useState('');
    const [templateId,   setTemplateId]   = useState('');
    const [templates,    setTemplates]    = useState([]);
    const [aiText,       setAiText]       = useState('');
    const [aiStage,      setAiStage]      = useState('');     /* '' | queued | building */
    const [busy,         setBusy]         = useState(false);
    const [modalError,   setModalError]   = useState('');

    const [settings,     setSettings]     = useState(null);
    const [settingsBusy, setSettingsBusy] = useState(false);
    const [settingsMsg,  setSettingsMsg]  = useState('');
    const [dragId,       setDragId]       = useState(null);
    const [menuId,       setMenuId]       = useState(null);   /* the form whose ⋮ menu is open */
    const [menuPos,      setMenuPos]      = useState({ top: 0, left: 0 });
    const [catalogue,    setCatalogue]    = useState(null);   /* { catalogue_code, catalogue_url, included[] } */
    const loadCatalogue = () => callRpc('form_catalogue_get', {})
      .then(d => setCatalogue(Array.isArray(d) ? d[0] : d))
      .catch(e => { setCatalogue(null); setSettingsMsg(e.message || String(e)); });
    const setInCatalogue = async (formId, on) => {
      try {
        await callRpc('form_catalogue_include', { set_form_id: formId, set_included: on });
        setCatalogue(c => c && { ...c, included: on ? [...(c.included || []), formId] : (c.included || []).filter(x => x !== formId) });
      } catch (e) { setSettingsMsg(e.message || String(e)); }
    };
    const aiPollRef = useRef(null);

    const selected = forms.find(f => f.form_id === selectedId) || null;

    /* ───── loads ───── */
    const loadForms = (keepId) => {
      setFormsLoading(true);
      return callRpc('form_get_list', {})
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setForms(list);
          setLoadError('');
          const keep = keepId && list.find(f => f.form_id === keepId) ? keepId : null;
          setSelectedId(prev => keep || (list.find(f => f.form_id === prev) ? prev : null));
          return list;
        })
        .catch(e => { setLoadError(e.message || String(e)); return []; })
        .finally(() => setFormsLoading(false));
    };

    const loadFields = (formId) => {
      if (!formId) { setFields([]); return Promise.resolve([]); }
      setFieldsLoading(true);
      return callRpc('form_fields_list', { get_form_id: formId })
        .then(data => { const l = Array.isArray(data) ? data : []; setFields(l); return l; })
        .catch(() => { setFields([]); return []; })
        .finally(() => setFieldsLoading(false));
    };

    useEffect(() => { loadForms(); loadCatalogue(); }, []);
    /* the countdown at the top of the menu: the list (names, Live / Draft) reloads quietly,
       keeping the selected form and anything being edited */
    useRefresh(() => { callRpc('form_get_list', {}).then(d => { if (Array.isArray(d)) setForms(d); }).catch(() => {}); });
    /* a failed first load (no connection, an old session) is retried when Settings is opened */
    useEffect(() => { if (tab === 'settings' && !catalogue) loadCatalogue(); }, [tab, selectedId]);

    useEffect(() => {
      callRpc('form_field_types', {})
        .then(d => setTypes(Array.isArray(d) ? d : []))
        .catch(() => {});
      return () => clearTimeout(aiPollRef.current);
    }, []);

    useEffect(() => {
      setOpenFieldId(null);
      setSettingsMsg('');
      loadFields(selectedId);
      setSettings(selected ? {
        form_name:              selected.form_name || '',
        form_description:       selected.form_description || '',
        form_no:                selected.form_no || '',
        form_background_colour: selected.form_background_colour || '#000000',
        form_body_colour:       selected.form_body_colour || '#FFFFFF',
        /* No longer editable in the UI, but form_update requires a valid
           M/T/D value and the preview sizes its frame from it. */
        layout:                 selected.layout || 'M',
        is_public:              !!selected.is_public,
        logo_pic_file_name:     selected.logo_pic_file_name || '',
      } : null);
    }, [selectedId]);

    /* ───── form level actions ───── */
    /* the account cannot hold two forms with one name — said here before the server says it */
    const nameTaken = (name) => forms.some(f => (f.form_name || '').trim().toLowerCase() === (name || '').trim().toLowerCase());

    const copyForm = async () => {
      if (nameTaken(newName)) { setModalError(t('forms_name_exists')); return; }
      setBusy(true); setModalError('');
      try {
        const res = await callRpc('form_copy', { copy_form_id: menuId, new_name: newName.trim() });
        const id = Array.isArray(res) ? res[0]?.form_id : res?.form_id;
        await loadForms(id);
        if (id) setSelectedId(id);
        setModal(null); setNewName(''); setMenuId(null);
      } catch (e) {
        setModalError(e.message || String(e));
      } finally { setBusy(false); }
    };

    const createForm = async () => {
      if (nameTaken(newName)) { setModalError(t('forms_name_exists')); return; }
      setBusy(true); setModalError('');
      try {
        const body = modal === 'template'
          ? { form_description: newName.trim(), form_template_id: templateId }
          : { form_description: newName.trim() };
        const res = await callRpc(modal === 'template' ? 'form_add_from_template' : 'form_add', body);
        const id = Array.isArray(res) ? res[0]?.form_id : res?.form_id;
        await loadForms(id);
        if (id) setSelectedId(id);
        setModal(null); setNewName(''); setTemplateId('');
      } catch (e) {
        setModalError(e.message || String(e));
      } finally { setBusy(false); }
    };

    const openTemplateModal = () => {
      setModal('template'); setNewName(''); setModalError(''); setTemplateId('');
      callRpc('form_templates', {})
        .then(d => {
          const list = Array.isArray(d) ? d : [];
          setTemplates(list);
          if (list.length) setTemplateId(list[0].form_template_id);
        })
        .catch(e => setModalError(e.message || String(e)));
    };

    /* form_ai_builder queues the request; utils.ai_queue is worked by a
       separate processor, so poll form_ai_status until it lands.        */
    const startAiBuild = async () => {
      if (nameTaken(newName.trim() || aiText.trim().slice(0, 40))) { setModalError(t('forms_name_exists')); return; }
      setBusy(true); setModalError(''); setAiStage('queued');
      try {
        const res = await callRpc('form_ai_builder', { suggestion: aiText.trim() });
        const row = Array.isArray(res) ? res[0] : res;
        if (!row?.ai_task_accepted) throw new Error(row?.al_error || t('forms_ai_rejected'));

        const started = Date.now();
        const poll = async () => {
          try {
            const st  = await callRpc('form_ai_status', { check_ai_queue_id: row.ai_task_id });
            const s   = Array.isArray(st) ? st[0] : st;
            if (s?.ai_failed) throw new Error(s.ai_error || t('forms_ai_failed'));
            if (s?.ai_ready) {
              setAiStage('building');
              const built = await callRpc('form_ai_build', {
                build_ai_queue_id: row.ai_task_id,
                form_name:         (newName.trim() || aiText.trim().slice(0, 40)),
              });
              const id = Array.isArray(built) ? built[0]?.form_id : built?.form_id;
              await loadForms(id);
              if (id) setSelectedId(id);
              setModal(null); setAiText(''); setNewName(''); setAiStage(''); setBusy(false);
              return;
            }
            if (Date.now() - started > 120000) throw new Error(t('forms_ai_timeout'));
            aiPollRef.current = setTimeout(poll, 3000);
          } catch (e) {
            setModalError(e.message || String(e)); setAiStage(''); setBusy(false);
          }
        };
        aiPollRef.current = setTimeout(poll, 2500);
      } catch (e) {
        setModalError(e.message || String(e)); setAiStage(''); setBusy(false);
      }
    };

    const deleteForm = async () => {
      setBusy(true); setModalError('');
      try {
        await callRpc('form_delete', { delete_form_id: selectedId });
        setSelectedId(null);
        await loadForms();
        setModal(null);
      } catch (e) {
        setModalError(e.message || String(e));
      } finally { setBusy(false); }
    };

    const togglePublish = async () => {
      if (!selected) return;
      const endpoint = selected.enabled ? 'form_disable' : 'form_publish';
      const key      = selected.enabled ? 'disable_form_id' : 'publish_form_id';
      setSettingsBusy(true);
      try {
        await callRpc(endpoint, { [key]: selected.form_id });
        setForms(fs => fs.map(f => f.form_id === selected.form_id ? { ...f, enabled: !f.enabled } : f));
      } catch (e) {
        setSettingsMsg(e.message || String(e));
      } finally { setSettingsBusy(false); }
    };

    /* the logo: read the chosen file, refuse a silly size here (the server checks again), send it */
    const [logoBusy, setLogoBusy] = useState(false);
    const pickLogo = (file) => {
      if (!file || !selected) return;
      setSettingsMsg('');
      if (file.size > 2 * 1024 * 1024) {
        setSettingsMsg(t('forms_s_logo_toobig').replace('{size}', (file.size / 1024 / 1024).toFixed(1) + ' MB'));
        return;
      }
      const rd = new FileReader();
      rd.onload = async () => {
        setLogoBusy(true);
        try {
          await callRpc('form_logo_set', { set_form_id: selected.form_id, file_name: file.name, file_base64: String(rd.result) });
          await loadForms(selected.form_id);
        } catch (e) { setSettingsMsg(e.message || String(e)); }
        finally { setLogoBusy(false); }
      };
      rd.readAsDataURL(file);
    };
    const clearLogo = async () => {
      setLogoBusy(true); setSettingsMsg('');
      try { await callRpc('form_logo_clear', { set_form_id: selected.form_id }); await loadForms(selected.form_id); }
      catch (e) { setSettingsMsg(e.message || String(e)); }
      finally { setLogoBusy(false); }
    };

    const saveSettings = async () => {
      if (!selected || !settings) return;
      setSettingsBusy(true); setSettingsMsg('');
      try {
        await callRpc('form_update', {
          update_form_id:             selected.form_id,
          set_form_name:              settings.form_name.trim(),
          set_form_description:       settings.form_description.trim() || null,
          set_form_background_colour: settings.form_background_colour,
          set_form_body_colour:       settings.form_body_colour,
          set_enabled:                !!selected.enabled,
          set_is_public:              !!settings.is_public,
          set_logo_pic_file_name:     selected.logo_pic_file_name || null,   /* the logo panel owns this */
          set_logo_pic_file_id:       selected.logo_pic_file_id || null,
          set_layout:                 settings.layout,
          set_form_no:                settings.form_no || null,
        });
        await loadForms(selected.form_id);
      } catch (e) {
        setSettingsMsg(e.message || String(e));
      } finally { setSettingsBusy(false); }
    };

    /* ───── field level actions ───── */
    const addField = async (type) => {
      setAddOpen(false);
      try {
        const res = await callRpc('form_field_add', {
          add_form_id: selectedId, add_form_field_type: type,
        });
        const id = Array.isArray(res) ? res[0]?.form_field_id : res?.form_field_id;
        await loadFields(selectedId);
        if (id) setOpenFieldId(id);
      } catch (e) { setLoadError(e.message || String(e)); }
    };

    const saveField = async (field, draft) => {
      /* form_build makes one sub_field per list_data item, so a plain
         field saved with no items would render nothing on the public
         form — fall back to the title for everything but option/HTML. */
      const plain = !isOptionType(draft.data_type_name) && draft.data_type_name !== 'HTML';
      const sep    = draft.data_type_name === 'SEPARATOR';
      /* Types with no Items box are always one control: keep list_data in
         step with the title, or a rename would leave the old label behind
         where the user can no longer see or edit it. */
      const single = !sep && hidesItems(draft.data_type_name);
      const items = sep ? '' : single ? (draft.field_title || '').trim()
                  : textToListData(draft.items) || (plain ? (draft.field_title || '').trim() : '');
      await callRpc('form_field_update', {
        set_form_id:       selectedId,
        set_form_field_id: field.form_field_id,
        set_field_title:   draft.field_title,
        set_field_caption: draft.field_caption,
        set_required:      sep ? false : !!draft.required,
        set_list_data:     items || null,
        set_field_colour:  draft.field_colour,
        set_field_column:  Number(draft.field_column) || 1,
        set_field_type:    draft.data_type_name,
        set_field_override_name: (draft.field_override_name || '').trim() || null,
      });
      await callRpc('form_field_config_set', {
        set_form_id:       selectedId,
        set_form_field_id: field.form_field_id,
        set_field_config:  buildFieldConfig(draft.data_type_name, draft),
      });
      await loadFields(selectedId);
      setOpenFieldId(null);
    };

    const deleteField = async (field) => {
      try {
        await callRpc('form_fields_delete', {
          delete_form_id: selectedId, delete_field_id: field.form_field_id,
        });
        setOpenFieldId(null);
        await loadFields(selectedId);
      } catch (e) { setLoadError(e.message || String(e)); }
    };

    const onDragStart = (e, field) => { setDragId(field.form_field_id); e.dataTransfer.effectAllowed = 'move'; };
    const onDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const onDrop = async (e, target) => {
      e.preventDefault();
      const from = dragId;
      setDragId(null);
      if (!from || from === target.form_field_id) return;
      const ids   = fields.map(f => f.form_field_id);
      const next  = ids.filter(id => id !== from);
      next.splice(ids.indexOf(target.form_field_id) > ids.indexOf(from)
        ? next.indexOf(target.form_field_id) + 1
        : next.indexOf(target.form_field_id), 0, from);
      /* optimistic — the server answers with the order it stored */
      setFields(fs => next.map(id => fs.find(f => f.form_field_id === id)).filter(Boolean));
      try {
        await callRpc('form_fields_reorder', {
          reorder_form_id: selectedId, ordered_field_ids: next,
        });
      } catch (err) { setLoadError(err.message || String(err)); }
      loadFields(selectedId);
    };

    /* the public pages follow the app's language; English is their default already */
    const withLang = (url) => (!url || !_currentLang || _currentLang === 'en') ? url : url + (url.includes('?') ? '&' : '?') + 'lang=' + _currentLang;
    /* one row of the URL Sharing panel: the address, Copy link and Open (greyed when not usable) */
    const [shareCopied, setShareCopied] = useState('');
    const shareRow = (kind, url, enabled, offHint) => {
      const live = !!(url && enabled);
      const offSt = live ? {} : { opacity:0.45, cursor:'not-allowed' };
      return (
        <div className={'s-share-row s-share-' + kind}>
          <div style={{background:'var(--tabs-bg)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px',
                       fontSize:12, color: live ? 'var(--text)' : 'var(--text-muted)', wordBreak:'break-all', marginBottom:8}}>
            {url || '—'}
          </div>
          <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
            <button className="s-share-copy" style={{...btnSt, ...offSt}} disabled={!live} title={live ? '' : offHint}
                    onClick={() => { navigator.clipboard?.writeText(withLang(url)); setShareCopied(kind); setTimeout(() => setShareCopied(c => c === kind ? '' : c), 2000); }}>
              {t('forms_copy_link')}
            </button>
            {live
              ? <a className="s-share-open" href={withLang(url)} target="_blank" rel="noreferrer"
                   style={{...btnSt, textDecoration:'none', display:'inline-block'}}>{t('forms_open')}</a>
              : <button className="s-share-open" style={{...btnSt, ...offSt}} disabled title={offHint}>{t('forms_open')}</button>}
            {shareCopied === kind && <span style={{fontSize:12, color:'#16a34a', fontWeight:600}}>✓ {t('fe_copied')}</span>}
            {!live && offHint && <span style={{fontSize:11.5, color:'var(--text-muted)'}}>{offHint}</span>}
          </div>
        </div>
      );
    };
    const copyLink = () => {
      if (!selected?.form_url || !selected.enabled) return;
      navigator.clipboard?.writeText(withLang(selected.form_url));
      setSettingsMsg(t('forms_link_copied'));
    };

    const headBtn = { display:'inline-flex', alignItems:'center', gap:6, height:32, lineHeight:'16px', boxSizing:'border-box' };
    const shown = forms.filter(f => (f.form_name || '').toLowerCase().includes(filter.trim().toLowerCase()));
    const btnSt = { padding:'7px 14px', borderRadius:8, border:'1px solid var(--border)',
                    background:'var(--input-bg)', color:'var(--text)', fontSize:12.5,
                    fontWeight:600, cursor:'pointer' };
    const inputSt = { width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid var(--input-border)',
                      background:'var(--input-bg)', color:'var(--text)', fontSize:13, boxSizing:'border-box' };
    const lockedSt = { opacity:0.5, cursor:'not-allowed' };
    const [addQ, setAddQ] = useState('');   /* greyed out while the AI builds */

    return (
      <div>
        <div className="lv-header">
          <div className="lv-header-left">
            <h2>{t('forms_title')}</h2>
            <p>{t('forms_sub')}</p>
          </div>
          <div className="forms-head-actions" style={{display:'flex', gap:8, flexWrap:'wrap', alignItems:'center'}}>
            <button type="button" className="fm-btn forms-tpl-btn" onClick={openTemplateModal}>{t('forms_from_template')}</button>
            <button type="button" className="fm-btn forms-ai-btn" onClick={() => { setModal('ai'); setAiText(''); setNewName(''); setModalError(''); setAiStage(''); }}>
              ✨ {t('forms_ai_build')}
            </button>
            <button type="button" className="fm-btn primary forms-new-btn" onClick={() => { setModal('new'); setNewName(''); setModalError(''); }}>
              {t('forms_new')}
            </button>
          </div>
        </div>

        <div>
          {loadError && (
            <div style={{background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:8,
                         padding:'10px 14px', marginBottom:14, fontSize:13, color:'var(--red)'}}>
              {loadError}
            </div>
          )}

          <div style={{display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap'}}>

            {/* ── Left: the form list ── */}
            <div style={{flex:'0 0 280px', minWidth:250, background:'var(--card-bg)', border:'1px solid var(--border)',
                         borderRadius:12, overflow:'hidden'}}>
              <div style={{padding:'12px 12px 10px', borderBottom:'1px solid var(--border)'}}>
                <SearchBox value={filter} onChange={setFilter} placeholder={t('forms_search')} width={'100%'} className="forms-search" />
              </div>
              <div style={{maxHeight:'calc(100vh - 260px)', overflowY:'auto'}}>
                {formsLoading && (
                  <div style={{padding:'26px 14px', textAlign:'center', color:'var(--text-muted)', fontSize:13}}>
                    {t('forms_loading')}
                  </div>
                )}
                {!formsLoading && !shown.length && (
                  <div style={{padding:'26px 14px', textAlign:'center', color:'var(--text-muted)', fontSize:13}}>
                    {forms.length ? t('forms_no_match') : t('forms_none')}
                  </div>
                )}
                {shown.map(f => (
                  <div key={f.form_id} onClick={() => setSelectedId(f.form_id)}
                       style={{
                         position:'relative',
                         padding:'11px 13px', cursor:'pointer', borderBottom:'1px solid var(--border)',
                         background: f.form_id === selectedId ? 'var(--nav-hover)' : 'transparent',
                         borderLeft: `3px solid ${f.form_id === selectedId ? 'var(--accent)' : 'transparent'}`,
                       }}>
                    {/* two columns, top-aligned: name / description / number on the left, badge and ⋮ on the right */}
                    <div style={{display:'flex', alignItems:'flex-start', gap:8}}>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:14.5, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                     lineHeight:'20px'}}>{f.form_name}</div>
                        {(f.form_description || '').trim() && (
                          <div className="form-desc" style={{fontSize:11, color:'var(--text-muted)', marginTop:2, lineHeight:1.35,
                                                             overflowWrap:'anywhere'}}>
                            {f.form_description.trim()}
                          </div>
                        )}
                        {(f.form_no || f.is_public) && (
                          <div style={{fontSize:11, color:'var(--text-muted)', marginTop:2}}>
                            {f.form_no || ''}{f.form_no && f.is_public ? ' · ' : ''}{f.is_public ? t('forms_public') : ''}
                          </div>
                        )}
                      </div>
                      <span style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2, flexShrink:0}}>
                        <span className={'fm-pill ' + (f.enabled ? 'ok' : 'muted')} style={{fontSize:11.5, padding:'3px 9px'}}>{f.enabled ? t('forms_live') : t('forms_draft')}</span>
                        <span style={{display:'flex', alignItems:'center', gap:2}}>
                        {/* ⋮ under the badge: Copy Form / Delete */}
                        <button className="form-more" title={t('forms_more')} aria-label={t('forms_more')}
                                onClick={e => { e.stopPropagation();
                                                /* fixed to the screen, or the list's scroll box clips it on the last rows */
                                                const r = e.currentTarget.getBoundingClientRect();
                                                setMenuPos({ top: Math.min(r.bottom + 4, window.innerHeight - 96), left: Math.max(8, r.right - 170) });
                                                setMenuId(m => m === f.form_id ? null : f.form_id); }}
                                style={{border:'none', background:'none', cursor:'pointer', color:'var(--text-muted)',
                                        fontSize:16, lineHeight:1, padding:'2px 6px', borderRadius:6}}>⋮</button>
                        </span>
                      </span>
                    </div>
                    {menuId === f.form_id && (
                      <>
                        <div onClick={e => { e.stopPropagation(); setMenuId(null); }} style={{position:'fixed', inset:0, zIndex:30}} />
                        <div className="form-menu" onClick={e => e.stopPropagation()}
                             style={{position:'fixed', top:menuPos.top, left:menuPos.left, zIndex:31, minWidth:170,
                                     background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:10,
                                     boxShadow:'0 12px 30px rgba(0,0,0,0.3)', overflow:'hidden'}}>
                          <div onClick={() => { setModal('copy'); setNewName(''); setModalError(''); }}
                               style={{padding:'9px 14px', fontSize:12.5, cursor:'pointer', borderBottom:'1px solid var(--border)'}}>
                            {t('forms_copy')}
                          </div>
                          <div onClick={() => { setSelectedId(f.form_id); setMenuId(null); setModal('delete'); setModalError(''); }}
                               style={{padding:'9px 14px', fontSize:12.5, cursor:'pointer', color:'var(--red)'}}>
                            {t('btn_delete')}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right: the editor ── */}
            <div style={{flex:'1 1 520px', minWidth:340}}>
              {!selected && (
                <div className="forms-empty" style={{background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:12, padding:'48px 32px', textAlign:'center'}}>
                  <div style={{width:56, height:56, borderRadius:14, margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(57,120,255,0.14)', color:'#7aa5ff'}}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  </div>
                  <div style={{fontSize:20, fontWeight:700}}>{t('forms_empty_title')}</div>
                  <div style={{fontSize:14, color:'var(--text-muted)', margin:'6px auto 24px', maxWidth:520}}>{t('forms_empty_sub')}</div>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:14, maxWidth:760, margin:'0 auto'}}>
                    {[['new', t('forms_new'), t('forms_empty_new'), () => { setModal('new'); setNewName(''); setModalError(''); }, <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>],
                      ['tpl', t('forms_from_template'), t('forms_empty_tpl'), openTemplateModal, <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>],
                      ['ai', t('forms_ai_build'), t('forms_empty_ai'), () => { setModal('ai'); setAiText(''); setNewName(''); setModalError(''); setAiStage(''); }, <path d="M12 3l1.9 5.7a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/>]].map(([k, h, sub, fn, ic]) => (
                      <button key={k} type="button" className={'forms-empty-' + k} onClick={fn}
                        style={{textAlign:'left', padding:'18px', borderRadius:12, border:'1px solid var(--border)', background:'var(--input-bg)', color:'var(--text)', cursor:'pointer', font:'inherit'}}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ic}</svg>
                        <div style={{fontSize:15, fontWeight:700, marginTop:10}}>{h}</div>
                        <div style={{fontSize:13, color:'var(--text-muted)', marginTop:4, lineHeight:1.45}}>{sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selected && (
                <div style={{background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:12}}>
                  {/* editor header */}
                  <div style={{padding:'14px 16px', borderBottom:'1px solid var(--border)',
                               display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
                    <div style={{flex:1, minWidth:140}}>
                      <div style={{fontSize:19, fontWeight:700, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>{selected.form_name}
                        <span className={'fm-pill ' + (selected.enabled ? 'ok' : 'muted')}>{selected.enabled ? t('forms_live') : t('forms_draft')}</span></div>
                      <div style={{fontSize:13, color:'var(--text-muted)', marginTop:3}}>
                        {t('forms_version')} {selected.form_version} · {fields.length} {t('forms_fields_count')}{selected.form_no ? ` · ${selected.form_no}` : ''}
                      </div>
                    </div>
                    <button type="button" className="fm-btn forms-publish" onClick={togglePublish} disabled={settingsBusy}>
                      {selected.enabled ? t('forms_unpublish') : t('forms_publish')}
                    </button>
                    {/* a draft has no public page yet: the link would not open */}
                    {selected.enabled ? (
                      <>
                        <button type="button" className="fm-btn" onClick={copyLink}>{t('forms_copy_link')}</button>
                        <a href={withLang(selected.form_url)} target="_blank" rel="noreferrer" className="fm-btn" style={{textDecoration:'none'}}>{t('forms_open')}</a>
                      </>
                    ) : (
                      <>
                        <button type="button" className="fm-btn" disabled title={t('forms_publish_first')}>{t('forms_copy_link')}</button>
                        <button type="button" className="fm-btn" disabled title={t('forms_publish_first')}>{t('forms_open')}</button>
                      </>
                    )}
                    <RowMenu className="forms-head-more" label={t('lbl_more_actions') + ': ' + selected.form_name} items={[
                      { key:'copy', label:t('forms_copy'), onClick: () => { setMenuId(selected.form_id); setModal('copy'); setNewName(''); setModalError(''); },
                        icon:<><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></> },
                      { key:'delete', label:t('btn_delete'), danger:true, onClick: () => { setModal('delete'); setModalError(''); },
                        icon:<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></> },
                    ]} />
                  </div>

                  {/* tabs */}
                  <div style={{display:'flex', gap:4, padding:'10px 16px 0', borderBottom:'1px solid var(--border)'}}>
                    {/* Automation only when config.js switches an integration on (OA_CONFIG.Automation_integrations) */}
                    {[['fields', t('forms_tab_fields')], ['settings', t('forms_tab_settings')],
                      AUTO_TABS().length ? ['automation', t('forms_tab_automation')] : null, ['audit', t('forms_tab_audit')]].filter(Boolean).map(([k, label]) => (
                      <button key={k} onClick={() => setTab(k)}
                              role="tab" aria-selected={tab === k}
                              style={{
                                padding:'10px 16px', border:'none', background:'none', cursor:'pointer',
                                fontSize:14.5, fontWeight:600,
                                color: tab === k ? 'var(--accent)' : 'var(--text-muted)',
                                borderBottom: `2px solid ${tab === k ? 'var(--accent)' : 'transparent'}`,
                              }}>{label}</button>
                    ))}
                  </div>

                  {tab === 'fields' && (
                    <div style={{display:'flex', gap:16, padding:16, alignItems:'flex-start', flexWrap:'wrap'}}>
                      <div style={{flex:'1 1 380px', minWidth:300}}>
                        <div style={{position:'relative', marginBottom:12}}>
                          <button type="button" className="fm-btn primary forms-add-field" aria-haspopup="dialog" aria-expanded={addOpen}
                                  onClick={() => { setAddQ(''); setAddOpen(o => !o); }}>
                            + {t('forms_add_field')}
                          </button>
                          {addOpen && (() => {
                            const GROUPS = [
                              ['sensor', ['SENSOR_VALUE', 'SENSOR_GRAPH', 'DYNAMIC_QR', 'QR', 'BARCODE', 'NFC_SCAN', 'METER_READING', 'GPS_POINT', 'AUTO_DATA']],
                              ['basic',  ['TEXT', 'MEMO', 'NUMBER', 'CURRENCY', 'PHONE', 'EMAIL', 'URL', 'ADDRESS']],
                              ['choice', ['LIST', 'CHECKBOX', 'YESNO', 'SLIDER', 'SCORE', 'EMOJI']],
                              ['time',   ['DATE', 'TIME', 'TIMESTAMP', 'DURATION']],
                              ['media',  ['IMAGE', 'PHOTO_ANNOTATED', 'FILE', 'AUDIO_NOTE', 'SIGN', 'DRAW', 'HOTSPOT']],
                              ['layout', ['LABEL', 'INFO', 'HTML']],
                            ];
                            const known = new Set(GROUPS.flatMap(g => g[1]));
                            const q = addQ.trim().toLowerCase();
                            const hit = ty => !q || formTypeLabel(ty.data_type_name, types).toLowerCase().includes(q) || (ty.data_type_description || '').toLowerCase().includes(q);
                            const groups = [...GROUPS, ['other', types.map(x => x.data_type_name).filter(n => !known.has(n))]]
                              .map(([g, names]) => [g, names.map(n => types.find(x => x.data_type_name === n)).filter(Boolean).filter(hit)]).filter(([, l]) => l.length);
                            return (
                            <>
                              <div onClick={() => setAddOpen(false)} style={{position:'fixed', inset:0, zIndex:20}} />
                              <div role="dialog" aria-label={t('forms_add_field')} className="forms-add-pop"
                                   onKeyDown={e => { if (e.key === 'Escape') setAddOpen(false); }}
                                   style={{position:'absolute', top:'100%', left:0, marginTop:6, zIndex:21, width:380,
                                           background:'var(--card-bg)', border:'1px solid var(--border)',
                                           borderRadius:12, boxShadow:'0 12px 30px rgba(0,0,0,0.35)', padding:10}}>
                                <input autoFocus type="search" className="fm-inp forms-add-search" value={addQ} onChange={e => setAddQ(e.target.value)}
                                  placeholder={t('forms_add_search')} aria-label={t('forms_add_search')} style={{height:38, padding:'0 12px', marginBottom:6}} />
                                <div style={{maxHeight:360, overflowY:'auto'}}>
                                  {groups.map(([g, list]) => (
                                    <div key={g}>
                                      <div style={{fontSize:11.5, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.06em', padding:'10px 8px 4px'}}>{t('forms_grp_' + g)}</div>
                                      {list.map(ty => {
                                        const label = formTypeLabel(ty.data_type_name, types), desc = ty.data_type_description || '';
                                        return (
                                          <button key={ty.data_type_name} type="button" className="forms-add-item" onClick={() => addField(ty.data_type_name)}
                                            style={{display:'block', width:'100%', textAlign:'left', padding:'8px 10px', border:'none', borderRadius:8, background:'transparent', color:'var(--text)', cursor:'pointer', font:'inherit'}}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--nav-hover)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <span style={{display:'block', fontSize:14, fontWeight:600}}>{label}</span>
                                            {desc && desc.toLowerCase() !== label.toLowerCase() && <span style={{display:'block', fontSize:12, color:'var(--text-muted)', marginTop:1}}>{desc}</span>}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  ))}
                                  {!groups.length && <div style={{padding:'14px 8px', fontSize:13.5, color:'var(--text-muted)'}}>{t('lbl_no_data')}</div>}
                                </div>
                              </div>
                            </>
                            );
                          })()}
                        </div>

                        {fieldsLoading && (
                          <div style={{padding:'26px 14px', textAlign:'center', color:'var(--text-muted)', fontSize:13}}>
                            {t('forms_loading')}
                          </div>
                        )}
                        {!fieldsLoading && !fields.length && (
                          <div style={{padding:'26px 14px', textAlign:'center', color:'var(--text-muted)', fontSize:13}}>
                            {t('forms_no_fields')}
                          </div>
                        )}
                        {fields.map(f => (
                          <FormFieldRow key={f.form_field_id} field={f} types={types}
                                        open={openFieldId === f.form_field_id}
                                        onOpen={setOpenFieldId} onSave={saveField} onDelete={deleteField}
                                        onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
                                        dragging={dragId === f.form_field_id} />
                        ))}
                        {fields.length > 1 && (
                          <div style={{fontSize:11, color:'var(--text-muted)', marginTop:8}}>{t('forms_drag_hint')}</div>
                        )}
                      </div>

                      <div style={{flex:'1 1 340px', minWidth:300}}>
                        <div style={{fontSize:12, fontWeight:600, color:'var(--text-muted)'}}>{t('forms_preview')}</div>
                        <FormPreview form={{ ...selected, ...(settings || {}) }} fields={fields} />
                      </div>
                    </div>
                  )}

                  {tab === 'automation' && AUTO_TABS().length > 0 && <FormAutomationTab form={selected} fields={fields} />}
                  {tab === 'audit' && <FormAuditTab form={selected} fields={fields} />}

                  {tab === 'settings' && settings && (
                    <div style={{display:'flex', gap:20, padding:16, alignItems:'flex-start', flexWrap:'wrap'}}>
                    {LOGO_UPLOAD && <div className="s-logo-panel" style={{flex:'0 0 260px', order:2, border:'1px solid var(--border)', borderRadius:10, padding:14}}>
                      <div style={{fontSize:12, fontWeight:700, color:'var(--text-muted)', marginBottom:10}}>{t('forms_s_logo_panel')}</div>
                      <div style={{height:120, borderRadius:8, border:'1px dashed var(--border)', background:'var(--input-bg)',
                                   display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', marginBottom:10}}>
                        {/^https:\/\//.test(selected.logo_pic_file_name || '')
                          ? <img className="s-logo-img" src={selected.logo_pic_file_name} alt="" style={{maxWidth:'100%', maxHeight:'100%', objectFit:'contain'}} />
                          : <span style={{fontSize:11.5, color:'var(--text-muted)', textAlign:'center', padding:'0 10px'}}>{t('forms_s_logo_none')}</span>}
                      </div>
                      <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                        <label className="s-logo-select" style={{...btnSt, cursor: logoBusy ? 'wait' : 'pointer', opacity: logoBusy ? 0.5 : 1}}>
                          {logoBusy ? t('btn_saving') : t('forms_s_logo_select')}
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" disabled={logoBusy} style={{display:'none'}}
                                 onChange={e => { pickLogo(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                        </label>
                        {/^https:\/\//.test(selected.logo_pic_file_name || '') && (
                          <button className="s-logo-remove" style={{...btnSt, color:'var(--red)'}} onClick={clearLogo} disabled={logoBusy}>{t('forms_s_logo_remove')}</button>
                        )}
                      </div>
                      <div style={{fontSize:11, color:'var(--text-muted)', marginTop:8, lineHeight:1.45}}>{t('forms_s_logo_hint')}</div>
                    </div>}
                    <div style={{flex:'1 1 360px', maxWidth:620, order:1}}>
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14}}>
                        <label style={{fontSize:12}}>
                          <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_s_name')}</div>
                          <input value={settings.form_name} style={inputSt}
                                 onChange={e => setSettings(s => ({ ...s, form_name: e.target.value }))} />
                        </label>
                        {/* Full width so it sits directly under the name */}
                        <label style={{fontSize:12, gridColumn:'1 / -1'}}>
                          <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_s_description')}</div>
                          <textarea rows={2} value={settings.form_description}
                                    placeholder={t('forms_s_description_ph')}
                                    onChange={e => setSettings(s => ({ ...s, form_description: e.target.value }))}
                                    style={{...inputSt, fontFamily:'inherit', resize:'vertical'}} />
                        </label>
                        <label style={{fontSize:12}}>
                          <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_s_number')}</div>
                          <input value={settings.form_no} style={inputSt}
                                 onChange={e => setSettings(s => ({ ...s, form_no: e.target.value }))} />
                        </label>
                        <label style={{fontSize:12}}>
                          <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_s_background')}</div>
                          <input type="color" value={settings.form_background_colour}
                                 onChange={e => setSettings(s => ({ ...s, form_background_colour: e.target.value }))}
                                 style={{...inputSt, height:36, padding:2}} />
                        </label>
                        <label style={{fontSize:12}}>
                          <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_s_body')}</div>
                          <input type="color" value={settings.form_body_colour}
                                 onChange={e => setSettings(s => ({ ...s, form_body_colour: e.target.value }))}
                                 style={{...inputSt, height:36, padding:2}} />
                        </label>
                      </div>

                      <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, marginTop:16, cursor:'pointer'}}>
                        <input type="checkbox" checked={settings.is_public}
                               onChange={e => setSettings(s => ({ ...s, is_public: e.target.checked }))} />
                        {t('forms_s_public')}
                      </label>

                      {/* URL Sharing: the account's catalogue first, then this form's own public link */}
                      <div className="s-share-panel" style={{marginTop:16, border:'1px solid var(--border)', borderRadius:10, padding:14}}>
                        <div style={{fontSize:12, fontWeight:700, color:'var(--text-muted)', marginBottom:12}}>{t('forms_s_share_title')}</div>

                        <div className="s-share-cat">
                          <div style={{fontSize:13, fontWeight:700, marginBottom:8}}>{t('forms_s_share_cat')}</div>
                          <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', marginBottom:8}}>
                            <input type="checkbox" className="s-catalogue"
                                   checked={!!(catalogue && (catalogue.included || []).includes(selected.form_id))}
                                   disabled={!catalogue}
                                   onChange={e => setInCatalogue(selected.form_id, e.target.checked)} />
                            {t('forms_s_catalogue')}
                          </label>
                          {/* the catalogue only lists this form once it is live and included */}
                          {shareRow('cat', catalogue && catalogue.catalogue_url,
                            !!(catalogue && selected.enabled && (catalogue.included || []).includes(selected.form_id)),
                            !selected.enabled ? t('forms_publish_first') : t('forms_cat_include_first'))}
                          <div style={{fontSize:11, color:'var(--text-muted)', marginTop:6}}>{t('forms_s_catalogue_hint')}</div>
                        </div>

                        <div style={{borderTop:'1px solid var(--border)', margin:'14px 0'}} />

                        <div className="s-share-public">
                          <div style={{fontSize:13, fontWeight:700, marginBottom:8}}>{t('forms_s_share_public')}</div>
                          {shareRow('pub', selected.form_url, !!selected.enabled, t('forms_publish_first'))}
                        </div>
                      </div>

                      {settingsMsg && (
                        <div style={{fontSize:12, marginTop:12, color:'var(--text-muted)'}}>{settingsMsg}</div>
                      )}

                      <button className="btn-primary" style={{width:'auto', padding:'9px 20px', marginTop:16}}
                              onClick={saveSettings} disabled={settingsBusy || settings.form_name.trim().length < 4}>
                        {settingsBusy ? t('btn_saving') : t('btn_save')}
                      </button>
                      {settings.form_name.trim().length < 4 && (
                        <div style={{fontSize:11, color:'var(--text-muted)', marginTop:6}}>{t('forms_s_name_hint')}</div>
                      )}
                    </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Modals ── */}
        {modal && (
          <>
            <div onClick={() => { if (!busy) { setModal(null); clearTimeout(aiPollRef.current); setAiStage(''); } }}
                 style={{position:'fixed', inset:0, zIndex:3999, background:'rgba(0,0,0,0.6)'}} />
            <div style={{position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:4000,
                         background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:14,
                         boxShadow:'0 20px 60px rgba(0,0,0,0.35)', padding:28, width: modal === 'ai' ? 600 : 500, maxWidth:'95vw'}}>

              {modal === 'delete' ? (
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:32, marginBottom:12}}>🗑️</div>
                  <div style={{fontWeight:700, fontSize:16, marginBottom:8}}>{t('forms_delete_confirm')}</div>
                  <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:20}}>
                    {t('forms_delete_warn')}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{fontWeight:700, fontSize:20, marginBottom:6}}>
                    {modal === 'new' ? t('forms_new') : modal === 'template' ? t('forms_from_template')
                     : modal === 'copy' ? t('forms_copy') : (modal === 'ai' ? '✨ ' : '') + t('forms_ai_build')}
                  </div>
                  <div style={{fontSize:13.5, color:'var(--text-muted)', marginBottom:18, lineHeight:1.5}}>
                    {modal === 'template' ? t('forms_tpl_help') : modal === 'ai' ? t('forms_ai_help') : modal === 'new' ? t('forms_new_help') : ''}
                  </div>

                  {modal === 'ai' && (
                    <label style={{fontSize:12, display:'block', marginBottom:12}}>
                      <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_ai_describe')}</div>
                      <textarea rows={4} value={aiText} onChange={e => setAiText(e.target.value)} className="forms-ai-text"
                                placeholder={t('forms_ai_placeholder')} disabled={busy}
                                style={{...inputSt, fontFamily:'inherit', fontSize:14.5, padding:'12px 14px', resize:'vertical', ...(busy ? lockedSt : {})}} />
                      <div style={{fontSize:13, fontWeight:600, color:'var(--text-muted)', margin:'12px 0 8px'}}>{t('forms_ai_examples')}</div>
                      <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
                        {['forms_ai_ex1', 'forms_ai_ex2', 'forms_ai_ex3'].map(k => (
                          <button key={k} type="button" className="fm-chip forms-ai-ex" disabled={busy} onClick={() => setAiText(t(k))} style={{height:34, borderRadius:17, fontWeight:500}}>{t(k)}</button>
                        ))}
                      </div>
                    </label>
                  )}

                  {modal === 'template' && (
                    <label style={{fontSize:12, display:'block', marginBottom:12}}>
                      <div style={{color:'var(--text-muted)', marginBottom:4}}>{t('forms_template')}</div>
                      <select value={templateId} onChange={e => setTemplateId(e.target.value)} disabled={busy}
                              style={{...inputSt, ...(busy ? lockedSt : {})}}>
                        {!templates.length && <option value="">{t('forms_no_templates')}</option>}
                        {templates.map(tp => (
                          <option key={tp.form_template_id} value={tp.form_template_id}>{tp.template_name}</option>
                        ))}
                      </select>
                    </label>
                  )}

                  {modal === 'copy' && (
                    <div style={{fontSize:12, color:'var(--text-muted)', marginBottom:10}}>
                      {t('forms_copy_hint')}
                    </div>
                  )}
                  <label style={{fontSize:12, display:'block'}}>
                    <div style={{color:'var(--text-muted)', marginBottom:4}}>{modal === 'copy' ? t('forms_copy_name') : t('forms_s_name')}</div>
                    <input value={newName} onChange={e => setNewName(e.target.value)} disabled={busy}
                           placeholder={modal === 'ai' ? t('forms_ai_name_ph') : ''} style={{...inputSt, height:40, fontSize:14.5, ...(busy ? lockedSt : {})}} />
                  </label>
                  {modal === 'ai' && <div style={{fontSize:12.5, color:'var(--text-muted)', marginTop:6}}>{t('forms_ai_name_help')}</div>}
                  {(modal === 'template' || modal === 'ai' || modal === 'new') && (
                    <div style={{display:'flex', gap:8, alignItems:'center', fontSize:13, color:'var(--text-muted)', marginTop:14, padding:'10px 12px', borderRadius:8, background:'var(--input-bg)'}}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flex:'none'}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      {t('forms_draft_note')}
                    </div>
                  )}

                  {aiStage && (
                    <div style={{fontSize:12, color:'var(--text-muted)', marginTop:12}}>
                      {aiStage === 'queued' ? t('forms_ai_waiting') : t('forms_ai_creating')}
                    </div>
                  )}
                </>
              )}

              {modalError && (
                <div style={{fontSize:12, color:'var(--red)', background:'rgba(239,68,68,0.08)',
                             border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, padding:'8px 12px', marginTop:12}}>
                  {modalError}
                </div>
              )}

              <div style={{display:'flex', justifyContent:'flex-end', gap:10, marginTop:22}}>
                <button type="button" className="fm-btn" disabled={busy}
                        onClick={() => { setModal(null); clearTimeout(aiPollRef.current); setAiStage(''); }}>
                  {t('btn_cancel')}
                </button>
                {modal === 'delete' ? (
                  <button onClick={deleteForm} disabled={busy}
                          style={{padding:'8px 18px', borderRadius:8, border:'none', background:'#dc2626',
                                  color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:busy?0.5:1}}>
                    {busy ? t('btn_deleting') : t('btn_delete')}
                  </button>
                ) : (
                  <button onClick={modal === 'ai' ? startAiBuild : modal === 'copy' ? copyForm : createForm}
                          disabled={busy || (modal === 'ai' ? aiText.trim().length < 5 : newName.trim().length < 3)
                                        || (modal === 'template' && !templateId)}
                          className="fm-btn primary forms-modal-go">
                    {busy ? (modal === 'ai' ? <>{t('forms_ai_building')}<RunningDots /></> : t('btn_saving'))
                      : modal === 'ai' ? '✨ ' + t('forms_ai_generate') : modal === 'copy' ? t('forms_create') : t('forms_create_draft')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
  return function Forms({ activePage, onNavSelect, onLogout }) {
    return (
      <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
        <div className="fm-page"><CfgFormsPage /></div>
      </AppShell>
    );
  };
})();

;
/* ── src/pages/Billing.jsx ── */
// Configuration > Billing — placeholder screen. The account's plan and invoices are
// available from app_api.account_billing when this is built out.
var Billing = function({ activePage, onNavSelect, onLogout }) {
  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>{t('billing_title')}</h2>
          <p>{t('billing_subtitle')}</p>
        </div>
      </div>
      <div className="app-empty-state">
        <p className="empty-hint">{t('billing_coming_soon')}</p>
      </div>
    </AppShell>
  );
};

;
/* ── src/pages/Preferences.jsx ── */
// Configuration › Preferences — settings for the whole account, the same screen concept as
// thermotrack.io's Preferences page.
//   list   preferences_list   every preference with its current value (ops.preference_types)
//   save   preference_set     one value, checked against its type on the server
// Every change saves at once and a ✓ confirms it. The cache in src/constants.js
// (cachePrefs / prefBool) is rewritten too, so the menu and the chat follow immediately.
var PREF_TEXT = {
  pref_chat_enabled:        'Chat',
  pref_chat_enabled_desc:   'Messages between the portal and your operators’ phones, with photos and acknowledgements. When off, the Messages button and the chat in the mobile app are hidden.',
  pref_forms_enabled:       'Forms',
  pref_forms_enabled_desc:  'Build the forms your operators and customers fill in. When off, the Forms menu is hidden here and in the mobile app.',
  pref_leave_enabled:       'Leave',
  pref_leave_enabled_desc:  'Operators apply for leave in the mobile app and you see it under Leave. When off, the Leave menu is hidden here and in the mobile app.',
  pref_claims_enabled:      'Claims',
  pref_claims_enabled_desc: 'Operators submit expense claims from the mobile app. When off, the Claims menu is hidden here and in the mobile app.',
  pref_operators_create_tasks:      'Operators create tasks',
  pref_operators_create_tasks_desc: 'Lets operators add tasks themselves in the mobile app, for ad-hoc work they do that is not part of a planned job. Off by default.',
  pref_group_ops:           'Operations',
  pref_group_ops_sub:       'Working hours and other values the account runs on.',
  pref_group_features:      'Features',
  pref_group_features_sub:  'Switch parts of OPS Assist on or off for the whole account.',
  pref_group_forms:         'Forms',
  pref_group_forms_sub:     'Which of your forms are used for particular jobs.',
  pref_incident_form:       'Incident Form',
  pref_incident_form_desc:  'The form operators fill in to report an incident. Pick any of your forms, or None.',
};

// On/off settings that are about how the account works rather than a feature, so they sit
// under Operations.
var PREF_OPS_SWITCHES = ['pref_operators_create_tasks'];

var Preferences = function({ activePage, onNavSelect, onLogout }) {
  var { useState, useEffect, useRef } = React;
  var [prefs, setPrefs]     = useState([]);
  var [draft, setDraft]     = useState({});    // type_id -> value being edited
  var [bad, setBad]         = useState({});    // type_id -> true when the server refused it
  var [savedId, setSavedId] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError]     = useState('');
  var [filter, setFilter]   = useState('');
  var timers = useRef({});
  // The saved values as of the last answer from the server. Saves finish in any order, so each
  // one updates this rather than the list its row was rendered with, or quick toggles undo each other.
  var savedRef = useRef([]);

  useEffect(function() { return function() { Object.values(timers.current).forEach(clearTimeout); }; }, []);

  var load = function() {
    setLoading(true); setError('');
    callRpc('preferences_list', {})
      .then(function(list) {
        list = Array.isArray(list) ? list : [];
        savedRef.current = list;
        setPrefs(list);
        setDraft(list.reduce(function(m, p) { m[p.type_id] = p.preference_value == null ? '' : p.preference_value; return m; }, {}));
        cachePrefs(list);
      })
      .catch(function(e) { setError(e.message || 'Could not load the preferences.'); })
      .finally(function() { setLoading(false); });
  };
  useEffect(load, []);
  // The account's forms, for FORM preferences such as Incident Form.
  var [formList, setFormList] = useState(null);
  useEffect(function() { callRpc('form_get_list', {}).then(function(l) { setFormList(Array.isArray(l) ? l : []); }).catch(function() { setFormList([]); }); }, []);

  // One preference's saves go out one after another, so the server ends on the last click.
  var queues = useRef({});
  var sent   = useRef({});   // type_id -> the last value sent
  var save = function(p, value) {
    sent.current[p.type_id] = value;
    var run = function() { return callRpc('preference_set', { type_id: p.type_id, preference_value: value }); };
    var q = queues.current[p.type_id] = (queues.current[p.type_id] || Promise.resolve()).then(run, run);
    q.then(function(res) {
        var r = Array.isArray(res) ? res[0] : res;
        if (!r || r.result !== 0) { setBad(function(b) { return Object.assign({}, b, { [p.type_id]: true }); }); return; }
        var next = savedRef.current.map(function(x) { return x.type_id === p.type_id ? Object.assign({}, x, { preference_value: value }) : x; });
        savedRef.current = next;
        setPrefs(next);
        cachePrefs(next);
        setBad(function(b) { return Object.assign({}, b, { [p.type_id]: false }); });
        setSavedId(p.type_id);
        setTimeout(function() { setSavedId(function(s) { return s === p.type_id ? null : s; }); }, 1800);
      })
      .catch(function() { setBad(function(b) { return Object.assign({}, b, { [p.type_id]: true }); }); });
  };

  // Switches save at once; typed values after a pause, so a keystroke is not a request.
  var change = function(p, value, immediate) {
    setDraft(function(d) { return Object.assign({}, d, { [p.type_id]: value }); });
    clearTimeout(timers.current[p.type_id]);
    var saved = savedRef.current.filter(function(x) { return x.type_id === p.type_id; })[0] || p;
    var last  = p.type_id in sent.current ? sent.current[p.type_id] : saved.preference_value;
    if (String(value).trim() === String(last == null ? '' : last)) return;
    if (immediate) save(p, String(value).trim());
    else timers.current[p.type_id] = setTimeout(function() { save(p, String(value).trim()); }, 600);
  };

  var text = function(key, fallback) { return (key && PREF_TEXT[key]) || fallback || ''; };
  var nameOf = function(p) { return text(p.label_key, p.preference_name); };
  var descOf = function(p) { return text(p.label_key && p.label_key + '_desc'); };
  var typeOf = function(p) { return String(p.preference_type || 'TEXT').toUpperCase(); };
  // Switches are Features (parts of the app on or off) unless listed in PREF_OPS_SWITCHES;
  // every other value is Operations.
  var groupOf = function(p) { if (typeOf(p) === 'FORM') return 'forms'; return typeOf(p) !== 'BOOLEAN' || PREF_OPS_SWITCHES.indexOf(p.label_key) !== -1 ? 'ops' : 'features'; };

  var q = filter.trim().toLowerCase();
  var shown = prefs.filter(function(p) { return !q || nameOf(p).toLowerCase().indexOf(q) !== -1 || descOf(p).toLowerCase().indexOf(q) !== -1; });
  var groups = [
    ['features', shown.filter(function(p) { return groupOf(p) === 'features'; })],
    ['ops',      shown.filter(function(p) { return groupOf(p) === 'ops'; })],
    ['forms',    shown.filter(function(p) { return groupOf(p) === 'forms'; })],
  ];

  var editor = function(p) {
    var val = draft[p.type_id] == null ? '' : draft[p.type_id];
    var type = typeOf(p);
    if (type === 'BOOLEAN') {
      var on = String(val).toLowerCase() === 'true';
      return (
        <button type="button" role="switch" aria-checked={on} aria-label={nameOf(p)} className={'pref-switch' + (on ? ' pref-switch--on' : '')}
                onClick={function() { change(p, String(!on), true); }}>
          <span className="pref-switch-track"><span className="pref-switch-knob" /></span>
          {on ? 'On' : 'Off'}
        </button>
      );
    }
    if (type === 'FORM') {   // one of the account's forms, saved on change
      var known = !val || (formList || []).some(function(fm) { return fm.form_id === val; });
      return (
        <select className={'form-input pref-input pref-select' + (bad[p.type_id] ? ' pref-input--bad' : '')} value={val}
                onChange={function(e) { change(p, e.target.value, true); }} disabled={formList === null}>
          <option value="">{formList === null ? 'Loading forms…' : 'None'}</option>
          {(formList || []).map(function(fm) { return <option key={fm.form_id} value={fm.form_id}>{fm.form_name}{fm.enabled === false ? ' (off)' : ''}</option>; })}
          {!known && <option value={val}>{'Form no longer available'}</option>}
        </select>
      );
    }
    var inputType = type === 'NUMBER' ? 'number' : type === 'DATE' ? 'date' : 'text';
    return <input className={'form-input pref-input' + (bad[p.type_id] ? ' pref-input--bad' : '')} type={inputType} value={val}
                  placeholder={type === 'TIME' ? 'HH:MM' : ''} onChange={function(e) { change(p, e.target.value, type === 'DATE'); }} />;
  };

  return (
    <AppShell activePage={activePage} onNavSelect={onNavSelect} onLogout={onLogout}>
      <div className="lv-header">
        <div className="lv-header-left">
          <h2>Preferences</h2>
          <p>Account-wide settings shared by everyone in this account</p>
        </div>
        <div className="lv-header-right">
          <button className="btn-export" onClick={load} disabled={loading}>Refresh</button>
        </div>
      </div>

      <div className="pref-scope">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        <div>
          <div className="pref-scope-title">These settings apply to everyone in this account</div>
          <div className="pref-scope-sub">Your own language and timezone are on the Profile page.</div>
        </div>
      </div>

      <div className="lv-toolbar">
        <div className="lv-search">
          <span className="lv-search-icon"><SearchIcon /></span>
          <input className="lv-search-input" placeholder="Search preferences" value={filter} onChange={function(e) { setFilter(e.target.value); }} />
          {filter && <button className="lv-search-clear" title="Clear search" onClick={function() { setFilter(''); }}>&times;</button>}
        </div>
        {error && <span className="pf-err">{error}</span>}
      </div>

      {loading && <div className="pref-empty">Loading…</div>}
      {!loading && !error && shown.length === 0 && <div className="pref-empty">{q ? 'No preference matches.' : 'No preferences yet.'}</div>}
      {!loading && groups.map(function(g) {
        var key = g[0], list = g[1];
        if (!list.length) return null;
        return (
          <div key={key} className="pf-card pref-group">
            <div className="pref-group-head">
              <div className="pf-card-title">{text('pref_group_' + key)}</div>
              <div className="pf-card-sub">{text('pref_group_' + key + '_sub')}</div>
            </div>
            {list.map(function(p) {
              return (
                <div key={p.type_id} className="pref-row">
                  <div className="pref-row-main">
                    <div className="pref-row-name">{nameOf(p)}</div>
                    {descOf(p) && <div className="pref-row-desc">{descOf(p)}</div>}
                    {typeOf(p) !== 'BOOLEAN' && p.preference_default != null && p.preference_default !== '' &&
                      <div className="pref-row-desc">Default: {p.preference_default}</div>}
                  </div>
                  <div className="pref-row-value">
                    {editor(p)}
                    <span className={'pref-saved' + (savedId === p.type_id ? ' pref-saved--on' : '')} aria-live="polite">&#10003;</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {!loading && prefs.length > 0 && <p className="pf-note">Changes are saved as you make them; a tick confirms each one.</p>}
    </AppShell>
  );
};

;
/* ── src/App.jsx ── */
var ROUTED_PAGES = ['dashboard', 'maps', 'jobs', 'tasks', 'forms', 'operators', 'leaves', 'claims', 'customers', 'gallery', 'profile', 'integrations', 'billing', 'preferences', 'exec_reports', 'data_export', 'form_entries'];

// The last menu item picked, so a reload or the next sign-in opens the same place.
var NAV_STORAGE_KEY = 'nav_page';
var savedNav  = function() { return localStorage.getItem(NAV_STORAGE_KEY) || 'home'; };
var pageForNav = function(id) { return ROUTED_PAGES.indexOf(id) !== -1 ? id : 'home'; };

var App = function() {
  var { useState, useEffect } = React;
  // A saved, unexpired JWT skips the login page and reopens the last menu item.
  var [page, setPage]             = useState(function() { return hasValidSession() ? pageForNav(savedNav()) : 'login'; });
  var [activePage, setActivePage] = useState(savedNav);
  // Opening the app with a saved token: login_completed runs first, before any page loads
  // its data (a fresh sign-in calls it in Login / Signup before onLogin).
  var [booted, setBooted] = useState(function() { return !hasValidSession(); });
  useEffect(function() {
    if (booted) return;
    callLoginCompleted(localStorage.getItem('jwt_token'))
      .catch(function() {})   // the session check below deals with a token the server refuses
      .finally(function() { setBooted(true); });
  }, []);

  var handleNavSelect = function(id) {
    localStorage.setItem(NAV_STORAGE_KEY, id);
    setActivePage(id);
    setPage(pageForNav(id));
  };

  var handleLogin = function() {
    var nav = savedNav();
    setPage(pageForNav(nav));
    setActivePage(nav);
  };

  var handleLogout = function() {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    var nav = savedNav();
    localStorage.clear();
    localStorage.setItem(NAV_STORAGE_KEY, nav); // a UI preference, not session data
    setPage('login');
    setActivePage(nav);
  };

  // Back to login when the token expires (checked each minute and when the tab
  // comes back into view — setTimeout can't wait out a months-long expiry) or
  // when the API rejects it (callRpc raises SESSION_EXPIRED_EVENT on a 401).
  var signedIn = page !== 'login' && page !== 'signup';
  useEffect(function() {
    if (!signedIn) return;
    var check = function() { if (!hasValidSession()) handleLogout(); };
    var onVisible = function() { if (document.visibilityState === 'visible') check(); };
    var timer = setInterval(check, 60 * 1000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleLogout);
    return function() {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleLogout);
    };
  }, [signedIn]);

  // The account's preferences (Configuration › Preferences), for the menu, chat and router.
  usePrefs();
  useEffect(function() { if (signedIn && booted) loadPrefs(); }, [signedIn, booted]);
  if (!booted) return <div className="app-booting">{t('common_loading')}</div>;

  var pageProps ={ activePage: activePage, onNavSelect: handleNavSelect, onLogout: handleLogout };

  if (page === 'login')     return <Login onLogin={handleLogin} onSignup={function() { setPage('signup'); }} />;
  if (page === 'signup')    return <Signup onSignin={function() { setPage('login'); }} onSignedUp={handleLogin} />;
  if (page === 'dashboard') return <Dashboard {...pageProps} />;
  if (page === 'maps')      return <Maps      {...pageProps} />;
  if (page === 'exec_reports') return <ExecReports {...pageProps} />;
  // Data Export and Form Entries: shown once their page files are loaded
  if (page === 'data_export'  && typeof DataExport  !== 'undefined') return <DataExport  {...pageProps} />;
  if (page === 'form_entries' && typeof FormEntries !== 'undefined') return <FormEntries {...pageProps} />;
  if (page === 'jobs')      return <Jobs      {...pageProps} />;
  if (page === 'tasks')     return <Tasks     {...pageProps} />;
  if (page === 'forms' && prefBool('Forms Enabled', true)) return <Forms {...pageProps} />;
  if (page === 'operators') return <Operators {...pageProps} />;
  if (page === 'leaves' && prefBool('Leave Enabled', true)) return <LeavePage {...pageProps} />;
  if (page === 'claims' && prefBool('Claims Enabled', true)) return <ClaimsPage {...pageProps} />;
  if (page === 'customers') return <Customers {...pageProps} />;
  if (page === 'gallery')   return <Gallery   {...pageProps} />;
  if (page === 'profile')   return <Profile   {...pageProps} />;
  if (page === 'integrations') return <Integrations {...pageProps} />;
  if (page === 'billing')   return <Billing   {...pageProps} />;
  if (page === 'preferences') return <Preferences {...pageProps} />;
  return <Home {...pageProps} />;
};

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
